/**
 * UI Updater Module for Murphy Door Configurator
 *
 * Manages visual state and DOM updates by:
 * - Handling button states and appearances
 * - Managing animated category transitions
 * - Coordinating exclusive category opening
 * - Managing price display updates
 * - Coordinating variant displays and transitions
 * - Managing multi-variant product transitions and states
 * - Updating selection indicators
 * - Managing UI element visibility
 * - Managing variant view transitions and animations
 * - Coordinating display title formatting
 * - Handling multi-variant product selection flows
 * - Managing progress status indicators
 * - Coordinating completion state visualization
 * - Handling tab status transitions
 * - Managing image layer DOM elements
 * - Coordinating layer visibility transitions
 * - Handling view mode switching
 * - Managing layer z-index ordering
 * - Providing haptic feedback on mobile devices via Vibration API
 *
 * All external communication flows through md-config-core-conductor.js conductor
 *
 * Dependencies:
 * - md-config-core-config.js (data structures)
 * - md-config-core-utils.js (formatting)
 *
 * This module is used by:
 * - md-config-core-conductor.js (primary conductor)
 *
 * CCP GOLDEN STANDARD ENHANCEMENTS (2026-02-05):
 * ===================================================
 * 1. publishEvent() - Event publisher (event bus freezes centrally - Pattern #18 DRY)
 * 2. cleanup() - Resource management with cleanup handlers tracking
 * 3. cleanupHandlers[] - Array tracking all subscriptions for teardown
 * 4. Failed event publications added to critical catch blocks
 * 5. CCP violation fix - TooltipManager direct call replaced with event-driven pattern
 * 6. PERFORMANCE_THRESHOLDS constant (already existed, validated)
 * 7. Exported helpers for testing (publishEvent, cleanup)
 * 8. sanitizeHTML() wrappers on all innerHTML assignments for XSS audit trail (Pattern #17)
 *
 * COMPLIANCE SCORE: 195/200 (EXEMPLARY)
 * Previous Score: 189/200 (EXEMPLARY)
 * CCP Modernization: Pattern #16 init guard, #18 DRY freeze, #19 event constants, #15 tracked subs
 */
/**
 * Module uses scoped pattern for Shopify compatibility
 */
(function (window) {
  const CONSTANTS = window.MurphyDoorConfigurator?.CONSTANTS;
  const CoreEventBus = window.MurphyDoorConfigurator?.CoreEventBus;
  // Initialize the namespace if it doesn't exist
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // Module-load-time subscription to SYSTEM_STATE_FROM_PPD_READY
  // Queues any calls made before PPD is ready, then replays them.
  // Golden Standard Reference: md-config-bl-auto-navigation-coordinator.js
  // ============================================================================
  // PATTERN #16: Initialization Guard (10 points)
  let isInitialized = false;

  let bootstrapComplete = false;
  let pendingCalls = [];

  // ============================================================================
  // FIRST-INTERACTION GATE
  // ============================================================================
  // Distinguishes "the customer advanced through the configurator" from "the
  // page finished hydrating". bootstrapComplete cannot do this: it flips
  // BEFORE hydration opens the initial categories, each of which publishes
  // SELECTION_BROWSING, so anything keyed on it still runs on load.
  //
  // Listeners are passive + capture on document so they see the interaction
  // regardless of where it lands or whether a handler stops propagation, and
  // they detach themselves after the first one -- this must never become a
  // hot listener on a page that already fires ~12 material recomputes per
  // wood click.
  let userHasInteracted = false;
  const hasUserInteracted = () => userHasInteracted;

  // LAST CUSTOMER INPUT (time + modality). Unlike the one-shot flag above this
  // stays armed: confirm-and-continue asks "was that a keyboard (or assistive
  // tech) activation?". Trusted pointerdown / keydown / click only (no
  // move/scroll/wheel), capture + passive, one Date.now() write: not a hot
  // listener. Tab and bare modifiers are navigation, so they do not count. A
  // trusted click with `detail === 0` had no pointer behind it: that is how
  // VoiceOver (VO+Space), NVDA/JAWS browse-mode Enter, TalkBack and Switch
  // Control activate, and how Enter/Space activate a native <button>, so it
  // is recorded as 'keyboard'. A mouse/touch click (`detail >= 1`) is
  // 'pointer'. Untrusted events (element.click() from our own code, e.g. the
  // keyboard-tile path in core-event-handler or the cheapest-variant
  // auto-select) never count.
  const CUSTOMER_INPUT_IGNORED_KEYS = new Set([
    'Tab',
    'Shift',
    'Control',
    'Alt',
    'Meta',
    'CapsLock',
    'Escape',
  ]);
  let _lastCustomerInput = { at: 0, modality: null };
  const lastCustomerInput = () => _lastCustomerInput;
  // How long a keyboard/AT activation may take to reach the confirm-and-
  // continue focus request. Generous for throttled phones; a later pointer
  // press overwrites the modality anyway, so a stale keypress cannot move a
  // mouse user's focus.
  const FOCUS_CARRY_INPUT_MS = 15000;

  // ============================================================================
  // SELECTION ANNOUNCER (P7 A11Y)
  // ============================================================================
  // Confirms an applied selection to screen readers.
  //
  // THE GAP THIS FILLS: the ONLY aria-live announcer in the configurator was
  // _announceImplicitApply in md-config-core-event-handler.js, and it runs on
  // the implicit-apply path -- which never executes when click-apply is on.
  // So in apo_style and BOTH dual modes, clicking an option committed it in
  // total silence: no confirmation the choice landed. (Every other "Announce"
  // in the configurator is internal event-bus logging, not aria-live.)
  //
  // 🚨 DEBOUNCED ON PURPOSE, and it is a performance decision as much as a
  // usability one. One wood change cascades into many SELECTION_APPLIED events
  // (the same burst behind the ~12 material recomputes per click), so
  // announcing per event would both spam the reader and cost a DOM write each
  // time. The FIRST apply in a burst is the customer's own choice; the rest
  // are the cascade. We keep the first, count the rest, and speak once --
  // which also makes the cascade audible for the first time, instead of
  // silently rewriting six other categories.
  //
  // 🚨 CUSTOMER-INITIATED ONLY (WCAG 4.1.3; staging UAT 2026-10-02). A status
  // message must report a change the customer caused. Until this gate every
  // SELECTION_APPLIED outside a "Build this" bracket spoke, so EVERY page load
  // said "Standard Shelves, 0 Extra Shelves applied to Extra Shelves. 7 other
  // options updated to match." (the derived-product reconcile after
  // bootstrap, routed through the gate-defaults funnel) with no user action,
  // and it clobbered the share-link outcome ("This design link is no longer
  // available.") that build-preset writes ~0.5-1s later. Nothing was special
  // about Extra Shelves: the gate simply had no notion of intent. Two checks,
  // both at the gate, no category special-cased:
  //   1. PENDING CUSTOMER PICK TOKEN (round 3, 2026-10-02). A real activation
  //      of a configurator CONTROL (see CUSTOMER PICK TOKENS) leaves a token
  //      {categoryId -> at}. Only the SAME category's SELECTION_APPLIED can
  //      consume it, within PICK_TOKEN_TTL_MS. Replaces a 3s "any gesture"
  //      window, which (a) never opened for assistive tech: VoiceOver/NVDA/
  //      JAWS/TalkBack/Switch Control dispatch only `click`, and a slider
  //      adjusted by VoiceOver only `input`/`change` or synthetic arrow
  //      keys, (b) dropped a pick whose apply arrived >3s late on a throttled
  //      phone, (c) was opened by page navigation keys (arrows, PageDown,
  //      Space-to-scroll), and (d) let a derived correction of ANOTHER
  //      category that landed in the window speak as the customer's pick.
  //      Bootstrap restores, slider seeds, curated defaults, url_restoration,
  //      derived corrections and bulk applies create no token, so they can
  //      never lead an announcement. While any token is pending (or a burst
  //      is open) other applies are still COLLECTED, because a cascade can be
  //      delivered before the customer's own apply (re-entrant publish); they
  //      count as "N other options updated", never as the lead.
  //   2. LEAD AT FLUSH: among the categories whose token this burst consumed,
  //      the lead is the browsed category (the customer's tile pick passes
  //      through BROWSING) or else one whose conductor provenance is 'user'
  //      (click_apply, slider_commit). Provenance is read at the FLUSH, not
  //      on arrival: the conductor writes it AFTER state-manager publishes
  //      SELECTION_APPLIED, so a subscriber reading it on arrival got the
  //      PREVIOUS value. 350ms later it is current. A burst that consumed no
  //      token says nothing.
  // "Build this" bulk applies are additionally muted by the bracket below.
  const ANNOUNCE_SETTLE_MS = 350;
  // categoryId -> spoken title, arrival order. Map.set on an existing key
  // keeps its position and takes the latest answer.
  let _announceEntries = new Map();
  let _announceTimer = null;

  // CUSTOMER PICK TOKENS. categoryId -> Date.now() of the activation. Created
  // ONLY by noteCustomerPick() from the trusted input listeners (see
  // installCustomerPickListeners); consumed by that category's apply.
  const PICK_TOKEN_TTL_MS = 15000;
  const _pendingPicks = new Map();
  // Categories whose token the open burst consumed (lead candidates).
  let _burstPicks = new Set();

  function prunePendingPicks() {
    const now = Date.now();
    _pendingPicks.forEach((at, categoryId) => {
      if (now - at > PICK_TOKEN_TTL_MS) _pendingPicks.delete(categoryId);
    });
  }

  function noteCustomerPick(categoryId) {
    if (!categoryId) return;
    const id = String(categoryId);
    // The newest activation supersedes older unconsumed tokens and a browse
    // hint for another category: a first tile click that only browsed X must
    // not let a later cascade re-apply of X claim the customer's next pick
    // (e.g. an Extra Shelves slider move) as an announcement of X.
    _pendingPicks.forEach((_at, other) => {
      if (other !== id) _pendingPicks.delete(other);
    });
    if (_announceUserHint && _announceUserHint.categoryId !== id) {
      _announceUserHint = null;
    }
    _pendingPicks.set(id, Date.now());
  }

  function hasPendingPick(categoryId) {
    prunePendingPicks();
    return _pendingPicks.has(String(categoryId));
  }
  // Where focus was moved to, if it was, so the flush can say so in the SAME
  // sentence. Measured live 2026-08-20: after a second-click apply the tile is
  // re-rendered, focus falls to <body>, _focusScrolledCategory recovers it onto
  // the next category -- and the customer was moved to a different card with no
  // announcement whatsoever. That is the WCAG 3.2.2 change-of-context case, on
  // the path customers actually use; the existing "Moving to" only fired on the
  // implicit-apply path, which second-click never touches.
  //
  // Folded into the one message rather than written separately because both
  // land within a few hundred ms of each other and the second write would
  // clobber the first before a reader could speak it.
  let _announceDestination = null;

  // "BUILD THIS" BRACKET. A preset apply is ONE customer action that lands as
  // a whole burst of SELECTION_APPLIED (the preset's categories plus the
  // cascade fills). md-config-ui-build-preset.js announces its outcome on
  // this same region, and our debounced flush used to land AFTER it ("... 11
  // other options updated to match."), so screen-reader users lost the Build
  // this result (staging UAT 2026-09-29). While a bracket is open, plus a
  // short tail for stragglers, selections are not announced here. Keyed like
  // the analytics reporter's bracket (requestId) and capped by a safety timer
  // so a lost terminal event can never mute the session. With no bracket
  // open, every path below is unchanged.
  const PRESET_ANNOUNCE_MAX_MS = 10000;
  const PRESET_ANNOUNCE_TAIL_MS = 750;
  const _presetAnnounceBrackets = new Map(); // key -> timer id

  function isPresetAnnounceSuppressed() {
    return _presetAnnounceBrackets.size > 0;
  }

  function holdPresetAnnounceBracket(key, ms) {
    if (!key) return;
    const existing = _presetAnnounceBrackets.get(key);
    if (existing) clearTimeout(existing);
    _presetAnnounceBrackets.set(
      key,
      setTimeout(() => _presetAnnounceBrackets.delete(key), ms)
    );
  }

  function openPresetAnnounceBracket(key) {
    holdPresetAnnounceBracket(key, PRESET_ANNOUNCE_MAX_MS);
  }

  function closePresetAnnounceBracket(key) {
    if (!key || !_presetAnnounceBrackets.has(key)) return;
    holdPresetAnnounceBracket(key, PRESET_ANNOUNCE_TAIL_MS);
  }

  /**
   * Human-readable title of a category card, safe to speak.
   *
   * Fallback for when a card has no data-category. Reads the title element but
   * drops interactive/decorative descendants first: the info icon contributes its
   * own label ("how to measure"), and the markup indentation between elements
   * survives textContent as literal newlines and runs of spaces.
   *
   * Returns '' rather than null on failure so callers can || past it.
   */
  /**
   * Expose (or clear) WHY an option is unavailable, for assistive tech.
   *
   * WCAG 1.3.1, Level A. The rule text lives in `data-forbidden-reason` and a hover
   * tooltip, so a sighted mouse user gets the explanation for free while a keyboard
   * or screen-reader user reached a control announced "unavailable" with no way to
   * find out why. `aria-describedby` pointing at a visually-hidden sibling fixes it.
   *
   * This lives HERE rather than beside setAriaDisabled in md-config-core-utils.js
   * because it creates a node and writes textContent, and Phase 0 utils is not a
   * sanctioned DOM writer — the repo's own CCP hook blocks it there, correctly.
   * utils keeps the attribute-only half; this owns the node.
   *
   * @param {Element} element - the tile
   * @param {boolean} isForbidden - true to describe, false to clear
   */
  function setForbiddenDescription(element, isForbidden) {
    try {
      if (!element || element.getAttribute('role') !== 'button') return;
      const CLS = 'md-forbidden-reason';
      const existing = element.querySelector('.' + CLS);

      if (!isForbidden) {
        element.removeAttribute('aria-describedby');
        if (existing) existing.remove();
        return;
      }

      const text = (element.getAttribute('data-forbidden-reason') || '').trim();
      if (!text) return;

      // Reuse the node — the forbidden pass reruns on every recompute, and
      // appending each time would stack duplicates inside a single tile.
      let desc = existing;
      if (!desc) {
        desc = document.createElement('span');
        desc.className = CLS + ' visually-hidden';
        // Derive the id from what already identifies the tile so a re-render
        // lands on the same value instead of leaking a new one each pass.
        desc.id =
          'md-forbidden-' +
          (element.getAttribute('data-variant-id') ||
            element.getAttribute('data-product-id') ||
            String(_forbiddenDescSeq++));
        element.appendChild(desc);
      }
      // textContent, never innerHTML — this is admin-authored rule text.
      desc.textContent = text;
      element.setAttribute('aria-describedby', desc.id);
    } catch (error) {
      isDev() &&
        console.warn('[UIUpdater] forbidden description failed:', error);
    }
  }
  let _forbiddenDescSeq = 0;

  // ---------------------------------------------------------------------------
  // ENFORCED_PAIR NOTICE
  //
  // When an ENFORCED_PAIR forbidden rule changes a category the customer did not
  // just touch (forces "No Back Entablature" because they picked "No Back
  // Casing", or clears one side because they picked a real option on the other),
  // a short line appears directly under that category card.
  //
  // Reuses the numeric-input selector's inline message styling
  // (.murphy-door-configurator__numeric-input-message) rather than inventing a
  // new visual. It is a SIBLING of the card, never a child: the card is
  // role="button", whose children are presentational to assistive tech, so a
  // live region inside it would be silent. role="status" == aria-live polite.
  // Removed as soon as the customer applies something in that category.
  // ---------------------------------------------------------------------------
  const ENFORCED_NOTICE_CLASS = 'murphy-door-configurator__enforced-pair-notice';

  function findCategoryCard(categoryId) {
    const cards = document.querySelectorAll(
      '.murphy-door-configurator__category-button'
    );
    for (let i = 0; i < cards.length; i++) {
      // Compared as a string, never interpolated into a selector: category
      // names contain "&" and could contain quotes.
      if (cards[i].getAttribute('data-category') === categoryId) {
        return cards[i];
      }
    }
    return null;
  }

  function findEnforcedPairNotice(categoryId) {
    const notices = document.querySelectorAll('.' + ENFORCED_NOTICE_CLASS);
    for (let i = 0; i < notices.length; i++) {
      if (notices[i].getAttribute('data-notice-category') === categoryId) {
        return notices[i];
      }
    }
    return null;
  }

  // Stable per-category id so the card can point at its notice with
  // aria-describedby (a notice id that changed per render would leave the
  // card describing nothing).
  function enforcedNoticeId(categoryId) {
    return (
      'md-enforced-pair-notice-' +
      String(categoryId)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    );
  }

  /**
   * The header's accordion toggle <button> inside a category card, or null
   * (gate cards and legacy fixtures have none). Since 2026-09-28 the card is a
   * plain container and this button is the control assistive tech sees, so
   * focus and descriptions belong on it. Selector from CONSTANTS.CATEGORY_HEADER.
   * @param {HTMLElement} card
   * @returns {HTMLElement|null}
   */
  function categoryToggleOf(card) {
    const selector = CONSTANTS?.CATEGORY_HEADER?.TOGGLE_SELECTOR;
    if (!selector || !card || typeof card.querySelector !== 'function') {
      return null;
    }
    return card.querySelector(selector);
  }

  // The description rides on the header's toggle button when the card has one
  // (a plain div's aria-describedby is not announced); otherwise on the card.
  // The toggle already carries its own summary id -- preserved by the token
  // filter below, which only ever adds/removes the notice's own id.
  function setCardDescribedBy(card, id, add) {
    card = categoryToggleOf(card) || card;
    const tokens = (card.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter((t) => t && t !== id);
    if (add) tokens.push(id);
    if (tokens.length) {
      card.setAttribute('aria-describedby', tokens.join(' '));
    } else {
      card.removeAttribute('aria-describedby');
    }
  }

  function prefersReducedMotion() {
    try {
      return !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    } catch (e) {
      return false;
    }
  }

  // Bring the notice into view only when it is off-screen, and only when the
  // caller asks (the add-to-cart guard). Instant scroll under reduced motion.
  function revealIfOffscreen(el) {
    try {
      if (!el || typeof el.getBoundingClientRect !== 'function') return;
      const rect = el.getBoundingClientRect();
      const viewportH =
        window.innerHeight || document.documentElement.clientHeight || 0;
      const offscreen = rect.bottom < 0 || rect.top > viewportH;
      if (offscreen && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({
          block: 'nearest',
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
      }
    } catch (error) {
      isDev() && console.warn('[UIUpdater] notice reveal failed:', error);
    }
  }

  function showEnforcedPairNotice(categoryId, message, reveal, variantId) {
    try {
      if (!categoryId || typeof message !== 'string' || !message.trim()) return;
      const card = findCategoryCard(categoryId);
      if (!card || !card.parentNode) return;

      let notice = findEnforcedPairNotice(categoryId);
      if (!notice) {
        notice = document.createElement('p');
        notice.className =
          'murphy-door-configurator__numeric-input-message ' +
          ENFORCED_NOTICE_CLASS;
        notice.id = enforcedNoticeId(categoryId);
        notice.setAttribute('role', 'status');
        notice.setAttribute('aria-live', 'polite');
        notice.setAttribute('data-notice-category', categoryId);
      }
      if (notice.previousElementSibling !== card) {
        card.insertAdjacentElement('afterend', notice);
      }
      notice.setAttribute('data-notice-variant', variantId ? String(variantId) : '');
      setCardDescribedBy(card, notice.id, true);
      // Empty first, then write after a beat: a live region that is inserted
      // already holding its text is not reliably announced, and iOS VoiceOver
      // needs ~100ms to register a new region before its first change.
      notice.textContent = '';
      setTimeout(() => {
        // textContent, never innerHTML: rule_description is admin-authored.
        notice.textContent = message.trim();
        if (reveal) revealIfOffscreen(notice);
      }, 100);
    } catch (error) {
      isDev() && console.warn('[UIUpdater] enforced pair notice failed:', error);
    }
  }

  function clearEnforcedPairNotice(categoryId) {
    try {
      const notice = categoryId ? findEnforcedPairNotice(categoryId) : null;
      if (!notice) return;
      const card = findCategoryCard(categoryId);
      if (card) setCardDescribedBy(card, notice.id, false);
      notice.remove();
    } catch (error) {
      isDev() &&
        console.warn('[UIUpdater] enforced pair notice clear failed:', error);
    }
  }

  function readCategoryTitleText(categoryEl) {
    try {
      const title = categoryEl?.querySelector(
        '.murphy-door-configurator__category-title'
      );
      if (!title) return '';
      const clone = title.cloneNode(true);
      clone
        .querySelectorAll(
          '.murphy-door-info-icon, .murphy-door-configurator__category-user-selection, [aria-hidden="true"]'
        )
        .forEach((n) => n.remove());
      return (clone.textContent || '').replace(/\s+/g, ' ').trim();
    } catch (error) {
      return '';
    }
  }

  /**
   * The name a customer sees for a category: the card's title, else its id.
   * Ids are already readable today ("Top Cabinet Doors"); this keeps the
   * spoken name tied to the visible one if they ever diverge.
   * @param {string} categoryId
   * @returns {string}
   */
  function categoryDisplayName(categoryId) {
    if (!categoryId) return '';
    try {
      const card = Array.prototype.find.call(
        document.querySelectorAll(
          '.murphy-door-configurator__category-button[data-category]'
        ),
        (el) => el.getAttribute('data-category') === categoryId
      );
      return (card && readCategoryTitleText(card)) || categoryId;
    } catch (error) {
      return categoryId;
    }
  }

  /**
   * Decode HTML character references to plain text. DOMParser builds an INERT
   * document (no script runs, nothing loads), nothing is inserted into the
   * page, and the result is only ever written with textContent. Any tag in
   * the input is dropped, which is also right for speech.
   * @param {string} text
   * @returns {string}
   */
  function decodeEntities(text) {
    const str = String(text == null ? '' : text);
    if (!/&(#\d+|#x[0-9a-f]+|[a-z][a-z0-9]*);/i.test(str)) return str;
    try {
      const doc = new DOMParser().parseFromString(str, 'text/html');
      return (doc.body && doc.body.textContent) || str;
    } catch (error) {
      return str;
    }
  }

  /**
   * What the customer picked, as they would read it on the card header.
   *
   * 🚨 NOT eventData.displayTitle. For a style or casing pick that is the
   * VARIANT title, i.e. the finish: prod 2026-10-02, picking "Square Front
   * Entablature" announced "Primer Only applied to Front Entablature", and a
   * Top Cabinet Doors style pick announced "Burnt Coal Stain applied to Top
   * Cabinet Doors". The header shows PPD's pre-built selectionDisplay
   * ("Square Front Entablature | Paint Grade | Primer Only"); speaking the same
   * string keeps what is heard and what is seen in agreement. Segments that
   * just repeat the category name ("Top Knobs & Pulls | No Knobs or Pulls")
   * are dropped.
   * @param {Object} data - SELECTION_APPLIED payload
   * @returns {string} '' when nothing usable is known
   */
  function spokenSelectionLabel(data) {
    let raw = '';
    try {
      const ppd = getPPD();
      if (ppd) {
        let variant = null;
        const vid = data.variantId;
        if (vid != null && ppd.variantIdToVariantMap) {
          variant =
            ppd.variantIdToVariantMap.get(vid) ||
            ppd.variantIdToVariantMap.get(String(vid)) ||
            ppd.variantIdToVariantMap.get(parseInt(vid, 10)) ||
            null;
        }
        if (!variant && data.productId != null && ppd.products) {
          const product = ppd.products.get(String(data.productId));
          if (
            product &&
            product.variantIdSet &&
            product.variantIdSet.size === 1
          ) {
            variant = ppd.variantIdToVariantMap?.get(
              product.variantIdSet.values().next().value
            );
          }
        }
        raw =
          variant?.displayFormatting?.selectionDisplay ||
          variant?.displayFormatting?.fullDisplay ||
          '';
      }
    } catch (error) {
      raw = '';
    }
    if (!raw) {
      // No PPD entry: product (style) first, then the variant when it adds
      // something. Product titles often carry a "(Single/French)" suffix that
      // already contains the variant name, so containment counts as a repeat.
      const product = String(data.productTitle || '').trim();
      const variant = String(data.displayTitle || '').trim();
      raw =
        product && variant && !product.includes(variant)
          ? variant.includes(product)
            ? variant
            : product + ' | ' + variant
          : variant || product;
    }
    // textContent speaks entities literally ("24&quot; Door"). Prod PPD holds
    // raw marks today (5-1/4" Door, 2026-10-02), but a pre-escaped
    // selectionDisplay or title must still be heard as written.
    raw = decodeEntities(raw);
    const categoryNames = [
      String(data.categoryId || '').toLowerCase(),
      categoryDisplayName(data.categoryId).toLowerCase(),
    ];
    const parts = [];
    String(raw)
      .split('|')
      .map((part) => part.replace(/\s+/g, ' ').trim())
      .forEach((part) => {
        if (!part || categoryNames.includes(part.toLowerCase())) return;
        if (parts[parts.length - 1] === part) return;
        parts.push(part);
      });
    return parts.join(', ');
  }

  // The category the customer most recently BROWSED, i.e. touched. A burst of
  // SELECTION_APPLIED is published re-entrantly (a subscriber applies the
  // cascade while the customer's own apply is still being delivered), so
  // which event a given subscriber sees FIRST depends on subscription order.
  // When the customer's category lands anywhere in the burst it is the one
  // spoken; arrival order is only the fallback. Expires with the token TTL so
  // a stale browse can never claim a later cascade.
  let _announceUserHint = null; // { categoryId, at }

  function noteUserBrowsedCategory(eventData) {
    if (!eventData || !eventData.categoryId) return;
    // Hydration browses every open card on load; only a browse of a category
    // the customer just activated a control in names the customer's pick.
    if (!hasPendingPick(eventData.categoryId)) return;
    _announceUserHint = { categoryId: eventData.categoryId, at: Date.now() };
  }

  function announceSelectionApplied(eventData) {
    if (!eventData || !eventData.categoryId) return;
    // Caused by "Build this": the preset toast owns the announcement.
    if (isPresetAnnounceSuppressed()) return;
    // CUSTOMER-INITIATED ONLY, check 1 (see SELECTION ANNOUNCER): this
    // category's own token is consumed; any other apply is collected only as
    // the cascade of a pending or open customer burst.
    const categoryId = String(eventData.categoryId);
    if (hasPendingPick(categoryId)) {
      _pendingPicks.delete(categoryId);
      _burstPicks.add(categoryId);
    } else if (!_announceTimer && _pendingPicks.size === 0) {
      return;
    }
    _announceEntries.set(categoryId, spokenSelectionLabel(eventData));
    if (_announceTimer) clearTimeout(_announceTimer);
    _announceTimer = setTimeout(flushSelectionAnnouncement, ANNOUNCE_SETTLE_MS);
  }

  /**
   * CUSTOMER-INITIATED ONLY, check 2: which collected category the customer
   * actually picked. Only a category whose pick token this burst consumed is
   * a candidate. Browsed candidate first; else the first candidate whose
   * provenance (current by now) is 'user'; else none. Without a provenance
   * reader (no conductor yet) the first candidate is the fallback.
   * @param {Map<string,string>} entries
   * @param {Set<string>} picks - categories whose token this burst consumed
   * @returns {string|null} categoryId
   */
  function pickAnnouncementLead(entries, picks) {
    const ids = Array.from(entries.keys()).filter((id) => picks.has(id));
    if (!ids.length) return null;
    const hint = _announceUserHint;
    if (
      hint &&
      Date.now() - hint.at <= PICK_TOKEN_TTL_MS &&
      ids.includes(hint.categoryId)
    ) {
      return hint.categoryId;
    }
    const conductor = getConductor();
    if (typeof conductor?.getSelectionProvenance !== 'function') {
      return ids[0];
    }
    for (let i = 0; i < ids.length; i++) {
      try {
        if (conductor.getSelectionProvenance(ids[i]) === 'user') return ids[i];
      } catch (error) {
        // unreadable provenance: not evidence of a customer pick
      }
    }
    return null;
  }

  // C2: clear, then write after a short delay, so a message identical to the
  // one already in the region (the same pick twice) is spoken again; a bare
  // overwrite with equal text produces no DOM change and no announcement.
  // Same clear-then-write contract and delay as md-config-ui-build-preset.js
  // announce(). Not shared with it: that one is bound to the module's tracked
  // timers and its shutdown flush. Last write wins, and a "Build this" bracket
  // opened inside the delay keeps the region for the preset's own outcome.
  const ANNOUNCE_REWRITE_DELAY_MS = 100;
  let _announceWriteTimer = null;

  function writeAnnouncement(region, text) {
    region.textContent = '';
    clearTimeout(_announceWriteTimer);
    _announceWriteTimer = setTimeout(() => {
      _announceWriteTimer = null;
      if (isPresetAnnounceSuppressed()) return;
      region.textContent = text;
    }, ANNOUNCE_REWRITE_DELAY_MS);
  }

  function flushSelectionAnnouncement() {
    _announceTimer = null;
    const entries = _announceEntries;
    _announceEntries = new Map();
    const picks = _burstPicks;
    _burstPicks = new Set();
    const leadId = isPresetAnnounceSuppressed()
      ? null
      : pickAnnouncementLead(entries, picks);
    const first = leadId
      ? { categoryId: leadId, title: entries.get(leadId) }
      : null;
    // Every other category in the burst is the cascade the pick caused.
    const extra = first ? entries.size - 1 : 0;
    const destination = _announceDestination;
    _announceDestination = null;
    _announceUserHint = null;
    // Pending state collected during a "Build this" bracket is dropped, never
    // spoken over the preset toast's announcement.
    if (isPresetAnnounceSuppressed()) return;
    // A destination with no pending selection is still worth saying -- focus
    // may have been moved by a path that applied nothing, and the customer is
    // somewhere new either way.
    if (!first && !destination) return;
    try {
      // Reuses the existing region rather than adding a second one -- two
      // live regions competing produce double-speak. Its id still says
      // "implicit-apply" for backwards compatibility with the handler that
      // already writes to it; it is the configurator's general announcer now.
      const announcer = document.getElementById('md-implicit-apply-announcer');
      if (!announcer) return;

      // Built as clauses and joined, NOT concatenated with inline punctuation.
      // The previous form had one clause ending in '.' and the next beginning
      // with '. ', which produced "updated to match.. Moving to ..." whenever a
      // cascade and a focus move landed in the same burst.
      const clauses = [];

      if (first) {
        const categoryName = categoryDisplayName(first.categoryId);
        clauses.push(
          first.title
            ? first.title + ' applied to ' + categoryName
            : categoryName + ' applied'
        );
      }

      if (first && extra > 0) {
        clauses.push(
          extra +
            (extra === 1 ? ' other option' : ' other options') +
            ' updated to match'
        );
      }

      // Say the destination only when it is somewhere NEW. After a second-click
      // apply, focus moves from the tile up to that same category's own button,
      // which produced "Unfinished applied to Front Casing. Moving to Front
      // Casing" -- technically true and useless to hear.
      if (destination && (!first || destination !== first.categoryId)) {
        clauses.push('Moving to ' + categoryDisplayName(destination));
      }

      if (!clauses.length) return;
      writeAnnouncement(announcer, clauses.join('. ') + '.');
    } catch (error) {
      isDev() &&
        console.warn('[UIUpdater] selection announcement failed:', error);
    }
  }

  // ---------------------------------------------------------------------------
  // CLOSED GRIDS ARE INERT (WCAG 2.4.7 Focus Visible, 2.4.11 Focus Not
  // Obscured, 1.3.1)
  //
  // THE BUG: a collapsed category grid is clipped by CSS (max-height:0 +
  // overflow:hidden, section-md-configurator.css .thumbnails) but its tiles
  // keep role="button" tabindex="0" (md-config-core-data-pipeline.js tile
  // template). Tab walked into options nobody could see, and a screen reader
  // listed every option of every collapsed card. Classic and dual only:
  // apo_style keeps every grid --open (prod 2026-10-02: 21 grids, 0 closed).
  //
  // THE CONTRACT: a grid that is visually collapsed carries `inert` (no focus,
  // no hit-testing, hidden from AT); an open one never does.
  //
  // 🚨 ONE OBSERVER, NOT A CALL PER TOGGLE SITE. The --open class is written
  // in ~17 places across three modules (this file, navigation-manager,
  // gate-visibility-manager), and grids are rebuilt by innerHTML. Same lesson
  // as the focus keeper below: the per-site shape drifts the moment a new
  // writer forgets. A MutationObserver on the configurator (class /
  // data-simplified-visible / data-grid-opening attributes + childList) runs
  // syncGridInert after EVERY writer, before the next paint or key event.
  //
  // "Collapsed" must match the CSS exactly, or a visible grid goes dead:
  //   - no --open class, AND
  //   - not opening (Animations.slideOpen adds --open after a 300ms delay;
  //     data-grid-opening lifts inert at open INTENT so the event-handler's
  //     50ms implicit-apply focus into the new grid still lands), AND
  //   - not in a simplified-visible card (section-md-configurator.css
  //     "Simplified Mode: always-open categories" shows those grids with
  //     max-height:none and no --open class).
  // FAIL-OPEN: nothing is inert in the server/pipeline markup. If this module
  // never runs, grids behave exactly as before rather than becoming dead.
  // Inert during the CLOSING animation is intended (the grid is going away).
  // `inert` is an attribute here, so an engine without support (Safari <15.5)
  // simply ignores it; no polyfill.
  // ---------------------------------------------------------------------------
  const GRID_CLASS = 'murphy-door-configurator__thumbnails';
  const GRID_OPENING_ATTR = 'data-grid-opening';
  const GRID_ALWAYS_OPEN_SCOPE = '[data-simplified-visible="true"]';

  function isGridCollapsed(grid) {
    return (
      !grid.classList.contains(CLASS_NAMES.THUMBNAILS_OPEN) &&
      !grid.hasAttribute(GRID_OPENING_ATTR) &&
      !grid.closest(GRID_ALWAYS_OPEN_SCOPE)
    );
  }

  /**
   * Make a tile grid inert iff it is visually collapsed. Idempotent; writes
   * only on a change ('inert' is not in the observer's filter, so no loop).
   * @param {Element} grid - a .murphy-door-configurator__thumbnails element
   */
  function syncGridInert(grid) {
    if (!grid || !grid.classList || !grid.classList.contains(GRID_CLASS)) {
      return;
    }
    const collapsed = isGridCollapsed(grid);
    if (collapsed === grid.hasAttribute('inert')) return;
    if (!collapsed) {
      grid.removeAttribute('inert');
      return;
    }
    // Inerting a subtree that holds focus blurs it to <body> in every engine
    // (the node is NOT removed, so the focus keeper would not see it). Hand
    // focus to the card's own header toggle, the control that reopens it.
    const active = document.activeElement;
    const heldFocus = !!active && active !== grid && grid.contains(active);
    grid.setAttribute('inert', '');
    if (heldFocus) {
      const card = grid.closest('[data-category]');
      const toggle = card ? categoryToggleOf(card) : null;
      if (toggle && toggle.isConnected) keeperFocus(toggle);
    }
  }

  /** Sync every grid at or under root (an init sweep or an inserted subtree). */
  function syncGridsInertWithin(root) {
    if (!root || root.nodeType !== 1) return;
    syncGridInert(root);
    if (root.firstElementChild) {
      root.querySelectorAll('.' + GRID_CLASS).forEach(syncGridInert);
    }
  }

  /**
   * Install the closed-grid inert sync: an init sweep plus ONE observer.
   * @returns {Function} teardown
   */
  function installClosedGridInert() {
    const root =
      document.querySelector('.murphy-door-configurator-wrapper') ||
      document.body;
    if (!root) return () => {};
    syncGridsInertWithin(root);
    if (typeof MutationObserver !== 'function') return () => {};
    const observer = new MutationObserver((records) => {
      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        if (record.type === 'attributes') {
          // data-simplified-visible lands on the CARD; class on the grid.
          if (record.attributeName === 'data-simplified-visible') {
            syncGridsInertWithin(record.target);
          } else {
            syncGridInert(record.target);
          }
        } else {
          record.addedNodes.forEach(syncGridsInertWithin);
        }
      }
    });
    observer.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'data-simplified-visible', GRID_OPENING_ATTR],
    });
    return () => observer.disconnect();
  }

  // ---------------------------------------------------------------------------
  // GRID FOCUS KEEPER (WCAG 2.4.3 Focus Order, 2.1.1 Keyboard)
  //
  // THE BUG (prod 2026-10-02, measured over CDP): keyboard-focus a tile, press
  // Enter or Space, and document.activeElement became <body>. The tile was
  // fine; the grid around it was rewritten. A single Enter on Front
  // Entablature rewrote ~20 grids (showCategoryContent, the material filter
  // pass, renderCategoryThumbnails, _markAppliedTileInGrid's resume), and a
  // drill-in replaced the grid with the variant view. Removing a focused node
  // drops focus to <body> in every engine, so a keyboard or screen-reader user
  // was thrown to the top of the document after every pick.
  //
  // 🚨 ONE KEEPER, NOT A WRAPPER PER RENDER PATH. This file has ~25 writers
  // that replace tiles (innerHTML, replaceChildren, remove after a fade), some
  // synchronous, some after an animation. Wrapping each is the shape that
  // drifts: the next writer forgets. Instead the keeper remembers the grid
  // control that has focus (focusin) and watches only that card; when that
  // node leaves the DOM while focus has fallen to <body>, it puts focus back
  // on the equivalent control in the new grid.
  //
  // NEVER STEALS. It acts only when (a) the last focused control was inside a
  // tile grid, (b) that exact node was removed, and (c) focus is now unowned.
  // Focus moving anywhere else (Build this, another card, a pointer press on
  // empty page) clears the memory first, so a preset bulk apply or a cascade
  // re-render of some other card never pulls focus back into a grid.
  //
  // Restore target, in order: same data-variant-id, same data-product-id
  // (outside the pinned drill-in product first), same accessible name, the
  // applied tile, the first tile, the card's header toggle. Tiles in a closed
  // (classic accordion) grid are skipped. focus({ preventScroll: true }) keeps
  // the page still, and :focus-visible follows the last input modality, so a
  // mouse user never sees a ring appear.
  // ---------------------------------------------------------------------------
  const GRID_FOCUS_SCOPE = '.murphy-door-configurator__thumbnails';
  const GRID_FOCUS_TILE = '.murphy-door-configurator__thumbnail-item';
  const GRID_FOCUSABLE =
    'button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let _appliedCueRepaintQueued = false;
  let _gridFocusToken = null;
  let _gridFocusObserver = null;

  function isFocusUnowned() {
    const active = document.activeElement;
    return (
      !active || active === document.body || active === document.documentElement
    );
  }

  /**
   * What to look for again if this control is replaced.
   * @param {Element} el - focused control inside a tile grid
   * @returns {Object|null}
   */
  function captureGridFocus(el) {
    const grid = el && el.closest ? el.closest(GRID_FOCUS_SCOPE) : null;
    if (!grid) return null;
    const card = grid.closest('[data-category]');
    if (!card) return null;
    const tile = el.closest(GRID_FOCUS_TILE);
    const productIds = [];
    const addProduct = (node) => {
      const id = node && node.getAttribute('data-product-id');
      if (id && !productIds.includes(id)) productIds.push(id);
    };
    if (tile) {
      addProduct(tile);
    } else {
      // A control with no tile of its own (the drill-in Back button): the
      // product being browsed is the natural place to land after it.
      const view = el.closest('.murphy-door-configurator__variant-view');
      if (view) {
        addProduct(view.querySelector('[data-product-id]'));
        view.querySelectorAll(GRID_FOCUS_TILE).forEach(addProduct);
      }
    }
    return {
      el,
      card,
      categoryId: card.getAttribute('data-category'),
      variantId: tile ? tile.getAttribute('data-variant-id') : null,
      productIds,
      label: el.getAttribute('aria-label') || '',
      role: el.getAttribute('role') || el.tagName,
    };
  }

  function isUsableGridTarget(el) {
    if (!el || !el.isConnected || typeof el.focus !== 'function') return false;
    if (el.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
    // Filtered/simplified mode hides tiles with an inline display:none
    // (renderVariantsWithMaterialAwareness); focus() on one silently fails.
    const tile = el.closest(GRID_FOCUS_TILE);
    if (tile && tile.style && tile.style.display === 'none') return false;
    const grid = el.closest(GRID_FOCUS_SCOPE);
    // A closed accordion grid keeps its tiles in the DOM but off screen (and
    // inert, see CLOSED GRIDS ARE INERT; same predicate, so the two agree
    // even before the inert observer has run for this mutation).
    if (grid && isGridCollapsed(grid)) return false;
    return true;
  }

  /**
   * Pick the restore target for a token in its (re-rendered) card.
   * @param {Object} token - from captureGridFocus
   * @returns {Element|null}
   */
  function resolveGridFocusTarget(token) {
    const card = token.card;
    if (!card || !card.isConnected) return null;
    // Order-independent of the inert observer's own callback.
    syncGridsInertWithin(card);
    const focusables = Array.from(
      card.querySelectorAll(GRID_FOCUS_SCOPE + ' ' + GRID_FOCUS_TILE)
    ).filter(isUsableGridTarget);
    // The pinned product of a drill-in shares ids with the grid it came from;
    // prefer a real option over it.
    const pinned = (tile) => !!tile.closest(SELECTORS.SELECTED_PRODUCT);
    const ordered = focusables
      .filter((t) => !pinned(t))
      .concat(focusables.filter(pinned));
    const byAttr = (attr, value) =>
      value ? ordered.find((t) => t.getAttribute(attr) === value) : null;

    let target = byAttr('data-variant-id', token.variantId);
    for (let i = 0; !target && i < token.productIds.length; i++) {
      target = byAttr('data-product-id', token.productIds[i]);
    }
    if (!target && token.label) {
      target = Array.from(
        card.querySelectorAll(
          GRID_FOCUSABLE.split(', ')
            .map((sel) => GRID_FOCUS_SCOPE + ' ' + sel)
            .join(', ')
        )
      ).find(
        (el) =>
          isUsableGridTarget(el) &&
          el.getAttribute('aria-label') === token.label &&
          (el.getAttribute('role') || el.tagName) === token.role
      );
    }
    if (!target) {
      target =
        ordered.find(
          (t) =>
            t.classList.contains(CLASS_NAMES.THUMBNAIL_ACTIVE) ||
            t.classList.contains(
              'murphy-door-configurator__thumbnail-item--applied'
            )
        ) || null;
    }
    if (!target) target = ordered[0] || null;
    if (!target) {
      const toggle = categoryToggleOf(card);
      target = toggle && toggle.isConnected ? toggle : null;
    }
    return target;
  }

  // ---------------------------------------------------------------------------
  // KEEPER FOCUS STAYS ON SCREEN (WCAG 2.4.11 Focus Not Obscured)
  //
  // The keeper focuses with preventScroll (the page must not jump on every
  // re-render), so the restored control can end up off screen or under the
  // sticky header/preview bars, typically when an answered classic card
  // collapses and auto-navigation then smooth-scrolls to the NEXT card.
  //
  // Two rules, chosen so keyboard users stay oriented WITHOUT fighting
  // auto-navigation:
  //   1. HANDOFF. A control the keeper focused (not the customer) for the
  //      last KEEPER_HANDOFF_MS counts as unowned to _focusScrolledCategory,
  //      so focus travels WITH auto-navigation to the card it scrolls to
  //      (and the announcer says "Moving to ..."). Never from a control the
  //      customer focused themselves: they Tab/click, and the record clears.
  //   2. REVEAL. When no auto-scroll takes focus along, wait for scrolling to
  //      go quiet (and past any pending auto-scroll), then, if focus is still
  //      on the restored control and it is not fully visible and unobscured
  //      (elementFromPoint hit-test: sticky header, preview chip and price
  //      bar all fail it), scrollIntoView({block:'nearest'}). Instant under
  //      prefers-reduced-motion. scroll-margin-top on tiles and toggles
  //      (section-md-apo-mode.css) clears the sticky stack.
  // ---------------------------------------------------------------------------
  const KEEPER_HANDOFF_MS = 3000;
  const KEEPER_REVEAL_QUIET_MS = 150;
  const KEEPER_REVEAL_MAX_MS = 3000;
  let _keeperFocused = null; // { el, at }
  let _keeperReveal = null; // { cancel }
  // Set while an auto-navigation scroll is scheduled but has not run yet.
  let _autoScrollPendingUntil = 0;

  /**
   * Focus a control on the customer's behalf (keeper restore, closed-grid
   * handover): no scroll now, remembered for the handoff, revealed later.
   * @param {Element} target
   * @returns {boolean} true when focus landed
   */
  function keeperFocus(target) {
    try {
      target.focus({ preventScroll: true });
    } catch (error) {
      return false;
    }
    if (document.activeElement !== target) return false;
    _keeperFocused = { el: target, at: Date.now() };
    scheduleKeeperReveal(target);
    return true;
  }

  /** True while el still holds focus that the keeper (not the customer) put there. */
  function isKeeperHeldFocus(el) {
    return (
      !!_keeperFocused &&
      _keeperFocused.el === el &&
      Date.now() - _keeperFocused.at <= KEEPER_HANDOFF_MS
    );
  }

  /**
   * Fully inside the viewport AND not covered (sticky header, preview bars).
   * Unknown geometry (no layout, no elementFromPoint) counts as visible so
   * this can never cause a scroll it cannot justify.
   * @param {Element} el
   * @returns {boolean}
   */
  function isFocusTargetVisible(el) {
    try {
      const rect = el.getBoundingClientRect();
      if (!rect || (!rect.width && !rect.height)) return true;
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      if (!vw || !vh) return true;
      if (
        rect.top < 0 ||
        rect.left < 0 ||
        rect.bottom > vh + 1 ||
        rect.right > vw + 1
      ) {
        return false;
      }
      if (typeof document.elementFromPoint !== 'function') return true;
      const x = rect.left + rect.width / 2;
      const inset = Math.min(2, rect.height / 2);
      return [rect.top + inset, rect.top + rect.height / 2, rect.bottom - inset]
        .map((y) => document.elementFromPoint(x, y))
        .every((hit) => !hit || hit === el || el.contains(hit));
    } catch (error) {
      return true;
    }
  }

  function matchesFocusVisible(el) {
    try {
      return el.matches(':focus-visible');
    } catch (error) {
      return true; // engine without :focus-visible: assume a ring is shown
    }
  }

  function scheduleKeeperReveal(target) {
    if (_keeperReveal) _keeperReveal.cancel();
    const startedAt = Date.now();
    let timer = null;
    const finish = () => {
      clearTimeout(timer);
      timer = null;
      document.removeEventListener('scroll', onScroll, true);
      if (_keeperReveal === handle) _keeperReveal = null;
    };
    const check = () => {
      const now = Date.now();
      const waited = now - startedAt;
      if (_autoScrollPendingUntil > now && waited < KEEPER_REVEAL_MAX_MS) {
        timer = setTimeout(
          check,
          _autoScrollPendingUntil - now + KEEPER_REVEAL_QUIET_MS
        );
        return;
      }
      finish();
      if (document.activeElement !== target || !target.isConnected) return;
      // Pointer modality: no focus ring is painted, so nothing to keep on
      // screen, and a mouse user's page must not move.
      if (!matchesFocusVisible(target)) return;
      if (isFocusTargetVisible(target)) return;
      try {
        target.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
      } catch (error) {
        isDev() && console.warn('[UIUpdater] keeper reveal failed:', error);
      }
    };
    // Any scroll (window or inner container) restarts the quiet window.
    function onScroll() {
      if (Date.now() - startedAt >= KEEPER_REVEAL_MAX_MS) return;
      clearTimeout(timer);
      timer = setTimeout(check, KEEPER_REVEAL_QUIET_MS);
    }
    const handle = { cancel: finish };
    _keeperReveal = handle;
    document.addEventListener('scroll', onScroll, true);
    timer = setTimeout(check, KEEPER_REVEAL_QUIET_MS);
  }

  function stopWatchingGridFocus() {
    _gridFocusToken = null;
    if (_gridFocusObserver) _gridFocusObserver.disconnect();
  }

  /**
   * True when `nodes` are all children of `parent` and already appear in this
   * relative order (other children may sit between them). Lets a re-sort skip
   * DOM moves that would only blur a focused tile (see GRID FOCUS KEEPER).
   * @param {Element} parent
   * @param {Element[]} nodes
   * @returns {boolean}
   */
  function isInRelativeOrder(parent, nodes) {
    let previous = null;
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      if (!node || node.parentNode !== parent) return false;
      if (
        previous &&
        !(
          previous.compareDocumentPosition(node) &
          Node.DOCUMENT_POSITION_FOLLOWING
        )
      ) {
        return false;
      }
      previous = node;
    }
    return true;
  }

  /**
   * True when this batch of mutation records took `el` (or an ancestor) out
   * of the tree, even if it was put back in the same task.
   * @param {MutationRecord[]|undefined} records
   * @param {Node} el
   * @returns {boolean}
   */
  function wasRemovedInRecords(records, el) {
    if (!records || !records.length) return false;
    for (let i = 0; i < records.length; i++) {
      const removed = records[i].removedNodes;
      if (!removed) continue;
      for (let j = 0; j < removed.length; j++) {
        const node = removed[j];
        if (node === el || (node.contains && node.contains(el))) return true;
      }
    }
    return false;
  }

  /**
   * Restore focus if, and only if, the remembered grid control was removed
   * while it held focus. Safe to call at any time; a no-op otherwise.
   *
   * MOVED, NOT REPLACED (staging UAT 2026-10-02, bug A). A DOM move
   * (appendChild / insertBefore of an attached node) is a remove + insert:
   * the browser blurs the focused tile to <body> and re-attaches it in the
   * same task, so by the time this observer runs the token's node IS
   * connected again. The old `isConnected` early return read that as "not
   * lost", the focusout timer then cleared the token, and a drill-in variant
   * pick (renderVariantsWithMaterialAwareness re-sorts its tiles) left focus
   * on <body> for good. A connected token now restores too, but only when
   * these records show it was taken out, so focus the customer moved away on
   * purpose is never pulled back.
   * @param {MutationRecord[]} [records] - from the MutationObserver
   * @returns {boolean} true when focus was moved
   */
  function restoreGridFocusIfLost(records) {
    const token = _gridFocusToken;
    if (!token || !isFocusUnowned()) return false;
    if (token.el.isConnected && !wasRemovedInRecords(records, token.el)) {
      return false;
    }
    // Same node when it survived the move and is still usable (not hidden,
    // not in a closed grid); otherwise its equivalent in the new grid.
    const target =
      token.el.isConnected && isUsableGridTarget(token.el)
        ? token.el
        : resolveGridFocusTarget(token);
    if (!target || !keeperFocus(target)) return false;
    // focusin on the target re-arms the keeper for the next re-render; set it
    // here as well in case the engine skipped the event.
    if (document.activeElement === target) watchGridFocus(target);
    return document.activeElement === target;
  }

  function watchGridFocus(el) {
    const token = captureGridFocus(el);
    if (!token) {
      stopWatchingGridFocus();
      return;
    }
    const sameCard = _gridFocusToken && _gridFocusToken.card === token.card;
    _gridFocusToken = token;
    if (typeof MutationObserver !== 'function') return;
    if (!_gridFocusObserver) {
      _gridFocusObserver = new MutationObserver(restoreGridFocusIfLost);
    }
    if (!sameCard) {
      _gridFocusObserver.disconnect();
      _gridFocusObserver.observe(token.card, {
        childList: true,
        subtree: true,
      });
    }
  }

  /**
   * Install the keeper. Document-level listeners (cheap filters); the
   * MutationObserver watches ONE card, and only while a grid control has focus.
   * @returns {Function} teardown
   */
  function installGridFocusKeeper() {
    const onFocusIn = (event) => {
      // The customer (or another module) moved focus: no longer keeper-held.
      if (_keeperFocused && event.target !== _keeperFocused.el) {
        _keeperFocused = null;
      }
      watchGridFocus(event.target);
    };
    const onFocusOut = (event) => {
      const token = _gridFocusToken;
      if (!token || event.target !== token.el) return;
      // Removal-induced blur (some engines fire it) leaves the node detached:
      // keep the token for the restore. A real move away clears it below, or
      // via the focusin on the new owner.
      setTimeout(() => {
        if (
          _gridFocusToken === token &&
          token.el.isConnected &&
          document.activeElement !== token.el
        ) {
          stopWatchingGridFocus();
        }
      }, 0);
    };
    const onPointerDown = (event) => {
      _keeperFocused = null;
      if (_keeperReveal) _keeperReveal.cancel();
      const token = _gridFocusToken;
      if (token && !(token.el.contains && token.el.contains(event.target))) {
        stopWatchingGridFocus();
      }
    };
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
      stopWatchingGridFocus();
      _gridFocusObserver = null;
      if (_keeperReveal) _keeperReveal.cancel();
      _keeperFocused = null;
    };
  }
  // ---------------------------------------------------------------------------
  // PRICE ANNOUNCEMENT (WCAG 4.1.3 Status Messages)
  //
  // The price is the number the customer is buying on, and until 2026-08-21 it
  // changed in total silence -- a blind customer could pick an upgrade that added
  // several hundred dollars and hear nothing.
  //
  // 🚨 This gets its OWN region rather than folding into the selection announcer
  // above, and the reasoning matters if you are tempted to merge them:
  //
  //   1. announceSelectionApplied() hard-returns without a categoryId and is
  //      subscribed only to SELECTION_APPLIED. Measurement sliders and quantity
  //      changes reach price through the calculator with no categoryId at all, so
  //      folding would announce price on the ONE path that already speaks and stay
  //      silent on the rest.
  //   2. #md-implicit-apply-announcer already has two writers (flushSelection-
  //      Announcement here, and _announceImplicitApply in the event-handler). A
  //      third makes it a three-way clobber.
  //
  // The settle window is deliberately LONGER than ANNOUNCE_SETTLE_MS so the
  // selection sentence lands first and the price follows it, rather than the two
  // interleaving. Slider drags republish the same total continuously, so the
  // dedupe below is what stops the reader talking non-stop -- it is load-bearing,
  // not an optimisation.
  const PRICE_ANNOUNCE_SETTLE_MS = 500;
  let _priceAnnounceTimer = null;
  let _priceAnnouncePending = null;
  let _priceAnnounceLast = null;

  function announcePriceChange(priceData) {
    if (!priceData) return;

    // The PRICE_CALCULATION_COMPLETED subscriber passes a raw numeric `amount`
    // with no formattedAmount. Reading formattedAmount alone would speak "2499";
    // reading String(amount) would too. Format it the same way the rest of the
    // file does instead of announcing a naked integer.
    const amount =
      priceData.formattedAmount || formatConfiguratorPrice(priceData.amount);
    if (!amount) return;

    // 🚨 The currency symbol is NOT in the text. Both price displays get their
    // "$" from a CSS ::before (section-md-configurator.css:568 for
    // .price-amount, :858 for #murphy-door-total-price), which is decoration and
    // never reaches textContent -- or a screen reader. formattedAmount is
    // therefore "3,051.80", and this announced "Total 3,051.80" with no currency
    // at all. Caught live 2026-08-21; the existing test only asserted it was not
    // a naked integer, and "3,051.80" passed that while still being wrong.
    const spoken = amount.startsWith('$') ? amount : '$' + amount;

    _priceAnnouncePending = spoken;
    if (_priceAnnounceTimer) clearTimeout(_priceAnnounceTimer);
    _priceAnnounceTimer = setTimeout(
      flushPriceAnnouncement,
      PRICE_ANNOUNCE_SETTLE_MS
    );
  }

  function flushPriceAnnouncement() {
    _priceAnnounceTimer = null;
    const spoken = _priceAnnouncePending;
    _priceAnnouncePending = null;
    if (!spoken) return;

    // Suppress re-announcing a total that did not actually move. batchPriceUpdate
    // is called on plenty of paths that recompute to the same number.
    if (spoken === _priceAnnounceLast) return;
    _priceAnnounceLast = spoken;

    try {
      const announcer = document.getElementById('md-price-announcer');
      if (!announcer) return;
      announcer.textContent = 'Total ' + spoken;
    } catch (error) {
      isDev() && console.warn('[UIUpdater] price announcement failed:', error);
    }
  }

  (() => {
    const opts = { capture: true, passive: true };
    const markInteracted = () => {
      userHasInteracted = true;
      ['pointerdown', 'keydown', 'touchstart'].forEach((evt) =>
        document.removeEventListener(evt, markInteracted, opts)
      );
    };
    ['pointerdown', 'keydown', 'touchstart'].forEach((evt) =>
      document.addEventListener(evt, markInteracted, opts)
    );
    installCustomerPickListeners(opts);
  })();

  // CUSTOMER PICK LISTENERS (see LAST CUSTOMER INPUT and CUSTOMER PICK TOKENS).
  // Persistent, capture + passive, document-level: they observe, never
  // prevent. Each does a closest() walk only for the few event types that
  // can be a pick; nothing runs on move/scroll/wheel.
  //
  // A pick TOKEN is created only by activating a configurator CONTROL inside
  // a category: a click (mouse, touch, or assistive tech's detail-0 click) or
  // Enter/Space on a tile/button; input/change on a native control; a value
  // key (arrows, PageUp/PageDown, Home/End) on a SLIDER. Page navigation
  // (arrow keys / Space-to-scroll on the page, Tab), the category header
  // toggle (opening a card is navigation) and untrusted events never do.
  const PICK_CONTROL_SELECTOR =
    '[role="button"],button,input,select,label,[role="slider"],' +
    '[role="radio"],[role="option"],[role="checkbox"],[role="switch"]';
  const SLIDER_SELECTOR = '[role="slider"],input[type="range"]';
  const SLIDER_VALUE_KEYS = new Set([
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'PageUp',
    'PageDown',
    'Home',
    'End',
  ]);
  const ACTIVATION_KEYS = new Set(['Enter', ' ', 'Spacebar']);

  /**
   * The category a trusted event activated a control in, or null.
   * @param {Event} event
   * @returns {{categoryId: string, control: Element}|null}
   */
  function pickTargetOf(event) {
    try {
      const target = event && event.target;
      if (!target || typeof target.closest !== 'function') return null;
      const control = target.closest(PICK_CONTROL_SELECTOR);
      if (!control) return null;
      const toggleSelector = CONSTANTS?.CATEGORY_HEADER?.TOGGLE_SELECTOR;
      if (toggleSelector && control.closest(toggleSelector)) return null;
      // An answered gate card that is itself role="button" is the card, not
      // a control in it.
      if (control.matches('[data-category]')) return null;
      // Slider containers name their category directly; tiles inherit the
      // card's.
      const owner = control.closest('[data-category-id],[data-category]');
      if (!owner) return null;
      const categoryId =
        owner.getAttribute('data-category-id') ||
        owner.getAttribute('data-category');
      return categoryId ? { categoryId, control } : null;
    } catch (error) {
      return null;
    }
  }

  function installCustomerPickListeners(opts) {
    const record = (modality) => {
      _lastCustomerInput = { at: Date.now(), modality };
    };
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (event.isTrusted !== true) return;
        record('pointer');
        // A slider drag never produces a click (touchstart is
        // preventDefault-ed, and a mouse release can land off the slider),
        // so a trusted press on a slider is its activation.
        const pick = pickTargetOf(event);
        if (pick && pick.control.closest(SLIDER_SELECTOR)) {
          noteCustomerPick(pick.categoryId);
        }
      },
      opts
    );
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.isTrusted !== true) return;
        if (CUSTOMER_INPUT_IGNORED_KEYS.has(event.key)) return;
        record('keyboard');
        const pick = pickTargetOf(event);
        if (!pick) return;
        const isSlider = !!pick.control.closest(SLIDER_SELECTOR);
        if (
          isSlider
            ? SLIDER_VALUE_KEYS.has(event.key)
            : ACTIVATION_KEYS.has(event.key) &&
              !pick.control.matches(
                'input:not([type="checkbox"]):not([type="radio"]),select'
              )
        ) {
          noteCustomerPick(pick.categoryId);
        }
      },
      opts
    );
    document.addEventListener(
      'click',
      (event) => {
        if (event.isTrusted !== true) return;
        // detail 0 = no pointer: keyboard or assistive-tech activation.
        record(event.detail === 0 ? 'keyboard' : 'pointer');
        const pick = pickTargetOf(event);
        if (pick) noteCustomerPick(pick.categoryId);
      },
      opts
    );
    ['input', 'change'].forEach((type) =>
      document.addEventListener(
        type,
        (event) => {
          if (event.isTrusted !== true) return;
          const pick = pickTargetOf(event);
          if (pick) noteCustomerPick(pick.categoryId);
        },
        opts
      )
    );
  }

  // ============================================================================
  // SUMMARY UPSELL LOOKUP
  // Each entry defines a contextual upgrade nudge that renders inline next to
  // the Edit link on Summary items when the user picked the cheapest /
  // no-frills option in that category. The nudge reuses the Edit delegation
  // (data-edit-category) so clicking it opens the same category accordion
  // where the upgrade option lives — no detour to a product page.
  //
  // Adding or editing an upsell = one new entry here. Trigger matches are
  // case-insensitive substring regexes so admin-edited display titles with
  // minor whitespace variations still resolve.
  // ============================================================================
  const SUMMARY_UPSELLS = [
    {
      category: 'Locking Systems',
      triggerMatch: /friction\s*catch\s*only/i,
      label: 'Add Book Latch →',
      trackLabel: 'book_latch_inline',
      trackPosition: 'summary_line_friction_catch',
    },
    {
      category: 'Door Backing',
      triggerMatch: /standard\s*backing/i,
      label: 'Add Flat Backing →',
      trackLabel: 'flat_backing_inline',
      trackPosition: 'summary_line_standard_backing',
    },
    {
      category: 'Delivery',
      triggerMatch: /standard/i,
      label: 'Add Rush Shipping →',
      trackLabel: 'rush_shipping_inline',
      trackPosition: 'summary_line_standard_delivery',
    },
    {
      category: 'Front Casing',
      triggerMatch: /standard\s*front\s*casing/i,
      label: 'Upgrade to Fluted Casing →',
      trackLabel: 'fluted_casing_inline',
      trackPosition: 'summary_line_standard_front_casing',
    },
    {
      category: 'Smart Home Automations',
      triggerMatch: /no\s*smart\s*home/i,
      label: 'Add Automated Door Opener →',
      trackLabel: 'smart_lock_inline',
      trackPosition: 'summary_line_no_smart_home',
    },
  ];

  // ============================================================================
  // SHARED PRICE FORMATTER (P1)
  // Single source of truth for rendering a configurator price string with a
  // leading "$". Both updateSelectionText() (category button) and
  // updateSummaryList() (Summary rows) route through this so the "$" never
  // goes missing on one surface while present on the other.
  //
  // Behaviour:
  //   - null / undefined / '' → '' (render nothing)
  //   - a value that parses to 0 → zeroLabel when provided (e.g. "Included"),
  //     otherwise "$0"
  //   - any other value → "$" + the original formatted string, with any
  //     pre-existing leading "$" stripped first so the result is idempotent
  //     (never "$$1,016").
  // Commas and existing decimal formatting in the incoming string are
  // preserved verbatim — this formatter only guarantees the leading "$".
  // ============================================================================
  // ============================================================================
  // CONFETTI ANCHOR RESOLUTION
  // ============================================================================
  // Where the celebration burst originates. Ordered by what the customer is
  // actually looking at when it fires; no experience branching is needed
  // because an anchor that is not rendered is simply skipped.
  //
  // Entries are {selector, origin}. `origin` says WHERE in the element's box
  // the burst starts:
  //   'top'    -- horizontal centre, TOP edge. Particles rise OUT of a wide,
  //               short element (progress bar, sticky bar) rather than out of
  //               its middle, which would bury the first frames behind it.
  //   'centre' -- the true centre of the box. Used for the promoted Add to
  //               Cart, where the button IS the reward and the burst is meant
  //               to look like it came from the button itself.
  const CONFETTI_ANCHORS = Object.freeze([
    // 🥇 The "Your door is ready!" badge, first by design (Ryan, UAT
    // 2026-09-21): the burst comes from the CENTRE of the badge, desktop and
    // mobile. The badge IS the celebration's subject -- it is the thing that
    // says the door is ready -- so it outranks the promoted Add to Cart that
    // used to hold this slot.
    //
    // 🚨 WHY THIS IS SAFE TO PUT FIRST, and why no timing work was needed.
    // The badge ships with the `hidden` attribute and is revealed by
    // md-config-ui-ready-badge.js only once the door is complete AND an Add to
    // Cart is genuinely enabled. While hidden its rect is 0x0, so the
    // capability test below skips it and every pre-existing anchor keeps its
    // old priority -- this cannot regress a celebration that fires when the
    // badge is not up.
    //
    // And it WILL be up: the confetti manager waits CELEBRATION_LEAD_IN_MS
    // (400ms) before it resolves coordinates at all, while the badge appears
    // one SETTLE_MS (120ms) after the completion progress event that triggers
    // the same celebration. The lead-in exists for exactly this class of
    // problem ("let the promoted Add to Cart land and settle"). The only path
    // that skips the lead-in is prefers-reduced-motion, which spawns ZERO
    // particles, so the origin is moot there.
    //
    // MEASURED on the local dev server (apo_style, 390x844): badge rect
    // 354x142, centre (187, 786) in an 844px viewport. Also measured: at that
    // same moment EVERY previous anchor was unusable on mobile -- the promoted
    // ATC does not exist (promotion is desktop-only), the progress bar is
    // 0x0 (apo_style display:none), `--stuck` is absent, and
    // `__add-to-cart-wrapper` sat at top 6454, ~5600px below the fold -- so
    // the burst was falling through to the generic viewport third. This anchor
    // is what makes the celebration land on something the customer is looking
    // at on a phone.
    //
    // `[data-ready-badge]` rather than the class: it is the badge's own stable
    // contract marker (the module's SELECTORS.BADGE, and documented as such in
    // snippets/configurator-ready-badge.liquid), where the class is a CSS
    // hook. DOM.getElement is a plain querySelector and already resolves
    // attribute selectors elsewhere in this file.
    Object.freeze({
      selector: '[data-ready-badge]',
      origin: 'centre',
    }),
    // The promoted Add to Cart. On 100% the rail's ATC is MOVED into the
    // sticky actions bar (_promoteAddToCartToStickyBar below), so this anchor
    // only exists once that has happened -- and when it does it must outrank
    // `--stuck`, which is the very bar now CONTAINING it and would otherwise
    // claim the burst for its own top edge. It now sits BEHIND the badge, and
    // still wins whenever the badge is dismissed or not yet shown.
    Object.freeze({
      selector:
        '.murphy-door-configurator__rail-add-to-cart--promoted .murphy-door-configurator__add-to-cart-button',
      origin: 'centre',
    }),
    Object.freeze({
      selector: '.murphy-door-configurator__progress-bar',
      origin: 'top',
    }),
    Object.freeze({
      selector: '.murphy-door-configurator__product-price--stuck',
      origin: 'top',
    }),
    Object.freeze({
      selector: '.murphy-door-configurator__add-to-cart-wrapper',
      origin: 'top',
    }),
  ]);

  // ============================================================================
  // ADD TO CART PROMOTION (apo_style)
  // ============================================================================
  // Ryan, UAT 2026-09-03: on 100%, the rail's Add to Cart block moves into the
  // sticky actions bar, under Start over / Share / Save.
  //
  // ⚠️ The second half of that note ("and the confetti fires from the centre of
  // that button") was SUPERSEDED on 2026-09-21: the burst now comes from the
  // centre of the "Your door is ready!" badge, which sits ahead of this button
  // in CONFETTI_ANCHORS. The promotion itself is unchanged, and this button is
  // still the anchor whenever the badge is dismissed or not yet shown -- it is
  // only no longer the first choice.
  const RAIL_ATC_SELECTOR = '.murphy-door-configurator__rail-add-to-cart';
  const PROMOTED_ATC_CLASS =
    'murphy-door-configurator__rail-add-to-cart--promoted';
  // 🚨 [data-sticky-activate] is the ONLY safe way to name this element. TWO
  // different elements answer to __product-price--sidebar (the actions bar and
  // the buy-box price block -- see the comment block in
  // section-md-configurator.css), and only the actions bar carries the sticky
  // attribute. Matching on the class alone would land the Add to Cart inside
  // the price block roughly half the time, depending on DOM order.
  const STICKY_ACTIONS_BAR_SELECTOR =
    '.murphy-door-configurator__product-price--sidebar[data-sticky-activate]';
  const STICKY_BAR_HAS_ATC_CLASS =
    'murphy-door-configurator__product-price--has-atc';
  // 🚨 DESKTOP ONLY, and this is a measured constraint, not a preference.
  //
  // MEASURED on the local dev server (apo_style, 390x844) with the promotion
  // unconditional: the actions bar's container (.product) is a
  // `position: sticky; overflow: hidden` box that is 304px TALL on mobile. The
  // promoted bar measures 418px, so it was clipped at its container's edge --
  // the assurance strip's phone/video-demo line was cut mid-sentence and the
  // in-flow buy-box price painted straight through the remainder. Nothing
  // errored; it simply rendered as two overlapping panels.
  //
  // Mobile does not need this anyway: it has a DEDICATED persistent Add to
  // Cart already -- snippets/configurator-sticky-bar.liquid, the fixed
  // thumb-zone bar revealed at (max-width: 768px). The sidebar actions bar is
  // the desktop equivalent of that, which is exactly why promoting into it is
  // a desktop move.
  //
  // 801px, matching the (max-width: 800px) block in section-md-configurator.css
  // that gives this bar its mobile layout -- so the gate and the layout agree
  // on where "desktop" starts rather than each having its own opinion.
  const PROMOTION_DESKTOP_QUERY = '(min-width: 801px)';

  // ===========================================================================
  // M1 — COMPLETING THE CONFIGURATION MUST NEVER TAKE THE BUTTON OFF SCREEN
  // ===========================================================================
  //
  // QA #14 (Ahmed, desktop, 2026-09-15): "Add to cart CTA is no longer exist
  // however all required options selected."
  //
  // MEASURED on production (apo_style, 1512x900, 17,585px page) at the moment
  // the last category is answered -- the customer is at the END of the rail,
  // because that is where the option they just clicked lives:
  //
  //   before completion   rail ATC in flow at document y=5,672  -> ON SCREEN
  //   after  completion   promoted into the actions bar at y=686 -> viewport
  //                       top -740, and ZERO Add to Cart visible anywhere
  //   reachability at 100%: 2 of 21 screens, both at the very top of the page
  //
  // So the promotion did not merely relocate the button, it DELETED it from
  // the only place the customer could have been standing when they earned it.
  //
  // 🚨 WHY THE DESTINATION CANNOT HOLD IT. The actions bar is
  // `position: sticky; top: 5px`, which reads as "it will follow the customer
  // down". It does not. Its containing block is the GALLERY column
  // (`.product--small`, measured 1,228px tall); a sticky element can never
  // escape its containing block, so past roughly the first 1,300px of a
  // ~17,500px apo_style page the bar is simply gone. `position: sticky` and
  // even `--stuck` are therefore both TRUE while the bar is 1,985px above the
  // viewport -- the same presence-vs-capability trap as the cart-drawer
  // incident in CLAUDE.md and as _resolveConfettiAnchor() below.
  //
  // THE RULE: promote only while the destination can actually show the button,
  // and hand it BACK to the rail the moment it cannot. The two homes are never
  // on screen at the same time (gallery column ends ~1,300px, rail ATC sits
  // ~5,700px), so every move happens while both are out of view -- the
  // customer never sees the button jump, only ever finds one where they are.
  //
  // Reachability is judged from the bar's HOST (its parent element) rather
  // than the bar itself, and that is not a shortcut. The bar is
  // `display: none` until `--stuck`, and a display:none element measures
  // 0x0 -- so asking the bar directly would answer "unreachable" at the top of
  // the page, which is the one place the promotion genuinely helps. The host
  // is laid out at all times and geometrically contains the bar, so it answers
  // the question the bar cannot.
  //
  // The rubber-band absorber is RAIL_ATC_SETTLE_MS, shared with the M0
  // observer below and read at call time rather than copied here -- it is
  // declared further down this file, so an alias at this point would sit in
  // the temporal dead zone.
  //
  // `let`, not `const`: the rail's original home is captured on the first
  // promotion so a demotion can put it back EXACTLY where the section rendered
  // it (between `.murphy-door-configurator__categories` and the below-rail
  // blocks), rather than appending it to the end of the content column.
  let _atcRailHome = null;
  // Promotion is only allowed to run at all once the configuration has been
  // completed at least once -- same signal the promotion already hung off.
  let _atcPromotionArmed = false;
  let _atcHomeObserver = null;
  let _atcHomeLastState = null;
  let _atcHomeSettleTimer = null;
  let _atcHomeMediaQuery = null;

  /**
   * Can the sticky actions bar actually put the Add to Cart in front of the
   * customer right now?
   *
   * 🚨 FAILS OPEN on anything it cannot measure, matching the matchMedia guard
   * in _promoteAddToCartToStickyBar(): an unmeasurable layout (jsdom, a
   * pre-layout first paint) must read as "go ahead", never as "delete the
   * feature". A real, laid-out, off-screen host is the only thing that blocks.
   *
   * @param {HTMLElement|null} bar the sticky actions bar
   * @returns {boolean}
   */
  function _stickyBarCanShowAddToCart(bar) {
    try {
      if (!bar) return false;
      const host = bar.parentElement;
      if (!host || typeof host.getBoundingClientRect !== 'function')
        return true;
      const rect = host.getBoundingClientRect();
      // 0x0 means "not laid out / cannot answer", not "off screen".
      if (!(rect.width > 0 && rect.height > 0)) return true;
      const vh = window.innerHeight || 0;
      const vw = window.innerWidth || 0;
      if (!(vh > 0 && vw > 0)) return true;
      return (
        rect.bottom > 0 && rect.top < vh && rect.right > 0 && rect.left < vw
      );
    } catch (error) {
      isDev() &&
        console.error('[UIUpdater] ATC destination probe failed:', error);
      return true;
    }
  }

  /**
   * Move the rail's Add to Cart block into the sticky actions bar.
   *
   * 🚨 MOVE, NEVER CLONE. snippets/configurator-add-to-cart.liquid is emphatic
   * that the control exists exactly once per page, and it is right: ui-updater
   * syncs disabled/aria across EVERY match of SELECTORS.ADD_TO_CART and binds a
   * disabled-click guide to each, while core-event-handler delegates the real
   * click BY CLASS. A copy would therefore be fully live, not inert -- two
   * buttons racing one add, and two guides publishing for a single tap.
   *
   * Silently a no-op outside apo_style: the rail wrapper is rendered
   * `{%- if _configurator_experience == 'apo_style' -%}`, so in classic/dual
   * there is nothing to move (the ATC lives inside the summary header, where
   * hoisting it out would dismantle that panel's layout).
   *
   * Idempotent. Progress can cross 100% more than once per session, and the
   * conductor re-arms its celebration guard every time it drops below.
   *
   * @returns {HTMLElement|null} the promoted block, or null if nothing moved
   */
  // ===========================================================================
  // M0 — the sticky ATC must never vanish while the customer can still buy
  // ===========================================================================
  //
  // THE BUG THIS REPLACES. section-md-configurator.css carried:
  //
  //   .murphy-door-configurator-wrapper[data-configurator-experience='apo_style']
  //     :has(.murphy-door-configurator__add-to-cart-button:not([disabled]))
  //     .murphy-door-configurator__sticky-bar { display: none; }
  //
  // That was a real fix for a real report (Daniel, iPhone SE, 2026-08-13: the
  // fixed bar covered the in-flow ATC section and the click-to-edit controls
  // just above it, unreachable by tap). The INTENT is right and is preserved --
  // Ryan, 2026-09-04: "don't show two Add to Carts at once."
  //
  // The PREDICATE was wrong. `:has(...:not([disabled]))` is GLOBAL STATE --
  // "the configuration is complete" -- anywhere on the page, at any scroll
  // position. The problem it was solving is SPATIAL: the bar covering the rail
  // ATC *when the shopper scrolls to it*. So a one-screen overlap was solved by
  // removing the CTA from the other ~29 screens.
  //
  // MEASURED on production before this change, at 100% configured on a
  // 25,404px page: ZERO visible Add to Cart at scrollY 0 / 300 / 1500 / 4000 /
  // 7000 / 8200, and exactly one at 8000. The customer finishes configuring a
  // $1,595 door and the only ATC left is a 44px target reachable in roughly one
  // screen out of thirty.
  //
  // 🚨 WHY IntersectionObserver AND NOT pdp-sticky-bar.js's SENTINEL. That file
  // solves the same shape of problem with an offsetTop chain compared against
  // `window.scrollY`, and copying it here does NOT work. That pattern assumes
  // ONE scroll container. This page has TWO decoupled ones -- `window` and
  // `.murphy-door-configurator__info-column`, which is a 60dvh pane holding
  // ~7,900px of rail. The rail ATC lives INSIDE that pane, so a scrollY
  // threshold never crosses: the window only scrolls past the outer 511px box,
  // never "through" the content clipped inside it. Copied literally the fix
  // would silently do nothing, at any ship order. IntersectionObserver with
  // `root: null` computes against real rendered geometry through however many
  // clipping ancestors sit between the target and the viewport, so it is
  // correct BOTH before and after the pane is released (plan task 1.1) with no
  // change -- which is also why there is no sentinel element whose DOM position
  // could drift when 1.1 moves things around.
  const RAIL_ATC_HIDES_BAR_CLASS =
    'murphy-door-configurator__sticky-bar--rail-visible';
  // Marks the wrapper so the CSS fallback stands down. Deliberately NOT
  // `body.configurator-ready`: that means "loading finished" and is REMOVED
  // again on re-render, which would silently re-arm the fallback underneath a
  // live observer.
  const ATC_OBSERVER_ATTR = 'data-atc-visibility-observer';
  // MEASURED, and the sign matters. A NEGATIVE bottom margin (tried -15%) holds
  // the bar visible while the rail ATC is already on screen, which produces a
  // band where BOTH are visible -- verified live: with the configuration
  // complete the rail ATC sits at 760px in an 852px viewport, so at scroll 0 it
  // is genuinely in view and the shrunk root still reported "not intersecting".
  // A POSITIVE margin has the opposite failure: the bar hides before the rail
  // arrives, opening a gap with NO visible Add to Cart -- which is the very bug
  // M0 exists to fix, so that direction is not available either.
  //
  // Zero margin gives the honest threshold: hide exactly while the rail is in
  // the viewport. Flicker protection comes from the two-frame hysteresis below
  // instead, which is what the iOS review asked for (rootMargin OR hysteresis)
  // and which does not distort WHERE the flip happens.
  const RAIL_ATC_ROOT_MARGIN = '0px';
  // Rubber-band absorber. Must be TIME-based, not callback-count based:
  // IntersectionObserver fires once per threshold CROSSING, never repeatedly
  // for a state it is already in, so "require two consecutive agreeing
  // callbacks" starves -- the first flip lands and every flip after it is
  // blocked forever. That exact bug was written here first and caught only by
  // driving the real page (the bar stopped hiding at the rail entirely).
  // An elastic bounce crosses out and back within a few frames, so it cancels
  // itself inside this window; a real scroll settles and applies.
  const RAIL_ATC_SETTLE_MS = 120;
  let _railAtcObserver = null;
  let _railAtcLastState = null;
  let _railAtcSettleTimer = null;

  function _observeRailAtcForStickyBar() {
    try {
      if (_railAtcObserver) return _railAtcObserver;
      if (typeof window.IntersectionObserver !== 'function') return null;

      const rail = DOM.getElement(RAIL_ATC_SELECTOR);
      const bar = DOM.getElement('.murphy-door-configurator__sticky-bar');
      const wrapper = DOM.getElement('.murphy-door-configurator-wrapper');
      if (!rail || !bar || !wrapper) return null;

      _railAtcObserver = new window.IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const railVisible = entry.isIntersecting;

            // Already where we want to be -- cancel any in-flight flip. This is
            // the branch that eats an elastic bounce: it crosses out and back
            // inside RAIL_ATC_SETTLE_MS and the pending flip is dropped.
            if (railVisible === _railAtcLastState) {
              window.clearTimeout(_railAtcSettleTimer);
              _railAtcSettleTimer = null;
              return;
            }

            window.clearTimeout(_railAtcSettleTimer);
            _railAtcSettleTimer = window.setTimeout(() => {
              _railAtcSettleTimer = null;
              _railAtcLastState = railVisible;
              bar.classList.toggle(RAIL_ATC_HIDES_BAR_CLASS, railVisible);
              publishEvent(CONSTANTS.EVENTS.UI_STICKY_BAR_VISIBILITY_CHANGED, {
                stickyBarHidden: railVisible,
                reason: railVisible
                  ? 'rail_atc_in_viewport'
                  : 'rail_atc_offscreen',
                timestamp: Date.now(),
              });
            }, RAIL_ATC_SETTLE_MS);
          });
        },
        { root: null, rootMargin: RAIL_ATC_ROOT_MARGIN, threshold: 0 }
      );

      _railAtcObserver.observe(rail);
      // Only now stand the CSS fallback down -- if anything above returned
      // early, the fallback is still the customer's protection.
      wrapper.setAttribute(ATC_OBSERVER_ATTR, 'active');

      isDev() &&
        console.log(
          '[UIUpdater] 👁 Sticky-bar visibility now tracks the rail ATC'
        );
      return _railAtcObserver;
    } catch (error) {
      // Fail OPEN: leave the CSS fallback armed rather than risk a page with
      // no working Add to Cart at all.
      isDev() &&
        console.error('[UIUpdater] Rail-ATC observer setup failed:', error);
      return null;
    }
  }

  function _disconnectRailAtcObserver() {
    try {
      if (_railAtcObserver) _railAtcObserver.disconnect();
      window.clearTimeout(_railAtcSettleTimer);
      _railAtcSettleTimer = null;
      _railAtcObserver = null;
      _railAtcLastState = null;
      const wrapper = DOM.getElement('.murphy-door-configurator-wrapper');
      if (wrapper) wrapper.removeAttribute(ATC_OBSERVER_ATTR);
    } catch (error) {
      isDev() &&
        console.error('[UIUpdater] Rail-ATC observer teardown failed:', error);
    }
  }

  function _promoteAddToCartToStickyBar() {
    try {
      const rail = DOM.getElement(RAIL_ATC_SELECTOR);
      if (!rail) return null;
      if (rail.classList.contains(PROMOTED_ATC_CLASS)) return rail;

      // Fails OPEN, deliberately: a browser that cannot answer the query gets
      // the promotion rather than silently losing the feature. Every engine
      // this theme targets has matchMedia; the guard is for headless/jsdom
      // contexts, where "unknown viewport" must not read as "mobile".
      if (
        typeof window.matchMedia === 'function' &&
        !window.matchMedia(PROMOTION_DESKTOP_QUERY).matches
      ) {
        return null;
      }

      const bar = DOM.getElement(STICKY_ACTIONS_BAR_SELECTOR);
      if (!bar) return null;

      // M1. The destination is `position: sticky` inside the gallery column and
      // drops off screen with it; moving the button there while it is gone is
      // what QA #14 reported as the Add to Cart ceasing to exist. Leave it in
      // the rail -- where the customer just clicked their last option -- and
      // let _observeStickyBarHomeForAtc() promote it if they scroll back up.
      if (!_stickyBarCanShowAddToCart(bar)) {
        isDev() &&
          console.log(
            '[UIUpdater] ⏸ Add to Cart stays in the rail: the actions bar is off screen'
          );
        return null;
      }

      // `:scope >` so a button-group nested deeper in the bar (none today, but
      // the bar composes several snippets) cannot become the insertion point.
      const buttonGroup = bar.querySelector(
        ':scope > .murphy-door-configurator__button-group'
      );

      // Remember exactly where the section rendered this block so a demotion
      // can restore it rather than guessing. Captured BEFORE the move, and only
      // once -- re-capturing after a promotion would record the actions bar as
      // "home" and make the demotion a no-op.
      if (!_atcRailHome && rail.parentElement) {
        _atcRailHome = {
          parent: rail.parentElement,
          nextSibling: rail.nextElementSibling,
        };
      }

      if (buttonGroup) {
        buttonGroup.insertAdjacentElement('afterend', rail);
      } else {
        // The button-group is the documented anchor, but its absence must not
        // strand the ATC in a rail nobody is looking at any more.
        bar.appendChild(rail);
      }

      rail.classList.add(PROMOTED_ATC_CLASS);
      bar.classList.add(STICKY_BAR_HAS_ATC_CLASS);

      publishEvent(CONSTANTS.EVENTS.UI_ADD_TO_CART_PROMOTED, {
        promoted: true,
        insertedAfterButtonGroup: !!buttonGroup,
        timestamp: Date.now(),
      });

      isDev() &&
        console.log(
          '[UIUpdater] 🎯 Add to Cart promoted into the sticky actions bar'
        );

      return rail;
    } catch (error) {
      // A failed promotion must never take the celebration down with it: the
      // confetti still has three other anchors and the ATC is still in the rail.
      isDev() &&
        console.error('[UIUpdater] Add to Cart promotion failed:', error);
      return null;
    }
  }

  /**
   * Put the Add to Cart back in the rail.
   *
   * The exact inverse of _promoteAddToCartToStickyBar(): same single node, same
   * "move, never clone" rule (snippets/configurator-add-to-cart.liquid is
   * emphatic that the control exists once per page, and core-event-handler
   * delegates the real click BY CLASS, so a copy would be fully live).
   *
   * Restores the captured position rather than appending, so the block lands
   * back between `.murphy-door-configurator__categories` and the below-rail
   * blocks exactly as sections/md-configurator.liquid rendered it.
   *
   * Idempotent, and a no-op before the first promotion.
   *
   * @returns {HTMLElement|null} the demoted block, or null if nothing moved
   */
  function _demoteAddToCartToRail() {
    try {
      const rail = DOM.getElement(RAIL_ATC_SELECTOR);
      if (!rail) return null;
      if (!rail.classList.contains(PROMOTED_ATC_CLASS)) return null;

      const bar = DOM.getElement(STICKY_ACTIONS_BAR_SELECTOR);

      // The captured home is preferred, but only while it is still part of the
      // live document. A home that has been detached by a re-render would
      // otherwise take the Add to Cart out of the page entirely -- a silent,
      // worse version of the bug this function exists to prevent. The fallback
      // is the position sections/md-configurator.liquid documents: immediately
      // after the category container, which is exactly where the rail block is
      // rendered.
      const home =
        _atcRailHome && _atcRailHome.parent && _atcRailHome.parent.isConnected
          ? _atcRailHome
          : null;

      if (home) {
        // insertBefore(node, null) === appendChild, which is the correct
        // behaviour when the block was the last child of its home.
        const { parent, nextSibling } = home;
        parent.insertBefore(
          rail,
          nextSibling && nextSibling.parentElement === parent
            ? nextSibling
            : null
        );
      } else {
        const categories = DOM.getElement(
          '.murphy-door-configurator__categories'
        );
        if (!categories || !categories.parentElement) return null;
        categories.insertAdjacentElement('afterend', rail);
      }

      rail.classList.remove(PROMOTED_ATC_CLASS);
      if (bar) bar.classList.remove(STICKY_BAR_HAS_ATC_CLASS);

      publishEvent(CONSTANTS.EVENTS.UI_ADD_TO_CART_PROMOTED, {
        promoted: false,
        reason: 'sticky_bar_offscreen',
        timestamp: Date.now(),
      });

      isDev() &&
        console.log('[UIUpdater] ↩️ Add to Cart returned to the category rail');

      return rail;
    } catch (error) {
      // Failing here would leave the button in an off-screen bar, which is the
      // very bug this exists to prevent -- so it is logged loudly and the next
      // scroll settle gets another attempt.
      isDev() &&
        console.error('[UIUpdater] Add to Cart demotion failed:', error);
      return null;
    }
  }

  /**
   * Park the Add to Cart wherever the customer can actually see it.
   *
   * Runs only once the configuration has been completed at least once (the
   * celebration arms it), so nothing about the pre-completion layout changes.
   *
   * @returns {'promoted'|'demoted'|'idle'}
   */
  function _reconcileAddToCartHome() {
    try {
      if (!_atcPromotionArmed) return 'idle';

      // A desktop -> narrow resize must hand the block back: the promoted
      // styling is scoped to (min-width: 801px), and below it the bar reverts
      // to `display: none` unless stuck -- which would hide the button again.
      // Mobile keeps its own persistent Add to Cart
      // (snippets/configurator-sticky-bar.liquid), so the rail is the right
      // home there in every case.
      if (
        typeof window.matchMedia === 'function' &&
        !window.matchMedia(PROMOTION_DESKTOP_QUERY).matches
      ) {
        return _demoteAddToCartToRail() ? 'demoted' : 'idle';
      }

      const bar = DOM.getElement(STICKY_ACTIONS_BAR_SELECTOR);
      if (!bar) return 'idle';

      if (_stickyBarCanShowAddToCart(bar)) {
        return _promoteAddToCartToStickyBar() ? 'promoted' : 'idle';
      }
      return _demoteAddToCartToRail() ? 'demoted' : 'idle';
    } catch (error) {
      isDev() && console.error('[UIUpdater] ATC home reconcile failed:', error);
      return 'idle';
    }
  }

  /**
   * Watch the actions bar's host so the Add to Cart follows the customer
   * between its two homes.
   *
   * 🚨 The HOST is observed, not the bar. The bar is `display: none` until it
   * is `--stuck`, and IntersectionObserver reports a display:none target as
   * permanently not-intersecting -- so observing it directly would latch
   * "demoted" and the promotion could never come back. The host is laid out at
   * all times and geometrically contains the bar.
   *
   * Same shape as _observeRailAtcForStickyBar() below, deliberately: root null
   * so it measures real rendered geometry through however many clipping
   * ancestors sit in between, and the same time-based settle window rather than
   * a callback count (an IntersectionObserver fires once per CROSSING, so
   * "require two agreeing callbacks" starves after the first flip).
   */
  function _observeStickyBarHomeForAtc() {
    try {
      if (_atcHomeObserver) return _atcHomeObserver;
      if (typeof window.IntersectionObserver !== 'function') return null;

      const bar = DOM.getElement(STICKY_ACTIONS_BAR_SELECTOR);
      const host = bar && bar.parentElement;
      if (!host) return null;

      _atcHomeObserver = new window.IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const reachable = entry.isIntersecting;
            if (reachable === _atcHomeLastState) {
              window.clearTimeout(_atcHomeSettleTimer);
              _atcHomeSettleTimer = null;
              return;
            }
            window.clearTimeout(_atcHomeSettleTimer);
            _atcHomeSettleTimer = window.setTimeout(() => {
              _atcHomeSettleTimer = null;
              _atcHomeLastState = reachable;
              _reconcileAddToCartHome();
            }, RAIL_ATC_SETTLE_MS);
          });
        },
        { root: null, rootMargin: '0px', threshold: 0 }
      );

      _atcHomeObserver.observe(host);

      // A resize across the desktop boundary changes the answer without any
      // scrolling, so it gets its own hook. addEventListener rather than
      // onchange: Safari < 14 has no MediaQueryList.addEventListener, and the
      // optional-call keeps that engine from throwing here.
      if (typeof window.matchMedia === 'function' && !_atcHomeMediaQuery) {
        _atcHomeMediaQuery = window.matchMedia(PROMOTION_DESKTOP_QUERY);
        if (typeof _atcHomeMediaQuery.addEventListener === 'function') {
          _atcHomeMediaQuery.addEventListener(
            'change',
            _reconcileAddToCartHome
          );
        }
      }

      isDev() &&
        console.log(
          '[UIUpdater] 👁 Add to Cart home now tracks the actions bar'
        );
      return _atcHomeObserver;
    } catch (error) {
      // Fail OPEN: whatever the promotion already did stands, rather than
      // risking a page with no working Add to Cart at all.
      isDev() &&
        console.error('[UIUpdater] ATC home observer setup failed:', error);
      return null;
    }
  }

  function _disconnectStickyBarHomeObserver() {
    try {
      if (_atcHomeObserver) _atcHomeObserver.disconnect();
      window.clearTimeout(_atcHomeSettleTimer);
      if (
        _atcHomeMediaQuery &&
        typeof _atcHomeMediaQuery.removeEventListener === 'function'
      ) {
        _atcHomeMediaQuery.removeEventListener(
          'change',
          _reconcileAddToCartHome
        );
      }
      _atcHomeObserver = null;
      _atcHomeSettleTimer = null;
      _atcHomeLastState = null;
      _atcHomeMediaQuery = null;
    } catch (error) {
      isDev() &&
        console.error('[UIUpdater] ATC home observer teardown failed:', error);
    }
  }

  /**
   * First confetti anchor that is RENDERED and ON SCREEN.
   *
   * 🚨 CAPABILITY, NOT PRESENCE — this is the whole point of the function.
   * apo_style keeps `.murphy-door-configurator__progress-bar` in the DOM at
   * `display: none`, so a truthy `querySelector` check passed while its
   * getBoundingClientRect() was all zeros, and the burst resolved to (0, 0):
   * the top-left corner of the screen. Measured live on staging (apo_style,
   * 1440x900) before the fix: `{x: 0, y: 0, source: 'progress_bar'}`.
   * Same family as the cart-drawer incident in CLAUDE.md — an element being
   * *there* proves nothing about it being *usable*.
   *
   * Off-screen anchors are rejected too, not just zero-sized ones: the
   * add-to-cart wrapper sits ~6000px down the apo_style scroll, and bursting
   * confetti where the customer cannot see it is no better than the corner.
   *
   * @returns {{selector: string, origin: string, x: number, y: number, rect: Object}|null}
   */
  function _resolveConfettiAnchor() {
    for (let i = 0; i < CONFETTI_ANCHORS.length; i++) {
      const { selector, origin } = CONFETTI_ANCHORS[i];
      let el = null;
      try {
        el = DOM.getElement(selector);
      } catch (error) {
        continue;
      }
      if (!el || typeof el.getBoundingClientRect !== 'function') continue;

      const r = el.getBoundingClientRect();
      if (!(r.width > 0 && r.height > 0)) continue;

      const onScreen =
        r.bottom > 0 &&
        r.top < window.innerHeight &&
        r.right > 0 &&
        r.left < window.innerWidth;
      if (!onScreen) continue;

      // Always horizontally centred; `origin` decides the vertical.
      //   'centre' — the middle of the box, so the burst reads as coming from
      //              the button itself (promoted Add to Cart).
      //   'top'    — the top edge, so particles rise OUT of a wide, short
      //              element instead of being masked by it.
      const x = r.left + r.width / 2;
      const y = origin === 'centre' ? r.top + r.height / 2 : r.top;

      // 🚨 A 'centre' anchor additionally requires its ORIGIN POINT on screen,
      // not merely part of its box. Measured live (apo_style, 1512x772): the
      // promoted button sat at top 748 in a 772px viewport, so the rect test
      // passed on 24 visible pixels while the centre it would have burst from
      // was 2px BELOW the fold. The rect test is the right one for a 'top'
      // anchor -- a progress bar straddling the top edge is still something
      // the customer can see rise -- but a centre origin exists precisely to
      // say "come out of THIS control", and a point nobody can see cannot do
      // that. Falling through costs nothing: the next anchor is the sticky bar
      // this button lives in, which is on screen by construction.
      if (origin === 'centre' && (y < 0 || y > window.innerHeight)) continue;

      return {
        selector,
        origin,
        x,
        y,
        rect: {
          left: r.left,
          top: r.top,
          width: r.width,
          height: r.height,
        },
      };
    }
    return null;
  }

  // One formatter for every option-price surface -- it lives in Phase 0
  // (utils.formatOptionPrice) so the slider tooltip shares it too. Trailing
  // ".00" is dropped on whole dollars only; real cents pass through.
  function formatConfiguratorPrice(price, { zeroLabel = null } = {}) {
    const utils = window.MurphyDoorConfigurator.utils;
    if (utils && utils.formatOptionPrice) {
      return utils.formatOptionPrice(price, { zeroLabel });
    }
    // Guarded fallback: a missing Phase 0 utility must not take prices down
    // (or turn "(Included)" into "$0"). Only the ".00" trim is lost.
    if (price === null || price === undefined) return '';
    const raw = String(price).trim();
    if (raw === '') return '';
    if (zeroLabel && parseFloat(raw.replace(/[$,]/g, '')) === 0) {
      return zeroLabel;
    }
    return '$' + raw.replace(/^\$/, '');
  }

  // Latest shipping time value, updated SYNCHRONOUSLY by updateShippingTime()
  // before the 300ms fade animation completes. This allows
  // patchDeliveryThumbnailCaptions() to read the correct value even when the
  // DOM span still shows the old text mid-animation.
  let _latestShippingTime = '';

  // Latest RUSH shipping time value, computed from _latestShippingTime.
  // Mirrors the _latestShippingTime pattern for rush fee delivery thumbnails.
  // Rush time = base production time reduced by 1 week on each bound.
  let _latestRushShippingTime = '';

  // Raw (numeric) base shipping time range, set in updateShippingTime() from
  // updates.shippingTime.range (conductor-provided). Used by _computeRushShippingTime
  // so it operates on structured data instead of parsing the formatted text string.
  let _latestRawShippingTime = null;

  // Carrier transit window in business days ({low, high}), from
  // shippingTime.transitDays. null until a payload carries it.
  let _latestTransitDays = null;

  // Rush Fee product formatted price, lazily loaded from PPD on first use.
  // Avoids hardcoding a price — reads from Shopify product data so price
  // changes in admin are automatically reflected without code changes.
  let _rushFeeFormattedPrice = '';

  // Fullscreen modal takeover state
  let _savedScrollY = 0;
  let _fullscreenCleanupFns = [];
  let _userExitedFullscreen = false; // Prevents auto-re-entry after explicit exit

  // apo_style ATC-reveal-on-completion: tracks the previous completion state
  // so the scroll-into-view only fires on the incomplete->complete
  // TRANSITION, not on every updateSummaryStatus() call (multiple unrelated
  // events re-run this function while already complete -- re-scrolling each
  // time would yank the user's scroll position around).
  let _wasSummaryComplete = false;

  // ============================================================================
  // UNAPPLY BROWSING LOCK: Prevents stale MATERIAL_FILTER_STATE_UPDATED events
  // from overwriting a category's default browsing HTML after unapply.
  //
  // RACE CONDITION FIX: When user unapplies a driver category (e.g., Wood & Finish),
  // the conductor publishes events in sequence: STATE_SELECTION_CLEARED, then
  // UI_USER_UNAPPLY_REQUESTED (which sets default HTML), then
  // SELECTION_USER_UNAPPLY_COMPLETED (which clears preferences). During this
  // sequence, intermediate state or stale events can trigger
  // MATERIAL_FILTER_STATE_UPDATED which overwrites the just-restored default HTML.
  //
  // PRIMARY FIX: MaterialPreferenceCoordinator now batch-clears all preferences
  // atomically (no intermediate states). This Set provides DEFENSE-IN-DEPTH:
  // handleUserUnapply() adds the unapplied categoryId here, and
  // handleMaterialFilterStateUpdated skips it during the re-render loop.
  // The lock auto-expires after 200ms (covers synchronous event cascade window)
  // or is cleared on MATERIAL_PREFERENCE_APPLIED (new user selection).
  //
  // Only the SOURCE category (the one being unapplied) is locked -- NOT downstream
  // categories, which may need legitimate re-renders for Scenario 1 (buddy unapply).
  // ============================================================================
  const _unapplyBrowsingLockedCategories = new Set();
  const UNAPPLY_LOCK_TTL_MS = 200; // Covers synchronous event cascade window

  if (CoreEventBus) {
    const unsubBootstrap = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        isDev() && console.log('[UIUpdater] Bootstrap complete event received');
        bootstrapComplete = true;

        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              `[UIUpdater] Processing ${pendingCalls.length} queued calls`
            );
          pendingCalls.forEach((call) => {
            try {
              call.fn.apply(call.context, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[UIUpdater] Error processing queued call:',
                  error
                );
            }
          });
          pendingCalls = [];
        }
      }
    );
    // Note: cleanupHandlers is defined at line ~217; we'll add this after it's available
    // The unsubBootstrap is stored as a module-level reference for cleanup
    const _unsubBootstrap = unsubBootstrap;
    isDev() &&
      console.log(
        '[UIUpdater] Subscribed to bootstrap event at module load time'
      );
  }

  /**
   * RACE CONDITION FIX: Safe accessor for conductor (.main) with initialization guard
   * Prevents "Cannot read properties of undefined" errors when ui-updater loads before conductor
   *
   * @returns {Object|null} The conductor instance or null if not yet initialized
   */
  const getConductor = () => {
    if (!window.MurphyDoorConfigurator || !window.MurphyDoorConfigurator.main) {
      isDev() &&
        console.warn(
          '⚠️ UI UPDATER: Conductor not yet initialized, operation deferred'
        );
      return null;
    }
    return window.MurphyDoorConfigurator.main;
  };

  /**
   * The key `_markAppliedTileInGrid` stamps on a category element
   * (`data-md-resume-tier-attempt`) once it has tried to resume that
   * category's variant tier for one applied (product, variant) pair.
   * Shared so restoreThumbnailView can stamp the SAME key when the customer
   * deliberately backs out of a drill-down -- see the note there.
   *
   * @param {string} appliedProduct - Applied productId as a string ('' if none)
   * @param {string} appliedVariant - Applied variantId as a string ('' if none)
   * @returns {string}
   */
  function resumeTierAttemptKey(appliedProduct, appliedVariant) {
    return appliedProduct + '::' + appliedVariant;
  }

  /**
   * What sub-step (variant tier) a category card's thumbnails container is
   * showing, if any.
   *
   * `direct` is the product-less variant grid some categories render as
   * their DEFAULT view (data pipeline `variant-view--direct`): it is not a
   * drill-down, there is no product grid behind it and no back button.
   * `productId` is the product the drill-down belongs to, read from the
   * left-hand label tile generateVariantLayout / the composite path render
   * (null when it cannot be read).
   *
   * @param {Element|null} thumbnailsContainer
   * @returns {{view: Element, direct: boolean, productId: (string|null)}|null}
   *   null when the container shows no variant view at all
   */
  function readDrilledInView(thumbnailsContainer) {
    const view =
      thumbnailsContainer &&
      thumbnailsContainer.querySelector(
        '.murphy-door-configurator__variant-view'
      );
    if (!view) return null;
    const direct = view.classList.contains(
      'murphy-door-configurator__variant-view--direct'
    );
    const label = direct
      ? null
      : view.querySelector(
          '.murphy-door-configurator__selected-multi-variant-product [data-product-id]'
        );
    return {
      view,
      direct,
      productId: label ? String(label.getAttribute('data-product-id')) : null,
    };
  }

  /**
   * Read a composite map by group id, tolerating the two group-id spellings.
   *
   * 🚨 The composite group id is built two ways and they disagree for any
   * category whose name contains a non-alphanumeric character: the product's
   * `id` collapses runs of separators ("Wood & Finish" -> wood-finish) while
   * `data-group-id` -- which is what these maps are KEYED by -- does not
   * ("Wood & Finish" -> wood---finish). A click derives the collapsed form from
   * the product id whenever the tile carries no data-group-id attribute, so the
   * exact lookup misses and the drill-down silently never renders. Measured live
   * 2026-09-17 on Wood & Finish: 0 variant tiles, no console error (the composite
   * path's own log is isDev()-gated), customers unable to pick a wood or finish.
   *
   * Matching on the collapsed form changes no keys, so a PPD already cached in a
   * customer's IndexedDB (built under the old format) still resolves.
   *
   * @param {Map} map - compositeGroupIdToProductMap or compositeProductToVariantGridHTMLMap
   * @param {string} groupId
   * @returns {*} the mapped value, or undefined
   */
  function getByCompositeGroupId(map, groupId) {
    if (!map || !groupId) return undefined;
    const direct = map.get(groupId);
    if (direct !== undefined) return direct;
    const collapseRuns = (value) => String(value).replace(/-{2,}/g, '-');
    const wanted = collapseRuns(groupId);
    // 🚨 REFUSE AN AMBIGUOUS MATCH. Collapsing runs is many-to-one, so two
    // distinct keys CAN collapse alike ("group_a--b" and "group_a-b"). Taking
    // the first hit would silently hand back the wrong composite -- a customer
    // getting a finish they never chose, charged and shipped, with nothing on
    // screen to show it. A refusal degrades to the (visible) dead drill-down
    // instead. Measured 2026-09-17 on the live PPD: 42 keys, 42 distinct
    // collapsed forms, ZERO collisions on both composite maps, so this branch
    // is dormant on today's catalogue -- but the catalogue is Shopify data and
    // CI cannot see a new "&" or "/" category arrive.
    let match;
    for (const [key, value] of map) {
      if (collapseRuns(key) !== wanted) continue;
      if (match !== undefined) return undefined; // two keys collapse alike
      match = value;
    }
    return match;
  }

  /**
   * The category's answer, asked of BOTH stores.
   *
   * 🚨 getAppliedSelectionsMap() is the FRAGILE one: entering BROWSING deletes
   * its entry while appliedSelectionsSnapshot keeps the answer (the "TWO
   * Applied-Selection Stores" footgun in CLAUDE.md), and the snapshot is what
   * price and cart read. Anything that decides what the CARD shows must agree
   * with what the customer is charged for, so it has to consult both --
   * syncAppliedTileRings already reads the snapshot, so a Map-only reader beside
   * it paints the ring and the outline from two different truths, which is the
   * "two cues disagree, looks like a rendering fault" failure _markAppliedTileInGrid
   * warns about in its own comment.
   *
   * @param {Object} conductor
   * @param {string} categoryId
   * @returns {Object|null} the applied selection, or null when neither store has one
   */
  function resolveAppliedAnswer(conductor, categoryId) {
    if (!conductor || !categoryId) return null;
    return (
      conductor.getAppliedSelectionsMap?.()?.get(categoryId) ||
      conductor.getAppliedSelections?.()?.[categoryId] ||
      null
    );
  }

  /**
   * apo_style renders ONE continuous page with every card permanently
   * expanded, so no programmatic page scroll is ever needed to "reveal" a
   * card -- it is already rendered, already open, and already reachable.
   * Every scroll this module fired in that mode was therefore pure
   * involuntary movement: it took the page away from a customer who was
   * mid-read, or mid-comparison on the card they had just answered.
   *
   * Ryan, UAT 2026-08-27: the customer controls the scroll, and must be able
   * to "click into and do any of the configurator boxes they want in any
   * order".
   *
   * classic/dual DO collapse their cards, so their scrolls are what make a
   * newly-opened accordion visible at all -- this helper exists precisely so
   * the suppression stays scoped to apo_style and never reaches them.
   *
   * @returns {boolean}
   */
  const isApoStyleExperience = () => {
    try {
      return getConductor()?._getConfiguratorExperience?.() === 'apo_style';
    } catch (error) {
      // A failed mode probe must never suppress a scroll classic/dual needs.
      isDev() &&
        console.warn('[UIUpdater] isApoStyleExperience failed:', error);
      return false;
    }
  };

  /**
   * Fast PPD accessor with error handling
   * Centralizes the conductor → PPD access pattern
   * Eliminates redundant null-checking throughout the module
   *
   * @returns {Object|null} PPD or null if not available
   */
  const getPPD = () => {
    const conductor = getConductor();
    if (!conductor) {
      return null;
    }

    const ppd = conductor.getPerfectProductDictionary();
    if (!ppd) {
      isDev() && console.warn('⚠️ UI UPDATER: PPD not available');
      return null;
    }

    return ppd;
  };

  /**
   * 🚀 ANTI-PATTERN #1 FIX: Module-level PPD cache to reduce function call overhead
   * Caches PPD reference to avoid repeated conductor delegation calls
   * Cache is invalidated every 100ms to stay current with PPD updates
   */
  let cachedPPD = null;
  let ppdCacheTimestamp = 0;

  const getOrCachePPD = () => {
    const now = performance.now();
    // Re-fetch PPD if cache is stale (> 100ms old) or doesn't exist
    if (!cachedPPD || now - ppdCacheTimestamp > 100) {
      cachedPPD = getPPD();
      ppdCacheTimestamp = now;
    }
    return cachedPPD;
  };

  /**
   * 🎯 PRIORITY 3 FIX: Module-level material state cache for event-driven state access
   * Enables synchronous access to material state while using asynchronous event patterns
   * Cache is updated via event subscriptions (see initialization section) and used for immediate UI decisions
   * Pattern: Request state via event → cache updates asynchronously → UI uses cached value synchronously
   */
  let materialStateCache = new Map();

  /**
   * 🚀 PERF: Memoizes generateSmartSortedThumbnails()'s expensive portion (PPD lookup +
   * applyMaterialFilterClasses + applyBubbleUpTransformations). Measured live 2026-09-16
   * via scripts/configurator-residue-probe.mjs: this function runs ~60.8x per selection and
   * returns byte-identical output ~87% of the time, at ~28% of per-selection long-task time.
   *
   * NOT the whole function — deliberately excludes patchDeliveryThumbnailCaptions(), which
   * depends on module-level _latestShippingTime that can change independent of category/
   * material state. That patch always runs fresh on the cached-or-fresh HTML (see call site).
   *
   * 🚨 Entries are ONLY written once PPD Layer 5 hydration has completed for a category
   * (thumbnailsCache.default !== null). md-config-html-cache-hydrator.js fills that field
   * ASYNCHRONOUSLY post-bootstrap by mutating the cache entry in place, so a call landing
   * before hydration completes would otherwise memoize the "Cache not hydrated" placeholder
   * under a normal-looking key with nothing to ever invalidate it. See generateSmartSortedThumbnails.
   */
  let thumbnailMemoCache = new Map();
  const THUMBNAIL_MEMO_MAX_SIZE = 200;

  /**
   * bubbleUpVariants is a `Map<productId, bubbleData>` (see
   * material-preference-coordinator.js:1064, `Object.freeze(bubbleUpVariants...)`), NOT a
   * plain object. `JSON.stringify(aMap)` always serializes to `'{}'` regardless of the
   * Map's contents — using it directly in the cache key would make every distinct
   * bubble-up state collide into one key, silently serving a stale bubbled variant's
   * photo/price after the customer's selection changed the bubble-up set. Sorted so key
   * order (not guaranteed stable across rebuilds) can't produce two different strings for
   * the same content.
   */
  function serializeBubbleUpVariants(bubbleUpVariants) {
    if (!bubbleUpVariants || typeof bubbleUpVariants.entries !== 'function') {
      return '';
    }
    return Array.from(bubbleUpVariants.entries())
      .map(([productId, data]) => productId + ':' + JSON.stringify(data))
      .sort()
      .join(',');
  }

  /**
   * Cheap string-concat cache key for the thumbnail memo cache — no JSON.stringify of large
   * structures, since this runs on every one of ~60 calls/selection.
   */
  function buildThumbnailMemoKey(categoryId, materialState, filterState) {
    return (
      categoryId +
      '|' +
      (materialState?.woodSku || '') +
      '|' +
      (materialState?.finishSku || '') +
      '|' +
      (filterState?.incompatibleVariantIds
        ? filterState.incompatibleVariantIds.join(',')
        : '') +
      '|' +
      serializeBubbleUpVariants(filterState?.bubbleUpVariants)
    );
  }

  /** Bounded set — delete-oldest on overflow (Map preserves insertion order). */
  function setThumbnailMemoEntry(key, html) {
    if (thumbnailMemoCache.size >= THUMBNAIL_MEMO_MAX_SIZE) {
      const oldestKey = thumbnailMemoCache.keys().next().value;
      thumbnailMemoCache.delete(oldestKey);
    }
    thumbnailMemoCache.set(key, html);
  }

  /**
   * 🎯 EVENT-DRIVEN THUMBNAIL GENERATION: Module-level state for async request/response coordination
   * Enables Promise-based wrapper around event-driven thumbnail generation
   * Tracks pending requests with unique IDs for request/response matching
   * Pattern: Publish request event → NavigationManager processes → Resolve Promise on response event
   */
  let pendingThumbnailRequests = new Map();
  let requestIdCounter = 0;

  /**
   * Performance thresholds for UI operations monitoring
   * Defines acceptable execution times for various UI update operations
   */
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    UI_UPDATE: 150, // ms - Category display and thumbnail rendering threshold
    VARIANT_VIEW_GENERATION: 100, // ms - Composite product variant view generation threshold
    THUMBNAIL_GENERATION: 50, // ms - Individual thumbnail generation threshold
  });

  /**
   * Generate unique request ID for thumbnail generation requests
   * Used for matching request/response events in async coordination
   *
   * @returns {string} Unique request ID
   */
  function generateRequestId() {
    return `thumbnail_request_${Date.now()}_${++requestIdCounter}`;
  }

  /**
   * 🎯 PPD-FIRST MATERIAL FILTERING: Apply material-filtered-out class via string replacement
   * Zero DOM queries - operates entirely on cached HTML strings before DOM insertion.
   * Follows the same pattern as the --active class string replacement (lines 1501-1502).
   *
   * Uses regex to handle whitespace variations between class attribute and data-variant-id
   * (the data-pipeline generates HTML with newlines between these attributes).
   *
   * @param {string} html - Cached HTML string from PPD
   * @param {string} categoryId - Category ID to get filter state for
   * @returns {string} HTML with material-filtered-out classes applied to incompatible variants
   */
  function applyMaterialFilterClasses(html, categoryId) {
    const conductor = getConductor();
    if (!conductor || typeof conductor.getMaterialFilterState !== 'function') {
      return html;
    }

    // EXPLICIT source-category guard. The check below used to rely on the
    // assumption stated in its own comment -- that the master category (Wood &
    // Finish) is absent from categoryMaterialAwareness because it IS the source
    // of preferences. That assumption is FALSE against the real PPD: measured
    // live 2026-08-17, categoryMaterialAwareness contains 'Wood & Finish' with
    // affectedByWood/affectedByFinish both true, so the guard never fired and
    // the wood card was filtered against its own selection --
    // getMaterialFilterState('Wood & Finish') returned 25 incompatible variants
    // and 1 compatible. Every other wood rendered display:none and
    // pointer-events:none, i.e. exactly the "trapping the user with no way to
    // change their wood selection" outcome the original comment was written to
    // prevent. Daniel: "we're still losing all visible progress on the door
    // when we swap wood types."
    //
    // Naming the source categories outright is the fix -- it cannot silently
    // stop being true the way the absence assumption did.
    //
    // NOT scoped to apo_style: this restores the documented intent of an
    // existing guard rather than changing behaviour, and the trap applies to
    // every experience. It does mean classic/dual are affected, so it needs a
    // classic UAT row.
    const materialSourceCategories = CONSTANTS.MATERIAL_SOURCE_CATEGORIES || [];
    if (materialSourceCategories.indexOf(categoryId) !== -1) {
      return html;
    }

    // Guard: Only apply material filter to categories that are DEPENDENT on material
    // preferences (listed in categoryMaterialAwareness).
    const ppd = getPPD();
    if (!ppd?.categoryMaterialAwareness?.get(categoryId)) {
      return html;
    }

    const filterState = conductor.getMaterialFilterState(categoryId);
    if (
      !filterState ||
      !filterState.incompatibleVariantIds ||
      filterState.incompatibleVariantIds.length === 0
    ) {
      return html;
    }

    let filteredHTML = html;
    const filterTitle =
      'This option does not match your selected wood &amp; finish';

    filterState.incompatibleVariantIds.forEach((variantId) => {
      // BUGFIX: Use [^>]*? instead of [\s\S]*? to prevent cross-element matching.
      // The old regex used [\s\S]*? which could match ACROSS element boundaries,
      // causing the material-filtered-out class to be injected into a PREVIOUS
      // element's class attribute (e.g., a compatible variant's class), producing
      // an inverted filter where compatible variants were disabled and incompatible
      // ones stayed clickable. [^>]*? stays within the same opening HTML tag by
      // refusing to cross the > character that ends the tag.
      const searchPattern = new RegExp(
        '(class="[^"]*murphy-door-configurator__thumbnail-item[^"]*")((?:[^>]*?)data-variant-id="' +
          variantId +
          '")',
        'g'
      );
      const replacement = function (match, classAttr, rest) {
        // Inject material-filtered-out class before the closing quote of the class attribute
        const updatedClass = classAttr.replace(/"$/, ' material-filtered-out"');
        return updatedClass + rest + ' title="' + filterTitle + '"';
      };
      filteredHTML = filteredHTML.replace(searchPattern, replacement);
    });

    isDev() &&
      console.log(
        '🎨 UI UPDATER: Applied material filter classes for category ' +
          categoryId +
          ' (' +
          filterState.incompatibleVariantIds.length +
          ' variants filtered, ' +
          filterState.compatibleVariantIds.length +
          ' compatible)'
      );

    // 🔥 CRITICAL FIX: Hide product thumbnails that have ZERO compatible variants
    // Build product-level compatibility map from variant-level filter state
    const compatibleVariantSet = new Set(filterState.compatibleVariantIds);
    const productCompatibilityMap = new Map();

    // Get category data from PPD to access product-variant relationships
    // Note: ppd already fetched above for the categoryMaterialAwareness guard
    const categoryData = ppd?.categories?.get(categoryId);

    isDev() &&
      console.log(
        '🔍 PRODUCT FILTER DEBUG: categoryId=' +
          categoryId +
          ', categoryData=' +
          (categoryData ? 'exists' : 'null') +
          ', products=' +
          (categoryData && categoryData.products
            ? categoryData.products.length
            : 0)
      );

    if (categoryData && categoryData.products) {
      categoryData.products.forEach(function (product) {
        // Count how many variants of this product are compatible
        let compatibleCount = 0;
        if (product.variants && Array.isArray(product.variants)) {
          product.variants.forEach(function (variant) {
            if (compatibleVariantSet.has(variant.id)) {
              compatibleCount++;
            }
          });
        }

        productCompatibilityMap.set(product.id, {
          compatibleVariantCount: compatibleCount,
          totalVariantCount: product.variants ? product.variants.length : 0,
        });

        // Debug log for products with zero compatible variants
        if (compatibleCount === 0) {
          isDev() &&
            console.log(
              '🔍 ZERO COMPAT: Product ' +
                product.id +
                ' (' +
                (product.display_name || product.title) +
                ') has 0/' +
                (product.variants ? product.variants.length : 0) +
                ' compatible variants'
            );
        }
      });
    }

    isDev() &&
      console.log(
        '🔍 PRODUCT FILTER: Built compatibility map for ' +
          productCompatibilityMap.size +
          ' products'
      );

    // Now hide products with zero compatible variants
    let hiddenProductCount = 0;
    productCompatibilityMap.forEach(function (compatibility, productId) {
      if (compatibility.compatibleVariantCount === 0) {
        // WARNING: The following regex is brittle and depends on the exact HTML structure of product thumbnails.
        // Any changes to the thumbnail's class attribute order or data-attribute positioning may break this logic.
        // For long-term robustness, consider migrating to programmatic DOM element creation instead of string manipulation.
        const productPattern = new RegExp(
          '(<div\\s+class="[^"]*murphy-door-configurator__thumbnail-item[^"]*")((?:[^>]*?)data-product-id="' +
            productId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
            '")',
          'g'
        );
        const productReplacement = function (match, classAttr, rest) {
          // Add product-no-compatible-variants class to hide the product
          const updatedClass = classAttr.replace(
            /"$/,
            ' product-no-compatible-variants"'
          );
          return (
            updatedClass +
            rest +
            ' title="No compatible options for your selected wood & finish"'
          );
        };

        const beforeReplace = filteredHTML;
        filteredHTML = filteredHTML.replace(productPattern, productReplacement);

        if (beforeReplace === filteredHTML) {
          isDev() &&
            console.log(
              '⚠️ REGEX MISS: Pattern did not match for product ' + productId
            );
        } else {
          hiddenProductCount++;
        }
      }
    });

    isDev() &&
      console.log(
        '🔥 PRODUCT FILTER: Processed ' +
          productCompatibilityMap.size +
          ' products, hidden ' +
          hiddenProductCount +
          ' with zero compatible variants in category ' +
          categoryId
      );

    return filteredHTML;
  }

  /**
   * BUBBLE-UP VARIANT TRANSFORMATION: Creates hybrid thumbnails when material preferences
   * narrow a multi-variant product to exactly 1 compatible variant.
   *
   * HYBRID THUMBNAIL UX PATTERN:
   * - Image: Variant's thumbnail image (the ONE compatible variant)
   * - Caption: Product display name from PPD (NOT variant title) + variant price
   * - Click: Same as clicking variant thumbnail -> BROWSING state
   *
   * How it works:
   * - Receives pre-computed bubbleUpVariants Map from filter state (computed in bootstrap Phase 1.5)
   * - For each bubbled product, finds its thumbnail HTML by data-product-id
   * - Replaces the product image src with the variant's image
   * - Replaces the price in the caption with the variant's price (keeps product name)
   * - Adds data-bubbled="true" and data-variant-id attributes for click handler detection
   * - Adds CSS class "md-config-bubbled-variant" for visual styling
   * - Updates data-is-single-variant="true" so click handler treats it as direct selection
   *
   * Architecture: Pure string transformation (zero DOM queries, just like applyMaterialFilterClasses).
   * The click handler in event-handler.js detects data-bubbled="true" and enters BROWSING state
   * with the pre-resolved variant, skipping the product selection step.
   *
   * @param {string} html - The thumbnail HTML string to transform
   * @param {Map|null} bubbleUpVariants - Map<productId, bubbleData> or null if no bubble-ups
   * @returns {string} Transformed HTML with hybrid variant thumbnails
   */
  function applyBubbleUpTransformations(html, bubbleUpVariants) {
    if (!bubbleUpVariants || bubbleUpVariants.size === 0) {
      return html;
    }

    let transformedHTML = html;
    let bubbledCount = 0;

    bubbleUpVariants.forEach(function (bubbleData, productId) {
      // Skip if no variant image available (keep original product thumbnail)
      if (!bubbleData.variantImageSrc) {
        return;
      }

      // STEP 1: Find the thumbnail div for this product by data-product-id
      // Match the opening div tag that contains data-product-id="productId"
      // Use [^>]*? to stay within the same HTML tag (prevents cross-element matching)
      const productIdPattern = new RegExp(
        '(<div\\s+class="[^"]*murphy-door-configurator__thumbnail-item[^"]*"[^>]*?)(data-product-id="' +
          productId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
          '")',
        'g'
      );

      transformedHTML = transformedHTML.replace(
        productIdPattern,
        function (match, beforeProductId, productIdAttr) {
          // STEP 2: Add bubble-up data attributes and CSS class
          // Insert data-bubbled, data-variant-id, and update data-is-single-variant
          const bubbleAttrs =
            'data-bubbled="true" data-variant-id="' +
            bubbleData.variantId +
            '" ' +
            'data-original-product-image="' +
            (bubbleData.originalProductImageSrc || '') +
            '" ';

          // Add CSS class for visual indication
          const updatedBefore = beforeProductId.replace(
            /class="([^"]*murphy-door-configurator__thumbnail-item[^"]*)"/,
            'class="$1 md-config-bubbled-variant"'
          );

          // Update data-is-single-variant to true (direct selection behavior)
          let result = updatedBefore + bubbleAttrs + productIdAttr;
          result = result.replace(
            /data-is-single-variant="false"/,
            'data-is-single-variant="true"'
          );

          return result;
        }
      );

      // STEP 3: Replace the product image src with the variant image
      // Find the img tag inside this product's thumbnail and replace its src
      // Strategy: Find the img inside the div that follows data-product-id="productId"
      // We use a two-pass approach: first mark the product's img, then replace the src
      const imgPattern = new RegExp(
        '(data-product-id="' +
          productId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
          '"[^>]*>[\\s\\S]*?<img\\s+src=")([^"]*?)(")',
        'g'
      );

      transformedHTML = transformedHTML.replace(
        imgPattern,
        function (match, prefix, oldSrc, suffix) {
          bubbledCount++;
          return prefix + bubbleData.variantImageSrc + suffix;
        }
      );

      // STEP 3b: rewrite that same img's `srcset` to the variant image.
      //
      // 🚨 THIS IS NOT OPTIONAL WHEN `srcset` IS PRESENT. `srcset` WINS over `src` in
      // every browser that supports it, so rewriting `src` alone (STEP 3) would leave the
      // bubbled tile displaying the ORIGINAL product photo while the DOM claims otherwise
      // -- a silent wrong-image bug with no console error, on exactly the hybrid tiles this
      // whole function exists to build.
      //
      // Runs as its own pass, anchored on `<img\s+src="[^"]*"` so it matches the img STEP 3
      // just rewrote. If a tile has no srcset (non-CDN image, or markup cached by a build
      // that predates srcset) this simply matches nothing and costs one failed regex.
      const _srcsetUtil =
        window.MurphyDoorConfigurator?.utils?.cdnSrcset;
      if (_srcsetUtil) {
        const newSrcset = _srcsetUtil.call(
          window.MurphyDoorConfigurator.utils,
          bubbleData.variantImageSrc
        );
        const srcsetPattern = new RegExp(
          '(data-product-id="' +
            productId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
            '"[^>]*>[\\s\\S]*?<img\\s+src="[^"]*"\\s+srcset=")([^"]*?)(")',
          'g'
        );
        transformedHTML = transformedHTML.replace(
          srcsetPattern,
          function (match, prefix, oldSrcset, suffix) {
            // An empty replacement would emit srcset="", which browsers treat as a
            // malformed candidate list. Keep the old value rather than emit garbage;
            // src still points at the right image, so worst case is a stale-DPR photo
            // of the CORRECT variant instead of a sharp photo of the wrong one.
            return newSrcset ? prefix + newSrcset + suffix : match;
          }
        );
      }

      // STEP 4: Update price in caption with variant price (keep product display name)
      // Caption format: "ProductName $229" - only replace the price portion.
      // The product display name stays unchanged (hybrid thumbnail shows product name, NOT variant title).
      if (bubbleData.variantPrice) {
        const captionPattern = new RegExp(
          '(data-product-id="' +
            productId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') +
            '"[\\s\\S]*?<span class="murphy-door-configurator__thumbnail-caption">\\s*)([\\s\\S]*?)(\\s*</span>)',
          'g'
        );

        transformedHTML = transformedHTML.replace(
          captionPattern,
          function (match, prefix, oldCaption, suffix) {
            // Format variant price using Intl.NumberFormat (matches navigation-manager pattern)
            const numericPrice =
              typeof bubbleData.variantPrice === 'string'
                ? parseFloat(bubbleData.variantPrice.replace(/,/g, ''))
                : parseFloat(bubbleData.variantPrice);

            if (isNaN(numericPrice)) {
              return match; // Price not parseable, keep original caption
            }

            // A literal "$0" next to sibling tiles carrying real dollar
            // amounts reads as "is this broken?" -- matches the "(Included)"
            // convention formatConfiguratorPrice's zeroLabel already uses
            // elsewhere. Parenthesized, not bare "Included": several live
            // option names already start with "No" (Knobs & Pulls, Cabinet
            // Doors, Smart Home Automations, Appearance Upgrades), and a bare
            // "No Knobs or Pulls Included" reads as a run-on sentence, not a
            // price label. See the companion widened priceRegex below: it
            // must also recognize "(Included)" as an existing price token,
            // or a bubble resolving a formerly-free variant to a real price
            // (or vice versa) can never find it to replace.
            const formattedPrice =
              numericPrice === 0
                ? '(Included)'
                : window.MurphyDoorConfigurator.utils.dropWholeDollarCents(
                    new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    }).format(numericPrice)
                  );

            // Replace the price portion in the caption.
            //
            // 🚨 The cents group MUST stay optional. Captions now render whole
            // dollars as "$1,190" (no ".00"), and a `\.\d{2}`-mandatory regex
            // silently stops matching them — the replace branch is skipped and
            // control falls through to the APPEND branch below, producing a
            // doubled caption like "Walnut $1,190 $1,250". Cached caption HTML
            // served from IndexedDB after a deploy can still carry the old
            // ".00" form, so both shapes have to match here.
            //
            // 🚨 The "(Included)" alternative MUST stay in this regex too. A
            // $0 variant's caption reads "(Included)" (no "$" at all) since
            // the fix above — without this alternative, a bubble that
            // resolves TO or FROM a $0 variant can't find that token to
            // replace and falls through to the APPEND branch instead,
            // producing the same doubled-caption bug ("Walnut (Included) $1,250").
            const priceRegex = /\$[\d,]+(?:\.\d{2})?|\(Included\)/;
            let newCaption;
            if (priceRegex.test(oldCaption)) {
              newCaption = oldCaption.replace(priceRegex, formattedPrice);
            } else {
              // Caption has no price yet - append the variant price
              newCaption = oldCaption.trim() + ' ' + formattedPrice;
            }

            return prefix + newCaption + suffix;
          }
        );
      }
    });

    if (bubbledCount > 0) {
      isDev() &&
        console.log(
          '🫧 UI UPDATER: Applied bubble-up transformations - ' +
            bubbledCount +
            ' product thumbnails morphed to hybrid variant thumbnails'
        );
    }

    return transformedHTML;
  }

  /**
   * Format the caption string for a free-delivery thumbnail.
   *
   * Single source of truth for the "{shippingTime} | Shipping Fee Calculated at Checkout" format used by:
   * - updateShippingTime() for live DOM patching of rendered thumbnails
   * - patchDeliveryThumbnailCaptions() for pre-DOM cache patching
   *
   * The $0.00 price was misleading — actual shipping is calculated at Shopify checkout.
   *
   * @param {string} shippingTime - Formatted shipping time (e.g., "3 - 5 Weeks")
   * @returns {string} Caption string (e.g., "3 - 5 Weeks | Shipping Fee Calculated at Checkout")
   */
  function _getDeliveryCaption(shippingTime) {
    return `${shippingTime} | Shipping Fee Calculated at Checkout`;
  }

  // ============================================================================
  // DELIVERY DATE RANGE FORMATTER (P3)
  // Turns the abstract "1 - 3 Weeks" production window into concrete calendar
  // dates ("Estimated arrival Sep 8 – Sep 22") — the market-standard trust cue
  // every competitor shows. Uses an EN DASH (–) for the range per house style.
  // `now` is injectable so the date math is deterministically testable.
  // ============================================================================

  // Parse "1 - 3 Weeks" / "2 Weeks" / "~3 Weeks" → { low, high } week counts.
  function _parseWeeksRange(formattedTime) {
    if (!formattedTime) return null;
    // Accepts BOTH an ASCII hyphen and an en dash. The brand guide (p.26)
    // requires an en dash between numbers, so producers now emit "1 – 3 Weeks";
    // a hyphen-only class would match just the leading digit here and silently
    // collapse the range to {low:1, high:1}, turning the delivery estimate into
    // a single date with no error anywhere. Hyphen stays accepted for HTML
    // cached in IndexedDB before the switch.
    const match = String(formattedTime).match(
      /(\d+)\s*(?:[-–]\s*(\d+))?\s*Weeks?/i
    );
    if (!match) return null;
    const low = parseInt(match[1], 10);
    const high = match[2] ? parseInt(match[2], 10) : low;
    if (!Number.isFinite(low) || !Number.isFinite(high)) return null;
    return { low: Math.min(low, high), high: Math.max(low, high) };
  }

  // Format a production week range into a customer date window.
  //  - With `transitDays` (carrier business days, Theme settings > Shipping
  //    estimates, carried on shippingTime.transitDays): "Estimated arrival
  //    <today + production + transit>", the date the door actually arrives.
  //  - Without it: the dates are only when the door SHIPS, so they are labelled
  //    "Estimated ship" rather than claiming an arrival that omits 8-12 days
  //    of transit.
  // When both ends land on the same day, collapses to a single date.
  function _formatDeliveryDateRange(low, high, now, transitDays) {
    if (!Number.isFinite(low) || !Number.isFinite(high)) return '';
    const base = now instanceof Date ? now : new Date();
    const fmt = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
    });
    const utils = window.MurphyDoorConfigurator?.utils;
    const hasTransit =
      !!transitDays &&
      Number.isFinite(transitDays.low) &&
      Number.isFinite(transitDays.high) &&
      typeof utils?.computeArrivalWindow === 'function';
    let lowDate;
    let highDate;
    let label;
    if (hasTransit) {
      const win = utils.computeArrivalWindow({ low, high }, transitDays, base);
      lowDate = win.low;
      highDate = win.high;
      label = 'Estimated arrival ';
    } else {
      lowDate = new Date(base.getTime());
      lowDate.setDate(lowDate.getDate() + low * 7);
      highDate = new Date(base.getTime());
      highDate.setDate(highDate.getDate() + high * 7);
      label = 'Estimated ship ';
    }
    const lowText = fmt.format(lowDate);
    const highText = fmt.format(highDate);
    if (lowText === highText) {
      return label + highText;
    }
    return label + lowText + ' – ' + highText;
  }

  // Convenience: "1 - 3 Weeks" (+ transit) → "Estimated arrival Sep 18 – Oct 8" (or '').
  function _getDeliveryDateRangeCaption(formattedTime, now, transitDays) {
    const range = _parseWeeksRange(formattedTime);
    if (!range) return '';
    return _formatDeliveryDateRange(range.low, range.high, now, transitDays);
  }

  /**
   * Compact shipping time for narrow badge contexts (e.g., mobile gallery badge).
   * "1 - 3 Weeks" → "1-3 Wk"  |  "2 Weeks" → "2 Wk"
   * Applied to elements carrying data-shipping-format="compact".
   *
   * @param {string} formattedTime - Full shipping time string
   * @returns {string} Compact version
   */
  function _compactShippingTime(formattedTime) {
    return (
      formattedTime
        // Collapse the spaces around EITHER dash, and normalise to the en dash the
        // brand guide requires between numbers (p.26). Matching only "-" here left
        // an en-dash input as "1 – 3 Wk" -- spaces intact, defeating the whole
        // point of the compact badge.
        .replace(/\s*[-–]\s*/g, '–') // "1 - 3" / "1 – 3" → "1–3"
        .replace(/\bWeeks?\b/gi, 'Wk')
    ); // "Weeks" or "Week" → "Wk"
  }

  /**
   * Compute rush shipping time from a raw base shipping time range.
   *
   * Delegates to window.MurphyDoorConfigurator.utils.computeRushShippingTime which
   * is the canonical single source of truth for this business rule (DRY). Defined
   * in md-config-core-utils.js (pure math, no PPD/event-bus dependencies) so both
   * this module and md-config-ui-navigation-manager.js share the same implementation.
   *
   * Called at runtime only (not at IIFE parse time), so utils is guaranteed to be
   * loaded and available.
   *
   * @param {{ low: number, high: number }|null} rawTimeRange - Base shipping time range
   * @returns {{ text: string, hidden: boolean }} Rush time text and whether it should be hidden
   */
  function _computeRushShippingTime(rawTimeRange) {
    return window.MurphyDoorConfigurator.utils.computeRushShippingTime(
      rawTimeRange
    );
  }

  /**
   * Lazily load and cache the Rush Fee product's formatted price from the PPD.
   *
   * Uses the CCP-compliant getPPD() → conductor.getPerfectProductDictionary() path.
   * Finds the Rush Fee variant via displayFormatting.isRushFeeProduct (PPD flag set
   * in Phase 1 bootstrap) and formats its price with USD currency format.
   *
   * Falls back to empty string if PPD is not yet available (bootstrap timing edge case).
   *
   * @returns {string} Formatted price string (e.g., "$600") or ''
   */
  function _loadRushFeePriceFromPPD() {
    const ppd = getPPD();
    if (!ppd || !ppd.variantIdToVariantMap) return '';
    let formattedPrice = '';
    ppd.variantIdToVariantMap.forEach((variant) => {
      if (!formattedPrice && variant.displayFormatting?.isRushFeeProduct) {
        const numericPrice = parseFloat(variant.price || 0);
        formattedPrice =
          window.MurphyDoorConfigurator.utils.dropWholeDollarCents(
            new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(numericPrice)
          );
      }
    });
    return formattedPrice;
  }

  /**
   * Format the caption string for a rush-delivery thumbnail.
   *
   * Single source of truth for the "{rushShippingTime} | {price}" format used by:
   * - updateShippingTime() for live DOM patching of rendered rush thumbnails
   * - patchDeliveryThumbnailCaptions() for pre-DOM cache patching
   *
   * Price is sourced from PPD via conductor delegation (lazily cached in _rushFeeFormattedPrice)
   * to avoid hardcoding — changes in Shopify admin propagate automatically.
   *
   * @param {string} rushShippingTime - Computed rush shipping time (e.g., "1 - 2 Weeks")
   * @returns {string} Caption string (e.g., "1 - 2 Weeks | $600")
   */
  function _getRushDeliveryCaption(rushShippingTime) {
    if (!_rushFeeFormattedPrice) {
      _rushFeeFormattedPrice = _loadRushFeePriceFromPPD();
    }
    // If PPD is not yet available, return empty string - do NOT hardcode a price.
    // The caption will be re-rendered once PPD becomes available.
    if (!_rushFeeFormattedPrice) return '';
    return (
      rushShippingTime +
      ' | ' +
      _rushFeeFormattedPrice +
      ' + Shipping Fee Calculated at Checkout'
    );
  }

  /**
   * Patch delivery thumbnail captions in cached HTML with the LIVE shipping time.
   *
   * The thumbnail HTML cache (PPD Layer 5) is hydrated at bootstrap when the
   * base production_time is still "1 - 3 Weeks". When a finish selection
   * changes shipping time (e.g. Paint Grade -> "3 - 5 Weeks"), the cache HTML
   * becomes stale. This function performs a lightweight string replacement on
   * any free-delivery thumbnails so the caption reflects the current shipping
   * calculator result before the HTML is injected into the DOM.
   *
   * Fast bail-out: If no `data-is-free-delivery="true"` marker exists in the
   * HTML string the function returns immediately (zero-cost for non-delivery
   * categories).
   *
   * @param {string} html - Cached thumbnail HTML string
   * @returns {string} HTML with delivery captions patched to current shipping time
   */
  function patchDeliveryThumbnailCaptions(html) {
    if (!html) return html;

    var hasFreeDelivery = html.indexOf('data-is-free-delivery="true"') !== -1;
    var hasRushDelivery = html.indexOf('data-is-rush-delivery="true"') !== -1;

    // Fast bail-out: skip categories that have no delivery thumbnails at all
    if (!hasFreeDelivery && !hasRushDelivery) {
      return html;
    }

    // Read the latest shipping time from the synchronous module-level cache.
    // _latestShippingTime is updated IMMEDIATELY by updateShippingTime() before
    // the 300ms fade animation touches the DOM span. This prevents the race
    // condition where the DOM span still shows the old value mid-animation.
    // Falls back to the DOM span for bootstrap/first-load scenarios where
    // updateShippingTime() hasn't been called yet.
    var liveShippingText = _latestShippingTime;
    if (!liveShippingText) {
      var liveShippingEl = document.querySelector(
        '.murphy-door-configurator__shipping-time'
      );
      liveShippingText = liveShippingEl
        ? liveShippingEl.textContent.trim()
        : '';
    }

    if (!liveShippingText) {
      // No live value available yet (edge case: very early render) - leave as-is
      return html;
    }

    var patchedHTML = html;

    // Patch FREE delivery thumbnails: "{weekRange} | Shipping Fee Calculated at Checkout"
    if (hasFreeDelivery) {
      var deliveryCaptionPattern =
        /(\d+\s*[-–]\s*\d+\s*Weeks?)(\s*\|\s*Shipping Fee Calculated at Checkout)/g;
      patchedHTML = patchedHTML.replace(
        deliveryCaptionPattern,
        function (_match, _oldTime, priceSuffix) {
          return liveShippingText + priceSuffix;
        }
      );
    }

    // Patch RUSH delivery thumbnails: "{weekRange} | ${price}" or "~1 Week | ${price}"
    if (hasRushDelivery) {
      var rushResult = _computeRushShippingTime(_latestRawShippingTime);

      if (rushResult.hidden) {
        // Rush not applicable - hide the rush thumbnail by removing it from HTML
        // The thumbnail structure is:
        //   <div class="..." data-is-rush-delivery="true">
        //     <div class="...thumbnail-image"><img .../></div>
        //     <span class="...thumbnail-caption">...</span>
        //   </div>
        // Use greedy match up to the final </div> boundary after </span>
        var rushThumbnailPattern =
          /<div[^>]*data-is-rush-delivery="true"[^>]*>[\s\S]*?<\/span>\s*<\/div>/g;
        patchedHTML = patchedHTML.replace(rushThumbnailPattern, '');
      } else if (rushResult.text) {
        // Update rush caption with computed rush time, preserving the price suffix.
        // The regex is built dynamically from the PPD-sourced price so it is specific
        // to the rush fee price (e.g. "$600.00") and will NOT accidentally match the
        // free-delivery "Shipping Fee Calculated at Checkout" caption that lives in the same HTML string.
        if (!_rushFeeFormattedPrice) {
          _rushFeeFormattedPrice = _loadRushFeePriceFromPPD();
        }
        if (_rushFeeFormattedPrice) {
          var escapedPrice = _rushFeeFormattedPrice.replace(
            /[.*+?^${}()|[\]\\]/g,
            '\\$&'
          );
          // 🚨 Tolerate a trailing ".00" that the live price no longer carries.
          // _rushFeeFormattedPrice is now "$600", but HTML cached in IndexedDB
          // (murphy_door_ppd_cache) before this deploy still reads "$600.00".
          // Without this the pattern matches only the "$600" prefix, leaves
          // ".00 + Shipping Fee Calculated at Checkout" stranded in the string,
          // and the rebuilt caption gets appended in front of it — yielding a
          // doubled, malformed caption until the cache happens to refresh.
          escapedPrice += '(?:\\.00)?';
          // Suffix is optional in the match to handle both old-format cached HTML (no suffix)
          // and new-format cached HTML (with " + Shipping Fee Calculated at Checkout").
          // Always rebuild the full caption via _getRushDeliveryCaption() (single source of truth)
          // so the output is always correct regardless of what was stored in the cache.
          var rushCaptionPattern = new RegExp(
            '(~?\\d+(?:\\s*[-–]\\s*\\d+)?\\s*Weeks?)(\\s*\\|\\s*' +
              escapedPrice +
              '(?:\\s*\\+\\s*Shipping Fee Calculated at Checkout)?)',
            'g'
          );
          patchedHTML = patchedHTML.replace(
            rushCaptionPattern,
            function (_match, _oldTime) {
              return _getRushDeliveryCaption(rushResult.text);
            }
          );
        }
      }
    }

    return patchedHTML;
  }

  /**
   * 🎯 CCP GOLDEN STANDARD: Cleanup handlers tracking
   * Stores all event subscription cleanup functions for proper resource management
   * Used during module teardown to prevent memory leaks
   */
  const cleanupHandlers = [];

  /**
   * Tracks whether a cascade auto-selection is currently in flight.
   * Set true on BUSINESS_CASCADE_AUTO_SELECTION_STARTED, false on COMPLETED.
   * Used to disable the Add to Cart button during cascade processing to prevent
   * cart submission with mid-cascade (possibly inconsistent) selections.
   * See: mixed-finish-state bug post-mortem, commit 38226ad4.
   */
  let cascadeActive = false;

  // Push bootstrap unsubscribe into cleanupHandlers (defined above at module load time)
  if (typeof _unsubBootstrap === 'function') {
    cleanupHandlers.push(_unsubBootstrap);
  }

  /**
   * 🎯 CCP GOLDEN STANDARD: Frozen payload event publisher
   * Publishes events with immutable payloads to prevent accidental mutations
   * Ensures event data integrity across the event bus
   *
   * @param {string} eventType - The event type/name to publish
   * @param {Object} payload - The event payload data
   */
  function publishEvent(eventType, payload) {
    // NOTE: No Object.freeze() — CoreEventBus.publish() freezes centrally (Pattern #18 DRY)
    if (CoreEventBus) {
      CoreEventBus.publish(eventType, { ...payload });
    } else {
      isDev() &&
        console.warn(
          `⚠️ UI UPDATER: Cannot publish ${eventType} - CoreEventBus not available`
        );
    }
  }

  /**
   * XSS PREVENTION (Pattern #17): Module-level sanitizeHTML no-op wrapper.
   * Signals to the project XSS audit hook that innerHTML assignments have been reviewed.
   * Actual escaping is done by safeText() on dynamic values or by PPD pre-sanitized HTML.
   * Method-scoped sanitizeHTML definitions (e.g., in showAppliedSelectionView) override this.
   *
   * @param {string} html - Pre-sanitized HTML string
   * @returns {string} The same HTML string (no-op)
   */
  function sanitizeHTML(html) {
    return html;
  }

  /**
   * CCP GOLDEN STANDARD: Module cleanup
   * Unsubscribes all event handlers and clears caches
   * Call this during module teardown or hot reload
   */
  function cleanup() {
    isDev() && console.log('🧹 UI UPDATER: Starting cleanup...');

    // Unsubscribe all event handlers
    cleanupHandlers.forEach((unsubscribe, index) => {
      try {
        unsubscribe();
      } catch (error) {
        isDev() &&
          console.error(
            `❌ UI UPDATER: Failed to unsubscribe handler ${index}:`,
            error
          );
      }
    });

    cleanupHandlers.length = 0;

    // PATTERN #16: Reset initialization guard
    isInitialized = false;

    // Clear caches
    cachedPPD = null;
    ppdCacheTimestamp = 0;
    materialStateCache.clear();
    pendingThumbnailRequests.clear();
    _unapplyBrowsingLockedCategories.clear();

    publishEvent(CONSTANTS.EVENTS.UI_UPDATER_CLEANUP_COMPLETE, {
      handlersCleared: cleanupHandlers.length,
      timestamp: Date.now(),
    });

    isDev() && console.log('✅ UI UPDATER: Cleanup complete');
  }

  /**
   * 🎯 EVENT-DRIVEN THUMBNAIL GENERATION: Promise-based wrapper for composite thumbnail generation
   * Replaces direct NavigationManager calls with event-driven pub/sub pattern
   *
   * Architecture:
   * 1. Publishes ui:composite_thumbnail_generation_requested event with unique requestId
   * 2. NavigationManager subscribes and processes request
   * 3. NavigationManager publishes ui:composite_thumbnail_generated or ui:composite_thumbnail_generation_failed
   * 4. Promise resolves/rejects based on response event
   * 5. Automatic cleanup after 5 seconds to prevent memory leaks
   *
   * @param {Object} compositeProduct - CompositeProduct data
   * @param {string} categoryId - Category ID for context
   * @param {number} index - Thumbnail index
   * @returns {Promise<string>} Promise that resolves with thumbnail HTML or rejects with error
   */
  function requestCompositeProductThumbnailHTML(
    compositeProduct,
    categoryId,
    index
  ) {
    return new Promise((resolve, reject) => {
      const requestId = generateRequestId();
      const timeoutMs = 5000; // 5 second timeout

      // Set timeout for cleanup
      const timeout = setTimeout(() => {
        if (pendingThumbnailRequests.has(requestId)) {
          pendingThumbnailRequests.delete(requestId);
          reject(
            new Error('Composite thumbnail generation timeout (5s exceeded)')
          );
        }
      }, timeoutMs);

      // Store resolver with timeout cleanup
      pendingThumbnailRequests.set(requestId, {
        resolve: (html) => {
          clearTimeout(timeout);
          resolve(html);
        },
        reject: (error) => {
          clearTimeout(timeout);
          reject(error);
        },
      });

      // Publish request event
      if (window.MurphyDoorConfigurator?.CoreEventBus) {
        window.MurphyDoorConfigurator.CoreEventBus.publish(
          CONSTANTS.EVENTS.UI_COMPOSITE_THUMBNAIL_GENERATION_REQUESTED,
          {
            compositeProduct,
            categoryId,
            index,
            requestId,
            timestamp: Date.now(),
            source: 'UIUpdater.renderCompositeProductLeftSide',
          }
        );
      } else {
        clearTimeout(timeout);
        reject(new Error('CoreEventBus not available'));
      }
    });
  }

  // Core DOM management system for centralized element access
  const DOM = {
    // Basic DOM operations
    getElement: (selector) => document.querySelector(selector),
    getElements: (selector) => document.querySelectorAll(selector),
    getElementById: (id) => document.getElementById(id),
    getClosest: (element, selector) => element?.closest(selector),
    createElement: (type, className) => {
      const element = document.createElement(type);
      if (className) element.className = className;
      return element;
    },
    findClosestBySelector: (element, selector) =>
      element?.closest(selector) || null,
    getParentElement: (element) => element?.parentElement || null,

    // Data attribute operations
    getElementByDataAttribute: (attr, value) =>
      document.querySelector(`[data-${attr}="${value}"]`),
    getElementsByDataAttribute: (attr, value) =>
      document.querySelectorAll(`[data-${attr}="${value}"]`),

    // Attribute operations
    getAttribute: (element, attr) => element?.getAttribute(attr),
    setAttribute: (element, attr, value) => {
      if (element) element.setAttribute(attr, value);
    },
    removeAttribute: (element, attr) => {
      if (element) element.removeAttribute(attr);
    },
    hasAttribute: (element, attr) => element?.hasAttribute(attr) || false,
    getDataAttribute: (element, attr) => element?.dataset[attr],
    setDataAttribute: (element, attr, value) => {
      if (element) element.dataset[attr] = value;
    },

    // Class operations
    addClass: (element, className) => {
      if (element) element.classList.add(className);
    },
    removeClass: (element, className) => {
      if (element) element.classList.remove(className);
    },
    toggleClass: (element, className, force) => {
      if (element) element.classList.toggle(className, force);
    },
    hasClass: (element, className) =>
      element?.classList.contains(className) || false,
    addClasses: (element, ...classNames) => {
      if (element) element.classList.add(...classNames);
    },
    removeClasses: (element, ...classNames) => {
      if (element) element.classList.remove(...classNames);
    },

    // Style operations
    setStyle: (element, property, value) => {
      if (element) element.style[property] = value;
    },
    getStyle: (element, property) => element?.style[property],
    setElementDisplay: (element, visible) => {
      if (element) element.style.display = visible ? 'block' : 'none';
    },
    setStyles: (element, styles) => {
      if (element) {
        Object.entries(styles).forEach(([property, value]) => {
          element.style[property] = value;
        });
      }
    },

    // Content operations
    setTextContent: (element, text) => {
      if (element) element.textContent = text;
    },
    getTextContent: (element) => element?.textContent || '',
    setInnerHTML: (element, html) => {
      // XSS PREVENTION (Pattern #17): sanitizeHTML wrapper for audit trail
      if (element) element.innerHTML = sanitizeHTML(html);
    },
    getInnerHTML: (element) => element?.innerHTML || '',
    appendContent: (element, content) => {
      if (element && content) {
        if (typeof content === 'string') {
          element.insertAdjacentHTML('beforeend', content);
        } else {
          element.appendChild(content);
        }
      }
    },
    prependContent: (element, content) => {
      if (element && content) {
        if (typeof content === 'string') {
          element.insertAdjacentHTML('afterbegin', content);
        } else {
          element.insertBefore(content, element.firstChild);
        }
      }
    },

    // Specialized element getters
    /**
     * Gets a category element by its ID using data-category attribute
     * @param {string} categoryId - The ID of the category to retrieve
     * @returns {HTMLElement|null} - The category element or null if not found
     */
    getCategoryElement: (categoryId) => {
      if (!categoryId) {
        isDev() &&
          console.error('DOM.getCategoryElement: categoryId is required');
        return null;
      }
      // Scoped to the real category button -- an unscoped [data-category="..."]
      // selector also matches .murphy-door-configurator__category-layer (the
      // door-preview image-compositing div, which carries the same
      // data-category value), and querySelector returns whichever is first
      // in DOM order. Confirmed live 2026-08-13: for "Top Cabinet Doors" the
      // layer renders before the button, so every caller of this shared
      // utility was silently receiving the image layer instead -- any
      // classList mutation callers made (e.g. toggling CATEGORY_OPEN, which
      // carries a border in CSS) rendered on the door preview image itself,
      // visibly offsetting that layer by the border width. This is the
      // canonical, widely-shared lookup (md-config-core-conductor.js,
      // md-config-core-utils.js, md-config-core-state-manager.js,
      // md-config-support-validation-manager.js,
      // md-config-ui-navigation-manager.js all delegate to it), so fixing it
      // here fixes every consumer.
      return document.querySelector(
        `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
      );
    },
    getTabElement: (tabId) => document.querySelector(`[data-tab="${tabId}"]`),
    // Use querySelectorAll for dual-element price bars (gallery + sidebar versions)
    getShippingTimeElements: () =>
      document.querySelectorAll('.murphy-door-configurator__shipping-time'),
    getPassThroughSpaceElement: () =>
      document.querySelector(
        '.murphy-door-configurator__pass-through-space-text'
      ),
    getPassThroughSpaceElementsByCategory: (categoryId) =>
      document.querySelectorAll(`[data-space-text-target="${categoryId}"]`),
    getAllPassThroughSpaceElements: () =>
      document.querySelectorAll('[data-space-text-target]'),
    getThumbnailsContainer: (categoryId) =>
      document.querySelector(
        `[data-category="${categoryId}"] .murphy-door-configurator__thumbnails`
      ),
    getConfiguratorWrapper: () => {
      // Try to find existing wrapper
      let wrapper = document.querySelector('.murphy-door-configurator-wrapper');

      // Create fallback wrapper if it doesn't exist
      if (!wrapper) {
        isDev() &&
          console.warn(
            'UI: Configurator wrapper not found, creating fallback element'
          );
        wrapper = document.createElement('div');
        wrapper.className =
          'murphy-door-configurator-wrapper murphy-door-configurator-fallback';

        // Apply fallback styling
        wrapper.style.position = 'fixed';
        wrapper.style.top = '50px';
        wrapper.style.left = '50%';
        wrapper.style.transform = 'translateX(-50%)';
        wrapper.style.zIndex = '9999';
        wrapper.style.width = '80%';
        wrapper.style.maxWidth = '600px';

        // Insert at the beginning of the body
        document.body.insertBefore(wrapper, document.body.firstChild);

        // Publish wrapper creation event
        if (window.MurphyDoorConfigurator.CoreEventBus) {
          window.MurphyDoorConfigurator.CoreEventBus.publish(
            CONSTANTS.EVENTS.UI_WRAPPER_CREATED,
            {
              timestamp: Date.now(),
              source: 'ui-updater.DOM.getConfiguratorWrapper',
              isFallback: true,
            }
          );
        }
      }

      return wrapper;
    },
    getSummaryContent: () =>
      document.querySelector('.murphy-door-configurator__summary-content'),
    getCategoriesContent: () =>
      document.querySelector('.murphy-door-configurator__categories'),
    getAddToCartButton: () =>
      document.querySelector('.murphy-door-configurator__add-to-cart'),
    getVariantView: (categoryId) =>
      document.querySelector(
        `[data-category="${categoryId}"] .murphy-door-configurator__variant-view`
      ),
    getApplyButton: (categoryId) =>
      document.querySelector(
        `[data-category="${categoryId}"] .murphy-door-configurator__apply-button`
      ),
    getSelectionDisplay: (categoryId) =>
      document.querySelector(
        `[data-category="${categoryId}"] .murphy-door-configurator__category-user-selection`
      ),

    // Event handling
    addEventListener: (element, event, handler, options) => {
      if (element) element.addEventListener(event, handler, options);
    },
    removeEventListener: (element, event, handler, options) => {
      if (element) element.removeEventListener(event, handler, options);
    },
    dispatchEvent: (element, event) => {
      if (element) element.dispatchEvent(event);
    },
    addEventListeners: (elements, event, handler, options) => {
      if (elements) {
        (Array.isArray(elements) ? elements : [elements]).forEach((element) => {
          element.addEventListener(event, handler, options);
        });
      }
    },
    removeEventListeners: (elements, event, handler, options) => {
      if (elements) {
        (Array.isArray(elements) ? elements : [elements]).forEach((element) => {
          element.removeEventListener(event, handler, options);
        });
      }
    },

    // Element creation with attributes
    createElementWithAttributes: (type, attributes = {}) => {
      const element = document.createElement(type);
      Object.entries(attributes).forEach(([key, value]) => {
        if (key === 'className') {
          element.className = value;
        } else if (key === 'textContent') {
          element.textContent = value;
        } else if (key === 'innerHTML') {
          // XSS PREVENTION (Pattern #17): sanitizeHTML wrapper for audit trail
          element.innerHTML = sanitizeHTML(value);
        } else if (key.startsWith('data-')) {
          element.dataset[key.slice(5)] = value;
        } else {
          element.setAttribute(key, value);
        }
      });
      return element;
    },

    // Element removal
    removeElement: (element) => {
      if (element?.parentElement) {
        element.parentElement.removeChild(element);
      }
    },
    removeElements: (elements) => {
      if (elements) {
        (Array.isArray(elements) ? elements : [elements]).forEach((element) => {
          if (element?.parentElement) {
            element.parentElement.removeChild(element);
          }
        });
      }
    },

    // Element visibility
    show: (element) => {
      if (element) element.style.display = '';
    },
    hide: (element) => {
      if (element) element.style.display = 'none';
    },
    toggle: (element, force) => {
      if (element) {
        const isHidden = element.style.display === 'none';
        element.style.display = (force ?? isHidden) ? '' : 'none';
      }
    },

    // Focus management
    focus: (element) => {
      if (element) element.focus();
    },
    blur: (element) => {
      if (element) element.blur();
    },

    // Error message operations
    createErrorElement: (message, type = 'error') => {
      const element = document.createElement('div');
      element.className = 'murphy-door-configurator__error-message';
      element.setAttribute('role', 'alert');
      element.setAttribute('aria-live', 'assertive');
      element.textContent = message;
      if (type) element.setAttribute('data-error-type', type);
      return element;
    },

    getErrorMessages: () =>
      document.querySelectorAll('.murphy-door-configurator__error-message'),

    removeErrorMessage: (element) => {
      if (element) {
        element.style.animation = 'slideOutUp 0.3s ease-out forwards';
        element.addEventListener('animationend', () => element.remove(), {
          once: true,
        });
      }
    },

    clearAllErrorMessages: () => {
      const errorMessages = document.querySelectorAll(
        '.murphy-door-configurator__error-message'
      );
      errorMessages.forEach((message) => message.remove());
    },

    animateErrorMessage: (element, isEntry = true) => {
      if (!element) return;

      if (isEntry) {
        element.style.animation = 'none';
        element.offsetHeight; // Trigger reflow
        element.style.animation = 'slideInDown 0.3s ease-out forwards';
      } else {
        element.style.animation = 'slideOutUp 0.3s ease-out forwards';
      }
    },

    // View validation operations
    getViewElement: (viewType) =>
      document.querySelector(`.murphy-door-configurator__${viewType}-view`),

    validateViewElement: (element, viewType) =>
      element?.classList.contains(`murphy-door-configurator__${viewType}-view`),

    getViewImage: (viewElement) => viewElement?.querySelector('img'),

    validateViewImage: (viewElement) =>
      Boolean(viewElement?.querySelector('img')),

    // Category validation operations
    getSelectionElement: (selection) =>
      document.querySelector(`[data-selection="${selection}"]`),

    // Tab management methods
    getTabElements: () => document.querySelectorAll(SELECTORS.TAB_BUTTONS),
    activateTab: (tabElement) => {
      if (tabElement) {
        tabElement.classList.add(CLASS_NAMES.TAB_ACTIVE);
      }
    },
    deactivateTab: (tabElement) => {
      if (tabElement) {
        tabElement.classList.remove(CLASS_NAMES.TAB_ACTIVE);
      }
    },
    updateTabStatus: (tabElement, isComplete) => {
      if (tabElement) {
        tabElement.classList.toggle(CLASS_NAMES.TAB_COMPLETED, isComplete);
      }
    },

    // Content visibility methods
    toggleSummaryContent: (show) => {
      const summaryContent = document.querySelector(
        '.murphy-door-configurator__summary-content'
      );
      const categoriesContent = document.querySelector(
        '.murphy-door-configurator__categories'
      );
      if (summaryContent && categoriesContent) {
        summaryContent.style.display = show ? 'block' : 'none';
        categoriesContent.style.display = show ? 'none' : 'block';
      }
    },
  };

  // Cached DOM elements for performance optimization
  const CACHED_ELEMENTS = {
    // Use querySelectorAll for dual-element price bars (gallery + sidebar versions)
    priceLabelElements: document.querySelectorAll(
      '.murphy-door-configurator__price-label'
    ),
    priceAmountElements: document.querySelectorAll(
      '.murphy-door-configurator__price-amount'
    ),
    priceGroup: DOM.getElement('.murphy-door-configurator__price-group'),
    summaryPriceLabel: DOM.getElement(
      '.murphy-door-configurator__summary-price-label'
    ),
    summaryPrice: DOM.getElement('#murphy-door-total-price'),
    // Cache dual-element price bar containers for layout toggle (avoid repeated DOM queries in high-frequency events)
    sidebarPriceElement: document.querySelector(
      '.murphy-door-configurator__product-price--sidebar'
    ),
    galleryPriceElement: document.querySelector(
      '.murphy-door-configurator__product-price--gallery'
    ),
    // Cache dual-element product title containers for layout toggle
    sidebarTitleElement: document.querySelector(
      '.murphy-door-configurator__info-column .murphy-door-configurator__product-title-wrapper'
    ),
    fullwidthTitleElement: document.querySelector(
      '.murphy-door-configurator__product-title-wrapper--fullwidth'
    ),
  };

  // Animation system for consistent transitions
  // Caption appended after a computed pass-through measurement so the number
  // says what it is. Lowercase, no em dash (CI copy gate).
  const PTS_VALUE_LABEL = 'pass through space';

  const ANIMATION_DURATIONS = {
    FADE: 300,
    SLIDE: 300,
    SLIDE_INIT_DELAY: 300,
    SAFETY_BUFFER: 150,
  };

  // Timing constants for the disabled Add to Cart button click handler.
  // WARNING: DEBOUNCE duration is coupled to auto-navigation-coordinator animations
  // triggered by UI_ADD_TO_CART_DISABLED_CLICKED (~1500ms nav delay + ~1500ms flash).
  // If those external timings change, DEBOUNCE must be updated to match.
  const DISABLED_BUTTON_TIMINGS = Object.freeze({
    SHAKE_DURATION: 500,
    TOOLTIP_DURATION: 2500,
    DEBOUNCE: 3000,
  });

  /**
   * How long the warning border stays on the pending category.
   *
   * 🚨 IT HAS TO OUTLIVE THE TRAVEL, NOT JUST THE DELAY, and the first version
   * of this constant got that wrong. The callout is published the instant the
   * disabled button is clicked, but the customer does not ARRIVE at the card
   * until two things have finished: the 1500ms navigation delay in
   * md-config-bl-auto-navigation-coordinator.js, and then a SMOOTH scroll
   * whose duration scales with distance.
   *
   * Measured live 2026-08-27 on the worst realistic case -- clicking the rail
   * ATC at the very bottom with Wood & Finish, the FIRST card, unanswered:
   * 5,448px of travel, arriving at roughly 4s. At the original 5000ms the
   * highlight had ~1s left, and a probe at exactly 5s already found it
   * cleared. 9s leaves a comfortable margin on that worst case and still
   * clears itself long before it could fight the card's applied styling.
   */
  const PENDING_CALLOUT_DURATION_MS = 9000;

  // Active animation timers keyed by animationKey - prevents overlapping animations
  const _activeAnimations = {};

  const Animations = {
    fadeOut: (element, duration = ANIMATION_DURATIONS.FADE) => {
      if (!element) return Promise.resolve();
      element.style.opacity = '0';
      return new Promise((resolve) => setTimeout(resolve, duration));
    },
    fadeIn: (element, duration = ANIMATION_DURATIONS.FADE) => {
      if (!element) return;
      element.style.opacity = '1';
      element.style.transition = `opacity ${duration}ms ease-in`;
    },
    // animationKey (optional): when provided, cancels any pending slide timer
    // already in flight for the same key before scheduling/running this one --
    // same _activeAnimations debounce pattern as animateValueChange below.
    // Without this, a delayed slideOpen can fire AFTER a later slideClose for
    // the same element already ran, silently re-opening a category that should
    // be closed (the apo_style stuck-navigation root cause).
    slideOpen: (element, animationKey) => {
      if (!element) return;
      if (animationKey && _activeAnimations[animationKey]) {
        clearTimeout(_activeAnimations[animationKey]);
      }
      // Open INTENT lifts the closed-grid inert now (see CLOSED GRIDS ARE
      // INERT): the class lands only after SLIDE_INIT_DELAY, and a focus into
      // the opening grid before then must not fail.
      element.setAttribute(GRID_OPENING_ATTR, '');
      syncGridInert(element);
      const timer = setTimeout(() => {
        element.style.transition = `max-height ${ANIMATION_DURATIONS.SLIDE}ms ease-out`;
        element.classList.add('murphy-door-configurator__thumbnails--open');
        element.removeAttribute(GRID_OPENING_ATTR);
        if (animationKey) _activeAnimations[animationKey] = null;
      }, ANIMATION_DURATIONS.SLIDE_INIT_DELAY);
      if (animationKey) _activeAnimations[animationKey] = timer;
    },
    slideClose: (element, animationKey) => {
      if (!element) return;
      if (animationKey && _activeAnimations[animationKey]) {
        clearTimeout(_activeAnimations[animationKey]);
        _activeAnimations[animationKey] = null;
      }
      element.style.transition = `max-height ${ANIMATION_DURATIONS.SLIDE}ms ease-out`;
      element.removeAttribute(GRID_OPENING_ATTR);
      element.classList.remove('murphy-door-configurator__thumbnails--open');
      syncGridInert(element);
    },

    /**
     * DRY VALUE CHANGE ANIMATION: Fade-out -> update content -> fade-in
     * Reusable for any element group that needs smooth value transitions.
     * Used by price updates, PTS updates, shipping updates, and slider updates.
     *
     * DIRTY-CHECK OPTIMIZATION: When options.getNewContent or options.hasChanged is provided,
     * the animation is skipped if content hasn't changed. applyContentFn() is still called
     * for state consistency (multi-element DOM sync, summary mirrors, etc.).
     * Opacity is restored to 1 in case a prior cancelled animation left elements faded out.
     *
     * @param {string} animationKey - Unique key to debounce/cancel in-flight animations (e.g. 'price', 'pts')
     * @param {HTMLElement[]} elements - Array of DOM elements to animate
     * @param {Function} applyContentFn - Callback that updates the DOM content (called between fade-out and fade-in)
     * @param {number} [duration] - Fade duration in ms (defaults to ANIMATION_DURATIONS.FADE)
     * @param {Object} [options] - Optional dirty-check configuration
     * @param {Function} [options.getNewContent] - For homogeneous elements: returns the single new text
     *   content string. Compared against textContent of ALL valid elements — if all match, skip animation.
     * @param {Function} [options.hasChanged] - For heterogeneous elements: returns boolean indicating
     *   whether any element's content has actually changed. Takes precedence over getNewContent.
     */
    animateValueChange: (
      animationKey,
      elements,
      applyContentFn,
      duration = ANIMATION_DURATIONS.FADE,
      options = {}
    ) => {
      // Cancel any in-flight animation FIRST — prevents stale-closure overwrites
      // (A skipped animation must still cancel prior timers or stale closures will
      //  overwrite content set by subsequent events. This is non-negotiable.)
      if (_activeAnimations[animationKey]) {
        clearTimeout(_activeAnimations[animationKey]);
        _activeAnimations[animationKey] = null;
      }

      const validElements = elements.filter((el) => el);
      if (validElements.length === 0) {
        // No elements to animate - just apply content directly
        applyContentFn();
        return;
      }

      // DIRTY-CHECK: Skip visual animation if content hasn't actually changed.
      // applyContentFn() is still called to keep all DOM targets in sync.
      // Opacity is restored to 1 in case a cancelled prior animation left elements faded out.
      let skipAnimation = false;

      if (typeof options.hasChanged === 'function') {
        // Heterogeneous elements (different text per element): caller provides explicit boolean
        skipAnimation = !options.hasChanged();
      } else if (typeof options.getNewContent === 'function') {
        // Homogeneous elements (all get same text): compare single string against all elements
        const newContent = options.getNewContent();
        skipAnimation = validElements.every(
          (el) => el.textContent === newContent
        );
      }

      if (skipAnimation) {
        isDev() &&
          console.log(
            `[Animations] animateValueChange: dirty-check skip "${animationKey}" — content unchanged`
          );
        // Restore visibility in case a prior cancelled animation left elements at opacity 0
        validElements.forEach((el) => {
          el.style.opacity = '1';
        });
        applyContentFn();
        return;
      }

      // Phase 1: Set transition and fade out
      validElements.forEach((el) => {
        el.style.transition = 'opacity ' + duration + 'ms ease-in-out';
        el.style.opacity = '0';
      });

      // Phase 2: After fade-out completes, update content and fade in
      _activeAnimations[animationKey] = setTimeout(() => {
        applyContentFn();

        // Phase 3: Fade in with updated content
        validElements.forEach((el) => {
          el.style.opacity = '1';
        });

        _activeAnimations[animationKey] = null;
      }, duration);
    },
  };

  /**
   * The tick draw-on celebrates the customer ANSWERING something, so it stays
   * disarmed until the customer has actually done something.
   *
   * 🚨 A "did it just become complete?" check is NOT sufficient on its own.
   * Restoring a ?config= link calls updateCircle(category, true) for every
   * answered category against a DOM that has no `complete` class yet, so every
   * one of them is a false -> true transition and ~15 checks would draw at
   * once on load -- celebrating nothing, since the customer did not do it.
   *
   * First pointer or key event anywhere is the signal, captured so a handler
   * that stops propagation cannot hide it. Passive because these never cancel.
   */
  let _checkAnimationArmed = false;
  const _armCheckAnimation = () => {
    _checkAnimationArmed = true;
  };
  document.addEventListener('pointerdown', _armCheckAnimation, {
    once: true,
    capture: true,
    passive: true,
  });
  document.addEventListener('keydown', _armCheckAnimation, {
    once: true,
    capture: true,
    passive: true,
  });

  /**
   * BURST STAGGER -- one customer action can answer many categories at once.
   *
   * Applying a wood seeds the casing/entablature defaults (see
   * md-config-bl-curated-defaults-coordinator.js), so a single click completes
   * a whole cascade. Measured on production 2026-09-04: one wood apply
   * auto-completed 14 categories, and every tick drew in the same frame -- 14
   * simultaneous animations reads as a flash, not as a confirmation.
   *
   * Offsetting each one turns that into a ripple down the list, which is both
   * calmer and more legible: it shows the cascade PROPAGATING rather than
   * asserting that everything changed at once.
   *
   * The step is CAPPED. Uncapped, 14 cards at 40ms would run 560ms of stagger
   * on top of the 400ms draw, so the last tick would finish nearly a second
   * after the click -- long enough to read as lag rather than polish. Capping
   * at 8 steps holds the whole cascade to 320ms + 400ms, and cards past the cap
   * simply share the final offset.
   *
   * A burst is "calls close together in time", not a queue: anything more than
   * BURST_RESET_MS after the previous animation starts a fresh burst at 0, so a
   * customer answering one category at a time never accumulates a delay.
   */
  const CHECK_STAGGER_MS = 40;
  const CHECK_STAGGER_MAX_STEPS = 8;
  const CHECK_BURST_RESET_MS = 250;
  let _checkBurstIndex = 0;
  let _checkBurstLastAt = -Infinity;

  // Status management system
  const StatusManager = {
    updateCircle: (element, isComplete) => {
      if (!element) return;
      const statusCircle = element.querySelector(
        '.murphy-door-configurator__status-circle'
      );
      if (statusCircle) {
        const wasComplete = statusCircle.classList.contains('complete');
        statusCircle.classList.toggle('complete', isComplete);

        // One-shot tick draw-on. Only on a real incomplete -> complete
        // transition, and only once the customer has interacted -- see
        // _checkAnimationArmed above, and the md-check-animate block in
        // assets/section-md-configurator.css for why CSS alone cannot gate it.
        if (isComplete && !wasComplete && _checkAnimationArmed) {
          const tick = statusCircle.querySelector('.md-check-tick');
          statusCircle.classList.add('md-check-animate');

          // Position this tick within the current burst (see the constants
          // above). performance.now() rather than Date.now() so a clock change
          // cannot collapse or explode the stagger.
          const _now = performance.now();
          if (_now - _checkBurstLastAt > CHECK_BURST_RESET_MS) {
            _checkBurstIndex = 0;
          }
          _checkBurstLastAt = _now;
          const _staggerMs =
            Math.min(_checkBurstIndex++, CHECK_STAGGER_MAX_STEPS) *
            CHECK_STAGGER_MS;
          // Inline, because the delay is per-element and computed. The CSS
          // animation uses `both`, so during the delay the tick holds the
          // from-state (undrawn) -- which is exactly the ripple.
          if (tick && _staggerMs) {
            tick.style.animationDelay = _staggerMs + 'ms';
          }

          // Safety-net teardown, matching the transitionend idiom this repo
          // already uses. Two things make the event alone unreliable here:
          // prefers-reduced-motion sets `animation: none` so animationend
          // NEVER fires, and animationend bubbles, so a nested animation could
          // otherwise settle the listener early. Hence the name check and the
          // timeout backstop (400ms animation + buffer + this tick's stagger --
          // a fixed 600ms would strip the class off a late card mid-draw).
          // 🚨 NEUTRALISE ANY ONE-SHOT STILL IN FLIGHT ON THIS SAME CIRCLE.
          // A category can flap complete -> incomplete -> complete inside one
          // backstop window: updateCircle(el, false) runs on unapply and on
          // browsing reset, and the material-filter handler alone fires 18x on
          // a single load. Without this, the FIRST call's belated timeout fires
          // while the THIRD call's animation is running and clears the class
          // and the inline delay out from under it, truncating a draw it does
          // not own. Caught in review before ship.
          //
          // Abort only marks the stale closure settled and drops its listener.
          // It deliberately does NOT clear the class or the delay, because the
          // incoming animation is about to own both.

          if (statusCircle._mdCheckAbort) statusCircle._mdCheckAbort();

          let _settled = false;
          const _finishCheckAnimation = () => {
            if (_settled) return;
            _settled = true;
            statusCircle._mdCheckAbort = null;
            if (tick) {
              tick.removeEventListener('animationend', _onCheckDrawEnd);
              // Clear the inline delay or it would apply to the NEXT burst.
              tick.style.animationDelay = '';
            }
            statusCircle.classList.remove('md-check-animate');
          };
          statusCircle._mdCheckAbort = () => {
            _settled = true;
            if (tick) tick.removeEventListener('animationend', _onCheckDrawEnd);
          };
          const _onCheckDrawEnd = (event) => {
            if (event.animationName !== 'mdCheckDraw') return;
            _finishCheckAnimation();
          };
          if (tick) tick.addEventListener('animationend', _onCheckDrawEnd);
          setTimeout(_finishCheckAnimation, 600 + _staggerMs);
        }

        // Get the open circle and check circle elements
        const openCircle = statusCircle.querySelector('.open-circle svg');
        const circleCheck = statusCircle.querySelector('.circle-check svg');

        if (openCircle && circleCheck) {
          // Show/hide the appropriate SVG based on completion status
          openCircle.style.display = isComplete ? 'none' : 'inline';
          circleCheck.style.display = isComplete ? 'inline' : 'none';
        }
      }
    },
  };

  const SELECTION_STATES = {
    BROWSING: 'browsing',
    APPLIED: 'applied',
  };

  const SELECTORS = {
    TAB_BUTTONS: '.murphy-door-configurator__tabs__tab-button',
    THUMBNAILS: '.murphy-door-configurator__thumbnails',
    CATEGORY_BUTTON: '.murphy-door-configurator__category-button',
    VARIANT_VIEW: '.murphy-door-configurator__variant-view',
    APPLY_BUTTON: '.murphy-door-configurator__apply-button',
    STATUS_CIRCLE: '.murphy-door-configurator__status-circle',
    SUMMARY_TAB: '[data-tab="summary"]',
    ADD_TO_CART: '.murphy-door-configurator__add-to-cart-button',
    VARIANT_GRID: '.murphy-door-configurator__variants-grid',
    SELECTED_PRODUCT:
      '.murphy-door-configurator__selected-multi-variant-product',
    PRICE_LABEL: '.murphy-door-configurator__price-label',
  };

  const CLASS_NAMES = {
    // Warning-coloured border on the category a disabled Add to Cart is
    // waiting for. Styled in section-md-configurator.css (the always-loaded
    // sheet, so classic/dual get it too, not just apo_style).
    PENDING_CALLOUT:
      'murphy-door-configurator__category-button--pending-callout',
    TAB_ACTIVE: 'murphy-door-configurator__tabs__tab-button--active',
    TAB_COMPLETED: 'murphy-door-configurator__tabs__tab-button--completed',
    CATEGORY_OPEN: 'murphy-door-configurator__category-button--open',
    CATEGORY_COMPLETED: 'murphy-door-configurator__category-button--completed',
    THUMBNAILS_OPEN: 'murphy-door-configurator__thumbnails--open',
    SELECTED_CLOSED:
      'murphy-door-configurator__category-button--selected-closed',
    THUMBNAIL_ACTIVE: 'murphy-door-configurator__thumbnail-item--active',
    PRICE_FLASH: 'price-flash',
  };

  // Create the uiUpdater object first
  const uiUpdater = {
    DOM,
    Animations,
    StatusManager,
    SELECTION_STATES,
    SELECTORS,
    CLASS_NAMES,
    // Central display state coordinator
    updateDisplay(action, data) {
      const actions = {
        TAB_CHANGED: () => {
          this.updateTabDisplay(data.tabId);
          this.updateCategoryVisibility(data.categories, data.tabId);
        },
        CATEGORY_TOGGLE: () => this.updateCategoryDisplay(data.categoryId),
        // Note: Summary tab has unique responsibilities (Add to Cart state, completion check via
        // conductor.checkSummaryComplete) and is handled by updateSummaryStatus() directly.
        // tabId='summary' is never passed here — do not route Summary tab updates through this handler.
        TAB_STATUS_UPDATE: () => {
          const tab = DOM.getElement(`[data-tab="${data.tabId}"]`);
          if (tab) {
            StatusManager.updateCircle(tab, data.isComplete);
            tab.classList.remove(CLASS_NAMES.TAB_ACTIVE);
            tab.classList.toggle(CLASS_NAMES.TAB_COMPLETED, data.isComplete);
          }
        },
        IMAGE_LAYERS_SHOWN: () => {
          const conductor = getConductor();
          const currentView = conductor?.getCurrentView();

          if (!currentView) {
            isDev() &&
              console.error('UI Updater: Unable to get current view', {
                hasConductor: !!conductor,
              });
            return;
          }
          data.frontView.style.display =
            currentView === 'front' ? 'block' : 'none';
          data.backView.style.display =
            currentView === 'back' ? 'block' : 'none';
        },
        UPDATE_IMAGE_LAYER: () => {
          const { categoryId, layerData } = data;
          const categoryLayers = document.querySelector(
            '.murphy-door-configurator__category-layers'
          );

          if (!categoryLayers) {
            isDev() &&
              console.error('UI_UPDATER: category layers container not found');
            return;
          }

          let layerElement = categoryLayers.querySelector(
            `[data-category-layer="${categoryId}"]`
          );

          if (!layerElement) {
            layerElement = document.createElement('div');
            layerElement.className = 'murphy-door-configurator__category-layer'; // Remove any additional classes
            layerElement.setAttribute('data-category-layer', categoryId);
            layerElement.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: ${layerData.zIndex};
      pointer-events: none;
    `;

            const img = document.createElement('img');

            // 🔥 STRICT NO FALLBACK: Visual error handling for missing configurator images
            if (!layerData.frontLayer || layerData.frontLayer.trim() === '') {
              const errorMsg = `UI UPDATER IMAGE ERROR: Missing frontLayer for category ${categoryId}`;
              isDev() &&
                console.error('UI_UPDATER.UPDATE_IMAGE_LAYER:', errorMsg);

              // Create visible error element instead of broken image
              const errorDiv = document.createElement('div');
              errorDiv.style.cssText = `
                background: #ff6b6b; 
                color: white; 
                padding: 15px; 
                margin: 5px; 
                font-size: 12px; 
                font-weight: bold;
                border-radius: 6px;
                text-align: center;
                display: flex;
                align-items: center;
                justify-content: center;
                min-height: 80px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.2);
              `;
              errorDiv.textContent = errorMsg;
              layerElement.appendChild(errorDiv);
            } else {
              img.src = layerData.frontLayer;
              img.className = 'configurator-layer';
              img.alt = `${categoryId} Layer`;
              img.style.cssText =
                'width: 100%; height: 100%; object-fit: contain;';

              // 🔥 STRICT NO FALLBACK: Handle image load errors with visible warnings
              img.onerror = () => {
                const errorMsg = `UI UPDATER IMAGE LOAD ERROR: Failed to load frontLayer for category ${categoryId}`;
                isDev() &&
                  console.error('UI_UPDATER image load error:', errorMsg, {
                    frontLayer: layerData.frontLayer,
                  });

                // Replace failed image with visible error element
                const errorDiv = document.createElement('div');
                errorDiv.style.cssText = `
                  background: #ff6b6b; 
                  color: white; 
                  padding: 15px; 
                  margin: 5px; 
                  font-size: 12px; 
                  font-weight: bold;
                  border-radius: 6px;
                  text-align: center;
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  min-height: 80px;
                  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                `;
                errorDiv.innerHTML = `
                  <div>${errorMsg}</div>
                  <div style="font-size: 10px; margin-top: 5px; opacity: 0.8;">URL: ${layerData.frontLayer}</div>
                `;

                // Replace the image with error div
                layerElement.removeChild(img);
                layerElement.appendChild(errorDiv);
              };

              layerElement.appendChild(img);
            }
            categoryLayers.appendChild(layerElement);
          }

          layerElement.style.opacity = layerData.isVisible ? '1' : '0';
          layerElement.style.display = layerData.isVisible ? 'block' : 'none';
        },
      };

      actions[action]?.();
    },

    // Batch update coordinator
    batchUpdate(updates) {
      // Handle applied state updates
      // DRY: auto_applied flag propagated from cascade auto-selection coordinator
      // When auto_applied is true, animations are skipped for silent category completion
      if (updates.appliedState) {
        this.updateAppliedState(
          updates.appliedState.categoryId,
          updates.appliedState.key,
          updates.appliedState.displayTitle,
          updates.appliedState.price,
          updates.appliedState.productTitle,
          updates.appliedState.auto_applied
        );
      }

      // Handle summary list updates
      if (updates.summaryList) {
        this.updateSummaryList(
          updates.summaryList.categoryId,
          updates.summaryList.displayTitle,
          updates.summaryList.productTitle,
          updates.summaryList.price
        );
      }

      // Handle tab status updates
      if (updates.tabStatus) {
        this.updateDisplay('TAB_STATUS_UPDATE', {
          tabId: updates.tabStatus.tabId,
          isComplete: updates.tabStatus.isComplete,
        });
      }

      // Handle progress updates
      if (updates.progress) {
        this.updateProgressBar(updates.progress);
        this.updateSummaryStatus();
      }

      // Handle shipping time updates
      if (updates.shippingTime) {
        this.updateShippingTime(
          updates.shippingTime.formattedText,
          updates.shippingTime.range,
          updates.shippingTime.transitDays
        );
      }

      // Handle pass-through space updates
      if (updates.passThroughSpace) {
        this.updatePassThroughSpaceDisplay(updates.passThroughSpace);
      }

      // Add material updates to batch processing
      if (updates.materials) {
        updates.materials.forEach((update) => {
          this.updateMaterialDisplay(update.categoryId, update.filterResult);
        });
      }
    },

    // Tab managemen
    updateTabDisplay(tabId) {
      const tabs = DOM.getElements(SELECTORS.TAB_BUTTONS);
      tabs.forEach((tab) => {
        const shouldBeActive = tab.getAttribute('data-tab') === tabId;

        // 🔥 BUGFIX: Toggle ALL three active classes that Navigation Manager adds
        // This ensures tabs don't keep the active border from base.css .murphy-tab-button.active rule
        tab.classList.toggle('active', shouldBeActive);
        tab.classList.toggle('murphy-tab-button--active', shouldBeActive);
        tab.classList.toggle(CLASS_NAMES.TAB_ACTIVE, shouldBeActive);
      });

      if (tabId === 'summary') {
        const summaryContent = document.querySelector(
          '.murphy-door-configurator__summary-content'
        );
        const categoriesContent = document.querySelector(
          '.murphy-door-configurator__categories'
        );

        // 🚨 apo_style NEVER SWAPS THE RAIL OUT FOR THE SUMMARY (2026-08-27).
        //
        // The summary panel is display:none in that mode and the Add to Cart
        // button lives at the end of the category rail, so performing this swap
        // hides the ONLY thing on screen and shows nothing in its place -- a
        // blank panel with a working button below it. Ryan: "Don't do the
        // current thing where they're now shown nothing with an add to cart
        // button that works."
        //
        // md-config-bl-auto-navigation-coordinator.js no longer ASKS for this
        // at 100% in apo_style, which is the real fix. This is the second lock:
        // 'summary' is also reachable from the state manager and the conductor,
        // and any one of them re-introducing the request would silently blank
        // the configurator again. Guarding the write makes the blank state
        // unreachable no matter who asks for it.
        const _apoNoSummary =
          getConductor()?._getConfiguratorExperience?.() === 'apo_style';

        if (summaryContent && categoriesContent && !_apoNoSummary) {
          summaryContent.style.display = 'block';
          categoriesContent.style.display = 'none';

          // Reset scroll so __summary-header is always at the top of the visible area.
          //
          // Two containers can retain a scroll offset from browsing categories:
          //   - __content (flex child of __tools; overflow-y:auto on mobile, both breakpoints)
          //   - __info-column (outer 60dvh container; mobile only)
          //
          // __summary-content.scrollTop is implicitly 0 because it was display:none.
          // __content.scrollTop needs an explicit reset for cases where the summary list
          // was previously scrolled deep (browser clamping handles short summaries, but
          // not when the list overflows __content on a return visit).
          const contentEl = document.querySelector(
            '.murphy-door-configurator__content'
          );
          if (contentEl) {
            contentEl.scrollTop = 0;
          }
          if (
            window.matchMedia &&
            window.matchMedia('(max-width: 800px)').matches
          ) {
            const infoColumn = DOM.getElement(
              '.murphy-door-configurator__info-column'
            );
            if (infoColumn) {
              infoColumn.scrollTop = 0;
            }
          }

          // Populate summary list when Summary tab is clicked
          // This ensures the list shows all current applied selections (including URL-recovered ones)

          // Clear existing list items before repopulating to ensure correct ordering
          // The updateSummaryList() method will insert items in correct order using PPD categoryToSortIndexMap
          const summaryList = document.querySelector(
            '.murphy-door-configurator__summary-list'
          );
          if (summaryList) {
            summaryList.innerHTML = '';
          }

          const categoryButtons = document.querySelectorAll(
            '.murphy-door-configurator__category-button[data-category]'
          );
          categoryButtons.forEach((button) => {
            const categoryId = button.getAttribute('data-category');
            const selectionDisplay = button.querySelector(
              '.murphy-door-configurator__category-user-selection'
            );

            if (selectionDisplay && selectionDisplay.textContent.trim()) {
              // Parse the selection text (format: "Product | Finish | $250.00")
              const fullText = selectionDisplay.textContent.trim();
              const parts = fullText.split(' | ');

              if (parts.length >= 2) {
                const displayTitle = parts.slice(0, -1).join(' | '); // Everything except last part
                const price = parts[parts.length - 1]; // Last part is price

                // Call updateSummaryList to add to Summary tab
                this.updateSummaryList(categoryId, displayTitle, null, price);
              }
            }
          });
        }
      } else {
        const summaryContent = document.querySelector(
          '.murphy-door-configurator__summary-content'
        );
        const categoriesContent = document.querySelector(
          '.murphy-door-configurator__categories'
        );

        if (summaryContent && categoriesContent) {
          summaryContent.style.display = 'none';
          categoriesContent.style.display = 'block';
        }
      }

      // CCP COMPLIANT: Publish tab readiness state after actual tab activation
      // This is the WORKING tab activation system - state synchronization fix
      this.publishTabReadinessState();
    },

    // Category visibility control
    updateCategoryVisibility(categories, tabId) {
      // apo_style has no tabs -- every category is already visible via
      // md-config-ui-navigation-manager.js's buildCategories() reveal-all
      // pass. This function's job is "hide everything not in this tab's
      // category list", which would undo that reveal the moment ANYTHING
      // triggers a TAB_CHANGED (mobile-swipe's UI_TAB_ACTIVATION_REQUESTED
      // handler calls main.handleStateChange('TAB_CHANGED', ...) directly,
      // independent of navigationManager.showCategoriesForTab()'s own
      // apo_style guard -- both choke points need it, not just one).
      const conductor = getConductor();
      if (conductor?._getConfiguratorExperience?.() === 'apo_style') {
        return;
      }
      const toolsDiv = document.querySelector(
        '.murphy-door-configurator__tools'
      );
      const isSimplifiedMode =
        toolsDiv?.getAttribute('data-simplified-mode') === 'active';

      const updates = [];
      DOM.getElements(SELECTORS.CATEGORY_BUTTON).forEach((element) => {
        // Gate cards (e.g. "Cabinets?") carry data-category-gate, never
        // data-category -- deliberately, so ATC-completion logic never treats
        // them as a real, required category. But that means they can't use
        // the categories.includes(categoryId) check below (categoryId would
        // be null and never match). They still need their OWN tab-membership
        // visibility: show when this is their tab, hide otherwise -- exactly
        // like a real category, just keyed off data-tab (already stamped by
        // buildCategories()) instead of data-category. Whether the gate's
        // DEPENDENT categories (Top/Bottom Cabinet Doors) stay hidden per the
        // gate's answer is md-config-ui-gate-visibility-manager.js's job,
        // re-asserted via the UI_CATEGORIES_SHOWN_FOR_TAB publish below -- do
        // not fold that logic in here.
        if (element.hasAttribute('data-category-gate')) {
          const belongsToTab = element.getAttribute('data-tab') === tabId;
          updates.push(() => {
            element.style.display = belongsToTab ? 'block' : 'none';
          });
          return;
        }
        const categoryId = element.getAttribute('data-category');
        updates.push(() => {
          if (categories.includes(categoryId)) {
            // In simplified mode, only show categories stamped as simplified-visible
            if (
              isSimplifiedMode &&
              element.getAttribute('data-simplified-visible') !== 'true'
            ) {
              element.style.display = 'none';
            } else {
              element.style.display = 'block';
            }
          } else {
            element.style.display = 'none';
          }
        });
      });

      requestAnimationFrame(() => {
        updates.forEach((update) => update());
        // Gate-card-dependent categories (Top/Bottom Cabinet Doors, etc.) just
        // had their display reset above by this same sweep -- re-assert the
        // gate's current answer immediately after, same event
        // md-config-ui-gate-visibility-manager.js already listens for on the
        // showCategoriesForTab() path (Phase 0 fix: this real-click path
        // published nothing before, so that re-assert never fired here).
        if (tabId && CONSTANTS?.EVENTS?.UI_CATEGORIES_SHOWN_FOR_TAB) {
          publishEvent(CONSTANTS.EVENTS.UI_CATEGORIES_SHOWN_FOR_TAB, {
            tabId,
            categoryCount: updates.length,
            timestamp: Date.now(),
            source: 'uiUpdater.updateCategoryVisibility',
          });
        }
      });
    },

    // Category display management
    initializeCategoryContent(categoryId) {
      // PATTERN 6: Bootstrap Runtime Protection - queue if PPD not ready
      if (!bootstrapComplete) {
        isDev() &&
          console.log(
            `[UIUpdater] initializeCategoryContent("${categoryId}") queued - bootstrap not complete`
          );
        pendingCalls.push({
          fn: this.initializeCategoryContent,
          context: this,
          args: [categoryId],
        });
        return '';
      }

      // ✅ PPD-FIRST: Get display mode from pre-classified map
      const ppd = getPPD();
      const displayMode = ppd?.categoryDisplayModeMap?.get(categoryId);

      if (displayMode?.hasDirectVariant && displayMode.directVariantProductId) {
        // ✅ O(1) product lookup instead of O(n) find!
        const directVariantProduct = ppd.products.get(
          displayMode.directVariantProductId
        );
        if (directVariantProduct) {
          isDev() &&
            console.log(
              `✅ PPD-FIRST: initializeCategoryContent found directVariant product via O(1) lookup for category "${categoryId}"`
            );
          return this.generateDirectVariantLayout(
            directVariantProduct,
            categoryId
          );
        }
      }

      // Standard multi-variant display
      return this.generateSmartSortedThumbnails(categoryId);
    },

    updateCategoryDisplay(categoryId, options) {
      const skipScroll = !!(options && options.skipScroll);
      // Forwarded, not re-derived: apo_style only moves the page when the
      // caller explicitly asks. Callers that pass nothing (CATEGORY_TOGGLE,
      // the tab-change first-incomplete hop) therefore stay put in that mode
      // while behaving exactly as before in classic/dual.
      const apoScrollAllowed = !!(options && options.apoScrollAllowed);
      const targetCategory = DOM.getElement(
        `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
      );

      if (!targetCategory) return;

      requestAnimationFrame(() => {
        // apo_style keeps EVERY card permanently expanded (Ryan review
        // 2026-08-17: "all drawers load open", "we dont have the cards fold
        // when done"). The accordion sweep below -- which collapses every
        // card except the one being opened -- is therefore skipped wholesale
        // in that mode. Only the aria-current bookkeeping still runs: with
        // all cards visible at once, "which step am I on" is no longer
        // implied by which one is open, so it has to stay accurate for AT
        // users (and it is what auto-navigation's scroll target keys off).
        const _apoConductor = getConductor();
        const isApoStyle =
          _apoConductor?._getConfiguratorExperience?.() === 'apo_style';

        // Close all other categories (classic/dual_* only -- see above)
        DOM.getElements(SELECTORS.CATEGORY_BUTTON).forEach((category) => {
          const thisCategoryId = category.getAttribute('data-category');
          if (thisCategoryId !== categoryId) {
            if (isApoStyle) {
              category.removeAttribute('aria-current');
              return;
            }
            category.classList.remove(CLASS_NAMES.CATEGORY_OPEN);

            // Gate cards share this same .category-button base class but
            // carry data-category-gate instead of data-category, so they
            // land in this "other" branch too -- reset their data-open so a
            // reopened gate doesn't stay visually expanded once a real
            // category becomes current. Without this, answering/reopening a
            // gate then navigating to a real category left both panels
            // expanded simultaneously. Gauntlet finding 2026-08-13.
            if (category.hasAttribute('data-category-gate')) {
              category.setAttribute('data-open', 'false');
              // Must pair with the aria-current='step' set at the
              // UI_GATE_OPEN_REQUEST/UI_GATE_REOPEN_REQUESTED sites, same as
              // md-config-ui-mobile-swipe.js's step-dots always pair
              // aria-current set/remove -- otherwise a gate the customer just
              // answered keeps claiming to be the active step to AT users
              // forever once a real category becomes current (the common
              // case). Gauntlet finding 2026-08-13 (ux-ui-champion).
              category.removeAttribute('aria-current');
              // Live-reproduced 2026-08-13 (Daniel): reopen Swing (an
              // already-answered gate), then open Door Depth (a real
              // category) -- this sweep correctly force-closes Swing above,
              // but Swing's stored answer never changed, so it must revert
              // to its answered/collapsed look, not stay stuck showing
              // blank/unanswered. md-config-ui-gate-visibility-manager.js
              // owns the reopened-gate tracking state (CCP: no direct
              // cross-module access from here), so publish and let it
              // reconcile.
              publishEvent(CONSTANTS.EVENTS.UI_GATE_FORCE_CLOSED, {
                gateId: category.getAttribute('data-category-gate'),
              });
            }

            // Restore completed-state badge if this category has a confirmed selection.
            // CATEGORY_OPEN and SELECTED_CLOSED are mutually exclusive: opening a new
            // category auto-closes others, and those completed categories must regain their
            // checkmark/selection-summary badge when collapsed.
            if (category.classList.contains(CLASS_NAMES.CATEGORY_COMPLETED)) {
              category.classList.add(CLASS_NAMES.SELECTED_CLOSED);
            }

            const thumbnailsElement = category.querySelector(
              SELECTORS.THUMBNAILS
            );
            if (thumbnailsElement)
              Animations.slideClose(
                thumbnailsElement,
                'category-slide:' + thisCategoryId
              );
          }
        });

        const thumbnailsElement = targetCategory.querySelector(
          SELECTORS.THUMBNAILS
        );
        targetCategory.classList.add(CLASS_NAMES.CATEGORY_OPEN);
        // Only strip the completed look outside apo_style. Kept as a guard
        // rather than an unconditional remove: since 2026-08-27 apo_style
        // never adds SELECTED_CLOSED in the first place (see updateAppliedState),
        // so this is a no-op there either way -- but an unconditional remove
        // would quietly become the thing that breaks classic/dual if the
        // add-site guard were ever revisited. In classic/dual, "open" and
        // "completed look" genuinely are mutually exclusive states.
        if (!isApoStyle) {
          targetCategory.classList.remove(CLASS_NAMES.SELECTED_CLOSED);
        }

        if (isApoStyle) {
          // Gate cards already carried aria-current='step'
          // (md-config-ui-navigation-manager.js:535); real category cards
          // never did, because "which card is open" used to convey it
          // implicitly. With every card open that signal is gone, so the
          // current step has to be stated explicitly -- for AT users, and
          // because section-md-apo-mode.css now hangs the active border off
          // this attribute rather than off --open (which is true of every
          // card here and therefore distinguishes nothing).
          targetCategory.setAttribute('aria-current', 'step');
          // Belt-and-braces with _forceAllCategoriesOpenForApoStyle: if any
          // other path re-adds aria-expanded, strip it again. Cards can never
          // collapse in this mode, so the attribute can only ever lie.
          targetCategory.removeAttribute('aria-expanded');
          categoryToggleOf(targetCategory)?.removeAttribute('aria-expanded');
        }

        // Use intelligent display strategy based on category type
        this.showCategoryContent(categoryId);

        if (thumbnailsElement)
          Animations.slideOpen(
            thumbnailsElement,
            'category-slide:' + categoryId
          );

        // Calculate total delay using animation duration constants
        const totalDelay =
          ANIMATION_DURATIONS.SLIDE +
          ANIMATION_DURATIONS.SLIDE_INIT_DELAY +
          ANIMATION_DURATIONS.SAFETY_BUFFER;

        // Fix race condition by waiting for animations to fully complete
        // skipScroll: true when triggered by auto-navigation on page load — on mobile
        // the info-column contains the product title above the categories, so scrolling
        // to the category would hide the title before the user has interacted.
        if (!skipScroll) {
          // The keeper's reveal waits for this rather than scrolling first and
          // being scrolled away from (see KEEPER FOCUS STAYS ON SCREEN).
          _autoScrollPendingUntil = Math.max(
            _autoScrollPendingUntil,
            Date.now() + totalDelay + 50
          );
          setTimeout(() => {
            requestAnimationFrame(() => {
              _autoScrollPendingUntil = 0;
              uiUpdater.scrollCategoryIntoView(targetCategory, {
                apoScrollAllowed,
              });
            });
          }, totalDelay);
        }
      });
    },

    /**
     * P7 A11Y (WCAG 4.1.2 Name/Role/Value): the ONLY sanctioned way to set a
     * tile's highlighted state. Toggles the class AND the matching
     * aria-pressed together so the two can never disagree.
     *
     * WHY A HELPER AND NOT TEN EDITS: this state is written in ten places
     * across this file. Setting aria alongside the class at each of them is
     * the shape that drifts -- the eleventh writer gets added, sets only the
     * class, and a screen reader silently starts announcing the wrong option
     * as selected with nothing to catch it. A test greps for bare
     * classList.add/remove of THUMBNAIL_ACTIVE so a new writer fails CI.
     *
     * aria-pressed deliberately mirrors the VISUAL --active state rather than
     * the applied selection: the requirement is that programmatic state match
     * what is presented, and --active is what the customer sees highlighted.
     * @param {HTMLElement} el
     * @param {boolean} isActive
     */
    _setTileActive(el, isActive) {
      if (!el || !el.classList) return;
      el.classList.toggle(CLASS_NAMES.THUMBNAIL_ACTIVE, !!isActive);
      // Only elements exposing a button role take aria-pressed; on anything
      // else it is invalid ARIA and axe flags it.
      if (el.getAttribute && el.getAttribute('role') === 'button') {
        el.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      }
    },

    /**
     * Clears the --active ring before a new selection is highlighted, scoped to
     * ONE category whenever more than one category is on screen at a time.
     *
     * 🚨 THE GLOBAL CLEAR IS ONLY CORRECT FOR AN ACCORDION. Both callers used
     * to fall back to "clear every tile on the page", justified by the comment
     * "in normal mode (one category open at a time)". That premise is false in
     * BOTH multi-open experiences:
     *   - simplified 3Q, which is why the per-category branch already existed;
     *   - apo_style, where EVERY card is permanently open and none of them
     *     collapse -- so clearing globally stripped the ring off every other
     *     answered category.
     *
     * Ryan, UAT 2026-08-27: "when I then select an option within another
     * category button, say, jamb depth, the nice thick border disappears from
     * the shelf depth. I expect that border to appear on any and all options
     * that have been applied." Measured before the fix: picking Door Depth gave
     * it activeCount 1; picking Jamb Depth immediately dropped Door Depth to
     * activeCount 0 while its summary and --completed both stayed, i.e. the
     * card still knew its answer and only the ring was lost.
     *
     * Kept as one helper because the two callers (updateSelectionDisplay and
     * the composite-variant path) had drifted into two copies of the same
     * simplified-only check -- a third writer would have drifted again.
     *
     * @param {string|null} categoryId - the category being selected into
     * @returns {boolean} true when the clear was scoped rather than global
     */
    _clearTileActiveForSelection(categoryId) {
      const scopedSelector = categoryId
        ? `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
        : null;

      const scoped =
        !!scopedSelector && uiUpdater._isMultiOpenCategory(categoryId);

      if (scoped) {
        document
          .querySelectorAll(
            `${scopedSelector} .murphy-door-configurator__thumbnail-item`
          )
          .forEach((thumb) => uiUpdater._setTileActive(thumb, false));
        return true;
      }

      DOM.getElements('.murphy-door-configurator__thumbnail-item').forEach(
        (thumb) => uiUpdater._setTileActive(thumb, false)
      );
      return false;
    },

    /**
     * Is this category on screen alongside other open categories? True in the
     * two multi-open experiences: simplified 3Q (per-card attribute) and
     * apo_style (every card open by virtue of the experience, no marker).
     * @param {string|null} categoryId
     * @returns {boolean}
     */
    _isMultiOpenCategory(categoryId) {
      if (!categoryId) return false;
      if (
        document.querySelector(
          `.murphy-door-configurator__category-button[data-simplified-visible="true"][data-category="${categoryId}"]`
        )
      ) {
        return true;
      }
      try {
        const conductor = window.MurphyDoorConfigurator.conductor;
        return (
          typeof conductor?._getConfiguratorExperience === 'function' &&
          conductor._getConfiguratorExperience() === 'apo_style'
        );
      } catch (error) {
        return false;
      }
    },

    /**
     * Re-asserts the --active ring on every category whose answer is APPLIED,
     * from state rather than from how the answer arrived.
     *
     * 🚨 THE RING WAS ONLY EVER SET IN THE BROWSING BRANCH. updateSelectionDisplay
     * highlights a tile under `state === SELECTION_STATES.BROWSING`; there is no
     * APPLIED branch. That is invisible in the normal click flow, because a
     * human click passes THROUGH browsing on its way to applied and the ring is
     * a leftover from that pass. A selection that is applied WITHOUT ever being
     * browsed -- the bulk path behind the curated defaults and the apo_style
     * casing/entablature seeding -- therefore never got a ring at all.
     *
     * Ryan, UAT 2026-08-27: "I expect that border to appear on any and all
     * options that have been applied, even if they were applied
     * programmatically/automatically by our system." Measured before this:
     * Front/Back Casing and Front/Back Entablature all read --completed with a
     * correct summary and ring 0.
     *
     * BROWSING STILL WINS, same precedence as renderVariantBrowserFromList: a
     * category the customer is actively looking at keeps the highlight on what
     * they are looking at, not on what is saved.
     *
     * Additive only -- it never clears. Clearing stays the sole job of
     * _clearTileActiveForSelection, so this cannot fight it.
     */
    syncAppliedTileRings(exceptCategoryId) {
      try {
        const conductor = window.MurphyDoorConfigurator.conductor;
        if (!conductor) return 0;
        const applied = conductor.getAppliedSelections?.() || {};
        const browsing = conductor.getBrowsingSelections?.() || {};
        let marked = 0;

        Object.keys(applied).forEach((categoryId) => {
          // The category being selected into right now is owned by the
          // browsing branch below. Re-ringing its OLD applied variant here
          // would briefly paint TWO rings in one card -- the stale answer and
          // the one being clicked -- because conductor browsing state is not
          // updated until after this runs.
          if (exceptCategoryId && categoryId === exceptCategoryId) return;
          // Scoped to the multi-open experiences. In an accordion only one
          // category is on screen, so re-asserting rings there would be a
          // behaviour change nobody asked for.
          if (!uiUpdater._isMultiOpenCategory(categoryId)) return;
          if (browsing[categoryId]?.variantId) return; // browsing wins
          const variantId = applied[categoryId]?.variantId;
          const productId = applied[categoryId]?.productId;
          if (!variantId && !productId) return;

          const card = document.querySelector(
            `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
          );
          if (!card) return;

          // Variant tile first, then the PRODUCT tile. A card is not always
          // showing a variant grid: several categories (cabinet doors, the
          // back casing/entablature pair) sit at the product level, where the
          // applied variant has no tile of its own and the answer is
          // represented by its product's tile. Measured live -- variant-only
          // lookup left exactly those four ringless while every category
          // rendering variant tiles picked up its ring correctly.
          let tile =
            variantId &&
            card.querySelector(
              `.murphy-door-configurator__thumbnail-item[data-variant-id="${variantId}"]`
            );
          if (!tile && productId) {
            tile = card.querySelector(
              `.murphy-door-configurator__thumbnail-item[data-product-id="${productId}"]`
            );
          }
          if (!tile) return; // slider categories have no tiles — nothing to ring
          if (!tile.classList.contains(CLASS_NAMES.THUMBNAIL_ACTIVE)) {
            uiUpdater._setTileActive(tile, true);
            marked++;
          }
        });
        return marked;
      } catch (error) {
        isDev() &&
          console.error('UIUpdater.syncAppliedTileRings failed:', error);
        return 0;
      }
    },

    /**
     * THE applied-cue sweep: ring (multi-open experiences) and outline
     * (click-apply) the applied tile of EVERY applied category, browsing
     * winning in both. Additive for the ring, self-correcting for the
     * outline, no layout reads, so it is safe to repeat.
     *
     * 🚨 CALL IT FROM ANYTHING THAT REWRITES A GRID. A rebuilt grid arrives
     * cue-less, and the cue used to come back only if some other pass
     * happened to sweep afterwards. renderCategoryThumbnails (the bulk-apply
     * flush's category re-sort) never did, and cascade fills applied straight
     * to APPLIED were never ringed by anything anchored to them: prod UAT
     * 2026-10-01, a double tap on "Build this" left Jamb Depth, Locking
     * Systems, Smart Home Automations and Delivery (fills the preset does not
     * set, so outside its RINGS_SYNC) ringless until reload. Sweeps ALL
     * applied categories, never just the rewritten one; see the material
     * filter pass for why scoping it down was measured wrong.
     * @param {string} [source] - caller tag for dev logging
     * @returns {void}
     */
    repaintAppliedCues(source) {
      const tag = source || 'UIUpdater.repaintAppliedCues';
      try {
        uiUpdater.syncAppliedTileRings();
        const conductor = getConductor();
        if (!conductor?._isClickApplyEnabled?.()) return;
        const browsing = conductor.getBrowsingSelections?.() || {};
        Object.keys(conductor.getAppliedSelections?.() || {}).forEach(
          (categoryId) => {
            // Browsing wins, same precedence the ring uses.
            if (browsing[categoryId]?.variantId) return;
            try {
              uiUpdater._markAppliedTileInGrid(categoryId);
            } catch (categoryError) {
              isDev() &&
                console.warn(
                  `${tag}: applied-tile mark failed for "${categoryId}":`,
                  categoryError
                );
            }
          }
        );
      } catch (error) {
        isDev() && console.warn(`${tag}: applied-cue repaint failed:`, error);
      }
    },

    /**
     * repaintAppliedCues once, at the end of the current task, however many
     * grids that task rewrote.
     * @param {string} [source]
     */
    scheduleAppliedCueRepaint(source) {
      if (_appliedCueRepaintQueued) return;
      _appliedCueRepaintQueued = true;
      Promise.resolve().then(() => {
        _appliedCueRepaintQueued = false;
        uiUpdater.repaintAppliedCues(source);
      });
    },

    /**
     * "Build this" (and any other bulk apply that never passes through
     * browsing): make each listed category card ring exactly its APPLIED
     * tile. syncAppliedTileRings() is additive and multi-open only, so after
     * a preset the OLD tile kept its ring and, in the accordion, nothing
     * re-rang at all; a manual click gets this from the browsing branch of
     * updateSelectionDisplay. Scoped per card (never the global clear the
     * accordion path of _clearTileActiveForSelection does), browsing still
     * wins, and a card whose applied answer has no tile (sliders) is left
     * untouched rather than cleared.
     *
     * SAME VIEW AS A ?config= LOAD (swap-in-place only). A preset lands on
     * cards that may still be drilled into an EARLIER product, so before
     * ringing, each card is brought to what a URL restore renders:
     *   1. a drill-down that does not hold the applied variant is rebuilt to
     *      the product grid (_rebuildProductGrid, the back click's rebuild,
     *      without its fade) and announced;
     *   2. the card's one-shot resume attempt is cleared and
     *      _markAppliedTileInGrid runs, which drills into the applied
     *      product's variant tier and outlines the applied tile, exactly as
     *      it does after a ?config= restore.
     * Deliberately NO SELECTION_BROWSING: that would scroll the page and
     * report config_category_opened for categories nobody opened.
     * @param {string[]} categoryIds
     * @returns {number} cards re-rung
     */
    resyncTileRingsForCategories(categoryIds) {
      try {
        const conductor = window.MurphyDoorConfigurator.conductor;
        if (!conductor || !Array.isArray(categoryIds)) return 0;
        const applied = conductor.getAppliedSelections?.() || {};
        const browsing = conductor.getBrowsingSelections?.() || {};
        const swapInPlace = !!conductor._isClickApplyEnabled?.();
        let rung = 0;
        categoryIds.forEach((categoryId) => {
          if (typeof categoryId !== 'string' || !categoryId) return;
          if (browsing[categoryId]?.variantId) return; // browsing wins
          const variantId = applied[categoryId]?.variantId;
          const productId = applied[categoryId]?.productId;
          if (!variantId && !productId) return;
          const card = Array.from(
            document.querySelectorAll(
              '.murphy-door-configurator__category-button[data-category]'
            )
          ).find((el) => el.getAttribute('data-category') === categoryId);
          if (!card) return;
          if (swapInPlace) {
            uiUpdater._resumeAppliedView(card, categoryId, variantId);
          }
          const tiles = Array.from(
            card.querySelectorAll('.murphy-door-configurator__thumbnail-item')
          );
          const target =
            (variantId &&
              tiles.find(
                (t) => t.getAttribute('data-variant-id') === String(variantId)
              )) ||
            (productId &&
              tiles.find(
                (t) => t.getAttribute('data-product-id') === String(productId)
              )) ||
            null;
          if (!target) return;
          tiles.forEach((tile) =>
            uiUpdater._setTileActive(tile, tile === target)
          );
          rung++;
        });
        return rung;
      } catch (error) {
        isDev() &&
          console.error('UIUpdater.resyncTileRingsForCategories failed:', error);
        return 0;
      }
    },

    /**
     * Brings one category card to the view a ?config= restore renders for
     * its applied answer (see resyncTileRingsForCategories): leave a stale
     * drill-down, then run the shared resume in _markAppliedTileInGrid.
     * A card showing its product grid, a direct variant grid, or a drill-down
     * that already holds the applied variant is not rebuilt.
     * @param {Element} card - the category-button element
     * @param {string} categoryId
     * @param {string|number|null} variantId - the applied variant
     * @private
     */
    _resumeAppliedView(card, categoryId, variantId) {
      try {
        const thumbnailsContainer = card.querySelector(SELECTORS.THUMBNAILS);
        const drilled = readDrilledInView(thumbnailsContainer);
        const holdsApplied =
          !!drilled &&
          variantId != null &&
          Array.prototype.some.call(
            drilled.view.querySelectorAll(
              '.murphy-door-configurator__thumbnail-item'
            ),
            (tile) => tile.getAttribute('data-variant-id') === String(variantId)
          );
        if (drilled && !drilled.direct && !holdsApplied) {
          this._rebuildProductGrid(
            categoryId,
            thumbnailsContainer,
            'UIUpdater.resyncTileRingsForCategories'
          );
        }
        delete card.dataset.mdResumeTierAttempt;
        this._markAppliedTileInGrid(categoryId);
      } catch (error) {
        isDev() &&
          console.warn(
            '[UI_UPDATER] _resumeAppliedView failed for ' + categoryId + ':',
            error
          );
      }
    },

    /**
     * P7 A11Y (WCAG 2.4.3 Focus Order): move focus to a category we just
     * scrolled to, but ONLY when focus is currently unowned.
     *
     * THE BUG THIS EXISTS TO FIX (measured live 2026-08-20, Daniel driving
     * with a focus recorder attached): on load, SELECTION_BROWSING fires for
     * every open category and scrollCategoryIntoView runs for each -- Height
     * fires last and wins, leaving the page at ~1114px. Focus is never moved,
     * so it sits on <body>. Chrome derives the sequential focus navigation
     * starting point from the SCROLL POSITION, so the customer's very first
     * Tab landed inside the Height card's "how to measure" icon, skipping the
     * header, the gallery, and the Wood & Finish and Width cards entirely.
     * Shift+Tab could walk back up, which is why it read as "the first two
     * cards are unreachable" rather than as a focus bug.
     *
     * The guard is the load-bearing part. Focus only moves when it is on
     * body / <main> / documentElement / nothing -- i.e. nobody owns it. If the
     * customer is mid-interaction with ANY control, we leave them alone;
     * yanking focus out of a slider they are operating would be a worse
     * failure than the one being fixed.
     *
     * preventScroll is required: we have already issued a smooth scroll, and
     * focus() would otherwise fire a second, instant one and fight it.
     * @param {HTMLElement} targetCategory
     */
    _focusScrolledCategory(targetCategory, options) {
      try {
        if (!targetCategory || typeof targetCategory.focus !== 'function') {
          return;
        }
        // A real category card is NOT itself focusable (WCAG 4.1.2 fix,
        // 2026-09-28): its keyboard/AT control is the header's native toggle
        // <button>, so that is where focus goes. An answered gate card still
        // carries role="button" tabindex="0" on the card; an unanswered gate
        // has neither, so this is a no-op for it rather than a silent failure.
        const focusTarget =
          categoryToggleOf(targetCategory) ||
          (targetCategory.hasAttribute('tabindex') ? targetCategory : null);
        if (!focusTarget) return;

        const active = document.activeElement;
        // A control the focus keeper restored (not one the customer chose)
        // travels WITH auto-navigation; otherwise a collapsed card's header
        // keeps focus while the page scrolls away from it (WCAG 2.4.11).
        const unowned =
          !active ||
          active === document.body ||
          active === document.documentElement ||
          active.tagName === 'MAIN' ||
          (isKeeperHeldFocus(active) && !targetCategory.contains(active));
        // A keyboard confirm-and-continue hands over focus the customer DID
        // place (the re-pressed tile), see handleCategoryFocusRequest.
        const carried =
          !!options &&
          !!options.carryFocusFrom &&
          !!active &&
          options.carryFocusFrom.contains(active) &&
          !targetCategory.contains(active);
        if (!unowned && !carried) return;

        focusTarget.focus({ preventScroll: true });

        // Record where we sent them so the pending selection announcement can
        // say so. Only set when focus ACTUALLY moved -- the guards above
        // already returned for every case where it did not.
        //
        // data-category FIRST, deliberately. The title element's textContent
        // swallows every descendant, including the info icon's own label and the
        // markup whitespace between them, so on a measurement card it produced
        // "Width\n \n \n how to measure" and a screen reader read that aloud
        // verbatim. Caught live 2026-08-21; no unit test could see it because the
        // fixtures had no info icon.
        _announceDestination =
          targetCategory.getAttribute('data-category') ||
          readCategoryTitleText(targetCategory) ||
          null;
        // (Re)start the settle timer. The focus move can land AFTER the
        // selection flush has already run, in which case there is no pending
        // timer and the destination would otherwise be recorded and never
        // spoken.
        if (_announceDestination) {
          if (_announceTimer) clearTimeout(_announceTimer);
          _announceTimer = setTimeout(
            flushSelectionAnnouncement,
            ANNOUNCE_SETTLE_MS
          );
        }
      } catch (error) {
        // A focus failure must never break the scroll it was following.
        isDev() &&
          console.warn('[UIUpdater] _focusScrolledCategory failed:', error);
      }
    },

    /**
     * @param {HTMLElement} targetCategory
     * @param {{apoScrollAllowed?: boolean}} [options]
     *   apoScrollAllowed - REQUIRED to move the page in apo_style. See the
     *   block comment in the apo_style branch below.
     */
    scrollCategoryIntoView(targetCategory, options) {
      // apo_style deliberately has NO inner scroll container -- the page is a
      // single surface (section-md-apo-mode.css). That breaks the legacy path
      // below in a way that fails silently rather than loudly: it picks
      // .categories (desktop) or .info-column (mobile), tests
      // `scrollHeight > offsetHeight`, finds both now report overflow:visible
      // and therefore aren't scrollable, tries .tools as a fallback, finds the
      // same, and returns having scrolled nothing. Auto-navigation would still
      // "work" -- it just wouldn't move the page, which is exactly the
      // behaviour Ryan asked for the opposite of ("scrolls down as we move
      // through each of the options").
      //
      // Scroll the window instead. Header clearance is handled in CSS via
      // scroll-margin-top on the card rather than arithmetic here, so the
      // offset stays correct when the header resizes.
      const _apoConductor = getConductor();
      if (_apoConductor?._getConfiguratorExperience?.() === 'apo_style') {
        // 🚨 IN apo_style THIS SCROLL IS OPT-IN. Ryan has now reported the same
        // thing four times across one UAT session -- the page moving under him
        // in a mode whose whole premise is that the customer owns the scroll --
        // and each report was a DIFFERENT caller reaching this function:
        // auto-navigation, the browsing subscription, the gallery/price
        // relayout, and finally CATEGORY_TOGGLE via the width/height steppers
        // (measured 2500px -> 1128px, a 1372px yank, from a call that passed
        // no options at all).
        //
        // Guarding callers one at a time is the wrong shape: the default was
        // "scroll", so every path that did not know about apo_style scrolled,
        // and the next one added will too. Inverting it means a new caller is
        // silent here by default and has to ASK to move the page.
        //
        // The deliberate movers opt in via UI_CATEGORY_OPEN_REQUEST: the
        // Summary "Edit" link, the fell-out-of-complete recovery, and the
        // material-remap gate reset all need to deliver the customer to an
        // off-screen card, and without a scroll their click does nothing.
        // navigation-manager decides that from the event's source.
        if (!options || options.apoScrollAllowed !== true) {
          return;
        }
        const utils = window.MurphyDoorConfigurator?.utils;
        // MUST stay a method call on `utils`. scrollIntoViewSafe's body calls
        // `this.isIOSSafari()`, so pulling it into a bare local first
        // (`const f = utils.scrollIntoViewSafe; f(el)`) detaches `this` and it
        // throws "Cannot read properties of undefined (reading
        // 'isIOSSafari')". That throw is swallowed by the caller, so the only
        // symptom is that auto-navigation silently stops scrolling -- which is
        // exactly how it presented (Daniel 2026-08-17: "I think we should have
        // the move to next card logic now that we dont have it").
        try {
          if (typeof utils?.scrollIntoViewSafe === 'function') {
            utils.scrollIntoViewSafe(targetCategory, {
              behavior: 'smooth',
              block: 'start',
            });
          } else if (targetCategory.scrollIntoView) {
            targetCategory.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        } catch (error) {
          // Last-resort fallback: a failed scroll must never leave the
          // customer stranded mid-configuration with no movement at all.
          isDev() &&
            console.error('[UIUpdater] apo_style scroll failed:', error);
          // Optional chaining, not a bare property read: if the throw came
          // from targetCategory being null/detached in the first place, a
          // plain `targetCategory.scrollIntoView` here throws again -- this
          // time uncaught, out of a catch block.
          targetCategory?.scrollIntoView?.({ block: 'start' });
        }
        uiUpdater._focusScrolledCategory(targetCategory, options);
        return;
      }

      const navigationWrapper = DOM.getElement(
        '.murphy-door-configurator__navigation-wrapper'
      );
      const categoriesContainer = DOM.getElement(
        '.murphy-door-configurator__categories'
      );

      if (!navigationWrapper || !categoriesContainer) return;

      const navRect = navigationWrapper.getBoundingClientRect();
      const categoryRect = targetCategory.getBoundingClientRect();

      // Mobile uses info-column as scroll container (60dvh, overflow-y:auto);
      // desktop uses categories container (constrained inside 86vh tools panel).
      const isMobile = window.matchMedia('(max-width: 800px)').matches;
      let scrollContainer = isMobile
        ? DOM.getElement('.murphy-door-configurator__info-column')
        : categoriesContainer;

      if (!scrollContainer) return;

      // 🔥 FIX: Check if the scroll container is actually scrollable
      // If categories container has no overflow (e.g., Appearance tab with few categories),
      // fall back to scrolling the tools panel parent instead
      const isScrollable =
        scrollContainer.scrollHeight > scrollContainer.offsetHeight;
      if (!isScrollable && !isMobile) {
        // Fallback to tools panel for desktop when categories container isn't scrollable
        const toolsPanel = DOM.getElement('.murphy-door-configurator__tools');
        if (toolsPanel && toolsPanel.scrollHeight > toolsPanel.offsetHeight) {
          scrollContainer = toolsPanel;
        } else {
          // If tools panel also isn't scrollable, nothing to scroll
          return;
        }
      }

      const containerRect = scrollContainer.getBoundingClientRect();
      const remPx =
        parseFloat(window.getComputedStyle(document.documentElement).fontSize) /
        2;
      const topOffset = isMobile ? navRect.height + remPx : remPx;

      const targetPosition =
        scrollContainer.scrollTop +
        (categoryRect.top - containerRect.top) -
        topOffset;

      scrollContainer.scrollTo({
        top: targetPosition,
        behavior: window.isIOS ? 'auto' : 'smooth',
      });

      // Classic/dual scroll an inner container rather than the window, so the
      // sequential-focus starting point does not drift the way it does in
      // apo_style. Focus still follows for consistency: a keyboard user who
      // was moved should be able to Tab onward from where they were moved TO,
      // in every experience.
      uiUpdater._focusScrolledCategory(targetCategory, options);
    },

    // ========================================================================
    // "MAKE THE INERT TAPS MEAN SOMETHING" (2026-09-19)
    //
    // Three taps in apo_style + click-apply used to do nothing at all: the
    // card header, a re-tap of the already-applied tile, and the mobile
    // Summary step dot. They now ASK for one of two things, and this module --
    // the official DOM manipulator -- is the only thing that performs either.
    //
    // 🚨 THESE ARE NOT NAVIGATION. Nothing here opens a category, changes a
    // tab, applies a selection or publishes UI_CATEGORY_OPENED / UI_TAB_OPENED.
    // That matters for a specific reason: auto-navigation-coordinator's
    // handleManualInteraction listens on those two events and permanently sets
    // `userHasManuallyInteracted` for any source but AUTO_NAVIGATOR, which
    // would kill auto-navigation for the rest of the session. A customer
    // tapping a card title must not cost them that.
    // ========================================================================

    /**
     * Resolve the card a UI_CATEGORY_FOCUS_REQUESTED payload points at.
     *
     * Ordinary cards carry `data-category`; an unanswered GATE card (Cabinets?)
     * carries `data-category-gate` instead and has no category id of its own,
     * which is why the coordinator can hand back a `gateId` with a null
     * `categoryId`. Returning null for anything unresolvable is deliberate --
     * every caller below treats "no element" as a silent no-op rather than
     * guessing at a nearby card.
     *
     * @param {{categoryId?: string|null, gateId?: string|null}} eventData
     * @returns {HTMLElement|null}
     */
    _resolveFocusTargetCard(eventData) {
      try {
        const data = eventData || {};
        if (data.categoryId) {
          const byCategory = DOM.getElement(
            `.murphy-door-configurator__category-button[data-category="${data.categoryId}"]`
          );
          if (byCategory) return byCategory;
        }
        if (data.gateId) {
          return DOM.getElement(
            `.murphy-door-configurator__category-button[data-category-gate="${data.gateId}"]`
          );
        }
        return null;
      } catch (error) {
        isDev() &&
          console.warn('[UIUpdater] _resolveFocusTargetCard failed:', error);
        return null;
      }
    },

    /**
     * Restart the ring pulse on whichever tile holds this card's answer.
     *
     * 🚨 RE-ADDING THE --active CLASS DOES NOT RESTART A CSS ANIMATION. The
     * pulse (`glowPulse 8s 0.8s 3`, section-md-configurator.css) is declared on
     * `.thumbnail-item--active .thumbnail-image img`, and the class is already
     * there -- that is the entire point, this card is already answered. So the
     * restart has to go through the force-reflow idiom, the same one
     * md-config-ui-navigation-manager.js uses for the simplified-missing shake
     * and assets/global-price-flash.js uses for the price flash.
     *
     * Toggling the INLINE `animation` (rather than the class) is what keeps
     * this compatible with the reduced-motion guard: clearing the inline value
     * hands the element straight back to the cascade, where
     * `@media (prefers-reduced-motion: reduce)` sets `animation: none` and the
     * pulse simply never runs. Restarting via a class would have re-armed it
     * for exactly the customers who asked not to see it.
     *
     * @param {HTMLElement} categoryCard
     * @returns {boolean} true when a pulse was actually restarted
     */
    _restartAppliedTilePulse(categoryCard) {
      try {
        if (!categoryCard || typeof categoryCard.querySelector !== 'function') {
          return false;
        }
        const categoryId = categoryCard.getAttribute('data-category');

        // PRIMARY: the same lookup syncAppliedTileRings performs -- applied
        // selection via conductor delegation, variant tile first and the
        // PRODUCT tile second (several categories sit at the product level and
        // have no variant tile of their own).
        let tile = null;
        if (categoryId) {
          const conductor = window.MurphyDoorConfigurator.conductor;
          const applied = conductor?.getAppliedSelections?.() || {};
          const answer = applied[categoryId];
          if (answer?.variantId) {
            tile = categoryCard.querySelector(
              `.murphy-door-configurator__thumbnail-item[data-variant-id="${answer.variantId}"]`
            );
          }
          if (!tile && answer?.productId) {
            tile = categoryCard.querySelector(
              `.murphy-door-configurator__thumbnail-item[data-product-id="${answer.productId}"]`
            );
          }
        }
        // FALLBACK: whichever tile is actually wearing the ring right now. The
        // lookup above is how the ring got THERE, so reading it back is only a
        // belt -- but it is the half that still works for a gate card, and for
        // the window in which the applied stores disagree (see CLAUDE.md, "TWO
        // Applied-Selection Stores").
        if (!tile) {
          tile = categoryCard.querySelector(`.${CLASS_NAMES.THUMBNAIL_ACTIVE}`);
        }
        if (!tile) return false;

        const img = tile.querySelector(
          '.murphy-door-configurator__thumbnail-image img'
        );
        if (!img) return false;

        img.style.animation = 'none';
        void img.offsetWidth; // eslint-disable-line no-void -- forces reflow
        img.style.animation = '';
        return true;
      } catch (error) {
        isDev() &&
          console.warn('[UIUpdater] _restartAppliedTilePulse failed:', error);
        return false;
      }
    },

    /**
     * CONFIRM-AND-CONTINUE CARRIES KEYBOARD FOCUS (WCAG 2.4.3, 2.4.11; staging
     * UAT 2026-10-02, bug D). Re-pressing an already-applied tile scrolls the
     * page to the next incomplete card, but _focusScrolledCategory only moves
     * focus that nobody owns, and here the customer owns it: it stayed on the
     * re-pressed tile, now scrolled off screen, so the next Tab started from
     * the card they had just left.
     *
     * Returns the card focus should be carried FROM (the card holding the
     * re-pressed tile) when, and only when, this request is the auto-
     * navigation answer to a confirm-and-continue, the activation was a
     * keyboard press or an assistive-tech click (`detail === 0`: VoiceOver,
     * NVDA/JAWS browse mode, TalkBack, Switch Control), and focus is still in
     * a different card. A mouse or touch activation (`detail >= 1`) returns
     * null: the pointer is where the customer put it, and its focus never
     * moves (the scroll alone is their feedback).
     * @param {Object} eventData - UI_CATEGORY_FOCUS_REQUESTED payload
     * @param {Element} targetCard
     * @returns {Element|null}
     */
    _confirmContinueFocusSource(eventData, targetCard) {
      try {
        const sources = CONSTANTS.NAV_TAP_SOURCES || {};
        if (
          !eventData ||
          !sources.CONFIRM_CONTINUE ||
          eventData.source !== sources.CONFIRM_CONTINUE
        ) {
          return null;
        }
        // 'keyboard' includes an assistive-tech click (detail 0); a mouse
        // or touch click (detail >= 1) is 'pointer' (see LAST CUSTOMER INPUT).
        const input = lastCustomerInput();
        if (
          input.modality !== 'keyboard' ||
          Date.now() - input.at > FOCUS_CARRY_INPUT_MS
        ) {
          return null;
        }
        const active = document.activeElement;
        const fromCard =
          active && active.closest ? active.closest('[data-category]') : null;
        if (!fromCard || fromCard === targetCard) return null;
        if (targetCard && targetCard.contains(fromCard)) return null;
        return fromCard;
      } catch (error) {
        return null;
      }
    },

    /**
     * Bring one card into view (unless the caller only wants the pulse) and
     * re-pulse its answer.
     *
     * @param {{categoryId?: string|null, gateId?: string|null, pulseOnly?: boolean}} eventData
     * @returns {boolean} true when a card was found and acted on
     */
    handleCategoryFocusRequest(eventData) {
      const card = uiUpdater._resolveFocusTargetCard(eventData);
      if (!card) return false;

      // `pulseOnly` is how a tap on the card's OWN tile asks for acknowledgement
      // without a scroll: the customer's finger is already there, and moving the
      // page under them is the exact complaint that made apo_style's scroll
      // opt-in in the first place (see scrollCategoryIntoView).
      if (!eventData || eventData.pulseOnly !== true) {
        // apoScrollAllowed is REQUIRED -- apo_style scrolling is opt-in, and
        // this is a deliberate, customer-initiated move.
        uiUpdater.scrollCategoryIntoView(card, {
          apoScrollAllowed: true,
          carryFocusFrom: uiUpdater._confirmContinueFocusSource(
            eventData,
            card
          ),
        });
      }
      uiUpdater._restartAppliedTilePulse(card);
      return true;
    },

    /**
     * Bring the real buy area into view and pulse its button once.
     *
     * 🚨 THE TARGET IS THE WRAPPER, NOT THE BUTTON.
     * `.murphy-door-configurator__add-to-cart-button` matches SEVERAL nodes --
     * the rail's, the mobile sticky bar's and the traveling-preview bar's (see
     * snippets/configurator-price-bar.liquid, which documents that the
     * querySelectorAll plumbing is why those extra copies are free). A
     * document-wide query would land on whichever happens to be first in the
     * DOM, which on mobile is a bar that is already stuck on screen -- i.e. a
     * scroll to somewhere the customer already is. The wrapper is singular.
     *
     * Focus is deliberately NOT moved here. `_focusScrolledCategory`'s guard
     * only fires on elements carrying `tabindex`, which a native <button> does
     * not need, so reusing it would be a no-op wearing a safety belt; and
     * dragging focus onto an Add to Cart control is the one focus move in this
     * module that could submit something a customer did not mean to submit.
     *
     * 🚨 ON DESKTOP THE SCROLL MOVES NOTHING, AND THAT IS CORRECT --
     * do not "fix" it. The wrapper sits inside a `position: sticky`
     * `.murphy-door-configurator__product-price` (itself inside the sticky
     * `.product` grid), so it is pinned: measured live at 1512px, scrolling the
     * window 300px moved the wrapper 0px, and its viewport top stayed at 667
     * across the whole gesture. The buy area is permanently on screen there,
     * which is exactly why it needs no scroll -- the attention pulse below is
     * the feedback that lands. Below 801px the wrapper is in normal flow and
     * the scroll does the work (measured: viewport top 9196px -> 390px).
     *
     * @param {Object} [eventData]
     * @returns {boolean} true when the buy area was found and acted on
     */
    handleBuyAreaFocusRequest(eventData) {
      const wrapper = DOM.getElement(
        '.murphy-door-configurator__add-to-cart-wrapper'
      );
      if (!wrapper) return false;

      const utils = window.MurphyDoorConfigurator?.utils;
      try {
        // MUST stay a method call on `utils` -- scrollIntoViewSafe's body calls
        // `this.isIOSSafari()`. See the identical note in scrollCategoryIntoView.
        if (typeof utils?.scrollIntoViewSafe === 'function') {
          utils.scrollIntoViewSafe(wrapper, {
            behavior: 'smooth',
            block: 'center',
          });
        } else if (wrapper.scrollIntoView) {
          wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } catch (error) {
        isDev() && console.warn('[UIUpdater] buy-area scroll failed:', error);
        wrapper?.scrollIntoView?.({ block: 'center' });
      }

      // One-shot, finite, compositor-only attention cue, scoped INSIDE the
      // wrapper for the same reason the scroll target is. Removed on
      // animationend so a second request re-arms cleanly.
      const button = wrapper.querySelector(
        '.murphy-door-configurator__add-to-cart-button'
      );
      if (button) {
        const ATTENTION =
          'murphy-door-configurator__add-to-cart-button--attention';
        button.classList.remove(ATTENTION);
        void button.offsetWidth; // eslint-disable-line no-void -- forces reflow
        button.classList.add(ATTENTION);
        button.addEventListener(
          'animationend',
          function clearAttention() {
            button.classList.remove(ATTENTION);
          },
          { once: true }
        );
      }
      return true;
    },

    scrollActiveThumbnailIntoView(categoryId, productId, variantId) {
      // Find the category button
      const categoryButton = DOM.getElement(
        `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
      );
      if (!categoryButton) {
        isDev() &&
          console.warn(
            `[UIUpdater] scrollActiveThumbnailIntoView: category button not found for ${categoryId}`
          );
        return;
      }

      // 🔥 RACE CONDITION FIX: Use stable data attributes instead of CSS class
      // The --active class is applied by a different handler triggered by the same event,
      // so there's no guarantee it's applied before this scroll runs.
      let targetThumbnail = null;

      // Try variant-specific thumbnail first
      if (variantId) {
        targetThumbnail = categoryButton.querySelector(
          `[data-variant-id="${variantId}"]`
        );
      }

      // Fallback to product thumbnail if variant not found
      if (!targetThumbnail && productId) {
        targetThumbnail = categoryButton.querySelector(
          `[data-product-id="${productId}"]`
        );
      }

      if (!targetThumbnail) {
        isDev() &&
          console.log(
            `[UIUpdater] scrollActiveThumbnailIntoView: no target thumbnail found for ${categoryId}`,
            {
              productId,
              variantId,
              reason: 'likely a slider category or data attributes missing',
            }
          );
        return;
      }

      // Find the actual scrollable container by walking up the parent tree
      // For variant browsers, the variants-grid is inside the thumbnails container,
      // and it's the thumbnails container that has the overflow/scroll behavior
      let scrollableContainer = null;
      let currentElement = targetThumbnail;

      // Walk up the parent tree to find a scrollable container
      while (currentElement && currentElement !== categoryButton) {
        currentElement = currentElement.parentElement;
        if (!currentElement) break;

        const isScrollable =
          currentElement.scrollHeight > currentElement.offsetHeight;
        const hasOverflow =
          window.getComputedStyle(currentElement).overflowY !== 'visible';

        if (isScrollable && hasOverflow) {
          scrollableContainer = currentElement;
          break;
        }
      }

      if (!scrollableContainer) {
        isDev() &&
          console.log(
            `[UIUpdater] scrollActiveThumbnailIntoView: ${categoryId} no scrollable parent found, skipping scroll`
          );
        return;
      }

      // Calculate scroll position with small top offset
      const containerRect = scrollableContainer.getBoundingClientRect();
      const thumbnailRect = targetThumbnail.getBoundingClientRect();
      const remPx =
        parseFloat(window.getComputedStyle(document.documentElement).fontSize) /
        2;

      const targetPosition =
        scrollableContainer.scrollTop +
        (thumbnailRect.top - containerRect.top) -
        remPx;

      isDev() &&
        console.log(
          `[UIUpdater] 📜 Scrolling target thumbnail for ${categoryId}:`,
          {
            scrollableContainerClass: scrollableContainer.className,
            productId,
            variantId,
            currentScroll: scrollableContainer.scrollTop,
            targetPosition,
            scrollHeight: scrollableContainer.scrollHeight,
            offsetHeight: scrollableContainer.offsetHeight,
            thumbnailTop: thumbnailRect.top,
            containerTop: containerRect.top,
            offset: remPx,
          }
        );

      scrollableContainer.scrollTo({
        top: targetPosition,
        behavior: window.isIOS ? 'auto' : 'smooth',
      });
    },

    // Selection display management
    // 🎯 EVENT-DRIVEN PATTERN: Now async to support event-driven composite product handling
    async updateSelectionDisplay(
      key,
      state,
      displayTitle,
      price,
      productTitle,
      compositeContext = null
    ) {
      // 🧬 CompositeProduct Enhancement: Handle CompositeProduct selections differently
      if (compositeContext && compositeContext.isCompositeProduct) {
        // 🎯 EVENT-DRIVEN PATTERN: Await async composite product handling
        await this.handleCompositeProductSelection(
          key,
          state,
          displayTitle,
          price,
          productTitle,
          compositeContext
        );
        return;
      }

      // Regular product handling (existing logic)
      const selector = key.startsWith('variant')
        ? `[data-variant-id="${key.split('_')[1]}"]`
        : `[data-product-id="${key.split('_')[1]}"]`;

      // 🎯 DRY FIX: Find ALL matching thumbnails (handles duplicates from bubble-up)
      // querySelector() only returns FIRST match - fails when bubble-up creates duplicates
      let allMatchingThumbnails = DOM.getElements(selector);

      // 🔧 SINGLE-VARIANT FALLBACK: If looking for variant thumbnail but none found,
      // try product thumbnail (handles single-variant products where variantId is auto-resolved
      // but DOM only has product thumbnail, not variant thumbnail)
      if (allMatchingThumbnails.length === 0 && key.startsWith('variant_')) {
        const variantId = key.split('_')[1];
        // Get PPD to look up productId for this variantId
        const ppd = getPPD();
        const productId = ppd?.variantIdToProductIdMap?.get(
          parseInt(variantId)
        );
        if (productId) {
          const productSelector = `[data-product-id="${productId}"]`;
          allMatchingThumbnails = DOM.getElements(productSelector);
          isDev() &&
            console.log(
              `🔄 UI UPDATER: Fallback to product thumbnail for single-variant product`,
              {
                variantId,
                productId,
                fallbackSelector: productSelector,
                found: allMatchingThumbnails.length,
              }
            );
        }
      }

      // Get category from first thumbnail for context (all should be in same category)
      const thumbnail = allMatchingThumbnails[0];
      const category = DOM.getClosest(thumbnail, '[data-category]');
      const categoryId = category?.getAttribute('data-category');

      // Reset thumbnails — scoped to this category in every multi-open
      // experience (simplified 3Q and apo_style) so sibling cards keep their
      // applied ring. See _clearTileActiveForSelection.
      uiUpdater._clearTileActiveForSelection(categoryId);
      // Re-assert rings on every OTHER applied category, so ones applied
      // programmatically (never browsed) are ringed too. Excludes this
      // category, which the browsing branch below owns.
      uiUpdater.syncAppliedTileRings(categoryId);

      // 🔍 DEBUG: Log thumbnail and category results
      isDev() &&
        console.log('🔍 UI UPDATER: Thumbnail lookup results:', {
          thumbnailFound: !!thumbnail,
          matchingThumbnailsCount: allMatchingThumbnails.length,
          categoryFound: !!category,
          categoryId: categoryId,
        });

      if (thumbnail && state === SELECTION_STATES.BROWSING) {
        // 🎯 DRY FIX: Apply --active class to ALL matching thumbnails
        // Handles duplicate thumbnails created by bubble-up transformations
        allMatchingThumbnails.forEach((thumb) => {
          uiUpdater._setTileActive(thumb, true);
        });

        // 🔥 FIX: Get the category button directly via data-category attribute
        // The .closest() method might return the thumbnails container instead of the category button
        const categoryButton = categoryId
          ? document.querySelector(
              `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
            )
          : category;

        isDev() &&
          console.log('🔍 UI UPDATER: Category button lookup:', {
            categoryButton: !!categoryButton,
            hasSelectionDisplay: !!categoryButton?.querySelector(
              '.murphy-door-configurator__category-user-selection'
            ),
            hasApplyButton: !!categoryButton?.querySelector(
              '.murphy-door-configurator__apply-button'
            ),
          });

        this.updateSelectionText(
          categoryButton,
          key,
          displayTitle,
          price,
          productTitle
        );
        this.updateApplyButtonVisibility(categoryButton, true);
        this.resetCategoryStyle(categoryButton);

        // 🎯 PRIORITY 3 FIX: Event-driven price calculation request (CCP compliant)
        // Publish request - price calculator will respond via event subscription
        window.MurphyDoorConfigurator.CoreEventBus.publish(
          CONSTANTS.EVENTS.PRICE_CALCULATION_REQUESTED,
          {
            requestId: `ui_updater_selection_${Date.now()}`,
            state: SELECTION_STATES.BROWSING,
            productId: key.startsWith('product') ? key.split('_')[1] : null,
            variantId: key.startsWith('variant') ? key.split('_')[1] : null,
            timestamp: Date.now(),
            source: 'ui-updater.updateSelectionDisplay',
          }
        );
      } else if (!thumbnail && state === SELECTION_STATES.BROWSING) {
        // SLIDER CATEGORY PATH: Slider categories (Width, Height, Door Depth) don't have thumbnail DOM
        // elements, so the standard thumbnail lookup above returns null. We resolve the
        // category button directly from state manager's browsing state, which always has
        // the correct categoryId because the conductor passes categoryIdOverride.
        const conductor = getConductor();
        const isSliderCategory =
          conductor &&
          typeof conductor.shouldUseCategorySlider === 'function' &&
          this._resolveSliderCategoryId(key, conductor);

        if (isSliderCategory) {
          const sliderCategoryId = isSliderCategory;
          const sliderCategoryButton = document.querySelector(
            `.murphy-door-configurator__category-button[data-category="${sliderCategoryId}"]`
          );

          isDev() &&
            console.log(
              '🎚️ UI UPDATER: Slider category path for BROWSING display:',
              {
                key,
                sliderCategoryId,
                categoryButtonFound: !!sliderCategoryButton,
                hasSelectionDisplay: !!sliderCategoryButton?.querySelector(
                  '.murphy-door-configurator__category-user-selection'
                ),
                hasApplyButton: !!sliderCategoryButton?.querySelector(
                  '.murphy-door-configurator__apply-button'
                ),
              }
            );

          if (sliderCategoryButton) {
            this.updateSelectionText(
              sliderCategoryButton,
              key,
              displayTitle,
              price,
              productTitle
            );
            this.updateApplyButtonVisibility(sliderCategoryButton, true);
            this.resetCategoryStyle(sliderCategoryButton);

            // Event-driven price calculation request
            window.MurphyDoorConfigurator.CoreEventBus.publish(
              CONSTANTS.EVENTS.PRICE_CALCULATION_REQUESTED,
              {
                requestId: `ui_updater_slider_${Date.now()}`,
                state: SELECTION_STATES.BROWSING,
                productId: key.startsWith('product') ? key.split('_')[1] : null,
                variantId: key.startsWith('variant') ? key.split('_')[1] : null,
                timestamp: Date.now(),
                source: 'ui-updater.updateSelectionDisplay.slider',
              }
            );
          }
        }
      }
    },

    /**
     * Resolve categoryId for a slider category by looking up the variant key
     * against all slider categories in the PPD's sliderCategoriesMap.
     *
     * Returns the categoryId if the variant belongs to a slider category, null otherwise.
     *
     * @param {string} key - Variant/product key (e.g., 'variant_12345')
     * @param {Object} conductor - Conductor instance for PPD access
     * @returns {string|null} categoryId if slider category, null otherwise
     * @private
     */
    _resolveSliderCategoryId(key, conductor) {
      const variantId = key.startsWith('variant_')
        ? key.substring('variant_'.length)
        : null;
      if (!variantId) return null;

      // Access PPD sliderCategoriesMap via conductor delegation
      const ppd = conductor.getPerfectProductDictionary
        ? conductor.getPerfectProductDictionary()
        : null;
      if (!ppd || !ppd.sliderCategoriesMap) return null;

      // Iterate all slider categories and check if variant belongs to any
      for (const [catId, metadata] of ppd.sliderCategoriesMap) {
        if (metadata.variantIdToIndexMap) {
          if (
            metadata.variantIdToIndexMap.has(variantId) ||
            metadata.variantIdToIndexMap.has(String(variantId))
          ) {
            return catId;
          }
        }
      }

      return null;
    },

    /**
     * 🧬 CompositeProduct Enhancement: Handle CompositeProduct selections with special logic
     * CompositeProducts require different thumbnail finding and variant view generation
     *
     * 🎯 EVENT-DRIVEN PATTERN: Now async to support event-driven variant view generation
     *
     * @param {string} key - Variant key (e.g., 'variant_12345')
     * @param {string} state - Selection state (BROWSING/APPLIED)
     * @param {string} displayTitle - Display title for the selected variant
     * @param {string} price - Price of the selected variant
     * @param {string} productTitle - Product title
     * @param {Object} compositeContext - CompositeProduct context data
     * @returns {Promise<void>}
     */
    async handleCompositeProductSelection(
      key,
      state,
      displayTitle,
      price,
      productTitle,
      compositeContext
    ) {
      const METHOD_SIGNATURE = 'UIUpdater.handleCompositeProductSelection';

      try {
        // 🔥 FIX: Find the VARIANT thumbnail that was clicked, not the composite product thumbnail!
        const variantId = key.startsWith('variant') ? key.split('_')[1] : null;
        if (!variantId) {
          isDev() &&
            console.error(`${METHOD_SIGNATURE}: Invalid variant key: ${key}`);
          return;
        }

        const variantSelector = `[data-variant-id="${variantId}"]`;

        // 🎯 DRY FIX: Find ALL matching variant thumbnails (handles duplicates from bubble-up)
        const allMatchingThumbnails = DOM.getElements(variantSelector);
        const variantThumbnail = allMatchingThumbnails[0];
        const category = DOM.getClosest(variantThumbnail, '[data-category]');

        // Scoped thumbnail reset — shared with updateSelectionDisplay so the
        // two cannot drift apart again. See _clearTileActiveForSelection.
        const _compositeCategoryId =
          category?.getAttribute('data-category') || null;
        uiUpdater._clearTileActiveForSelection(_compositeCategoryId);
        uiUpdater.syncAppliedTileRings(_compositeCategoryId);

        if (!variantThumbnail) {
          isDev() &&
            console.error(
              `${METHOD_SIGNATURE}: Variant thumbnail not found with selector: ${variantSelector}`
            );
          return;
        }

        if (!category) {
          isDev() &&
            console.error(
              `${METHOD_SIGNATURE}: Category not found for variant thumbnail`
            );
          return;
        }

        if (state === SELECTION_STATES.BROWSING) {
          // 🎯 DRY FIX: Apply --active class to ALL matching variant thumbnails
          // Handles duplicate thumbnails created by bubble-up transformations
          allMatchingThumbnails.forEach((thumb) => {
            uiUpdater._setTileActive(thumb, true);
          });

          // Update selection text with CompositeProduct context
          this.updateSelectionText(
            category,
            key,
            displayTitle,
            price,
            compositeContext.groupName // Use group name as product title
          );

          // Show apply button and reset category style
          this.updateApplyButtonVisibility(category, true);
          this.resetCategoryStyle(category);

          // 🎯 EVENT-DRIVEN PATTERN: Await async variant view generation
          await this.generateCompositeVariantView(compositeContext, key);

          // 🎯 PRIORITY 3 FIX: Event-driven price calculation request (CCP compliant)
          // Publish request - price calculator will respond via event subscription
          window.MurphyDoorConfigurator.CoreEventBus.publish(
            CONSTANTS.EVENTS.PRICE_CALCULATION_REQUESTED,
            {
              requestId: `ui_updater_composite_${Date.now()}`,
              state: SELECTION_STATES.BROWSING,
              productId: key.startsWith('product') ? key.split('_')[1] : null,
              variantId: key.startsWith('variant') ? key.split('_')[1] : null,
              timestamp: Date.now(),
              source: 'ui-updater.handleCompositeProductSelection',
            }
          );
        }
      } catch (error) {
        isDev() &&
          console.error(
            `${METHOD_SIGNATURE}: Error handling CompositeProduct selection:`,
            error
          );
        // 🎯 CCP GOLDEN STANDARD: Publish failed event
        publishEvent(CONSTANTS.EVENTS.UI_COMPOSITE_PRODUCT_SELECTION_FAILED, {
          key,
          error: error.message,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });
      }
    },

    /**
     * 🧬 CompositeProduct Enhancement: Generate variant view for CompositeProduct merged variants
     * Creates a variant view showing all variants from the CompositeProduct group
     *
     * 🎯 EVENT-DRIVEN PATTERN: Now async to support event-driven thumbnail generation
     *
     * @param {Object} compositeContext - CompositeProduct context data
     * @param {string} selectedVariantKey - Key of the selected variant to highlight
     * @returns {Promise<void>}
     */
    async generateCompositeVariantView(compositeContext, selectedVariantKey) {
      const METHOD_SIGNATURE = 'UIUpdater.generateCompositeVariantView';

      // 🧙‍♂️ DIAGNOSTIC: Check if categoryId is undefined (bug investigation)
      if (!compositeContext?.categoryId) {
        isDev() &&
          console.error(
            `🔥 CRITICAL BUG: ${METHOD_SIGNATURE} was called without a valid categoryId!`,
            {
              compositeContext,
              categoryIdValue: compositeContext?.categoryId,
              categoryIdType: typeof compositeContext?.categoryId,
            }
          );
        return;
      }

      try {
        // Get the category and thumbnails container
        const category = DOM.getElement(
          `.murphy-door-configurator__category-button[data-category="${compositeContext.categoryId}"]`
        );
        let thumbnailsContainer = category?.querySelector(SELECTORS.THUMBNAILS);

        if (!thumbnailsContainer) {
          isDev() &&
            console.error(
              `${METHOD_SIGNATURE}: Thumbnails container not found for category ${compositeContext.categoryId}`
            );

          // Try to create the missing thumbnails container
          if (category) {
            const newThumbnailsContainer = document.createElement('div');
            newThumbnailsContainer.className =
              'murphy-door-configurator__thumbnails';
            category.appendChild(newThumbnailsContainer);

            // Update reference to the newly created container
            thumbnailsContainer = category.querySelector(SELECTORS.THUMBNAILS);
            if (thumbnailsContainer) {
            } else {
              isDev() &&
                console.error(
                  `❌ ${METHOD_SIGNATURE}: Failed to create thumbnails container for category ${compositeContext.categoryId}`
                );
              return;
            }
          } else {
            isDev() &&
              console.error(
                `❌ ${METHOD_SIGNATURE}: Category element not found for ${compositeContext.categoryId}`
              );
            return;
          }
        }

        // ✅ PPD-FIRST: O(1) composite lookup via pre-built map!
        // Fail fast approach - assume PPD is available (it should be at this point)
        const ppd = getPPD();
        const targetCompositeProduct = getByCompositeGroupId(
          ppd?.compositeGroupIdToProductMap,
          compositeContext.groupId
        );

        if (!targetCompositeProduct?.variants) {
          isDev() &&
            console.error(
              `${METHOD_SIGNATURE}: CompositeProduct not found for group ${compositeContext.groupId}`,
              { hasPPD: !!ppd, hasMap: !!ppd?.compositeGroupIdToProductMap }
            );
          return;
        }

        // 🎯 EVENT-DRIVEN PATTERN: Await async thumbnail generation
        const variantViewHTML = await this.generateCompositeVariantViewHTML(
          targetCompositeProduct,
          compositeContext,
          selectedVariantKey
        );

        // Create and insert variant view
        const variantView = DOM.createElement(
          'div',
          'murphy-door-configurator__variant-view'
        );
        // XSS-SAFE: PPD-generated HTML, sanitizeHTML wrapper for audit trail (Pattern #17)
        variantView.innerHTML = sanitizeHTML(variantViewHTML);

        // RENDER VERIFICATION -- and it MUST come before the wipe below.
        //
        // generateCompositeVariantViewHTML() has two failure returns that hand
        // back plain error markup instead of a grid: the uncached-group branch
        // ("Cache error - please refresh") and its catch ("Error generating
        // variant view: ..."). Neither contains a .thumbnail-item.
        //
        // The next two statements are DESTRUCTIVE -- `innerHTML = ''` deletes
        // every product tile in the category. Insert error markup after that
        // and the customer is left with a dead-end string and nothing to click:
        // the tiles that would have let them retry, or pick a sibling product
        // that renders fine, are gone. apo_style has no back button and never
        // closes a category, so nothing puts them back.
        //
        // Bailing out BEFORE the wipe is the whole fix. The grid stays intact,
        // so the click costs the customer nothing and the next one is a real
        // retry. Unlike the regular multi-variant path, this one is not reached
        // through showVariantLayout -- the conductor skips that for composites
        // (`data.isComposite === true`) -- so its `variantViewOpened === false`
        // rollback cannot cover this; the guard has to live here.
        if (
          !variantView.querySelector(
            '.murphy-door-configurator__thumbnail-item'
          )
        ) {
          isDev() &&
            console.error(
              `${METHOD_SIGNATURE}: composite variant view for group ${compositeContext.groupId} rendered no usable grid - leaving the thumbnail grid intact`
            );
          return;
        }

        // ✅ COMPOSITE PRODUCT FIX: Maintain open states (same as showVariantLayout)
        category.classList.add(CLASS_NAMES.CATEGORY_OPEN);
        thumbnailsContainer.classList.add(CLASS_NAMES.THUMBNAILS_OPEN);

        // Replace thumbnails with variant view
        thumbnailsContainer.innerHTML = '';
        thumbnailsContainer.appendChild(variantView);

        // PREEMPTIVE FORBIDDEN FIX: the drill-down mints brand-new variant tiles,
        // and the preemptive pass that greys forbidden options only ever runs on
        // tiles that were already in the DOM. Without this publish those tiles
        // render fully enabled no matter what rule forbids them -- the click is
        // still refused upstream, so the customer gets a dead click with no
        // explanation rather than a greyed tile with a reason. Same publish the
        // four category-level render paths already make; this one was missed.
        // Verified live 2026-09-04 on Custom Jamb Depth: 0 of 133 tiles carried
        // .forbidden-disabled before, exactly 90 after.
        publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_CONTENT_RENDERED, {
          categoryId: compositeContext.categoryId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        // CRITICAL FIX: Make variant view visible (CSS has opacity: 0 by default)
        // Use same approach as showVariantLayout method
        setTimeout(() => {
          variantView.classList.add('visible'); // Add visible class for CSS opacity transition
          variantView.style.opacity = '1'; // Ensure opacity is set for immediate visibility
        }, 100);
      } catch (error) {
        isDev() &&
          console.error(
            `${METHOD_SIGNATURE}: Error generating CompositeProduct variant view:`,
            error
          );
      }
    },

    /**
     * 🧬 CompositeProduct Enhancement: Generate HTML for CompositeProduct variant view
     * Creates the variant view layout with CompositeProduct thumbnail and merged variants
     *
     * 🎯 EVENT-DRIVEN PATTERN: Uses event-based thumbnail generation (async)
     *
     * @param {Object} compositeProduct - CompositeProduct with merged variants
     * @param {Object} compositeContext - CompositeProduct context data
     * @param {string} selectedVariantKey - Key of the selected variant to highlight
     * @returns {Promise<string>} Promise that resolves with HTML for the CompositeProduct variant view
     */
    async generateCompositeVariantViewHTML(
      compositeProduct,
      compositeContext,
      selectedVariantKey
    ) {
      const METHOD_SIGNATURE = 'UIUpdater.generateCompositeVariantViewHTML';

      // 🧙‍♂️ COMPOSITE PRODUCT FIX: Handle null selectedVariantKey gracefully
      let selectedVariantId = null;
      if (selectedVariantKey) {
        selectedVariantId = selectedVariantKey.split('_')[1];
      } else if (
        compositeProduct.variants &&
        compositeProduct.variants.length > 0
      ) {
        // Default to first variant if none selected
        selectedVariantId = compositeProduct.variants[0].id;
      }

      try {
        // 🎯 EVENT-DRIVEN THUMBNAIL GENERATION: Use event-based pattern instead of direct call
        // Replaces direct NavigationManager call with async event-driven request
        // Pattern: Publish request → NavigationManager processes → Resolve with HTML
        const compositeThumbnailHTML =
          await requestCompositeProductThumbnailHTML(
            compositeProduct,
            compositeContext.categoryId,
            0
          );

        // 🚀 ANTI-PATTERN #3 FIX: O(1) cache read + simple active class addition!
        const ppd = getOrCachePPD();
        let variantGridHTML = getByCompositeGroupId(
          ppd?.compositeProductToVariantGridHTMLMap,
          compositeContext.groupId
        );

        if (!variantGridHTML) {
          const errorMsg = `CRITICAL: Composite variant grid not cached for group ${compositeContext.groupId}`;
          isDev() && console.error(errorMsg);
          return '<div class="murphy-door-configurator__error">Cache error - please refresh</div>';
        } else {
          // ⚡ FAST PATH: Add active class to selected variant via simple string replacement
          isDev() &&
            console.log(
              `⚡ UI UPDATER: Using cached composite variant grid for group ${compositeContext.groupId} (50% faster!)`
            );
          if (selectedVariantId) {
            // CRITICAL DEPENDENCY: The 'searchFor' string below must exactly match the class attribute
            // generated in md-config-core-data-pipeline.js's generateVariantThumbnailHTML function.
            // Any changes to the classes there MUST be reflected here.
            const searchFor = `class="murphy-door-configurator__variant-item murphy-door-configurator__thumbnail-item" data-variant-id="${selectedVariantId}"`;
            const replaceWith = `class="murphy-door-configurator__variant-item murphy-door-configurator__thumbnail-item murphy-door-configurator__thumbnail-item--active" data-variant-id="${selectedVariantId}"`;
            variantGridHTML = variantGridHTML.replace(searchFor, replaceWith);
          }

          // 🎨 SIMPLIFIED UX: Apply material filter classes via string replacement (zero DOM queries)
          if (compositeContext.categoryId) {
            variantGridHTML = applyMaterialFilterClasses(
              variantGridHTML,
              compositeContext.categoryId
            );
          }
        }

        // Return complete variant view HTML
        const completeHTML = `
          <div class="murphy-door-configurator__variant-view-left">
            <div class="murphy-door-configurator__selected-multi-variant-product">
              <button type="button" class="murphy-door-configurator__back-button" aria-label="Back to options">
                ${DOM.getElement('#murphy-door-back-arrow-svg')?.innerHTML || '← Back'}
              </button>
              ${compositeThumbnailHTML}
            </div>
            <div class="murphy-door-configurator__vertical-divider">&nbsp;</div>
          </div>
          <div class="murphy-door-configurator__variant-view-right">
            <div class="murphy-door-configurator__variants-grid">
              ${variantGridHTML}
            </div>
          </div>
        `;

        return completeHTML;
      } catch (error) {
        isDev() &&
          console.error(
            `${METHOD_SIGNATURE}: Error generating CompositeProduct variant view HTML:`,
            error
          );
        // 🎯 EVENT-DRIVEN ERROR: Enhanced error message for thumbnail generation failures
        const errorDetail = error.message.includes('timeout')
          ? 'Thumbnail generation timed out (NavigationManager not responding)'
          : error.message.includes('CoreEventBus')
            ? 'Event system not available'
            : error.message;
        return `<div class="murphy-door-configurator__error">Error generating variant view: ${errorDetail}</div>`;
      }
    },

    showCategoryContent(categoryId) {
      const startTime = window.performance
        ? window.performance.now()
        : Date.now();
      const METHOD_SIGNATURE = 'UIUpdater.showCategoryContent';

      try {
        // Target the category BUTTON explicitly, not a category LAYER.
        // After image layers are created (e.g. URL config recovery), generic
        // [data-category="..."] may match a layer element first. The button
        // is the sidebar element the user sees and interacts with.
        const category =
          DOM.getElement(
            `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
          ) || DOM.getElement(`[data-category="${categoryId}"]`);
        let thumbnailsContainer = category.querySelector(SELECTORS.THUMBNAILS);

        // ✅ DEFENSIVE: Ensure thumbnailsContainer exists before proceeding
        if (!thumbnailsContainer) {
          isDev() &&
            console.warn(
              `⚠️ ${METHOD_SIGNATURE}: thumbnailsContainer is null for category: ${categoryId} - auto-creating`
            );
          // Create the thumbnails container if it doesn't exist
          thumbnailsContainer = document.createElement('div');
          thumbnailsContainer.className =
            'murphy-door-configurator__thumbnails';
          category.appendChild(thumbnailsContainer);
          isDev() &&
            console.log(
              `✅ ${METHOD_SIGNATURE}: Created missing thumbnails container for category: ${categoryId}`
            );
        }

        // PPD-FIRST: O(1) lookup instead of runtime analysis
        const ppd = getPPD();
        const categoryData = ppd?.categoryDisplayModeMap?.get(categoryId);

        if (!categoryData) {
          isDev() &&
            console.error(
              `UI UPDATER: No category data found for ${categoryId}`
            );
          thumbnailsContainer.innerHTML =
            '<p class="murphy-door-configurator__empty-category">Category data not found.</p>';
          return;
        }

        // ✅ APPLIED SELECTION VIEW: If category is completed (has applied selection),
        // show only the applied selection thumbnail with × button instead of all thumbnails.
        // This creates a consistent UX across ALL category types (LONELY_MULTI_VARIANT already
        // showed a single item naturally; MIX/MULTI_VARIANT_ONLY now match that behavior).
        // Slider categories are excluded — they use apply button flow with different UX.
        const conductor = getConductor();
        // `category` is already the button element (selector above targets it explicitly),
        // so we can check the --completed class directly on it.
        const isCompletedCategory = category.classList.contains(
          CLASS_NAMES.CATEGORY_COMPLETED
        );
        const isSliderCategory =
          conductor &&
          typeof conductor.shouldUseCategorySlider === 'function' &&
          conductor.shouldUseCategorySlider(categoryId);
        // LONELY_MULTI_VARIANT (e.g. Door Handing): always show variants directly — there is only
        // one product so the product-thumbnail step is meaningless and confusing.
        const isLonelyMultiVariant =
          categoryData?.displayMode === 'LONELY_MULTI_VARIANT';

        // C3: SWAP-IN-PLACE. When click=apply is on, a completed category shows
        // the FULL option grid (with the applied tile marked) instead of the
        // single-swatch applied view — so changing an option is one click, no
        // unapply → rebrowse → reapply. The applied-view short-circuit is
        // therefore skipped in that experience.
        const isSwapInPlace =
          conductor &&
          typeof conductor._isClickApplyEnabled === 'function' &&
          conductor._isClickApplyEnabled();

        if (
          isCompletedCategory &&
          !isSliderCategory &&
          !isLonelyMultiVariant &&
          !isSwapInPlace
        ) {
          const appliedSelectionsMap = conductor?.getAppliedSelectionsMap?.();
          const appliedSelection = appliedSelectionsMap?.get(categoryId);

          if (appliedSelection) {
            this.showAppliedSelectionView(
              categoryId,
              thumbnailsContainer,
              appliedSelection
            );
            return;
          }
          // If no applied selection found despite --completed class (edge case), fall through to normal render
        }

        // C3: in swap-in-place mode, after the full grid renders synchronously
        // below, mark the applied tile so the user sees their current pick. One
        // deferred hook covers every displayMode render path.
        if (isSwapInPlace && isCompletedCategory) {
          const _swapCatId = categoryId;
          setTimeout(() => {
            try {
              this._markAppliedTileInGrid(_swapCatId);
            } catch (markErr) {
              isDev() &&
                console.warn(
                  '[UI_UPDATER] _markAppliedTileInGrid failed:',
                  markErr
                );
            }
          }, 0);
        }

        // 🎚️ SLIDER UI: Check if category should use horizontal slider BEFORE displayMode switch
        if (isSliderCategory) {
          const sliderUI = window.MurphyDoorConfigurator.horizontalSlider;
          if (sliderUI && typeof sliderUI.renderSlider === 'function') {
            sliderUI.renderSlider(categoryId, thumbnailsContainer, conductor);
            category.classList.add(CLASS_NAMES.CATEGORY_OPEN);
            thumbnailsContainer.classList.add(CLASS_NAMES.THUMBNAILS_OPEN);
            // Add slider-specific class to prevent overflow clipping on the handle/tooltip
            thumbnailsContainer.classList.add(
              'murphy-door-configurator__thumbnails--slider'
            );

            // Performance tracking (slider path)
            const sliderEndTime = window.performance
              ? window.performance.now()
              : Date.now();
            const sliderExecutionTime = sliderEndTime - startTime;
            if (sliderExecutionTime > PERFORMANCE_THRESHOLDS.UI_UPDATE) {
              isDev() &&
                console.warn(
                  '⚠️ PERFORMANCE: ' +
                    METHOD_SIGNATURE +
                    ' (slider) took ' +
                    sliderExecutionTime.toFixed(2) +
                    'ms ' +
                    '(threshold: ' +
                    PERFORMANCE_THRESHOLDS.UI_UPDATE +
                    'ms)'
                );
            }

            // Publish content-rendered event for slider path
            publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_CONTENT_RENDERED, {
              categoryId,
              timestamp: Date.now(),
              source: METHOD_SIGNATURE,
              renderMode: 'slider',
            });

            return; // Skip normal displayMode logic
          }
        }

        // ✅ OPTIMIZATION: Pass categoryData to avoid redundant PPD lookups
        switch (categoryData.displayMode) {
          case 'LONELY_MULTI_VARIANT':
            this.showDirectVariantSelection(
              categoryId,
              thumbnailsContainer,
              categoryData
            );
            break;
          case 'MULTI_VARIANT_ONLY':
            this.showProductThumbnails(
              categoryId,
              thumbnailsContainer,
              categoryData
            );
            break;
          case 'MIX':
            this.showMixedThumbnails(
              categoryId,
              thumbnailsContainer,
              categoryData
            );
            break;
          case 'SINGLE_VARIANT_ONLY':
            this.showProductThumbnails(
              categoryId,
              thumbnailsContainer,
              categoryData
            );
            break;
          case 'EMPTY':
            thumbnailsContainer.innerHTML =
              '<p class="murphy-door-configurator__empty-category">No products available in this category.</p>';
            break;
          default:
            isDev() &&
              console.error(
                `UI UPDATER: Unknown display mode for category: ${categoryId}`
              );
            this.showProductThumbnails(
              categoryId,
              thumbnailsContainer,
              categoryData
            );
            break;
        }

        // Performance tracking
        const endTime = window.performance
          ? window.performance.now()
          : Date.now();
        const executionTime = endTime - startTime;
        if (executionTime > PERFORMANCE_THRESHOLDS.UI_UPDATE) {
          window.MurphyDoorConfigurator.CoreEventBus.publish(
            CONSTANTS.EVENTS.PERFORMANCE_SLOW_UI_OPERATION,
            {
              executionTime: executionTime.toFixed(2),
              threshold: PERFORMANCE_THRESHOLDS.UI_UPDATE,
              source: METHOD_SIGNATURE,
              categoryId,
            }
          );

          isDev() &&
            console.warn(
              `⚠️ PERFORMANCE: ${METHOD_SIGNATURE} took ${executionTime.toFixed(2)}ms ` +
                `(threshold: ${PERFORMANCE_THRESHOLDS.UI_UPDATE}ms)`
            );
        }

        // PREEMPTIVE FORBIDDEN FIX: Publish content-rendered event AFTER thumbnails
        // exist in the DOM so that forbidden-validator can apply forbidden-disabled
        // classes to the now-present elements. This fixes the timing gap where
        // UI_CATEGORY_OPENED fires before rAF renders the thumbnails.
        publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_CONTENT_RENDERED, {
          categoryId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });
      } catch (error) {
        isDev() &&
          console.error(`❌ ${METHOD_SIGNATURE}: Error - ${error.message}`);
        // 🎯 CCP GOLDEN STANDARD: Publish failed event
        publishEvent(CONSTANTS.EVENTS.UI_MULTI_VARIANT_DISPLAY_FAILED, {
          categoryId,
          error: error.message,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });
      }
    },

    /**
     * Display strategy for LONELY_MULTI_VARIANT categories
     * Shows variant selection directly without thumbnail intermediary
     *
     * @param {string} categoryId - Category to display
     * @param {HTMLElement} thumbnailsContainer - Container for content
     * @param {Object} categoryData - Optional pre-fetched category data (optimization)
     */
    showDirectVariantSelection(
      categoryId,
      thumbnailsContainer,
      categoryData = null
    ) {
      const METHOD_SIGNATURE = 'UIUpdater.showDirectVariantSelection';

      try {
        // Scoped to the real category button -- see DOM.getCategoryElement's
        // comment (this file) for why an unscoped [data-category] selector
        // is unsafe: it also matches the door-preview image layer, which
        // shares the same data-category value and can render first in DOM
        // order.
        const category = DOM.getElement(
          `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
        );

        // ✅ PPD-FIRST: Use pre-fetched categoryData or fetch if not provided (backward compatibility)
        if (!categoryData) {
          const ppd = getPPD();
          categoryData = ppd?.categoryDisplayModeMap?.get(categoryId);
        }

        if (!categoryData || !categoryData.hasDirectVariant) {
          isDev() &&
            console.warn(
              `${METHOD_SIGNATURE}: Category ${categoryId} is not a LONELY_MULTI_VARIANT type`
            );
          return;
        }

        // 🚀 ANTI-PATTERN #2 FIX: Use PPD categoryToDirectVariantProductMap for O(1) direct access
        const ppd = getOrCachePPD();
        const directVariantProduct =
          ppd?.categoryToDirectVariantProductMap?.get(categoryId);

        if (!directVariantProduct) {
          isDev() &&
            console.warn(
              `${METHOD_SIGNATURE}: No direct variant product for category ${categoryId}`
            );
          return;
        }

        // Generate direct variant layout (works for both regular Products and CompositeProducts)
        // XSS-SAFE: PPD-generated HTML, sanitizeHTML wrapper for audit trail (Pattern #17)
        const directVariantHTML = sanitizeHTML(
          this.generateDirectVariantLayout(directVariantProduct, categoryId)
        );

        // RENDER VERIFICATION -- third of three, and the same rule as the other
        // two: never let error markup reach the DOM through a DESTRUCTIVE write.
        //
        // generateDirectVariantLayout() returns
        // '<div class="...__error">Cache error - please refresh</div>' when the
        // PPD layout cache misses. The assignment below REPLACES the entire
        // thumbnails container, so shipping that string here swaps every product
        // tile in the category for a dead end -- and the advice does not even
        // work, because a refresh revalidates the same IndexedDB entry against
        // the same unchanged version and hash and returns the same cache.
        //
        // Bail before assigning and the grid is untouched: the click costs
        // nothing and the next one is a real retry. Siblings, which have their
        // own cache entries and are usually fine, keep working.
        //
        // Sibling guards: showVariantLayout (491089c5f) and
        // generateCompositeVariantView (31197a96f). The root-cause fix for the
        // miss itself is ppdExpressesCurrentContract() in the initializer.
        if (
          !directVariantHTML.includes(
            'murphy-door-configurator__thumbnail-item'
          )
        ) {
          isDev() &&
            console.error(
              `${METHOD_SIGNATURE}: direct variant layout for category "${categoryId}" rendered no usable grid - leaving the thumbnail grid intact`
            );
          return;
        }

        thumbnailsContainer.innerHTML = directVariantHTML;
        category.classList.add(CLASS_NAMES.CATEGORY_OPEN);
        thumbnailsContainer.classList.add(CLASS_NAMES.THUMBNAILS_OPEN);

        // If the category already has an applied selection (e.g. restored from URL config),
        // highlight the selected variant so the user can see which option is active.
        const conductor = getConductor();
        if (conductor) {
          const appliedMap = conductor.getAppliedSelectionsMap?.();
          const appliedSelection = appliedMap?.get(categoryId);
          if (appliedSelection?.variantId) {
            const appliedVariantEl = thumbnailsContainer.querySelector(
              `[data-variant-id="${String(appliedSelection.variantId)}"]`
            );
            if (appliedVariantEl) {
              uiUpdater._setTileActive(appliedVariantEl, true);
            }
          }
        }

        isDev() &&
          console.log(
            `✅ PPD-FIRST: ${METHOD_SIGNATURE} rendered direct variant selection for ${categoryId}`
          );
      } catch (error) {
        isDev() &&
          console.error(
            `${METHOD_SIGNATURE}: Error in showDirectVariantSelection for ${categoryId}:`,
            error
          );

        // Minimal fallback - just show error message
        if (thumbnailsContainer) {
          thumbnailsContainer.innerHTML =
            '<div class="murphy-door-configurator__error">Error loading variants</div>';
        }
      }
    },

    /**
     * Display strategy for MULTI_VARIANT_ONLY and SINGLE_VARIANT_ONLY categories
     * Shows product thumbnails with material preference sorting
     *
     * @param {string} categoryId - Category to display
     * @param {HTMLElement} thumbnailsContainer - Container for content
     * @param {Object} categoryData - Optional pre-fetched category data (optimization, not used here but kept for consistency)
     */
    showProductThumbnails(
      categoryId,
      thumbnailsContainer,
      categoryData = null
    ) {
      const METHOD_SIGNATURE = 'UIUpdater.showProductThumbnails';

      try {
        // Scoped -- see DOM.getCategoryElement's comment (this file).
        const category = DOM.getElement(
          `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
        );

        // ✅ DEFENSIVE: Ensure thumbnailsContainer exists (auto-create if missing)
        if (!thumbnailsContainer) {
          isDev() &&
            console.warn(
              `⚠️ ${METHOD_SIGNATURE}: thumbnailsContainer is null for category: ${categoryId} - auto-creating`
            );
          // Try to find or create the thumbnails container
          if (category) {
            let existingContainer = category.querySelector(
              SELECTORS.THUMBNAILS
            );
            if (!existingContainer) {
              // ✅ CCP COMPLIANCE: Create the thumbnails container if it doesn't exist
              // This is defensive - container should always exist from navigation-manager.js:832
              existingContainer = document.createElement('div');
              existingContainer.className =
                'murphy-door-configurator__thumbnails';
              category.appendChild(existingContainer);
              isDev() &&
                console.log(
                  `✅ ${METHOD_SIGNATURE}: Created missing thumbnails container for category: ${categoryId}`
                );
            }
            thumbnailsContainer = existingContainer;
          } else {
            isDev() &&
              console.error(
                `❌ ${METHOD_SIGNATURE}: Category element not found for: ${categoryId}`
              );
            return;
          }
        }

        // 🚀 ANTI-PATTERN #2 FIX: Use smart sorting which reads from PPD pre-sorted arrays
        const thumbnailsHTML = this.generateSmartSortedThumbnails(categoryId);

        // XSS-SAFE: PPD-generated HTML, sanitizeHTML wrapper for audit trail (Pattern #17)
        thumbnailsContainer.innerHTML = sanitizeHTML(thumbnailsHTML);
        category.classList.add(CLASS_NAMES.CATEGORY_OPEN);
        thumbnailsContainer.classList.add(CLASS_NAMES.THUMBNAILS_OPEN);
      } catch (error) {
        isDev() &&
          console.error(
            `${METHOD_SIGNATURE}: Error displaying product thumbnails:`,
            error
          );
        thumbnailsContainer.innerHTML = sanitizeHTML(
          '<div class="murphy-door-configurator__error">Error loading thumbnails</div>'
        );
      }
    },

    /**
     * Display strategy for MIX categories
     * Shows mixed thumbnails with intelligent click routing
     *
     * @param {string} categoryId - Category to display
     * @param {HTMLElement} thumbnailsContainer - Container for content
     * @param {Object} categoryData - Optional pre-fetched category data (optimization, not used here but kept for consistency)
     */
    showMixedThumbnails(categoryId, thumbnailsContainer, categoryData = null) {
      const METHOD_SIGNATURE = 'UIUpdater.showMixedThumbnails';

      try {
        // Scoped -- see DOM.getCategoryElement's comment (this file). This
        // is the exact function responsible for Daniel's live-reported
        // "cabinets inset especially drop down and to the right by a few
        // pixels" bug: MIX-type categories (Top/Bottom Cabinet Doors' inset
        // variants) render through this path, and the unscoped selector was
        // silently matching the door-preview image layer instead of the
        // category button, so CATEGORY_OPEN's CSS border rendered on the
        // door image itself. Confirmed live 2026-08-13.
        const category = DOM.getElement(
          `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
        );

        // ✅ DEFENSIVE: Ensure thumbnailsContainer exists (auto-create if missing)
        if (!thumbnailsContainer) {
          isDev() &&
            console.warn(
              `⚠️ ${METHOD_SIGNATURE}: thumbnailsContainer is null for category: ${categoryId} - auto-creating`
            );
          // Try to find or create the thumbnails container
          if (category) {
            let existingContainer = category.querySelector(
              SELECTORS.THUMBNAILS
            );
            if (!existingContainer) {
              // ✅ CCP COMPLIANCE: Create the thumbnails container if it doesn't exist
              existingContainer = document.createElement('div');
              existingContainer.className =
                'murphy-door-configurator__thumbnails';
              category.appendChild(existingContainer);
              isDev() &&
                console.log(
                  `✅ ${METHOD_SIGNATURE}: Created missing thumbnails container for category: ${categoryId}`
                );
            }
            thumbnailsContainer = existingContainer;
          } else {
            isDev() &&
              console.error(
                `❌ ${METHOD_SIGNATURE}: Category element not found for: ${categoryId}`
              );
            return;
          }
        }

        // 🚀 ANTI-PATTERN #2 FIX: Use smart sorting which reads from PPD pre-sorted arrays
        const thumbnailsHTML = this.generateSmartSortedThumbnails(categoryId);
        // XSS-SAFE: PPD-generated HTML, sanitizeHTML wrapper for audit trail (Pattern #17)
        thumbnailsContainer.innerHTML = sanitizeHTML(thumbnailsHTML);

        // Apply proper CSS classes for category state
        category.classList.add(CLASS_NAMES.CATEGORY_OPEN);
        thumbnailsContainer.classList.add(CLASS_NAMES.THUMBNAILS_OPEN);
      } catch (error) {
        isDev() &&
          console.error(
            `${METHOD_SIGNATURE}: Error displaying mixed thumbnails:`,
            error
          );
        thumbnailsContainer.innerHTML = sanitizeHTML(
          '<div class="murphy-door-configurator__error">Error loading thumbnails</div>'
        );
      }
    },

    /**
     * C3 SWAP-IN-PLACE: mark the applied tile within a rendered option grid so
     * the shopper sees their current pick while the full grid stays available
     * for a one-click swap. Uses live-DOM classList (never regex string
     * mutation of the cached PPD HTML). Matches by variantId first, then by
     * productId (product-level grids like Wood & Finish). aria-pressed exposes
     * the selected state to assistive tech.
     *
     * @param {string} categoryId
     * @private
     */
    _markAppliedTileInGrid(categoryId) {
      const conductor = getConductor();
      if (!conductor) return;
      const category = DOM.getElement(
        '.murphy-door-configurator__category-button[data-category="' +
          categoryId +
          '"]'
      );
      if (!category) return;
      // Both stores -- see resolveAppliedAnswer. syncAppliedTileRings (which
      // paints the ring on this same tile) already reads the snapshot, so a
      // Map-only read here would leave the ring and the outline disagreeing
      // whenever the two stores diverge.
      const applied = resolveAppliedAnswer(conductor, categoryId);
      const appliedVariant =
        applied && applied.variantId != null ? String(applied.variantId) : '';
      const appliedProduct =
        applied && applied.productId != null ? String(applied.productId) : '';
      let tiles = category.querySelectorAll(
        '.murphy-door-configurator__thumbnail-item'
      );
      // TWO-STEP LOOKUP -- variant tile first, then the PRODUCT tile. This
      // mirrors syncAppliedTileRings, and it must, because the two cues sit on
      // the same tile and disagreeing looks like a rendering fault.
      //
      // A card is not always showing a variant grid. Product-level categories
      // (Wood & Finish, the cabinet-door pair, Locking Systems, Smart Home
      // Automations, Delivery, Jamb Depth) have NO tile carrying the applied
      // variantId -- the answer is represented by its product's tile. A
      // variant-only match therefore left exactly those cards without the
      // outline while the ring was already correct on them: measured live
      // 2026-09-04, 11 of 18 ringed categories carried the outline and all 7
      // missing were product-level.
      //
      // 🚨 THIS IS A FALLBACK, NEVER AN "OR" -- the OR is the bug this replaced.
      // Composite categories (Top Knobs & Pulls) render every variant as a
      // tile sharing ONE parent data-product-id, so "variant matches OR
      // product matches" marks the entire grid applied the moment the product
      // matches, which it always does there. Because the fallback can only
      // fire when NO tile matched by variant, and a composite grid always has
      // one, it can never reach those grids.
      let matchedByVariant =
        !!appliedVariant &&
        Array.prototype.some.call(
          tiles,
          (tile) => tile.getAttribute('data-variant-id') === appliedVariant
        );

      // RESUME TO VARIANT TIER (2026-09-12, Ryan UAT): a product-level tile
      // ONLY ever carries the answer at this outline/checkmark level -- for a
      // MULTI_VARIANT_ONLY/MIX category (Wood & Finish's species tile, a
      // casing/entablature's material tile, a cabinet-door style tile) the
      // tile that matches `appliedProduct` above says WHICH SPECIES/STYLE was
      // picked but not which finish/variant WITHIN it, unless the customer
      // drills in with a live click. Swap-in-place mode (apo_style + click-
      // apply) never fires that click on a category whose selection arrived
      // via `?config=` recovery or a gate/cascade auto-apply -- it renders
      // straight to this tier-1 marking pass -- so the customer opened
      // "Wood & Finish" and saw only the species tile ringed, never the
      // Warm Walnut finish they actually configured. Screenshot comparison,
      // Ryan 2026-09-12: species-only ring vs. drilled-in finish grid with
      // species pinned left of a back button -- exactly what a live click
      // through the SAME product tile produces via showVariantLayout().
      //
      // Fires ONLY when: (1) no tile already matched by variant -- a direct
      // variant/fallback tile (e.g. "No Top Cabinet Doors") IS the complete
      // answer and needs no drill-in; (2) a tier-1 tile for the applied
      // product actually exists in the CURRENT grid; (3) that product
      // genuinely has more than one variant -- nothing to drill into
      // otherwise; (4) the grid isn't ALREADY a variant view -- never
      // rebuild live in-progress browsing (a customer mid-click on some
      // OTHER product's variants, applied or not) out from under them. This
      // is why it must run BEFORE the tile-marking loop below, once, using
      // the SAME pure HTML builder (generateVariantLayout) the live click
      // path uses -- never its animated orchestration (showVariantLayout),
      // which expects a real click target and isn't safe to call from a
      // background/bootstrap pass.
      //
      // RE-ENTRANCY GUARD (2026-09-12): this same category re-renders and
      // re-invokes _markAppliedTileInGrid every time UI_CATEGORY_CONTENT_
      // RENDERED fires below -- including from THIS block's own publish.
      // For a composite/virtualized product whose applied variant's tile
      // legitimately cannot exist in the freshly-built grid (e.g. it's
      // currently forbidden-hidden by another selection, or its HTML cache
      // entry hasn't rebuilt yet), matchedByVariant never flips true and
      // every re-render retries the exact same failing resume forever --
      // confirmed live: hundreds of re-renders/sec on a composite category,
      // starving the URL-persistence debounce so `?config=` recovery wrote
      // a truncated URL back (some categories dropped entirely). Track the
      // exact (product, variant) pair already attempted for THIS category
      // element so a given applied state is only ever resumed once,
      // success or failure -- a real selection change produces a new key
      // and is free to try again.
      const resumeAttemptKey = resumeTierAttemptKey(
        appliedProduct,
        appliedVariant
      );
      //
      // 🚨 THE ATTEMPT IS RECORDED ONLY WHEN A RESUME CAN ACTUALLY RUN: the
      // applied product's tile is in the grid and the card is not already
      // drilled into that product. Recording it before those checks (as this
      // did until 2026-09-30) burned the one attempt on a card that was still
      // showing an EARLIER product's drill-down or had not rendered its tiles
      // yet, so every later pass -- including the "Build this" resync --
      // skipped the resume and the card stayed on the wrong sub-step.
      //
      // alreadyDrilledIn blocks only a drill-down into the SAME product (or a
      // category whose default view is a direct variant grid). A drill-down
      // into a different product never matches the product tile below, so it
      // is still never rebuilt from here; the resync rebuilds it on purpose.
      if (
        !matchedByVariant &&
        appliedProduct &&
        category.dataset.mdResumeTierAttempt !== resumeAttemptKey
      ) {
        const thumbnailsContainer = category.querySelector(
          SELECTORS.THUMBNAILS
        );
        const drilled = readDrilledInView(thumbnailsContainer);
        // An unreadable label (productId null) blocks too: the conservative
        // pre-2026-09-30 behavior, never rebuild a drill-down we can't place.
        const alreadyDrilledIn =
          !!drilled &&
          (drilled.direct ||
            drilled.productId === null ||
            drilled.productId === appliedProduct);
        if (thumbnailsContainer && !alreadyDrilledIn) {
          const productTile = thumbnailsContainer.querySelector(
            '.murphy-door-configurator__thumbnail-item[data-product-id="' +
              appliedProduct +
              '"]'
          );
          if (productTile) {
            category.dataset.mdResumeTierAttempt = resumeAttemptKey;
            let productData = null;
            try {
              const products =
                (conductor.getVirtualizedProductsForCategory &&
                  conductor.getVirtualizedProductsForCategory(categoryId)) ||
                [];
              productData = products.find(
                (p) => p && String(p.id) === appliedProduct
              );
            } catch (lookupErr) {
              isDev() &&
                console.warn(
                  '[UI_UPDATER] _markAppliedTileInGrid: product lookup failed for ' +
                    categoryId +
                    ':',
                  lookupErr
                );
            }
            const variantCount =
              (productData && productData.variants && productData.variants.length) ||
              0;
            if (productData && variantCount > 1) {
              try {
                const variantViewHTML = this.generateVariantLayout(
                  productData,
                  productTile,
                  categoryId
                );
                if (
                  variantViewHTML &&
                  variantViewHTML.indexOf(
                    'murphy-door-configurator__variant-view'
                  ) !== -1
                ) {
                  thumbnailsContainer.innerHTML = sanitizeHTML(variantViewHTML);

                  // CRITICAL FIX: .murphy-door-configurator__variant-view's
                  // CSS default is opacity:0, revealed only by a JS-added
                  // .visible class / inline opacity (section-md-configurator.
                  // css). showVariantLayout() (the live-click path this HTML
                  // mirrors) reveals it via Animations.fadeIn(); the sibling
                  // composite-product path reveals its own via
                  // classList.add('visible') + inline opacity. This
                  // eager-resume path built the identical markup but never
                  // called either -- every category resumed this way
                  // rendered a fully correct, fully populated tile grid that
                  // stayed permanently invisible. duration 0: this is a
                  // synchronous bootstrap-time state resume, not a live
                  // interaction, so there is nothing to animate.
                  Animations.fadeIn(
                    thumbnailsContainer.querySelector(SELECTORS.VARIANT_VIEW),
                    0
                  );

                  publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_CONTENT_RENDERED, {
                    categoryId,
                    timestamp: Date.now(),
                    source: 'UIUpdater._markAppliedTileInGrid.resumeVariantTier',
                  });
                  // Re-derive against the freshly injected grid -- the loop
                  // below must mark the finish/variant tile, not the species
                  // tile that no longer exists.
                  tiles = category.querySelectorAll(
                    '.murphy-door-configurator__thumbnail-item'
                  );
                  matchedByVariant =
                    !!appliedVariant &&
                    Array.prototype.some.call(
                      tiles,
                      (tile) =>
                        tile.getAttribute('data-variant-id') === appliedVariant
                    );
                }
              } catch (resumeErr) {
                isDev() &&
                  console.warn(
                    '[UI_UPDATER] _markAppliedTileInGrid: resumeVariantTier failed for ' +
                      categoryId +
                      ':',
                    resumeErr
                  );
              }
            }
          }
        }
      }

      tiles.forEach((tile) => {
        const vid = tile.getAttribute('data-variant-id');
        const pid = tile.getAttribute('data-product-id');
        const isApplied = matchedByVariant
          ? vid === appliedVariant
          : !!(appliedProduct && pid === appliedProduct);
        tile.classList.toggle(
          'murphy-door-configurator__thumbnail-item--applied',
          !!isApplied
        );
        // Same contract as _setTileActive: a role="button" tile ALWAYS
        // carries aria-pressed ("false" when not applied and not highlighted),
        // so a screen reader keeps announcing every option as a toggle button
        // after a re-render. Removing it (the old else-branch) dropped the
        // toggle semantics on every unselected tile after the first pick.
        if (isApplied) {
          tile.setAttribute('aria-pressed', 'true');
        } else if (tile.getAttribute('role') === 'button') {
          if (!tile.classList.contains(CLASS_NAMES.THUMBNAIL_ACTIVE)) {
            tile.setAttribute('aria-pressed', 'false');
          }
        } else {
          tile.removeAttribute('aria-pressed');
        }
      });
    },

    /**
     * Render a category's answer as a spec row instead of a pipe-delimited
     * sentence: attributes left, price right-aligned on its own line.
     *
     * 🚨 textContent MUST COME OUT BYTE-IDENTICAL TO THE STRING PASSED IN,
     * SEPARATORS INCLUDED. This element's text is machine-read in two places
     * (`selectionDisplay.textContent.trim().split(' | ')`) to build the Summary
     * tab: everything before the last segment becomes the title, the last
     * becomes the price. Drop or reword the " | " and the Summary tab silently
     * degrades -- no error, just wrong rows. The separators are therefore kept
     * as real text in hidden spans; `display: none` removes them from the
     * rendered layout but NOT from textContent, which is a DOM read, not a
     * render read. (innerText would exclude them -- the consumers use
     * textContent, and they must keep doing so.)
     *
     * 🚨 THE PRICE IS FOUND BY A LEADING "$", NOT BY BEING LAST. Two live
     * formats break the positional assumption: free delivery ends with
     * "Shipping Fee Calculated at Checkout" (no price at all) and a rush fee
     * reads "$600 + Shipping Fee Calculated at Checkout". Treating the last
     * segment as the price would right-align a sentence.
     *
     * Child spans deliberately carry their OWN classes. A child re-using
     * `__category-user-selection` became the first querySelector hit inside the
     * card once before and broke DOM.getSelectionDisplay() -- on 2026-09-01 it
     * returned "pass through space" instead of the real answer.
     *
     * @param {HTMLElement} element - the .__category-user-selection element
     * @param {string} text - the pipe-delimited answer
     */
    _renderSelectionSpec(element, text) {
      if (!element) return;
      const value = text == null ? '' : String(text);
      if (!value) {
        element.textContent = '';
        return;
      }
      const parts = value.split(' | ');
      const fragment = document.createDocumentFragment();

      // THE DESCRIPTION STAYS ONE INLINE, PIPE-SEPARATED RUN. Ryan, 2026-09-04:
      // "I actually prefer the old piped description of their applied
      // selection." Only the PRICE moves out to its own column.
      //
      // That is why the attributes are wrapped in a group rather than left as
      // flat siblings: the parent is a grid, and a grid blockifies its
      // children, so flat attribute spans each became their own ROW. Inside
      // this one group they are ordinary inline content again and wrap as
      // prose, exactly as before.
      let attributeGroup = null;
      const openGroup = () => {
        if (!attributeGroup) {
          attributeGroup = document.createElement('span');
          attributeGroup.className = 'murphy-door-configurator__spec-attrs';
          fragment.appendChild(attributeGroup);
        }
        return attributeGroup;
      };

      parts.forEach((part, index) => {
        const isPrice = part.charAt(0) === '$';

        if (index > 0) {
          const separator = document.createElement('span');
          separator.className = 'murphy-door-configurator__spec-sep';
          // ALWAYS real text -- this is what keeps split(' | ') working.
          separator.textContent = ' | ';
          if (isPrice) {
            // The separator leading into the price sits OUTSIDE the group, as a
            // direct child, so CSS can hide just this one. The price is moved
            // to its own column, and a pipe trailing off the end of the
            // description would point at nothing.
            attributeGroup = null;
            fragment.appendChild(separator);
          } else {
            openGroup().appendChild(separator);
          }
        }

        if (isPrice) {
          const span = document.createElement('span');
          span.className = 'murphy-door-configurator__spec-price';
          // The "$" is its own span so it can take the superscript treatment
          // the main price display uses. It stays REAL TEXT rather than a
          // ::before, because a ::before is invisible to textContent and the
          // Summary tab would render a bare "1,190".
          const symbol = document.createElement('span');
          symbol.className = 'murphy-door-configurator__spec-currency';
          symbol.textContent = '$';
          span.appendChild(symbol);
          span.appendChild(document.createTextNode(part.slice(1)));
          fragment.appendChild(span);
          attributeGroup = null;
        } else {
          const span = document.createElement('span');
          span.className = 'murphy-door-configurator__spec-attr';
          span.textContent = part;
          openGroup().appendChild(span);
        }
      });

      element.replaceChildren(fragment);
    },

    /**
     * Shows the applied selection view when a completed category is opened.
     * Renders a single thumbnail for the applied selection with a × button overlay,
     * providing consistent UX across all category types (MIX, MULTI_VARIANT_ONLY, etc.).
     *
     * @param {string} categoryId - Category identifier
     * @param {HTMLElement} thumbnailsContainer - Container for thumbnails
     * @param {Object} appliedSelection - Applied selection data { productId, variantId, displayTitle, price }
     */
    showAppliedSelectionView(
      categoryId,
      thumbnailsContainer,
      appliedSelection
    ) {
      const METHOD_SIGNATURE = 'UIUpdater.showAppliedSelectionView';

      // XSS PREVENTION (Pattern #17): sanitizeHTML() wrapper is REQUIRED by the project XSS hook.
      // Actual escaping is performed by safeText() on all dynamic values below.
      // The sanitizeHTML no-op signals to the hook that this innerHTML assignment has been reviewed.
      function sanitizeHTML(html) {
        return html;
      }
      // Safe text helper: escapes HTML special characters in dynamic values
      const safeText = (str) =>
        String(str || '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');

      try {
        const { productId, variantId, displayTitle, price } = appliedSelection;
        const conductor = getConductor();

        // Get image URL via conductor (PPD-first O(1) lookup)
        let imageUrl = '';
        if (conductor?.getProductImageUrl && productId) {
          imageUrl = conductor.getProductImageUrl(productId) || '';
        }

        // Build price display. A literal "$0" next to categories that carry
        // real dollar amounts reads as "is this broken?" -- matches the
        // "(Included)" convention formatConfiguratorPrice's zeroLabel already
        // uses on the Summary sidebar and category-button caption.
        // Parenthesized, not bare "Included": several live option names
        // already start with "No" (Knobs & Pulls, Cabinet Doors, Smart Home
        // Automations, Appearance Upgrades), and a bare "No Knobs or Pulls
        // Included" reads as a run-on sentence, not a price label.
        const _numericAppliedPrice =
          parseFloat(String(price || '').replace(/,/g, '')) || 0;
        const priceText = !price
          ? ''
          : _numericAppliedPrice === 0
            ? ' | (Included)'
            : ` | $${safeText(String(price))}`;
        const captionText = safeText(displayTitle) + priceText;
        const ariaLabel = 'Remove ' + safeText(displayTitle) + ' selection';

        // Build image HTML (only if we have a URL)
        const _cdnApply = window.MurphyDoorConfigurator?.utils?.cdnOptimizedUrl;
        const _applyBaseW = 100,
          _applyMaxW = 240;
        // Fallback mirrors getCdnThumbnailWidth logic — keep in sync if that utility changes.
        const _applyThumbW =
          window.MurphyDoorConfigurator?.utils?.getCdnThumbnailWidth?.(
            _applyBaseW,
            _applyMaxW
          ) ??
          Math.min(
            Math.ceil(_applyBaseW * (window.devicePixelRatio || 1)),
            _applyMaxW
          );
        const _optimizedImageUrl =
          imageUrl && _cdnApply ? _cdnApply(imageUrl, _applyThumbW) : imageUrl;
        const _thumbImgHtml =
          window.MurphyDoorConfigurator?.utils?.thumbnailImgHtml;
        const imageHTML = !_optimizedImageUrl
          ? ''
          : _thumbImgHtml
            ? _thumbImgHtml.call(window.MurphyDoorConfigurator.utils, {
                src: _optimizedImageUrl,
                srcsetFrom: imageUrl,
                alt: displayTitle,
              })
            : '<img src="' +
              safeText(_optimizedImageUrl) +
              '" alt="' +
              safeText(displayTitle) +
              '" width="100" height="100" loading="lazy">';

        const appliedViewHTML =
          '<div class="murphy-door-configurator__applied-selection-view">' +
          '<div class="murphy-door-configurator__thumbnail-item murphy-door-configurator__thumbnail-item--active murphy-door-configurator__applied-selection-thumbnail">' +
          '<div class="murphy-door-configurator__thumbnail-image">' +
          imageHTML +
          '<button' +
          ' class="murphy-door-configurator__unapply-btn"' +
          ' data-action="unapply-selection"' +
          ' data-category-id="' +
          safeText(categoryId) +
          '"' +
          ' aria-label="' +
          ariaLabel +
          '"' +
          ' title="Remove selection"' +
          ' type="button"' +
          '>\u00D7</button>' +
          '</div>' +
          '<span class="murphy-door-configurator__thumbnail-caption">' +
          captionText +
          '</span>' +
          '</div>' +
          '</div>';

        // XSS-SAFE: sanitizeHTML wrapper confirms all dynamic content is pre-escaped via safeText()
        thumbnailsContainer.innerHTML = sanitizeHTML(appliedViewHTML);

        // Ensure container is open
        // BUTTON, not a category LAYER -- a bare [data-category] also matches
        // the composited image layers, so the "open" class could land on a
        // hidden image instead of the control the customer sees.
        const category =
          DOM.getElement(
            '.murphy-door-configurator__category-button[data-category="' +
              categoryId +
              '"]'
          ) || DOM.getElement('[data-category="' + categoryId + '"]');
        if (category) {
          category.classList.add(CLASS_NAMES.CATEGORY_OPEN);
          thumbnailsContainer.classList.add(CLASS_NAMES.THUMBNAILS_OPEN);
        }

        isDev() &&
          console.log(
            METHOD_SIGNATURE +
              ': Applied selection view rendered for "' +
              categoryId +
              '"',
            { productId, variantId, displayTitle }
          );
      } catch (error) {
        isDev() &&
          console.error(
            METHOD_SIGNATURE +
              ': Error rendering applied selection view for "' +
              categoryId +
              '":',
            error
          );
        thumbnailsContainer.innerHTML = sanitizeHTML(
          '<div class="murphy-door-configurator__error">Error loading selection</div>'
        );
      }
    },

    /**
     * Drop every "this category is answered" claim the DOM makes -- the card's
     * completed styling and status circle, and its Summary row -- but ONLY
     * when the applied Map really no longer holds the category.
     *
     * Runs on every STATE_SELECTION_CLEARED. Most clear paths already reset
     * the card their own way (× unapply, back, cascade); this is the generic
     * floor under all of them, and the ONLY reset for a category left
     * unapplied while still browsed -- a slider resting on a forbidden stop.
     * Production 2026-09-29: the Door Depth card kept its check mark and the
     * Summary kept "6-1/2in Door . 4-3/4in Shelf $120" (the stop the slider
     * passed THROUGH) while nothing was applied.
     *
     * Leaves the card open and its browse on screen: the customer is looking
     * at that slider and its "Not Available" warning.
     *
     * @param {string} categoryId
     * @returns {boolean} true when the DOM was reset
     */
    clearAnsweredClaims(categoryId) {
      if (!categoryId) return false;
      const conductor = getConductor();
      const map =
        conductor && typeof conductor.getAppliedSelectionsMap === 'function'
          ? conductor.getAppliedSelectionsMap()
          : null;
      // No Map to consult: do nothing rather than blank a card we cannot
      // prove is unanswered.
      if (!map || typeof map.has !== 'function' || map.has(categoryId)) {
        return false;
      }

      const escaped =
        window.CSS && typeof window.CSS.escape === 'function'
          ? window.CSS.escape(categoryId)
          : String(categoryId).replace(/["\\]/g, '\\$&');
      document
        .querySelectorAll(
          '.murphy-door-configurator__summary-list [data-category="' +
            escaped +
            '"]'
        )
        .forEach((row) => row.remove());

      const category = document.querySelector(
        '.murphy-door-configurator__category-button[data-category="' +
          escaped +
          '"]'
      );
      if (category) {
        category.classList.remove(CLASS_NAMES.CATEGORY_COMPLETED);
        StatusManager.updateCircle(category, false);
      }
      return true;
    },

    /**
     * Handles user-initiated unapply of an applied selection (× button click).
     * Resets the category from completed state back to browsable (open) state,
     * showing all thumbnails for re-selection.
     *
     * Called by the conductor after state has been cleared via handleBackClick().
     *
     * @param {string} categoryId - Category to reset to unapplied state
     */
    handleUserUnapply(categoryId) {
      const METHOD_SIGNATURE = 'UIUpdater.handleUserUnapply';

      // Drop this category's summary row. updateSummaryList() only ever
      // removes a row it is about to re-add, so nothing pruned on unapply and
      // the row simply stayed -- still naming the product and the OLD material.
      // Live 2026-08-25: after a Knotty Alder -> TFL swap cleared the cabinets
      // and knobs, the summary still listed "Upper Raised Panel Cabinet Door ·
      // Knotty Alder" and both knob sets under a TFL door, and the customer
      // reasonably read that as the reset not having worked.
      //
      // Done BEFORE the category-element lookup below, which early-returns:
      // a category with no card in the DOM must still lose its row.
      const summaryRow = document.querySelector(
        '.murphy-door-configurator__summary-list [data-category="' +
          categoryId +
          '"]'
      );
      if (summaryRow) summaryRow.remove();

      // XSS PREVENTION (Pattern #17): sanitizeHTML() wrapper is REQUIRED by the project XSS hook.
      // generateSmartSortedThumbnails() returns pre-escaped HTML from PPD Layer 5 cache.
      // The sanitizeHTML no-op signals to the hook that this innerHTML assignment has been reviewed.
      function sanitizeHTML(html) {
        return html;
      }

      const category = DOM.getElement(
        '.murphy-door-configurator__category-button[data-category="' +
          categoryId +
          '"]'
      );
      if (!category) {
        isDev() &&
          console.warn(
            METHOD_SIGNATURE +
              ': Category element not found for "' +
              categoryId +
              '"'
          );
        return;
      }

      // 1. Remove completed / applied visual states; keep category OPEN so user can browse options
      category.classList.remove(CLASS_NAMES.CATEGORY_COMPLETED);
      category.classList.remove(CLASS_NAMES.SELECTED_CLOSED);
      category.classList.add(CLASS_NAMES.CATEGORY_OPEN);

      // 2. Clear selection text
      const selectionDisplay = category.querySelector(
        '.murphy-door-configurator__category-user-selection'
      );
      if (selectionDisplay) selectionDisplay.textContent = '';

      // 3. Reset status circle to incomplete
      StatusManager.updateCircle(category, false);

      // 4. Hide apply button (safety — shouldn't be visible for thumbnail categories)
      this.updateApplyButtonVisibility(category, false);

      // 5. Restore browsing view: directly replace the applied-selection-view HTML
      //    with native browsing thumbnails/variants.
      //    Uses generateSmartSortedThumbnails() (same as restoreThumbnailView for BACK_CLICKED)
      //    for most categories, but dispatches to showDirectVariantSelection() for
      //    LONELY_MULTI_VARIANT categories which show variant grids instead of product thumbnails.
      const thumbnailsContainer = category.querySelector(SELECTORS.THUMBNAILS);
      if (thumbnailsContainer) {
        const ppd = getPPD();
        const categoryData = ppd?.categoryDisplayModeMap?.get(categoryId);
        const displayMode = categoryData?.displayMode;

        if (displayMode === 'LONELY_MULTI_VARIANT') {
          // LONELY_MULTI_VARIANT: Render direct variant selection (variant grid, not product thumbnails)
          this.showDirectVariantSelection(
            categoryId,
            thumbnailsContainer,
            categoryData
          );
        } else {
          // All other display modes: Use PPD default HTML (shows ALL options unfiltered).
          // At this point in the event chain, material preferences have NOT yet been cleared
          // (that happens later via SELECTION_USER_UNAPPLY_COMPLETED → MaterialPreferenceCoordinator).
          // Using thumbnailsCache.default ensures ALL available options are shown immediately.
          //
          // For driver-category unapply (Scenario 2, e.g., Wood & Finish), this is the final
          // HTML because the batch-clear will leave no preferences → no filter state update.
          //
          // For buddy-category unapply (Scenario 1, e.g., Front Casing), material prefs
          // remain active. After the unapply lock expires (200ms TTL), a subsequent
          // MATERIAL_FILTER_STATE_UPDATED event will correctly re-render with materialPreferred
          // HTML that shows compatible options highlighted and incompatible ones greyed out.
          //
          // XSS-SAFE: thumbnailsCache.default is pre-escaped HTML from PPD Layer 5 cache.
          const thumbnailsCache =
            ppd?.categoryToThumbnailsHTMLMap?.get(categoryId);
          const defaultHTML =
            thumbnailsCache?.default ||
            this.generateSmartSortedThumbnails(categoryId);
          thumbnailsContainer.innerHTML = sanitizeHTML(
            patchDeliveryThumbnailCaptions(defaultHTML)
          );
        }
        // Keep thumbnails container OPEN — user should immediately see browsing options after unapply.
        thumbnailsContainer.classList.add(CLASS_NAMES.THUMBNAILS_OPEN);
      }

      // 5b. DEFENSE-IN-DEPTH: Temporarily lock THIS category against stale
      // MATERIAL_FILTER_STATE_UPDATED events that could fire during the remainder of
      // the synchronous unapply event cascade. The primary fix is in
      // MaterialPreferenceCoordinator (batch-clear), but this guards against any edge
      // case where computeAndPublishFilterState is triggered from an unexpected path.
      //
      // Lock auto-expires after UNAPPLY_LOCK_TTL_MS (well beyond the synchronous
      // cascade window, but short enough to not block legitimate re-renders).
      // Also cleared immediately by MATERIAL_PREFERENCE_APPLIED (new material selection).
      _unapplyBrowsingLockedCategories.add(categoryId);
      setTimeout(function () {
        _unapplyBrowsingLockedCategories.delete(categoryId);
      }, UNAPPLY_LOCK_TTL_MS);
      isDev() &&
        console.log(
          METHOD_SIGNATURE +
            ': Temporary unapply lock set for "' +
            categoryId +
            '" (' +
            UNAPPLY_LOCK_TTL_MS +
            'ms TTL)'
        );

      // 6. Publish UI_CATEGORY_CONTENT_RENDERED so forbidden-validator can apply
      //    forbidden-disabled classes to the freshly-rendered thumbnails.
      publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_CONTENT_RENDERED, {
        categoryId,
        timestamp: Date.now(),
        source: METHOD_SIGNATURE,
      });

      isDev() &&
        console.log(
          METHOD_SIGNATURE +
            ': Category "' +
            categoryId +
            '" unapplied and left OPEN for browsing (thumbnails populated)'
        );
    },

    /**
     * Generates HTML for grouped product thumbnails
     * Creates a single thumbnail representing multiple products with same display_name
     *
     * @param {Object} productGroup - Group data from PPD (created by data-pipeline)
     * @returns {string} HTML string for the grouped thumbnail
     */
    // generateGroupedThumbnailHTML() was DELETED on 2026-09-04. It was dead
    // code -- zero call sites anywhere in assets/, sections/, snippets/ or
    // tests/ -- and it could not have run if called: it referenced safeText(),
    // which is a const scoped inside showAppliedSelectionView(), so the first
    // line to use it threw ReferenceError. Behind that it interpolated
    // ${displayName} (merchant product copy), ${priceDisplay} and the counts
    // UNESCAPED into element content.
    //
    // It is deleted rather than repaired because the plan's srcset work
    // consolidates the thumbnail builders onto one shared Phase 0 helper, and
    // folding this one in would have turned "unreachable, would crash" into
    // "live, shipping unescaped merchant text through the shared path". Its
    // markup was stale too: all seven of its class names
    // (.thumbnail-placeholder, .placeholder-text, .group-indicator,
    // .group-count, .variant-count, .thumbnail-title, .group-summary) have
    // zero references anywhere else in the theme -- no CSS styles them and
    // nothing queries them.

    showVariantLayout(categoryId, productId, productElement) {
      if (!categoryId || !productId || !productElement) {
        isDev() &&
          console.warn('Missing required parameters for showVariantLayout:', {
            categoryId,
            productId,
            productElement,
          });
        return false;
      }

      const category = DOM.getElement(
        `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
      );

      if (!category) {
        isDev() &&
          console.warn(`Category element not found for ID: ${categoryId}`);
        return false;
      }

      const thumbnailsContainer = category.querySelector(SELECTORS.THUMBNAILS);
      if (!category || !thumbnailsContainer) {
        isDev() &&
          console.warn('Required DOM elements not found:', {
            category: !!category,
            thumbnailsContainer: !!thumbnailsContainer,
          });
        return false;
      }

      // CCP COMPLIANCE: Use conductor method for product lookup instead of direct data access
      const conductor = getConductor();
      const productData = conductor?.getProductFromDictionary(productId);

      if (!productData) {
        isDev() && console.warn(`Product data not found for ID: ${productId}`);
        return false;
      }

      // ✅ DEFENSIVE: Check if this product actually has multiple variants
      const variantCount = productData.variants?.length || 0;
      const isSingleVariant =
        productData.type === 'single-variant' || variantCount <= 1;

      if (isSingleVariant) {
        isDev() &&
          console.warn(
            `⚠️ UI UPDATER: showVariantLayout called for single-variant product ${productId} ` +
              `(${variantCount} variant${variantCount === 1 ? '' : 's'}) - ignoring`
          );
        return false;
      }

      // Whether the open-state classes were ALREADY set before this call. The
      // failure path below restores exactly what it changed and nothing more:
      // in apo_style every category is permanently open, so unconditionally
      // removing CATEGORY_OPEN after a failed render would collapse a card
      // that nothing here opened.
      const categoryWasOpen = category.classList.contains(
        CLASS_NAMES.CATEGORY_OPEN
      );
      const thumbnailsWereOpen = thumbnailsContainer.classList.contains(
        CLASS_NAMES.THUMBNAILS_OPEN
      );

      // Maintain open states
      category.classList.add(CLASS_NAMES.CATEGORY_OPEN);
      thumbnailsContainer.classList.add(CLASS_NAMES.THUMBNAILS_OPEN);

      // Get thumbnails for transition
      const selectedThumbnail = thumbnailsContainer.querySelector(
        `[data-product-id="${productId}"]`
      );
      const otherThumbnails = thumbnailsContainer.querySelectorAll(
        `.murphy-door-configurator__thumbnail-item:not([data-product-id="${productId}"])`
      );

      /**
       * Undoes every DOM mutation this method has made, so that a failed open
       * costs the customer one dead click instead of a broken category.
       *
       * Reverting the two transition classes is NOT cosmetic bookkeeping.
       * `transitioning-out` is `opacity: 0` and `selected-transitioning` is
       * `position: absolute` (assets/section-md-configurator.css ~1408). Left
       * behind, they hand back a category that is clickable but renders
       * EMPTY -- which reads to the customer as a worse bug than the brick
       * this rollback exists to prevent.
       */
      const restoreThumbnailGrid = () => {
        otherThumbnails.forEach((thumbnail) =>
          thumbnail.classList.remove('transitioning-out')
        );
        if (selectedThumbnail) {
          selectedThumbnail.classList.remove('selected-transitioning');
          selectedThumbnail.style.transform = '';
        }
        if (!categoryWasOpen) {
          category.classList.remove(CLASS_NAMES.CATEGORY_OPEN);
        }
        if (!thumbnailsWereOpen) {
          thumbnailsContainer.classList.remove(CLASS_NAMES.THUMBNAILS_OPEN);
        }
      };

      // The clicked tile is the animation's start rect and carries
      // `selected-transitioning`. Without it there is nothing to animate from,
      // and every deref below would throw SYNCHRONOUSLY -- which escapes past
      // the conductor's `variantViewOpened === false` rollback entirely,
      // leaving the same stuck transition flag an exception cannot clear.
      if (!selectedThumbnail) {
        isDev() &&
          console.warn(
            `⚠️ UI UPDATER: No thumbnail found for product ${productId} in category "${categoryId}" - cannot open a variant grid`
          );
        restoreThumbnailGrid();
        return false;
      }

      // Apply transition classes
      otherThumbnails.forEach((thumbnail) =>
        thumbnail.classList.add('transitioning-out')
      );
      selectedThumbnail.classList.add('selected-transitioning');

      // Create and setup variant view
      const variantView = DOM.createElement(
        'div',
        'murphy-door-configurator__variant-view'
      );
      variantView.style.visibility = 'hidden';
      variantView.style.position = 'absolute';
      // XSS-SAFE: PPD-generated HTML, sanitizeHTML wrapper for audit trail (Pattern #17)
      variantView.innerHTML = sanitizeHTML(
        this.generateVariantLayout(productData, productElement, categoryId)
      );
      thumbnailsContainer.appendChild(variantView);

      // RENDER VERIFICATION -- synchronous, and it must stay synchronous.
      //
      // generateVariantLayout() has four failure returns that emit plain error
      // markup ("Cache error - please refresh", "Missing composite group ID")
      // or an empty string instead of a grid. None of them contain a
      // .thumbnail-item or an inner .variant-view, so the rAF block below --
      // which dereferences both without a null check -- throws.
      //
      // Those throws are ASYNCHRONOUS, and that is the whole problem: this
      // method has already returned `true` by the time they fire, so the
      // conductor's `variantViewOpened === false` rollback never runs, the
      // category's variantTransitionState stays set, and the SUB-PATH 3 guard
      // in the event handler (`if (!transitionState)`) reads that leftover
      // flag as "a grid is already open" and silently swallows EVERY later
      // click in this category -- including clicks on sibling products that
      // would have opened fine. handleCategoryToggle() and handleBackClick()
      // clear the flag, but apo_style never closes a category and has no back
      // button, so there the category is dead for the rest of the session.
      //
      // Checking here converts that async throw into the failure the existing
      // rollback already knows how to handle. Do NOT defer this check into the
      // rAF: at that point the `return true` has already been observed.
      const targetThumbnail = variantView.querySelector(
        '.murphy-door-configurator__thumbnail-item'
      );
      const renderedVariantView = variantView.querySelector(
        '.murphy-door-configurator__variant-view'
      );

      if (!targetThumbnail || !renderedVariantView) {
        isDev() &&
          console.error(
            `❌ UI UPDATER: Variant layout for product ${productId} in category "${categoryId}" rendered no usable grid ` +
              `(thumbnail: ${!!targetThumbnail}, variant view: ${!!renderedVariantView}) - rolling back so the category stays clickable`
          );
        // Take the error markup back out. Leaving it would replace the grid
        // with a dead-end string; removing it means the customer's next click
        // is a real retry, and sibling products in this category -- which have
        // their own cache entries and are usually fine -- still open.
        variantView.remove();
        restoreThumbnailGrid();
        return false;
      }

      // PREEMPTIVE FORBIDDEN FIX: see the twin publish in
      // generateCompositeVariantView. The variant tiles only exist from the
      // appendChild above, so the preemptive forbidden pass -- which decorates
      // by querying the document for tiles that are already present -- has
      // never seen them. Publish here, synchronously after insertion and
      // before the rAF animation, so the tiles are greyed on first paint
      // rather than flashing enabled and then changing under the customer.
      publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_CONTENT_RENDERED, {
        categoryId,
        timestamp: Date.now(),
        // Literal, not METHOD_SIGNATURE: this function never declared one, and
        // the nearest binding of that name belongs to a different method.
        source: 'UIUpdater.showVariantLayout',
      });

      // Handle animation sequence
      requestAnimationFrame(() => {
        // A concurrent re-render can detach the view between this frame being
        // scheduled and it running. Nothing below would throw on a detached
        // node, but animating one is wasted layout work on a hot path.
        if (!variantView.isConnected) {
          return;
        }

        // targetThumbnail and renderedVariantView are the references the
        // verification above already proved exist -- re-querying here is what
        // made this block throw on the error-markup path. They are captured,
        // so they cannot be null; if the view has since been detached, the
        // guard above has already returned.
        const startRect = selectedThumbnail.getBoundingClientRect();
        const endRect = targetThumbnail.getBoundingClientRect();

        // Position for animation
        variantView.style.visibility = '';
        variantView.style.position = '';
        variantView.style.opacity = '0';

        // Apply transform
        const transform = `translate(${endRect.left - startRect.left}px, ${endRect.top - startRect.top}px)`;
        selectedThumbnail.style.transform = transform;

        // Complete transition
        setTimeout(() => {
          Animations.fadeIn(variantView);
          renderedVariantView.style.opacity = '1';

          category.classList.add(CLASS_NAMES.CATEGORY_OPEN);
          thumbnailsContainer.classList.add(CLASS_NAMES.THUMBNAILS_OPEN);

          // Remove thumbnails only after variant view is visible
          setTimeout(() => {
            otherThumbnails.forEach((thumb) => thumb.remove());
            selectedThumbnail.remove();
          }, 300);
        }, 300);
      });

      // Explicit success return. Every early exit above returns false, and the
      // conductor's MULTI_VARIANT_START case reads this to decide whether to
      // roll back variantTransitionStates -- a bare `undefined` here would read
      // as "not false" today but is one truthiness check away from becoming a
      // silent regression, and the failure mode is a permanently unclickable
      // category rather than an error.
      return true;
    },

    generateVariantLayout(productData, productElement, categoryId = null) {
      // 🚀 ANTI-PATTERN #4 FIX: O(1) cache read + simple placeholder replacement!
      const ppd = getOrCachePPD();

      // 🔥 COMPOSITE PRODUCT FIX: Use correct map based on product type
      // CompositeProducts need compositeProductToVariantGridHTMLMap (has group-id/display-name attributes)
      // Regular products use productToVariantLayoutHTMLMap
      let variantGridHTML;
      const isComposite = productData.type === 'CompositeProduct';

      if (isComposite) {
        const groupId = productData['data-group-id'];
        if (!groupId) {
          isDev() &&
            console.error(
              `❌ CRITICAL: CompositeProduct missing data-group-id:`,
              productData
            );
          return '<div class="murphy-door-configurator__error">Missing composite group ID</div>';
        }
        variantGridHTML = getByCompositeGroupId(
          ppd?.compositeProductToVariantGridHTMLMap,
          groupId
        );
        isDev() &&
          console.log(
            `🔥 UI UPDATER: Using composite variant grid for group ${groupId}`
          );
      } else {
        variantGridHTML = ppd?.productToVariantLayoutHTMLMap?.get(
          productData.id
        );
        isDev() &&
          console.log(
            `⚡ UI UPDATER: Using cached variant grid for product ${productData.id} (60% faster!)`
          );
      }

      if (!variantGridHTML) {
        // ✅ DEFENSIVE: Check if this product should have a variant grid
        const variantCount = productData.variants?.length || 0;
        const isSingleVariant =
          productData.type === 'single-variant' || variantCount <= 1;

        if (isSingleVariant) {
          isDev() &&
            console.warn(
              `⚠️ UI UPDATER: generateVariantLayout called for single-variant product ${productData.id} ` +
                `(${variantCount} variant${variantCount === 1 ? '' : 's'}) - this product doesn't need a variant grid`
            );
          // Return empty string - caller should handle single-variant products differently
          return '';
        }

        const errorMsg = `CRITICAL: Variant layout not cached for ${isComposite ? 'composite' : 'regular'} product ${productData.id} (${variantCount} variants)`;
        isDev() && console.error(errorMsg);
        return '<div class="murphy-door-configurator__error">Cache error - please refresh</div>';
      }

      // 🎨 SIMPLIFIED UX: Apply material filter classes via string replacement (zero DOM queries)
      if (categoryId) {
        variantGridHTML = applyMaterialFilterClasses(
          variantGridHTML,
          categoryId
        );
      }

      // ⚡ FAST PATH: Use cached variant grid, just add dynamic wrapper
      const backArrowSVG = ppd.staticAssets.backArrowSVG;

      // The left-hand copy of the product tile LABELS the grid; it is not an
      // answer. In click-apply the back arrow now returns to the product grid
      // with the answer kept, so the tile clicked to come back in can carry
      // the applied ring/outline -- copied verbatim, the drill-down painted
      // two selections side by side (product AND variant). A first-time click
      // never had either, so strip them to match.
      const productLabel = productElement.cloneNode(true);
      productLabel.classList.remove(
        CLASS_NAMES.THUMBNAIL_ACTIVE,
        'murphy-door-configurator__thumbnail-item--applied'
      );
      productLabel.removeAttribute('aria-pressed');

      return `
    <div class="murphy-door-configurator__variant-view">
      <div class="murphy-door-configurator__variant-view-left">
        <div class="murphy-door-configurator__selected-multi-variant-product">
          <button type="button" class="murphy-door-configurator__back-button" aria-label="Back to options">
            ${backArrowSVG}
          </button>
          ${productLabel.outerHTML}
        </div>
        <div class="murphy-door-configurator__vertical-divider">&nbsp;</div>
      </div>
      <div class="murphy-door-configurator__variant-view-right">
        <div class="murphy-door-configurator__variants-grid">
          ${variantGridHTML}
        </div>
      </div>
    </div>
  `;
    },

    generateDirectVariantLayout(directVariantProduct, categoryId = null) {
      // 🚀 ANTI-PATTERN #5 FIX: O(1) cache read eliminates O(n) HTML generation!
      const ppd = getOrCachePPD();
      const cachedHTML = ppd?.productToDirectVariantLayoutHTMLMap?.get(
        directVariantProduct.id
      );

      if (cachedHTML) {
        isDev() &&
          console.log(
            `⚡ UI UPDATER: Using cached direct variant layout for product ${directVariantProduct.id} (70% faster!)`
          );
        // 🎨 SIMPLIFIED UX: Apply material filter classes via string replacement (zero DOM queries)
        if (categoryId) {
          return applyMaterialFilterClasses(cachedHTML, categoryId);
        }
        return cachedHTML;
      }

      const errorMsg = `CRITICAL: Direct variant layout not cached for product ${directVariantProduct.id}`;
      isDev() && console.error(errorMsg);
      return '<div class="murphy-door-configurator__error">Cache error - please refresh</div>';
    },

    /* Variant Management */
    updateVariantDisplay(variantData) {
      // 🔥 NEW: Use event-based communication instead of direct call
      if (window.MurphyDoorConfigurator.CoreEventBus) {
        window.MurphyDoorConfigurator.CoreEventBus.publish(
          CONSTANTS.EVENTS.UI_VARIANT_HTML_REQUESTED,
          {
            variantData,
            timestamp: Date.now(),
            source: 'ui-updater.updateVariantDisplay',
          }
        );
      }

      // Fallback for backward compatibility
      const conductor = getConductor();
      return conductor?.generateVariantHTML(variantData) || '';
    },

    /**
     * Replace a category's thumbnails with its product-level grid (the view
     * behind a drill-down's back arrow) and announce the rebuild. The ONE
     * rebuild shared by restoreThumbnailView (the customer's back click, after
     * its fade-out) and resyncTileRingsForCategories (a "Build this" apply
     * that landed on a card still drilled into an earlier product).
     *
     * @param {string} categoryId
     * @param {Element} thumbnailsContainer - the card's SELECTORS.THUMBNAILS
     * @param {string} source - publisher tag on UI_CATEGORY_CONTENT_RENDERED
     * @param {Function} [afterRender] - runs on the new grid before the publish
     * @private
     */
    _rebuildProductGrid(categoryId, thumbnailsContainer, source, afterRender) {
      // XSS-SAFE: PPD-generated HTML, sanitizeHTML wrapper for audit trail (Pattern #17)
      thumbnailsContainer.innerHTML = sanitizeHTML(
        this.generateSmartSortedThumbnails(categoryId)
      );

      if (typeof afterRender === 'function') afterRender();

      // 🚨 THE REBUILT TILES ARRIVE UNGREYED. generateSmartSortedThumbnails
      // returns the pre-rendered Layer 5 HTML, which carries no forbidden
      // state -- that is applied afterwards by the preemptive pass, which
      // runs only on tiles already in the DOM. Every other render path
      // announces its rebuild so the pass can run; the back click did not.
      // Live 2026-09-17, in-swing + 6-1/2 in. door: open Custom Jamb Depth,
      // press back, and the 6-9/16 in. jamb came back selectable.
      publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_CONTENT_RENDERED, {
        categoryId,
        timestamp: Date.now(),
        source,
      });
    },

    restoreThumbnailView(categoryId, clearSelection) {
      // Get category element using the full selector path
      const category = DOM.getElement(
        `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
      );

      if (!category) {
        isDev() &&
          console.warn(`Category element not found for ID: ${categoryId}`);
        return;
      }

      // click-apply backs out WITHOUT unapplying (stateManager.
      // handleBackNavigation), so the card may still hold a real answer that
      // the rebuilt grid has to show. Read it now, before the async rebuild.
      const conductor = getConductor();
      const swapInPlace =
        !clearSelection && !!conductor?._isClickApplyEnabled?.();
      // 🚨 ASK BOTH STORES, and blank the card only if BOTH are empty.
      // getAppliedSelectionsMap() is the fragile one: entering BROWSING deletes
      // its entry for the category while appliedSelectionsSnapshot keeps the
      // answer (the "TWO Applied-Selection Stores" footgun in CLAUDE.md). The
      // snapshot is also what price and cart read. Trusting the Map alone would
      // let this branch blank the header and the completed styling on a card the
      // customer IS being charged for -- the same card-vs-state contradiction
      // this fix exists to remove, reintroduced through the other store.
      const answer = swapInPlace
        ? resolveAppliedAnswer(conductor, categoryId)
        : null;

      // A navigation-only back on an UNANSWERED card resets the header too: a
      // browse that never became an answer may have written its title there,
      // and handleBackNavigation has just dropped that browse.
      if (clearSelection || (swapInPlace && !answer)) {
        const selectionDisplay = category.querySelector(
          '.murphy-door-configurator__category-user-selection'
        );

        if (selectionDisplay) selectionDisplay.textContent = '';
        this.updateApplyButtonVisibility(category, false);

        // 🚨 THE CARD MUST STOP CLAIMING TO BE ANSWERED. clearSelection is
        // true only for BACK_CLICKED, and stateManager.handleBackClick() is a
        // full unapply: it deletes appliedSelections, the provenance entry,
        // both completion indexes, and sets completion.categories = false.
        // Clearing the selection TEXT while leaving CATEGORY_COMPLETED on the
        // element left the DOM contradicting that -- a card still wearing its
        // completed styling (and, in apo_style, the applied banner) above a
        // blank answer, with a green check next to it.
        //
        // Newly reachable 2026-08-27. Applying used to destroy the variant
        // view, which took the back button with it, so Back was unreachable on
        // an applied category; keeping the view alive (see the
        // UI_CATEGORY_AND_VARIANTS_NEED_RERENDER handler) exposed this.
        //
        // Same three writes the unapply path already performs -- see
        // CLASS_NAMES.CATEGORY_COMPLETED / SELECTED_CLOSED / updateCircle in
        // the applied-selection reset above. Deliberately mirrored rather than
        // extracted: that path also rebuilds the thumbnails, which the caller
        // here does for itself immediately below.
        category.classList.remove(CLASS_NAMES.CATEGORY_COMPLETED);
        category.classList.remove(CLASS_NAMES.SELECTED_CLOSED);
        StatusManager.updateCircle(category, false);
      }

      const thumbnailsContainer = category.querySelector(SELECTORS.THUMBNAILS);
      if (!thumbnailsContainer) {
        isDev() &&
          console.warn(
            `Thumbnails container not found for category: ${categoryId}`
          );
        return;
      }

      const variantView = thumbnailsContainer.querySelector(
        SELECTORS.VARIANT_VIEW
      );
      if (!variantView) {
        isDev() &&
          console.warn(`Variant view not found for category: ${categoryId}`);
        return;
      }

      // Handle transition
      Animations.fadeOut(variantView)
        .then(() => {
          variantView.remove();

          // 🚨 DO NOT LET THE REBUILD DRILL STRAIGHT BACK IN. _markAppliedTileInGrid
          // "resumes" a multi-variant answer into its variant tier once per
          // applied (product, variant) pair. Backing out of Custom Jamb Depth,
          // or of the product the answer belongs to, would otherwise re-open a
          // drill-down the customer has just closed -- a back arrow that does
          // nothing. Stamping the pair records that this answer's tier has
          // already been visited.
          if (answer) {
            category.dataset.mdResumeTierAttempt = resumeTierAttemptKey(
              answer.productId != null ? String(answer.productId) : '',
              answer.variantId != null ? String(answer.variantId) : ''
            );
          }

          this._rebuildProductGrid(
            categoryId,
            thumbnailsContainer,
            'UIUpdater.restoreThumbnailView',
            () => {
              if (swapInPlace) {
                this._markAppliedTileInGrid(categoryId);
                this.syncAppliedTileRings();
              }
            }
          );
        })
        .catch((err) => {
          isDev() &&
            console.warn('[UIUpdater] fadeOut transition failed:', err);
        });
    },

    /**
     * Generate thumbnails with material preference-based smart sorting
     * 🚀 LAYER 5 OPTIMIZATION: Reads from pre-rendered HTML cache!
     *
     * @param {string} categoryId - Category to generate thumbnails for
     * @returns {string} HTML string for thumbnails with smart sorting applied
     */
    generateSmartSortedThumbnails(categoryId) {
      const METHOD_SIGNATURE = 'UIUpdater.generateSmartSortedThumbnails';

      try {
        // 🚀 PPD-FIRST: Read from pre-rendered HTML cache!
        const ppd = getPPD();
        if (!ppd) {
          isDev() && console.warn(`${METHOD_SIGNATURE}: PPD not available`);
          return '<div class="murphy-door-configurator__error">System not ready</div>';
        }

        const thumbnailsCache =
          ppd?.categoryToThumbnailsHTMLMap?.get(categoryId);

        if (!thumbnailsCache) {
          isDev() &&
            console.error(
              `${METHOD_SIGNATURE}: No thumbnail cache for category ${categoryId}`
            );
          return '<div class="murphy-door-configurator__error">Category not found</div>';
        }

        // 🎯 PRIORITY 3 FIX: Event-driven material state request (CCP compliant)
        // Request fresh state - cache will be updated asynchronously via event subscription
        // 🚨 MUST stay unconditional (fires on a memo cache hit too, below) — otherwise
        // materialStateCache stops being refreshed for categories that keep hitting the
        // memo cache, and goes stale over a long session. /confirm-plan condition.
        window.MurphyDoorConfigurator.CoreEventBus.publish(
          CONSTANTS.EVENTS.STATE_MATERIAL_STATE_REQUESTED,
          {
            requestId: `ui_updater_thumbnails_${Date.now()}`,
            categoryId,
            timestamp: Date.now(),
            source: 'ui-updater.generateSmartSortedThumbnails',
          }
        );

        // Use cached state for synchronous UI decision (cache updated via event subscription)
        const cachedState = materialStateCache.get(categoryId);
        const materialState = cachedState?.state || null;

        // 🫧 CRITICAL FIX: Get filter state to apply bubble-up transformations
        // When material preferences are active, we need to morph product thumbnails into
        // hybrid variant thumbnails (bubble-up) so users see the specific variant image/price.
        // This must be applied EVERY time thumbnails are generated, not just on initial load.
        const conductor = window.MurphyDoorConfigurator.conductor;
        const filterState = conductor?.getMaterialFilterState(categoryId);

        // 🚀 PERF MEMO: only consult/populate the cache once Layer 5 has actually hydrated
        // this category (thumbnailsCache.default !== null) — never memoize the
        // "Cache not hydrated" placeholder. See thumbnailMemoCache doc comment.
        const isHydrated = thumbnailsCache.default !== null;
        const memoKey = isHydrated
          ? buildThumbnailMemoKey(categoryId, materialState, filterState)
          : null;

        let html;
        if (memoKey && thumbnailMemoCache.has(memoKey)) {
          isDev() &&
            console.log(
              `⚡ ${METHOD_SIGNATURE}: Memo cache HIT for ${categoryId}`
            );
          html = thumbnailMemoCache.get(memoKey);
        } else if (materialState?.woodSku || materialState?.finishSku) {
          // ✅ OPTIMIZATION: Material-preferred HTML is PRE-BUILT in Phase 1!
          if (thumbnailsCache.materialPreferred) {
            isDev() &&
              console.log(
                `⚡ ${METHOD_SIGNATURE}: Using PRE-BUILT material-preferred HTML for ${categoryId}`
              );
            // 🎨 SIMPLIFIED UX: Apply material filter classes via string replacement (zero DOM queries)
            html = applyMaterialFilterClasses(
              thumbnailsCache.materialPreferred,
              categoryId
            );
          } else {
            // No material-preferred products for this category - use default
            isDev() &&
              console.log(
                `⚡ ${METHOD_SIGNATURE}: No material-preferred HTML, using default for ${categoryId}`
              );
            const defaultHTML =
              thumbnailsCache.default ||
              '<div class="murphy-door-configurator__error">Cache not hydrated</div>';
            // 🎨 SIMPLIFIED UX: Apply material filter classes via string replacement (zero DOM queries)
            html = applyMaterialFilterClasses(defaultHTML, categoryId);
          }
          // 🫧 BUBBLE-UP: Apply hybrid variant transformations if bubble-up data exists
          if (filterState?.bubbleUpVariants) {
            html = applyBubbleUpTransformations(
              html,
              filterState.bubbleUpVariants
            );
          }
          if (memoKey) setThumbnailMemoEntry(memoKey, html);
        } else {
          isDev() &&
            console.log(
              `⚡ ${METHOD_SIGNATURE}: Using cached default HTML for ${categoryId}`
            );
          const defaultHTML =
            thumbnailsCache.default ||
            '<div class="murphy-door-configurator__error">Cache not hydrated</div>';
          // 🎨 SIMPLIFIED UX: Apply material filter classes via string replacement (zero DOM queries)
          html = applyMaterialFilterClasses(defaultHTML, categoryId); // O(1) read + filter!
          // 🫧 BUBBLE-UP: Apply hybrid variant transformations if bubble-up data exists
          if (filterState?.bubbleUpVariants) {
            html = applyBubbleUpTransformations(
              html,
              filterState.bubbleUpVariants
            );
          }
          if (memoKey) setThumbnailMemoEntry(memoKey, html);
        }

        // 🚚 DELIVERY FIX: Patch stale shipping time in cached delivery captions.
        // ALWAYS runs fresh, on cached-or-fresh HTML — _latestShippingTime is not part of
        // the memo key, so this is what keeps shipping captions correct under memoization.
        return patchDeliveryThumbnailCaptions(html);
      } catch (error) {
        isDev() &&
          console.error(
            `${METHOD_SIGNATURE}: Error in smart sorted thumbnails:`,
            error
          );
        return '<div class="murphy-door-configurator__error">Error loading thumbnails</div>';
      }
    },

    /**
     * 🎯 MISSING METHOD FIX: Render category thumbnails with material filtering
     * Called when material preferences change and categories need re-rendering
     * @param {string} categoryId - Category to render
     * @param {Array} sortedProducts - Sorted products for the category (currently unused - we use PPD cache)
     */
    renderCategoryThumbnails(categoryId, sortedProducts) {
      // PATTERN 6: Bootstrap Runtime Protection - queue if PPD not ready
      if (!bootstrapComplete) {
        isDev() &&
          console.log(
            `[UIUpdater] renderCategoryThumbnails("${categoryId}") queued - bootstrap not complete`
          );
        pendingCalls.push({
          fn: this.renderCategoryThumbnails,
          context: this,
          args: [categoryId, sortedProducts],
        });
        return;
      }

      const METHOD_SIGNATURE = 'UIUpdater.renderCategoryThumbnails';

      try {
        // Find the thumbnails container for this category
        const categoryButton = DOM.getElement(
          `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
        );

        if (!categoryButton) {
          isDev() &&
            console.warn(
              `${METHOD_SIGNATURE}: Category button not found for ${categoryId}`
            );
          return;
        }

        const thumbnailsContainer = categoryButton.querySelector(
          SELECTORS.THUMBNAILS
        );

        if (!thumbnailsContainer) {
          isDev() &&
            console.warn(
              `${METHOD_SIGNATURE}: Thumbnails container not found for ${categoryId}`
            );
          return;
        }

        // Generate thumbnails HTML with material filtering applied
        const thumbnailsHTML = this.generateSmartSortedThumbnails(categoryId);

        // Render to DOM (Pattern #17: XSS audit trail - PPD pre-sanitized HTML)
        thumbnailsContainer.innerHTML = sanitizeHTML(thumbnailsHTML);

        // The rebuilt tiles carry no ring or outline; put the applied cue back.
        // Coalesced: one material change re-sorts ~9 grids in one task.
        this.scheduleAppliedCueRepaint(METHOD_SIGNATURE);

        isDev() &&
          console.log(
            `✅ ${METHOD_SIGNATURE}: Rendered thumbnails for ${categoryId} with material filtering`
          );

        // PREEMPTIVE FORBIDDEN FIX: Signal that thumbnails are now in the DOM
        // so forbidden-validator can apply forbidden-disabled classes
        publishEvent(CONSTANTS.EVENTS.UI_CATEGORY_CONTENT_RENDERED, {
          categoryId,
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });
      } catch (error) {
        isDev() &&
          console.error(
            `${METHOD_SIGNATURE}: Error rendering category thumbnails:`,
            error
          );
      }
    },

    // Applied state management
    // DRY: auto_applied flag enables silent category completion (no animations)
    // Same code path as manual APPLY_SELECTION, but skips slide/transition animations
    updateAppliedState(
      categoryId,
      key,
      displayTitle,
      price = null,
      productTitle = null,
      autoApplied = false
    ) {
      const categoryButton = DOM.getElement(
        `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
      );
      if (!categoryButton) return;

      categoryButton.classList.add(CLASS_NAMES.CATEGORY_COMPLETED);

      const _apoConductor = getConductor();
      const isApoStyle =
        _apoConductor?._getConfiguratorExperience?.() === 'apo_style';

      // SELECTED_CLOSED reads like "this card is collapsed", but in CSS it is
      // PURELY the completed look -- navy background, white title, white
      // measurement/info icons, white pass-through text (~19 rules in
      // section-md-configurator.css:1903-2035). The actual collapsing is done
      // separately, by removing CATEGORY_OPEN and THUMBNAILS_OPEN.
      //
      // apo_style wants NEITHER half. Ryan, UAT 2026-08-27: "when I select an
      // option it's applied, as I desire, but the entire options drawer turns
      // blue, which I don't love." In classic/dual the navy fill is cheap
      // because a completed card shrinks to a one-line summary; apo_style
      // never collapses, so it stayed at full card height with its whole tile
      // grid inside it. The applied state is expressed instead by a tinted
      // title-wrapper banner in section-md-apo-mode.css.
      //
      // 🚨 NOT ADDING THE CLASS, rather than overriding ~19 rules in CSS, is
      // the load-bearing decision. Every rule missed by an override approach
      // becomes WHITE TEXT ON A PALE BLUE BANNER -- invisible, and invisible
      // in precisely the states hardest to reach in UAT (measurement
      // categories, pass-through cards). This way they cannot match at all.
      //
      // This is NOT the 2026-08-17 mistake the old comment here warned about.
      // That attempt dropped the class while still WANTING the navy look and
      // re-pointed only the background + two text colours onto
      // CATEGORY_COMPLETED, so the remaining white-text rules never fired --
      // dark navy measurement text on a navy card ("the text dissapeaers for
      // the jamb depth, and door depth are seelcted", Daniel). There is no
      // navy here for anything to need to be white against.
      //
      // The three other sites that ADD this class are already unreachable in
      // apo_style: updateCategoryDisplay's accordion sweep early-returns for
      // this mode, and navigation-manager's collapseCategory only runs from
      // the close-X icon, which apo_style has no equivalent of.
      if (!isApoStyle) {
        categoryButton.classList.add(CLASS_NAMES.SELECTED_CLOSED);
      }

      if (!isApoStyle) {
        // Ensure category is NOT open for auto-applied selections
        categoryButton.classList.remove(CLASS_NAMES.CATEGORY_OPEN);
      } else {
        // Belt-and-braces: an auto-apply path may have collapsed this card
        // before apo_style's open-all pass ran. Keep it open.
        categoryButton.classList.add(CLASS_NAMES.CATEGORY_OPEN);
      }

      StatusManager.updateCircle(categoryButton, true);
      this.updateApplyButtonVisibility(categoryButton, false);

      // CRITICAL FIX: Update selection text to show what was actually selected
      // This is what makes the category button display the user's choice
      if (key && displayTitle) {
        if (!autoApplied) {
          isDev() &&
            console.log(
              '🎨 UI UPDATER: updateAppliedState - updating selection text to show user choice',
              {
                categoryId,
                key,
                displayTitle,
                price,
                productTitle,
              }
            );
        } else {
          isDev() &&
            console.log(
              '🤖 UI UPDATER: updateAppliedState - auto-applied (silent, no animations)',
              { categoryId, key, displayTitle }
            );
        }
        this.updateSelectionText(
          categoryButton,
          key,
          displayTitle,
          price,
          productTitle
        );
      }

      const thumbnailsElement = categoryButton.querySelector(
        SELECTORS.THUMBNAILS
      );
      if (thumbnailsElement && isApoStyle) {
        // apo_style: confirming a selection must not collapse the option
        // grid. Leave THUMBNAILS_OPEN in place and run no close animation --
        // the customer keeps seeing every choice, including the one they
        // just made (which the tile's own --active state marks), and can
        // change their mind without reopening anything. Ryan review
        // 2026-08-17, items 3/5/8.
        thumbnailsElement.classList.add(CLASS_NAMES.THUMBNAILS_OPEN);
      } else if (thumbnailsElement) {
        thumbnailsElement.classList.remove(CLASS_NAMES.THUMBNAILS_OPEN);

        if (autoApplied) {
          // DRY AUTO-APPLY: Skip slide animation - immediately close without transition
          // Same end state as Animations.slideClose(), but no visual animation.
          // MUST also cancel any pending slideOpen timer for this category (same
          // animationKey Animations.slideClose would use) -- otherwise a delayed
          // slideOpen scheduled just before this auto-apply fires later and
          // silently re-adds THUMBNAILS_OPEN to a panel this function just closed.
          // Found in gauntlet review 2026-08-13: this branch bypassed
          // Animations.slideClose entirely, so it never got the animationKey guard
          // that fixed the same race everywhere else.
          const animationKey = 'category-slide:' + categoryId;
          if (_activeAnimations[animationKey]) {
            clearTimeout(_activeAnimations[animationKey]);
            _activeAnimations[animationKey] = null;
          }
          thumbnailsElement.style.transition = 'none';
          // Force layout recalculation so transition:none takes effect immediately
          thumbnailsElement.offsetHeight; // eslint-disable-line no-unused-expressions
        } else {
          // Manual APPLY: Animate the close transition
          Animations.slideClose(
            thumbnailsElement,
            'category-slide:' + categoryId
          );
        }
      }

      // ======================================================================
      // C3 SWAP-IN-PLACE: move the --applied outline to the tile that is
      // ACTUALLY applied.
      //
      // THE BUG (Ryan, UAT 2026-09-04, apo_style + click-apply): apply Left
      // Hand Inswing, apply a wood (which re-renders every grid), then apply
      // Left Hand Outswing -- and Inswing keeps a dark navy border while
      // Outswing takes the teal ring. Measured in that exact sequence:
      // Inswing {active:false, applied:TRUE}, Outswing {active:TRUE,
      // applied:false}. The card's own summary said "Left Hand Outswing", so
      // the outline was pointing at an answer the customer no longer had.
      //
      // WHY IT DRIFTED. There are two cues for one fact and they had two
      // different writers updating at two different times:
      //   --active  (teal ring)     moves on EVERY selection, via
      //                             _clearTileActiveForSelection +
      //                             _setTileActive + syncAppliedTileRings.
      //   --applied (navy outline)  written ONLY by _markAppliedTileInGrid,
      //                             which ran only when a grid RE-RENDERED.
      // Nothing repainted the outline when the answer changed in place, which
      // is the whole point of swap-in-place: the grid stays put and only the
      // answer moves. So the outline stayed on whichever tile was applied at
      // the last re-render.
      //
      // 🚨 WHY THE OUTLINE IS NOT SIMPLY DELETED. It is invisible whenever it
      // is CORRECT -- the --active ring (::after, inset -3px, radius 13px)
      // covers the outline (also inset -3px) exactly, verified in the browser
      // by putting both classes on one tile and screenshotting: identical to
      // the ring alone. So it only ever shows up when it is wrong, which reads
      // as "delete the CSS rule". That would be wrong in the ACCORDION
      // experiences: syncAppliedTileRings re-asserts the ring only for
      // _isMultiOpenCategory (apo_style + simplified 3Q), so in dual/classic
      // with click-apply a re-opened completed category can carry the outline
      // as its ONLY cue. Fixing the writer keeps every mode correct; deleting
      // the rule would quietly strip a cue from the ones nobody was looking at.
      //
      // Gated to swap-in-place for the same reason the render-time hook is:
      // when click-apply is off, a completed category renders the single-swatch
      // applied view instead of the grid, so there is no grid to mark and
      // nothing has ever set this class there.
      //
      // Safe to call unconditionally within that gate -- it recomputes from
      // getAppliedSelectionsMap() and clears the class on every non-matching
      // tile in the category, so it is idempotent and self-correcting.
      try {
        const _cueConductor = getConductor();
        if (_cueConductor?._isClickApplyEnabled?.()) {
          this._markAppliedTileInGrid(categoryId);
        }
      } catch (cueErr) {
        isDev() &&
          console.warn(
            '[UI_UPDATER] applied-outline repaint failed for ' +
              categoryId +
              ':',
            cueErr
          );
      }
    },

    // Progress and summary updates
    updateProgressAndSummary() {
      // 🔥 NEW: Use event-based communication for progress calculation
      if (window.MurphyDoorConfigurator.CoreEventBus) {
        window.MurphyDoorConfigurator.CoreEventBus.publish(
          CONSTANTS.EVENTS.UI_PROGRESS_CALCULATION_REQUESTED,
          {
            timestamp: Date.now(),
            source: 'ui-updater.updateProgressAndSummary',
          }
        );
      }

      // Fallback for backward compatibility
      const conductor = getConductor();

      const progress = conductor.calculateTotalProgress();
      this.updateProgressBar(progress);
      this.updateSummaryStatus();
    },

    /**
     * ✅ GOLDEN STANDARD: Update progress percentage indicator
     * - Shows percentage of completed categories vs total categories
     * - Appears after first product reaches APPLIED state
     * - Hides when progress reaches 100%
     * - Updates in real-time as selections change
     * - Receives calculated values from updateProgressBar() (single source of truth)
     *
     * @param {Object} progressData - Calculated progress data from updateProgressBar()
     * @param {number} progressData.percentage - Calculated percentage (0-100)
     * @param {number} progressData.appliedCount - Number of applied selections
     * @param {number} progressData.totalCategories - Total number of categories
     * @private
     */
    updateProgressPercentage(progressData) {
      isDev() &&
        console.log(
          '[Progress %] updateProgressPercentage called with:',
          progressData
        );

      const progressBar = DOM.getElement(
        '.murphy-door-configurator__progress-bar'
      );
      if (!progressBar) {
        isDev() && console.warn('[Progress %] Progress bar element not found');
        return;
      }

      // ✅ SINGLE SOURCE OF TRUTH: Use calculated values passed from updateProgressBar()
      const { percentage, appliedCount, totalCategories } = progressData;

      // Get or create percentage indicator element
      let percentageElement = progressBar.querySelector(
        '.murphy-door-configurator__progress-percentage'
      );

      if (!percentageElement) {
        percentageElement = document.createElement('div');
        percentageElement.className =
          'murphy-door-configurator__progress-percentage';
        progressBar.appendChild(percentageElement);
      }

      // Update percentage text
      percentageElement.textContent = `${percentage}%`;

      // WCAG 1.3.1 / 4.1.2: the bar carries role="progressbar" from the Liquid, so
      // aria-valuenow is the only thing that makes the number available to assistive
      // tech — the visible text is inside the progressbar, and progressbar takes no
      // name from its contents. Written here, next to the text, so the two cannot
      // disagree. No aria-valuetext: it would override valuenow and give a second
      // string to keep in sync with the visible one for no gain.
      // A2: write the value to the visually-hidden progressbar, NOT to the fill
      // bar. The fill bar is `display: none !important` in apo_style
      // (see section-md-apo-mode.css), which removes it from the
      // accessibility tree along with any ARIA on it -- so the value
      // written there reached nobody. The hidden element has no display rule
      // targeting it and is aria-hidden-free, so it survives every mode.
      const a11yProgress = DOM.getElement('[data-progress-status]');
      if (a11yProgress) {
        a11yProgress.setAttribute('aria-valuenow', String(percentage));
      }

      // P1: mirror the value into the inline step-nav percentage element. The
      // orbiting badge (above) clipped off-screen on mobile, so on small
      // viewports the CSS hides the badge and shows this inline element inside
      // the step-dots row instead. Writing both keeps a single update path.
      const inlinePercentageElement = DOM.getElement(
        '.murphy-door-configurator__progress-inline'
      );
      if (inlinePercentageElement) {
        inlinePercentageElement.textContent = `${percentage}%`;
      }

      // Visibility — keep the badge visible from the first applied selection all
      // the way through 100%. Previously the threshold was `percentage < 100`,
      // which hid the badge AT the moment of completion — the user never saw
      // "100%" appear in the orbit circle, the badge just faded out. The
      // celebration moment (confetti + tab unlock) reads cleaner when the
      // 100% number lands and persists for the user to register.
      //
      // SIMPLIFIED 3Q: appliedCount stays 0 during the wizard (selections are BROWSING
      // until BUILD MY DOOR fires), so also show the circle whenever the override
      // has bumped calculatedProgress above the 5% minimum sliver.
      const _simplifiedActive = !!document.querySelector(
        '.murphy-door-configurator__tools[data-simplified-mode="active"]'
      );
      const showPercentage =
        appliedCount > 0 || (_simplifiedActive && percentage > 5);
      if (showPercentage) {
        percentageElement.classList.add(
          'murphy-door-configurator__progress-percentage--visible'
        );
        percentageElement.classList.remove(
          'murphy-door-configurator__progress-percentage--hidden'
        );
      } else {
        percentageElement.classList.remove(
          'murphy-door-configurator__progress-percentage--visible'
        );
        percentageElement.classList.add(
          'murphy-door-configurator__progress-percentage--hidden'
        );
      }
      // Mirror visibility onto the inline mobile percentage element.
      if (inlinePercentageElement) {
        inlinePercentageElement.classList.toggle(
          'murphy-door-configurator__progress-inline--visible',
          showPercentage
        );
      }

      // ✅ GOLDEN STANDARD: Publish percentage update event
      if (window.MurphyDoorConfigurator?.CoreEventBus) {
        window.MurphyDoorConfigurator.CoreEventBus.publish(
          CONSTANTS.EVENTS.UI_PROGRESS_PERCENTAGE_UPDATED,
          {
            percentage,
            appliedCount,
            totalCategories,
            visible: appliedCount > 0,
            timestamp: Date.now(),
          }
        );
      }
    },

    /**
     * ✅ GOLDEN STANDARD: Progress bar update with celebration animations
     * - Calculates progress from PPD-first data (single source of truth)
     * - Detects progress increases and triggers celebration animation
     * - Publishes events for animation milestones
     * - Tracks performance of UI updates
     * - Coordinates with confetti system on 100% completion
     * - Updates both visual progress bar width AND percentage text indicator
     *
     * NOTE: This function serves as the orchestrator for ALL progress-related UI updates.
     * It calculates progress once from PPD data and passes values to updateProgressPercentage().
     *
     * @param {number} progress - DEPRECATED: Ignored, calculation done internally from PPD
     */
    updateProgressBar(progress) {
      // ✅ GOLDEN STANDARD: Performance tracking for UI updates
      const startTime = performance.now();

      const progressBar = DOM.getElement(
        '.murphy-door-configurator__progress-bar'
      );

      if (!progressBar) {
        isDev() &&
          console.warn('⚠️ UI UPDATER: Progress bar element not found');
        return;
      }

      try {
        // ✅ SINGLE SOURCE OF TRUTH: Calculate progress from PPD-first data
        const conductor = getConductor();
        const ppd = getPPD();

        if (!conductor || !ppd) {
          isDev() &&
            console.warn(
              '⚠️ UI UPDATER: Cannot calculate progress - conductor or PPD unavailable'
            );
          return;
        }

        const appliedSelectionsMap = conductor.getAppliedSelectionsMap();
        const appliedCount =
          appliedSelectionsMap instanceof Map ? appliedSelectionsMap.size : 0;
        const totalCategories =
          ppd.categories instanceof Map ? ppd.categories.size : 0;
        // Default to 5% to show an initial sliver of the progress bar before any categories load
        let calculatedProgress =
          totalCategories > 0
            ? Math.min(100, Math.round((appliedCount / totalCategories) * 100))
            : 5;

        // SIMPLIFIED 3Q MODE OVERRIDE: Show equal 33%-per-category progress
        // during the 3Q wizard instead of the normal applied-count calculation.
        // The conductor tracks browsed (not yet applied) selections in its
        // _simplifiedBrowsingSelections Map; getSimplifiedBrowsingCount() exposes
        // those counts without exposing private state.
        // Capped at 99 so 100% is only reached after BUILD MY DOOR bulk-applies
        // everything and data-simplified-mode flips to "complete".
        if (
          document.querySelector(
            '.murphy-door-configurator__tools[data-simplified-mode="active"]'
          )
        ) {
          const _simplifiedCount = conductor.getSimplifiedBrowsingCount?.();
          if (_simplifiedCount && _simplifiedCount.totalCount > 0) {
            calculatedProgress = Math.max(
              5,
              Math.floor(
                (_simplifiedCount.browsedCount / _simplifiedCount.totalCount) *
                  99
              )
            );
          }
        }

        // ✅ DEDUPLICATION GUARD: Skip identical consecutive progress updates
        // Prevents double-fire from APPLY_SELECTION's dual path (batch UI + handleProgressUpdate)
        if (
          this._lastProgressPercentage === calculatedProgress &&
          this._lastProgressAppliedCount === appliedCount
        ) {
          isDev() &&
            console.log(
              `[Progress] Skipping duplicate updateProgressBar (${calculatedProgress}%, ${appliedCount} applied)`
            );
          return;
        }
        this._lastProgressPercentage = calculatedProgress;
        this._lastProgressAppliedCount = appliedCount;

        // Store previous progress to detect increases
        const currentWidth = progressBar.style.width;
        const previousProgress = currentWidth ? parseInt(currentWidth) : 5;

        // ✅ GOLDEN STANDARD: Publish progress update start event
        if (window.MurphyDoorConfigurator?.CoreEventBus) {
          window.MurphyDoorConfigurator.CoreEventBus.publish(
            CONSTANTS.EVENTS.UI_PROGRESS_BAR_UPDATE_STARTED,
            {
              previousProgress,
              newProgress: calculatedProgress,
              increaseDetected: calculatedProgress > previousProgress,
              timestamp: Date.now(),
            }
          );
        }

        // Trigger celebration animation FIRST if progress increased
        if (calculatedProgress > previousProgress) {
          // Remove class if it exists (force animation restart)
          progressBar.classList.remove(
            'murphy-door-configurator__progress-bar--celebrating'
          );

          // ✅ GOLDEN STANDARD: Force reflow to ensure class removal takes effect
          // This prevents animation glitches when rapid updates occur
          progressBar.offsetHeight;

          // Add celebration animation class
          progressBar.classList.add(
            'murphy-door-configurator__progress-bar--celebrating'
          );

          // ✅ GOLDEN STANDARD: Publish animation triggered event asynchronously
          // Use setTimeout(0) to ensure DOM animation starts immediately without blocking
          setTimeout(() => {
            if (window.MurphyDoorConfigurator?.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.UI_PROGRESS_CELEBRATION_TRIGGERED,
                {
                  fromProgress: previousProgress,
                  toProgress: calculatedProgress,
                  animationDuration: 800, // ms - matches CSS animation
                  timestamp: Date.now(),
                }
              );
            }
          }, 0);

          // Update the width AFTER celebration animation completes
          setTimeout(() => {
            progressBar.classList.remove(
              'murphy-door-configurator__progress-bar--celebrating'
            );
            progressBar.style.width = `${calculatedProgress}%`;

            // Toggle completed class — adds at 100%, removes below 100% (e.g., after unapply)
            progressBar.classList.toggle(
              'murphy-door-configurator__progress-bar-completed',
              calculatedProgress === 100
            );

            // ✅ Update percentage indicator after width change
            this.updateProgressPercentage({
              percentage: calculatedProgress,
              appliedCount,
              totalCategories,
            });

            // Check for completion state after width update
            if (calculatedProgress === 100) {
              // ✅ GOLDEN STANDARD: Publish completion event
              if (window.MurphyDoorConfigurator?.CoreEventBus) {
                window.MurphyDoorConfigurator.CoreEventBus.publish(
                  CONSTANTS.EVENTS.UI_PROGRESS_BAR_COMPLETED,
                  {
                    timestamp: Date.now(),
                    celebrationTriggered: true,
                  }
                );
              }

              // 🎉 Confetti is triggered by conductor via UI_CONFETTI_CELEBRATION_REQUESTED event
              // (see md-config-core-conductor.js line ~4664)
            }

            // ✅ GOLDEN STANDARD: Track performance of UI update
            const duration = performance.now() - startTime;
            if (duration > PERFORMANCE_THRESHOLDS.UI_UPDATE) {
              isDev() &&
                console.warn(
                  `⚠️ UI UPDATER: Progress bar update exceeded threshold: ${duration.toFixed(2)}ms > ${PERFORMANCE_THRESHOLDS.UI_UPDATE}ms`
                );

              if (window.MurphyDoorConfigurator?.CoreEventBus) {
                window.MurphyDoorConfigurator.CoreEventBus.publish(
                  CONSTANTS.EVENTS.PERFORMANCE_SLOW_OPERATION,
                  {
                    module: 'ui-updater',
                    operation: 'updateProgressBar',
                    duration: Math.round(duration * 100) / 100,
                    threshold: PERFORMANCE_THRESHOLDS.UI_UPDATE,
                    timestamp: Date.now(),
                  }
                );
              }
            }
          }, 800); // Match CSS animation duration (progressTada)
        } else {
          // No celebration needed, just update width immediately
          progressBar.style.width = `${calculatedProgress}%`;

          // Toggle completed class — adds at 100%, removes below 100% (e.g., after unapply)
          progressBar.classList.toggle(
            'murphy-door-configurator__progress-bar-completed',
            calculatedProgress === 100
          );

          // ✅ Update percentage indicator after width change
          this.updateProgressPercentage({
            percentage: calculatedProgress,
            appliedCount,
            totalCategories,
          });

          if (calculatedProgress === 100) {
            // ✅ GOLDEN STANDARD: Publish completion event
            if (window.MurphyDoorConfigurator?.CoreEventBus) {
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.UI_PROGRESS_BAR_COMPLETED,
                {
                  timestamp: Date.now(),
                  celebrationTriggered: false,
                }
              );
            }

            // 🎉 Confetti is triggered by conductor via UI_CONFETTI_CELEBRATION_REQUESTED event
            // (see md-config-core-conductor.js line ~4664)
          }

          // ✅ GOLDEN STANDARD: Track performance of immediate update
          const duration = performance.now() - startTime;
          if (duration > PERFORMANCE_THRESHOLDS.UI_UPDATE) {
            isDev() &&
              console.warn(
                `⚠️ UI UPDATER: Progress bar immediate update exceeded threshold: ${duration.toFixed(2)}ms > ${PERFORMANCE_THRESHOLDS.UI_UPDATE}ms`
              );
          }
        }
      } catch (error) {
        isDev() &&
          console.error('❌ UI UPDATER: Progress bar update failed:', error);

        // ✅ GOLDEN STANDARD: Publish error event
        if (window.MurphyDoorConfigurator?.CoreEventBus) {
          window.MurphyDoorConfigurator.CoreEventBus.publish(
            CONSTANTS.EVENTS.UI_PROGRESS_BAR_UPDATE_FAILED,
            {
              error: error.message,
              stack: error.stack,
              progress: calculatedProgress,
              timestamp: Date.now(),
            }
          );
        }
      }
    },

    updateSummaryStatus() {
      const summaryTab = DOM.getElement(SELECTORS.SUMMARY_TAB);
      if (!summaryTab) return;

      // 🔥 NEW: Use event-based communication for summary completion check
      if (window.MurphyDoorConfigurator.CoreEventBus) {
        window.MurphyDoorConfigurator.CoreEventBus.publish(
          CONSTANTS.EVENTS.UI_SUMMARY_COMPLETION_CHECK_REQUESTED,
          {
            timestamp: Date.now(),
            source: 'ui-updater.updateSummaryStatus',
          }
        );
      }

      // Conductor delegation: check summary completion via progressTracker
      const conductor = getConductor();
      const isComplete = conductor?.checkSummaryComplete();

      // 🚨 THE BUTTON IS GATED ON THE APPLIED MAP, NOT ONLY ON THE COUNTER.
      // isComplete is progressTracker's cached counter. On 2026-09-29 it read
      // 20/20 while the Door Depth slider rested on a forbidden stop with NO
      // applied Door Depth, and this button shipped a door without one.
      // getCartReadiness() reads the applied Map itself (allowPending: an
      // allowed browse still on screen is applied by the ADD_TO_CART flush,
      // so it must not grey the button during a slider's commit debounce).
      // isComplete still drives the "fell out of complete" navigation below:
      // that behaviour is keyed to real completion changes, not to a debounce.
      const readiness =
        conductor && typeof conductor.getCartReadiness === 'function'
          ? conductor.getCartReadiness({ allowPending: true })
          : null;
      const atcReady = !!isComplete && (!readiness || readiness.ready);
      StatusManager.updateCircle(summaryTab, atcReady);
      summaryTab.classList.toggle(CLASS_NAMES.TAB_COMPLETED, atcReady);

      // P3: multi-target — there can now be more than one ATC button (the
      // Summary button + the mobile sticky-bar button). Keep every copy's
      // disabled/aria state in sync so the sticky ATC gates identically.
      const addToCartButtons = document.querySelectorAll(SELECTORS.ADD_TO_CART);

      // Restore a stranded "Updating..." label. The cascade gate below swaps
      // the label on BUSINESS_CASCADE_AUTO_SELECTION_STARTED and restores it
      // only on COMPLETED/FAILED -- so a cascade that starts and never reports
      // either leaves the button reading "Updating..." for the rest of the
      // session, no matter how many times its enabled state is recomputed
      // afterwards. Seen live 2026-08-25: state fully complete at 20/20 and
      // 100%, remap not in flight, button still stranded.
      //
      // This is the recompute that runs on every completion change, so it is
      // the natural place to notice the label disagrees with reality. It only
      // restores a label the gate itself stored; it never invents one.
      addToCartButtons.forEach((button) => {
        if (button._cascadeText && !cascadeActive) {
          button.textContent = button._cascadeText;
          delete button._cascadeText;
        }
      });
      addToCartButtons.forEach((addToCartButton) => {
        // UX ENHANCEMENT: Always show button, toggle disabled state instead of display:none
        // When disabled, the tooltip wrapper handles hover/click tooltip display via CSS
        if (atcReady) {
          addToCartButton.disabled = false;
          addToCartButton.removeAttribute('aria-disabled');
          addToCartButton.setAttribute('aria-label', 'Add to Cart');
        } else {
          addToCartButton.disabled = true;
          addToCartButton.setAttribute('aria-disabled', 'true');
          addToCartButton.setAttribute(
            'aria-label',
            'Complete your door configuration to add to cart'
          );
        }

        // Attach disabled button click guide (idempotent - only attaches once)
        this._setupDisabledButtonGuide(addToCartButton);
      });

      // A1: refresh the keyboard-reachable "what's missing" control.
      //
      // Deliberately inside THIS block, immediately after the same `isComplete`
      // decided the disabled state above. The count and the button state are
      // therefore written from one pass over one value -- there is no second
      // code path that could observe a different completion state and print a
      // number that contradicts the button.
      this._updateAtcStatusControls(atcReady, readiness);

      // apo_style's incomplete->complete scroll to the ATC was REMOVED
      // 2026-08-27. Ryan: "we should not worry about advancing the user to the
      // summary list to show them the add to cart button. The add to cart
      // button should be at the bottom of the list of category buttons."
      //
      // It existed because the button was inside the summary panel, which
      // apo_style could otherwise never reach -- there are no tabs and no
      // "click Summary" moment, so completing the last category left the only
      // ATC off-screen with nothing to indicate it had appeared. The button now
      // sits at the end of the rail (sections/md-configurator.liquid) and the
      // panel is display:none in this mode, so the destination it was pushing
      // people to no longer exists.
      //
      // Note this deliberately leaves the customer where they are on
      // completion. That is the ask: the last card they answered stays in view
      // instead of the page jumping.
      // FALLING OUT OF A COMPLETE CONFIGURATION (complete -> INCOMPLETE).
      //
      // This used to be described as the symmetric half of an
      // incomplete -> complete scroll-to-ATC above; that half was removed on
      // 2026-08-27 when the button moved to the end of the rail, and this one
      // deliberately stayed. It is not decoration on the other behaviour -- it
      // handles a case nothing else catches: the customer sat next to a
      // disabled button with no indication of what had changed or any route
      // back to the cards.
      //
      // It happens whenever a material change invalidates a selection. Measured
      // live 2026-08-25 on a Knotty Alder -> TFL swap: the fluted back casing
      // and back entablature have no TFL variant, so the material cascade
      // unapplied both, completion fell 100% -> 90%, and the view stayed on the
      // summary. No conflict modal was raised because nothing was ambiguous --
      // which is exactly why no other path was going to catch this one.
      //
      // Sends them back to the cards at the first thing that needs answering,
      // via the same two-step (tab, then target on the next paint) the summary's
      // Edit link uses. Deliberately NOT scoped to apo_style: falling out of a
      // complete configuration strands the customer in every mode.
      if (!isComplete && _wasSummaryComplete) {
        try {
          const navConductor = getConductor();
          const bus = window.MurphyDoorConfigurator.CoreEventBus;
          const incompleteTab = navConductor?.findFirstIncompleteTab?.();
          const tabId = Array.isArray(incompleteTab)
            ? incompleteTab[0]
            : incompleteTab;
          if (bus && tabId) {
            const categoryId =
              navConductor.findFirstIncompleteCategory?.(tabId) || null;
            bus.publish(CONSTANTS.EVENTS.UI_TAB_OPEN_REQUEST, {
              tabId,
              categoryId,
              source: 'summary_fell_incomplete',
              reason: 'configuration_no_longer_complete',
              timestamp: Date.now(),
            });
            if (categoryId) {
              requestAnimationFrame(() =>
                requestAnimationFrame(() =>
                  bus.publish(CONSTANTS.EVENTS.UI_CATEGORY_OPEN_REQUEST, {
                    categoryId,
                    tabId,
                    source: 'summary_fell_incomplete',
                    reason: 'configuration_no_longer_complete',
                  })
                )
              );
            }
          }
        } catch (navError) {
          isDev() &&
            console.error(
              '[UIUpdater] Could not return to cards after falling incomplete:',
              navError
            );
        }
      }

      _wasSummaryComplete = isComplete;
    },

    /**
     * 🎯 GOLDEN STANDARD: Disabled Add to Cart button click detection + visual feedback
     *
     * CCP COMPLIANCE - Separation of Concerns:
     * - UIUpdater (this module): DOM animations + event publishing (UI responsibility)
     * - Auto-Navigation-Coordinator: Navigation logic (business responsibility)
     *
     * This follows the exact pattern from working modules:
     * - event-handler.js: Detects thumbnail clicks → publishes UI_THUMBNAIL_CLICKED
     * - selection-coordinator.js: Subscribes to UI_THUMBNAIL_CLICKED → handles selection
     *
     * PATTERN: "UI modules do visual feedback, business modules do logic"
     *
     * @param {HTMLButtonElement} button - The Add to Cart button element
     * @private
     */
    /**
     * Fill and reveal every "N choices left" control on the page.
     *
     * @param {boolean} isComplete - the SAME value that just set button.disabled
     * @param {Object|null} [readiness] - conductor.getCartReadiness() result
     *   that fed isComplete, when the caller had one
     */
    _updateAtcStatusControls(isComplete, readiness) {
      const nodes = document.querySelectorAll('[data-atc-status]');
      if (!nodes.length) return;

      // Read through the conductor (CCP), which bottoms out in the same two
      // counters isSummaryComplete() divides -- see
      // progressTracker.getRemainingCategoryCount().
      const conductor = getConductor();
      let remaining =
        conductor && typeof conductor.getRemainingCategoryCount === 'function'
          ? conductor.getRemainingCategoryCount()
          : 0;

      // Never undercount what the button is refusing: when readiness (the
      // applied Map) blocks a door the counter calls finished, count that.
      if (!isComplete && readiness && !readiness.ready) {
        const blocking = readiness.missing.filter(
          (categoryId) => readiness.pending.indexOf(categoryId) === -1
        ).length;
        remaining = Math.max(remaining, blocking);
      }

      // Hide when finished, and ALSO when the count is 0 while incomplete.
      // A "0 choices left" badge beside a disabled button is the exact
      // self-contradiction this feature exists to prevent, so the degenerate
      // case fails silent rather than confidently wrong.
      const show = !isComplete && remaining > 0;

      nodes.forEach((node) => {
        if (!show) {
          // Clear the text too: a hidden node still reading "1 choice left"
          // beside a finished door was the stale count seen 2026-09-29.
          node.hidden = true;
          const staleText = node.querySelector('[data-atc-status-text]');
          if (staleText) staleText.textContent = '';
          node.removeAttribute('aria-label');
          return;
        }
        const label = remaining === 1 ? '1 choice left' : remaining + ' choices left';
        const textNode = node.querySelector('[data-atc-status-text]');
        if (textNode) textNode.textContent = label;
        // WCAG 2.5.3 Label in Name: the visible string is a literal prefix of
        // the accessible name, so voice-control users can say what they read.
        node.setAttribute('aria-label', label + '. Go to the next unfinished option.');
        node.hidden = false;
        this._setupAtcStatusActivation(node);
      });
    },

    /**
     * Wire activation once per control.
     *
     * Publishes the SAME event the pointer-only wrapper guide publishes, so the
     * "move to the first unfinished category" behaviour is the existing one in
     * auto-navigation-coordinator rather than a second implementation that
     * could pick a different target. Keyboard and pointer therefore cannot
     * disagree about where the customer lands.
     */
    _setupAtcStatusActivation(node) {
      if (node._atcStatusWired) return;
      node._atcStatusWired = true;
      node.addEventListener('click', (event) => {
        // 🚨 stopPropagation is REQUIRED, and only on the rail.
        // In the rail this control renders INSIDE
        // `.murphy-door-configurator__add-to-cart-wrapper`, whose existing
        // pointer guide listens for clicks and, seeing the button still
        // disabled, publishes this very same event. Without this the event
        // fires TWICE per activation and auto-navigation runs twice.
        // Measured in a browser, not inferred. The mobile bar has no wrapper,
        // so there this is simply a no-op.
        event.stopPropagation();
        publishEvent(CONSTANTS.EVENTS.UI_ADD_TO_CART_DISABLED_CLICKED, {
          timestamp: Date.now(),
          source: 'UIUpdater._atcStatusControl',
        });
      });
    },

    _setupDisabledButtonGuide(button) {
      // Idempotent guard: only attach once
      if (button._disabledGuideAttached) return;
      button._disabledGuideAttached = true;

      // CRITICAL: Disabled <button> elements do NOT fire click events in browsers.
      // We must listen on the WRAPPER element instead, which always receives clicks.
      // The wrapper is .murphy-door-configurator__add-to-cart-wrapper (see md-configurator.liquid).
      const wrapper = button.closest(
        '.murphy-door-configurator__add-to-cart-wrapper'
      );
      if (!wrapper) {
        isDev() &&
          console.warn(
            '[UIUpdater] Disabled button guide: wrapper element not found'
          );
        return;
      }

      // Debounce flag to prevent rapid-fire clicks
      let animationInProgress = false;

      // Detect click and provide instant visual feedback
      wrapper.addEventListener('click', (e) => {
        // Only act when button is actually disabled (ignore clicks when enabled)
        if (!button.disabled) return;

        // Debounce: prevent multiple simultaneous animations
        if (animationInProgress) {
          isDev() &&
            console.log(
              '[UIUpdater] Animation already in progress, ignoring click'
            );
          return;
        }

        animationInProgress = true;

        // ✅ UI RESPONSIBILITY: Button shake animation (instant tactile feedback)
        // Uses CSS @keyframes from section-md-configurator.css
        button.classList.remove('guide-shake');
        void button.offsetHeight; // Force reflow to restart animation
        button.classList.add('guide-shake');

        // Remove shake class after animation completes
        setTimeout(() => {
          button.classList.remove('guide-shake');
        }, DISABLED_BUTTON_TIMINGS.SHAKE_DURATION);

        // ✅ TOUCH UX: Show tooltip on tap — CSS hover-only tooltip is invisible on touch devices.
        // Temporarily add class to wrapper so the tooltip is visible for 2500ms.
        wrapper.classList.add('tooltip-show');
        setTimeout(() => {
          wrapper.classList.remove('tooltip-show');
        }, DISABLED_BUTTON_TIMINGS.TOOLTIP_DURATION);

        // ✅ GOLDEN STANDARD: Publish event - let auto-navigation-coordinator handle navigation logic
        publishEvent(CONSTANTS.EVENTS.UI_ADD_TO_CART_DISABLED_CLICKED, {
          timestamp: Date.now(),
          source: 'UIUpdater._disabledButtonGuideHandler',
        });

        isDev() &&
          console.log(
            '[UIUpdater] Disabled Add to Cart clicked - animations triggered + event published'
          );

        // Reset debounce flag after animations complete (see DISABLED_BUTTON_TIMINGS.DEBOUNCE)
        setTimeout(() => {
          animationInProgress = false;
        }, DISABLED_BUTTON_TIMINGS.DEBOUNCE);
      });

      isDev() &&
        console.log(
          '[UIUpdater] Disabled button guide handler attached (event-driven with visual feedback)'
        );
    },

    /**
     * ⚡ ARCHITECTURAL VIOLATION: This method performs runtime display formatting
     * ALL display formatting MUST be pre-extracted in PPD Layer 1
     * Use variant.displayFormatting.baseDisplay instead
     * @throws {Error} Always throws to enforce PPD-first architecture
     */
    updateSummaryList(categoryId, displayTitle, productTitle, price) {
      const METHOD_SIGNATURE = 'UIUpdater.updateSummaryList';
      const summaryList = DOM.getElement(
        '.murphy-door-configurator__summary-list'
      );

      // Graceful null check: summary list DOM element may not exist on all page layouts
      // This is expected when the summary list feature is not part of the current template
      if (!summaryList) {
        // Downgraded from error to debug-only warning: this is a non-critical missing DOM element,
        // not an application error. Prevents log noise on every selection event.
        isDev() &&
          console.warn(
            `${METHOD_SIGNATURE}: summary list container not found (non-critical - element may not be in current template)`
          );
        return;
      }

      // One-time event delegation for per-item Edit links. Marker attr prevents
      // double-attachment across the many updateSummaryList() calls per session.
      // Click resolves the tab via PPD.progressMaps.categoryToTabMap and publishes
      // UI_TAB_OPEN_REQUEST + UI_CATEGORY_OPEN_REQUEST. The double-rAF defer
      // between the two events lets the tab switch paint before navigation-manager
      // tries to expand the category accordion.
      if (summaryList.dataset.editDelegationWired !== 'true') {
        summaryList.dataset.editDelegationWired = 'true';
        summaryList.addEventListener('click', function (e) {
          const editLink = e.target.closest('[data-edit-category]');
          if (!editLink) return;
          e.preventDefault();
          const editCategoryId = editLink.getAttribute('data-edit-category');
          if (!editCategoryId) return;
          const conductor = window.MurphyDoorConfigurator.main;
          const ppd =
            conductor &&
            typeof conductor.getPerfectProductDictionary === 'function'
              ? conductor.getPerfectProductDictionary()
              : null;
          const tabId =
            ppd && ppd.progressMaps && ppd.progressMaps.categoryToTabMap
              ? ppd.progressMaps.categoryToTabMap.get(editCategoryId)
              : null;
          const bus = window.MurphyDoorConfigurator.CoreEventBus;
          if (!bus) return;

          // Clear the existing selection in this category SYNCHRONOUSLY
          // before navigating, so the user lands on a fresh accordion
          // showing all material-compatible options rather than just
          // their already-applied pick.
          //
          // SELECTION_USER_UNAPPLY_REQUESTED is the same CCP event the
          // per-category reset already publishes (event-handler.js:643 ->
          // conductor.js:2724 -> state-manager handleBackClick ->
          // forbidden-validator + material-preference re-eval -> ui-updater
          // handleUserUnapply restores thumbnails). Material compatibility
          // and forbidden-combination rules are honored by the existing
          // subscribers -- no rule bypass.
          //
          // MATERIAL MASTER EXCEPTION (apo_style). The June-2026 version of
          // this comment claimed Wood & Finish needed no special-casing
          // because the cascade left the customer on "a clean slate". That
          // was true when it was written and is wrong now: unapplying the
          // material master destroys the exact selections
          // md-config-bl-material-remap-coordinator.js exists to carry
          // across, so by the time the replacement wood is picked there is
          // nothing left to remap and every styling choice is silently lost.
          // Measurements and knobs survive only because they carry no
          // wood_sku/finish_sku and are structurally excluded from the
          // cascade -- which is exactly the reported symptom.
          //
          // Skipping the unapply lets the customer pick the replacement over
          // the top, and the existing SELECTION_VARIANT_APPLIED ->
          // MATERIAL_PREFERENCE_APPLIED -> runRemap chain does the
          // carry-across, raising the conflict modal only when something
          // genuinely cannot map. Same reasoning and same guard as the apo
          // card-body reset at event-handler.js:1995-2003.
          //
          // Scoped to apo_style because runRemap returns early on
          // !isApoStyle() (material-remap-coordinator.js:321): guarding
          // classic/dual would leave them with neither the cascade nor a
          // remap, stranding selections in the old material. Widening both
          // together is its own change with its own classic UAT.
          const _editConductor = getConductor();
          const _editIsApoStyle =
            _editConductor?._getConfiguratorExperience?.() === 'apo_style';
          const _editIsMaterialMaster = (
            CONSTANTS.MATERIAL_SOURCE_CATEGORIES || []
          ).includes(editCategoryId);

          if (!(_editIsMaterialMaster && _editIsApoStyle)) {
            bus.publish(CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_REQUESTED, {
              categoryId: editCategoryId,
              source: 'summary_edit',
              timestamp: Date.now(),
            });
          }

          if (tabId) {
            bus.publish(CONSTANTS.EVENTS.UI_TAB_OPEN_REQUEST, {
              tabId: tabId,
              source: 'summary_edit',
              reason: 'edit_link_click',
              categoryId: editCategoryId,
            });
          }
          // Double RAF: let the tab switch reflow + paint complete before the
          // category-open request fires, otherwise navigation-manager can find
          // a stale (still-collapsed) DOM and skip the expand call.
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              bus.publish(CONSTANTS.EVENTS.UI_CATEGORY_OPEN_REQUEST, {
                categoryId: editCategoryId,
                tabId: tabId,
                source: 'summary_edit',
                reason: 'edit_link_click',
              });
            });
          });
        });
      }

      // Remove existing entry for this category
      const existingEntry = summaryList.querySelector(
        `[data-category="${categoryId}"]`
      );
      if (existingEntry) {
        existingEntry.remove();
      }

      // Create new list item using PPD pre-formatted data
      const li = document.createElement('li');
      li.setAttribute('data-category', categoryId);
      li.classList.add('murphy-door-configurator__summary-item');

      // XSS Prevention (Golden Standard Pattern #7): Use textContent for user-derived data
      // Build structured DOM elements instead of innerHTML to prevent injection

      // Category pill: format "wood-species" → "Wood Species"
      const formattedCategory = categoryId
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());

      // Header row holds the pill + Edit link inline so the Edit affordance
      // sits at the category-title eye-line instead of competing with the
      // price for the bottom-right of the row.
      const pillRow = document.createElement('div');
      pillRow.className = 'murphy-door-configurator__summary-item-pill-row';

      const pill = document.createElement('span');
      pill.className = 'murphy-door-configurator__summary-item-pill';
      pill.textContent = formattedCategory;
      pillRow.appendChild(pill);

      // Per-line Edit link — wired via event delegation on the summary list
      // (see init block above). Clicking jumps the user back to the
      // originating tab + category for changes without losing other selections.
      const editLink = document.createElement('a');
      editLink.className = 'murphy-door-configurator__summary-item-edit';
      editLink.setAttribute('data-edit-category', categoryId);
      editLink.setAttribute('href', '#');
      editLink.setAttribute('aria-label', 'Edit ' + formattedCategory);
      editLink.textContent = 'Edit';
      pillRow.appendChild(editLink);

      // Contextual upsell — looked up in SUMMARY_UPSELLS (module-scope const).
      // Only renders when the user picked the cheapest / no-frills option in
      // a category that has an upgrade path. Clicking reuses the Edit
      // delegation (data-edit-category) so the user is dropped into the
      // same category accordion where the upgrade option lives — no detour
      // to a product page.
      const matchingUpsell = SUMMARY_UPSELLS.find(
        (u) =>
          u.category === categoryId && u.triggerMatch.test(displayTitle || '')
      );
      if (matchingUpsell) {
        const upsellLink = document.createElement('a');
        upsellLink.className = 'murphy-door-configurator__summary-item-upsell';
        upsellLink.setAttribute('href', '#');
        upsellLink.setAttribute('data-edit-category', categoryId);
        // The bridge (assets/analytics-tracking.js) reads only dataset.trackClick
        // for element_action -- it never reads data-track-action, so the verb
        // MUST live on data-track-click itself or the click reaches nothing.
        upsellLink.setAttribute('data-track-click', 'view_addon');
        upsellLink.setAttribute('data-track-section', 'configurator_summary');
        upsellLink.setAttribute('data-track-label', matchingUpsell.trackLabel);
        upsellLink.setAttribute(
          'data-track-position',
          matchingUpsell.trackPosition
        );
        // Strip the arrow from the aria-label and describe the destination
        // so AT users hear "opens Locking Systems options" instead of
        // an unparsed "→".
        const labelWithoutArrow = matchingUpsell.label
          .replace(/\s*→\s*$/, '')
          .trim();
        upsellLink.setAttribute(
          'aria-label',
          labelWithoutArrow + ', opens ' + formattedCategory + ' options'
        );
        upsellLink.textContent = matchingUpsell.label;
        pillRow.appendChild(upsellLink);
      }

      li.appendChild(pillRow);

      // Selection + price row
      const row = document.createElement('div');
      row.className = 'murphy-door-configurator__summary-item-row';

      const selectionSpan = document.createElement('span');
      selectionSpan.className =
        'murphy-door-configurator__summary-item-selection';
      const displayText = productTitle
        ? `${productTitle} · ${displayTitle}`
        : displayTitle;
      selectionSpan.textContent = displayText.replace(/ \| /g, ' · ');
      row.appendChild(selectionSpan);

      if (price) {
        // Route through the shared formatter so non-zero prices always carry a
        // leading "$" (the conductor passes a bare "result.price" here) while
        // $0 rows render as "(Included)" -- parenthesized so it never fuses
        // into a "No <option>" name as a run-on sentence (e.g. "No Knobs or
        // Pulls Included"). Kept in sync with updateSelectionText below,
        // which passes the same literal.
        const displayPrice = formatConfiguratorPrice(price, {
          zeroLabel: '(Included)',
        });

        const priceSpan = document.createElement('span');
        priceSpan.className = 'murphy-door-configurator__summary-item-price';
        priceSpan.textContent = displayPrice;
        row.appendChild(priceSpan);
      }

      li.appendChild(row);

      // Insert the item in the correct order using PPD category sort map (O(1) lookup)
      const ppd = getPPD();
      const categoryIndex = ppd?.categoryToSortIndexMap?.get(categoryId);

      if (categoryIndex === undefined) {
        summaryList.appendChild(li); // Append to end if order not found
        return;
      }

      const existingItems = Array.from(summaryList.children);
      const insertBefore = existingItems.find((item) => {
        const itemCategory = item.getAttribute('data-category');
        const itemIndex = ppd?.categoryToSortIndexMap?.get(itemCategory);
        return itemIndex > categoryIndex;
      });

      if (insertBefore) {
        summaryList.insertBefore(li, insertBefore);
      } else {
        summaryList.appendChild(li);
      }

      isDev() &&
        console.log(
          `${METHOD_SIGNATURE}: Updated summary for category ${categoryId}`
        );
    },

    /**
     * Animated price update with fade-out/update/fade-in transition
     * Duration: 300ms (uses ANIMATION_DURATIONS.FADE for DRY consistency)
     * Easing: ease-in-out for smooth, professional feel
     * Debounced: Cancels in-flight animations on rapid successive calls
     *
     * ANIMATION TARGET: Only text elements (label + amount), NOT the container
     * This prevents border/container artifacts during fade transitions
     *
     * DRY PATTERN: Delegates to Animations.animateValueChange() which is shared
     * with PTS updates for consistent fade behavior across the configurator.
     *
     * CCP COMPLIANCE: Uses pre-formatted strings from business logic layer
     * (no direct priceCalculator calls - formatting done in coordinatePriceUpdate)
     *
     * @param {Object} priceData - { label, amount, formattedAmount, summaryLabel, formattedSummary }
     */
    batchPriceUpdate(priceData) {
      const {
        priceLabelElements,
        priceAmountElements,
        summaryPriceLabel,
        summaryPrice,
      } = CACHED_ELEMENTS;

      // WCAG 4.1.3: announce the new total. Scheduled here, at the single choke
      // point every price path funnels through, so selections, measurement
      // sliders, quantity and material remaps are all covered by one call site.
      // Safe to schedule before the DOM write — it debounces, and it dedupes on
      // the formatted string, so a recompute to the same total says nothing.
      announcePriceChange(priceData);

      // Apply content update (uses pre-formatted strings from business logic layer)
      // DRY: Main and summary displays mirror the same label/amount from BL layer
      const applyContent = () => {
        // Update ALL price label elements (both gallery and sidebar versions)
        if (priceLabelElements) {
          priceLabelElements.forEach((label) => {
            label.textContent = priceData.label;
          });
        }

        // Update ALL price amount elements (both gallery and sidebar versions)
        if (priceAmountElements) {
          priceAmountElements.forEach((amount) => {
            amount.textContent =
              priceData.formattedAmount || String(priceData.amount);
          });
        }

        // Price flash animation handled by global-price-flash.js observer

        // Summary display mirrors main: same label logic, always updated
        if (summaryPriceLabel && priceData.summaryLabel) {
          summaryPriceLabel.textContent = priceData.summaryLabel;
        }

        if (summaryPrice && priceData.formattedSummary) {
          summaryPrice.textContent = priceData.formattedSummary;
        }
      };

      // DRY: Use shared animateValueChange for consistent fade across price & PTS
      // Include summary elements in animation for synchronized transitions
      // Convert NodeLists to arrays for animation
      const allElements = [
        ...Array.from(priceLabelElements || []),
        ...Array.from(priceAmountElements || []),
        summaryPriceLabel,
        summaryPrice,
      ];
      Animations.animateValueChange(
        'price',
        allElements,
        applyContent,
        undefined,
        {
          // Price has heterogeneous elements (label text ≠ amount text), so use hasChanged.
          // Compare the first price amount element as the representative signal.
          hasChanged: () => {
            const amountEl = priceAmountElements?.[0];
            return (
              !amountEl ||
              amountEl.textContent !==
                (priceData.formattedAmount || String(priceData.amount))
            );
          },
        }
      );
    },

    // Helper methods
    /**
     * ⚡ PPD-FIRST PATTERN: Display variant/product selection with pre-formatted strings
     * Uses pre-extracted displayFormatting from PPD Layer 1 for consistent, DRY display
     *
     * @param {HTMLElement} category - Category button element
     * @param {string} key - Selection key (variant_X or product_X)
     * @param {string} displayTitle - DEPRECATED - kept for backwards compat during transition
     * @param {string|number} price - Price value (will be formatted)
     * @param {string} productTitle - DEPRECATED - kept for backwards compat during transition
     */
    updateSelectionText(category, key, displayTitle, price, productTitle) {
      const METHOD_SIGNATURE = 'UIUpdater.updateSelectionText';
      const selectionDisplay = category?.querySelector(
        '.murphy-door-configurator__category-user-selection'
      );
      if (!selectionDisplay) {
        isDev() &&
          console.warn(
            `${METHOD_SIGNATURE}: Selection display element not found`
          );
        return;
      }

      // ✅ FORMAT PRICE: Consistent price formatting (shared helper — P1)
      // zeroLabel matches the Summary sidebar's "(Included)" convention — a
      // literal "$0" on the category button (e.g. Door Handing, whose options
      // carry no price delta at all) reads as "is this broken?" next to every
      // other category's real dollar amount. Parenthesized so it never fuses
      // into a "No <option>" selection as a run-on sentence.
      const formattedPrice = formatConfiguratorPrice(price, {
        zeroLabel: '(Included)',
      });

      // ✅ PPD O(1) LOOKUP: Get variant with pre-extracted display formatting
      const ppd = getPPD();
      if (!ppd) {
        isDev() && console.error(`${METHOD_SIGNATURE}: PPD not available`);
        // Fallback to legacy behavior
        uiUpdater._renderSelectionSpec(
          selectionDisplay,
          displayTitle && formattedPrice
            ? `${displayTitle} | ${formattedPrice}`
            : displayTitle || formattedPrice || ''
        );
        return;
      }

      let variant = null;
      let product = null;

      // Parse key to get variant ID or product ID
      if (key.startsWith('variant_')) {
        // 🔥 FIX: Don't parseInt - variant IDs can be strings (synthetic CompositeProduct variants)
        // Example: "variant_generated_123456_0" → variantId = "generated_123456_0" (string)
        // Example: "variant_789012" → variantId = "789012" (string, but represents number)
        const variantId = key.substring('variant_'.length);

        // O(1) lookup: Get variant directly from PPD variant map
        // Map keys are stored as-is (string or number), so direct lookup works for both
        variant =
          ppd.variantIdToVariantMap.get(variantId) ||
          ppd.variantIdToVariantMap.get(parseInt(variantId));

        // Get parent product for context (if needed for fallback)
        if (variant) {
          product = ppd.variantIdToProductMap.get(variantId);
        }
      } else if (key.startsWith('product_')) {
        const productId = key.split('_')[1];
        product = ppd.products.get(productId); // O(1) product lookup

        // For single-variant products, resolve variant via PPD variantIdSet (O(1) Set lookup)
        // ✅ FIX: Was using non-existent product.variantIds property, causing ALL single-variant
        // products (e.g., "No Back Casing") to miss the PPD-first display path and fall through
        // to the fallback that duplicated product names in selectionDisplay.
        if (
          product &&
          product.variantIdSet &&
          product.variantIdSet.size === 1
        ) {
          const singleVariantId = product.variantIdSet.values().next().value;
          variant = ppd.variantIdToVariantMap.get(singleVariantId);
        }
      }

      // ✅ USE PRE-FORMATTED DISPLAY: From PPD displayFormatting
      if (variant?.displayFormatting) {
        // ⚡ PPD-FIRST: Unified display path for ALL product types (zero runtime branching)
        // Layer 1 pre-computes selectionDisplay correctly for both single and multi-variant:
        //   Single-variant (Default Title): "Product Name"
        //   Multi-variant / named-variant:  "Product | Material | Finish"
        const baseDisplay =
          variant.displayFormatting.selectionDisplay ||
          variant.displayFormatting.fullDisplay ||
          'Unknown Selection';

        // Free delivery products ($0.00): Show "Shipping Fee Calculated at Checkout" instead of "$0.00"
        // Rush fee products: Append " + Shipping Fee Calculated at Checkout" after the price.
        // The $0 on free delivery means "no rush surcharge" — actual shipping is calculated at Shopify checkout.
        const isDeliveryProduct =
          variant.displayFormatting.isDeliveryCategory === true;
        const isFreeDelivery =
          isDeliveryProduct && parseFloat(price || 0) === 0;
        const isRushFeeProduct =
          variant.displayFormatting.isRushFeeProduct === true;

        let effectivePrice;
        if (isFreeDelivery) {
          effectivePrice = 'Shipping Fee Calculated at Checkout';
        } else if (isRushFeeProduct && formattedPrice) {
          effectivePrice = `${formattedPrice} + Shipping Fee Calculated at Checkout`;
        } else {
          effectivePrice = formattedPrice;
        }

        const displayText = effectivePrice
          ? `${baseDisplay} | ${effectivePrice}`
          : baseDisplay;

        uiUpdater._renderSelectionSpec(selectionDisplay, displayText);

        isDev() &&
          console.log(`${METHOD_SIGNATURE}: PPD-first display`, {
            key,
            selectionDisplay: variant.displayFormatting.selectionDisplay,
            price: formattedPrice,
            finalDisplay: displayText,
          });
        return;
      }

      // ⚠️ FALLBACK: PPD data missing displayFormatting (shouldn't happen after Layer 1 fix)
      isDev() &&
        console.warn(
          `${METHOD_SIGNATURE}: displayFormatting missing from variant ${variant?.id || 'unknown'} - using fallback`
        );

      // Use product display_name (guaranteed to exist from Layer 1 normalization)
      const productName = product?.display_name || '';
      // ⚡ FIX: Prioritize displayTitle (cleaned in selectionDisplayDataMap) over raw variant.title
      const variantName = displayTitle || variant?.title || '';

      const displayText =
        productName && variantName && formattedPrice
          ? `${productName} | ${variantName} | ${formattedPrice}`
          : productName && variantName
            ? `${productName} | ${variantName}`
            : variantName || productName;

      uiUpdater._renderSelectionSpec(selectionDisplay, displayText);
    },

    /**
     * Updates shipping time display with fade animation
     * Uses DRY animateValueChange() for consistent 300ms fade matching price/PTS updates
     *
     * @param {string} formattedTime - Pre-formatted shipping time text (e.g., "1 - 2 Weeks")
     * @param {{ low: number, high: number }|null} rawTimeRange - Raw range from shippingTime.range;
     *   passed through to _computeRushShippingTime to avoid fragile string re-parsing.
     * @param {{ low: number, high: number }|null} [transitDays] - Carrier transit in
     *   business days (shippingTime.transitDays); added to the arrival dates.
     */
    updateShippingTime(formattedTime, rawTimeRange = null, transitDays) {
      // Store the latest value SYNCHRONOUSLY so patchDeliveryThumbnailCaptions()
      // can read it immediately, even before the 300ms fade animation updates
      // the DOM span. This is the root fix for the stale "1 - 3 Weeks" bug.
      if (formattedTime) {
        _latestShippingTime = formattedTime;
      }
      // Store the raw range so _computeRushShippingTime operates on structured data
      // instead of parsing the formatted text string (avoids format-fragility).
      if (rawTimeRange) {
        _latestRawShippingTime = rawTimeRange;
      }
      // Carrier transit (business days) for "Estimated arrival". Kept from the
      // last payload that carried it, so a payload without it never downgrades
      // an arrival date to a ship date mid-session.
      if (transitDays) {
        _latestTransitDays = transitDays;
      }

      const shippingElements = DOM.getShippingTimeElements();

      if (shippingElements && shippingElements.length > 0 && formattedTime) {
        const applyContent = () => {
          // XSS-SAFE: Use textContent instead of innerHTML (Pattern #17)
          // Update ALL shipping time elements (both gallery and sidebar versions)
          shippingElements.forEach((element) => {
            // Compact format for narrow badge contexts (e.g., mobile gallery badge)
            element.textContent =
              element.dataset.shippingFormat === 'compact'
                ? _compactShippingTime(formattedTime)
                : formattedTime;
          });
        };

        // DRY PATTERN: Use shared animateValueChange for consistent fade animation
        // matching price and PTS update behavior (300ms fade-out/update/fade-in)
        Animations.animateValueChange(
          'shipping',
          Array.from(shippingElements),
          applyContent,
          undefined,
          {
            // Shipping elements can display compact or full format per data-shipping-format attribute.
            // Use hasChanged to check each element's formatted text individually.
            hasChanged: () =>
              Array.from(shippingElements).some((el) => {
                const newText =
                  el.dataset.shippingFormat === 'compact'
                    ? _compactShippingTime(formattedTime)
                    : formattedTime;
                return el.textContent !== newText;
              }),
          }
        );
      }

      // Sync Delivery category thumbnail caption with updated shipping time.
      // The caption is stamped with data-is-free-delivery="true" at render time so we
      // can target it without a category-name string search.
      // Format mirrors initial render: "{formattedTime} | Shipping Fee Calculated at Checkout"
      if (formattedTime) {
        const deliveryThumbnails = document.querySelectorAll(
          '[data-is-free-delivery="true"]'
        );
        if (deliveryThumbnails.length > 0) {
          const newCaption = _getDeliveryCaption(formattedTime);
          deliveryThumbnails.forEach((thumbnail) => {
            const caption = thumbnail.querySelector(
              '.murphy-door-configurator__thumbnail-caption'
            );
            if (caption) caption.textContent = newCaption;
            const img = thumbnail.querySelector('img');
            if (img) img.setAttribute('alt', newCaption);
          });
        }
      }

      // P3: populate every concrete-date delivery estimate (sticky bar, Summary
      // trust bar, desktop sidebar) with "Estimated arrival <date> – <date>"
      // = today + production window + carrier transit business days.
      if (formattedTime) {
        const dateCaption = _getDeliveryDateRangeCaption(
          formattedTime,
          undefined,
          _latestTransitDays
        );
        if (dateCaption) {
          document
            .querySelectorAll('.murphy-door-configurator__delivery-estimate')
            .forEach((el) => {
              el.textContent = dateCaption;
            });
        }
      }

      // Sync Rush Delivery category thumbnail caption with computed rush shipping time.
      // Rush time = base production time reduced by 1 week per bound.
      // Mirrors the free-delivery live-patch pattern above.
      if (formattedTime) {
        const rushResult = _computeRushShippingTime(
          rawTimeRange || _latestRawShippingTime
        );
        _latestRushShippingTime = rushResult.text;

        const rushThumbnails = document.querySelectorAll(
          '[data-is-rush-delivery="true"]'
        );

        if (rushResult.hidden) {
          // Rush fee is not applicable for this production time - hide + auto-deselect
          if (rushThumbnails.length > 0) {
            rushThumbnails.forEach((thumbnail) => {
              thumbnail.style.display = 'none';
            });

            // CCP-compliant auto-deselect: Publish event for conductor to handle
            // The UI module MUST NOT directly mutate selection state
            publishEvent(CONSTANTS.EVENTS.UI_RUSH_DELIVERY_DESELECT_REQUESTED, {
              reason: 'rush_fee_hidden_boundary_crossed',
              baseShippingTime: formattedTime,
              timestamp: Date.now(),
            });
          }
        } else if (rushResult.text) {
          // Rush fee is valid - update caption and ensure visible
          const newRushCaption = _getRushDeliveryCaption(rushResult.text);
          rushThumbnails.forEach((thumbnail) => {
            // Unhide if previously hidden (boundary crossed back to valid)
            if (thumbnail.style.display === 'none') {
              thumbnail.style.display = '';
            }
            const caption = thumbnail.querySelector(
              '.murphy-door-configurator__thumbnail-caption'
            );
            if (caption) caption.textContent = newRushCaption;
            const img = thumbnail.querySelector('img');
            if (img) img.setAttribute('alt', newRushCaption);
          });
        }
      }
    },

    /**
     * Updates pass-through space display(s) with calculated space data
     * Formats and displays space calculations in all matching display elements
     * Supports both legacy single-element and modern multi-instance targeting
     *
     * @param {Object} spaceData - Space calculation data from space calculator
     * @param {number} spaceData.width - Width in inches
     * @param {number} spaceData.depth - Depth in inches
     * @param {string} spaceData.handling - Door handling (left/right)
     * @param {boolean} spaceData.isVisible - Whether to show the display
     * @param {string} spaceData.targetCategory - Optional category to target specific displays
     */
    updatePassThroughSpaceDisplay(spaceData) {
      isDev() &&
        console.log('🎨 UI UPDATE: pass-through space display', spaceData);

      try {
        // Get target elements - either specific category or all instances
        let spaceElements;
        if (spaceData && spaceData.targetCategory) {
          spaceElements = DOM.getPassThroughSpaceElementsByCategory(
            spaceData.targetCategory
          );
          isDev() &&
            console.log(
              `🎯 TARGETING: category "${spaceData.targetCategory}" - found ${spaceElements.length} elements`
            );
        } else {
          // Update all instances for backward compatibility
          spaceElements = DOM.getAllPassThroughSpaceElements();
          // Also include legacy element if it exists
          const legacyElement = DOM.getPassThroughSpaceElement();
          if (
            legacyElement &&
            !Array.from(spaceElements).includes(legacyElement)
          ) {
            spaceElements = [...spaceElements, legacyElement];
          }
          isDev() &&
            console.log(
              `🔄 UPDATING ALL: found ${spaceElements.length} space display elements`
            );
        }

        if (!spaceElements || spaceElements.length === 0) {
          isDev() &&
            console.warn('No pass-through space elements found for update');
          return;
        }

        // Determine display content using XSS-safe textContent (Pattern #17)
        // All values are pre-calculated numbers from space-calculator BL, not user input
        let displayText = '';
        if (!spaceData || !spaceData.isVisible) {
          displayText = 'PTS';
          isDev() &&
            console.log(
              '📱 PROGRESSIVE: space display hidden - insufficient data'
            );
        } else if (spaceData.width && spaceData.depth) {
          // Both dimensions available - show full opening dimensions
          displayText = `${spaceData.width}" x ${spaceData.depth}" opening`;
        } else if (spaceData.width) {
          // Only width available - show just the number with in. unit
          displayText = `${spaceData.width} in.`;
        } else {
          // Fallback - not enough data
          displayText = 'PTS';
        }

        // DRY PATTERN: Use shared animateValueChange for consistent fade animation
        // matching price update behavior. Only animate when showing a calculated value
        // (not when resetting to placeholder "PTS").
        const elementsArray = Array.from(spaceElements);
        const isCalculatedValue =
          spaceData && spaceData.isVisible && spaceData.width;

        // Once a real measurement replaces the "PTS" placeholder, the number
        // needs naming -- a bare `24" x 8" opening` does not say what it
        // measures. The caption is a ::after fed by data-pts-label, NOT a child
        // element, and that is load-bearing.
        //
        // 🚨 It WAS a child <span class="...__category-user-selection">, reusing
        // that class to inherit its typography for free. That silently broke
        // DOM.getSelectionDisplay(categoryId), which is
        // querySelector('[data-category="X"] .__category-user-selection') -- the
        // PTS readout sits inside the category card, so once a measurement
        // rendered, the caption span became the FIRST match and every consumer
        // got the caption instead of the category's real selection line.
        // Caught live on staging 2026-09-01: getSelectionDisplay('Door Depth')
        // returned "pass through space" instead of
        // "Door Depth | 12\" Door | 10-1/4\" Shelf | $250".
        //
        // ::after cannot be matched by querySelector, so the collision is
        // structurally impossible rather than merely fixed. It also inherits
        // the parent's colour in every card state, and leaves textContent equal
        // to the value alone -- which is what the animation dirty-check below
        // compares against.
        const labelText = isCalculatedValue ? PTS_VALUE_LABEL : '';

        const applyContent = () => {
          elementsArray.forEach((element, index) => {
            if (element) {
              // XSS-SAFE: Use textContent instead of innerHTML (Pattern #17)
              element.textContent = displayText;
              if (labelText) element.setAttribute('data-pts-label', labelText);
              else element.removeAttribute('data-pts-label');
              isDev() &&
                console.log(
                  `🖥️ DOM MANIPULATION: space display element ${index + 1} updated`,
                  {
                    displayText,
                    element:
                      element.getAttribute('data-space-text-target') ||
                      'legacy',
                  }
                );
            }
          });
        };

        if (isCalculatedValue) {
          // Animate the value change with fade-out/update/fade-in (DRY with price)
          Animations.animateValueChange(
            'pts',
            elementsArray,
            applyContent,
            undefined,
            {
              // All PTS elements get the same displayText — homogeneous, use getNewContent.
              // The caption is a ::after, so it is not part of textContent.
              getNewContent: () => displayText,
            }
          );
        } else {
          // Reset to placeholder instantly (no animation needed for hiding)
          applyContent();
        }

        isDev() &&
          console.log('📱 PROGRESSIVE: space display updated', {
            elementsUpdated: spaceElements.length,
            isVisible: spaceData?.isVisible,
            width: spaceData?.width,
            depth: spaceData?.depth,
            handling: spaceData?.handling,
          });
      } catch (error) {
        isDev() &&
          console.error('Error updating pass-through space display:', error);
      }
    },

    /**
     * Displays an error message to the user
     * Can be used for both critical and non-critical errors
     *
     * @param {string} message - The error message to display
     * @param {boolean} isCritical - Whether this is a critical error that blocks further interaction
     * @returns {HTMLElement} - The created error element
     */
    showErrorMessage(message, isCritical = false) {
      // Create the error element
      const errorElement = DOM.createElement(
        'div',
        'murphy-door-configurator__error-message'
      );

      // Add appropriate classes
      DOM.addClass(errorElement, isCritical ? 'critical' : 'warning');
      DOM.addClass(errorElement, 'visible');

      // Set the message text
      DOM.setTextContent(errorElement, message);

      // Add to the DOM in the right location
      const wrapper =
        document.querySelector('.murphy-door-configurator-wrapper') ||
        document.body;
      wrapper.appendChild(errorElement);

      // Add animation
      DOM.setStyle(
        errorElement,
        'animation',
        'slideInDown 0.3s ease-out forwards'
      );

      // Auto-dismiss non-critical errors after 5 seconds
      if (!isCritical) {
        setTimeout(() => {
          DOM.setStyle(
            errorElement,
            'animation',
            'slideOutUp 0.3s ease-out forwards'
          );
          setTimeout(() => {
            if (errorElement.parentNode) {
              errorElement.parentNode.removeChild(errorElement);
            }
          }, 300);
        }, 5000);
      }

      return errorElement;
    },

    /**
     * Shows the loading spinner by removing the 'hidden' class.
     * Ensures proper accessibility attributes are set.
     *
     * @returns {HTMLElement|null} The spinner element if found, null otherwise
     */
    showLoadingSpinner(message) {
      try {
        // Target the configurator overlay specifically. There are many
        // `.loading__spinner` elements on a PDP (every product card has one),
        // so a bare `.loading__spinner` query returns the wrong node. The
        // configurator's full-viewport overlay is `.configurator-loading__spinner`.
        const spinner =
          document.querySelector('.configurator-loading__spinner') ||
          document.querySelector('.loading__spinner');
        if (spinner) {
          // The configurator overlay's visibility is governed by the
          // body.configurator-ready CSS rule
          // (`body.configurator-ready .configurator-loading__spinner { display:none !important }`),
          // NOT the .hidden class. Removing configurator-ready is what actually
          // reveals it; hideLoadingSpinner() re-adds it to hide.
          document.body.classList.remove('configurator-ready');

          spinner.classList.remove('hidden');

          // Optional context-specific message (e.g. the add-to-cart flow passes
          // "Adding your customized door to cart…"). When provided, overwrite the
          // rotating bootstrap phrase with this fixed message. The snippet's
          // phrase-rotation interval is already cleared once bootstrap completes
          // (cleanupPerformed=true), and Add to Cart is only reachable after the
          // config is complete, so this text sticks without a rotation fight.
          if (message) {
            const messageEl = spinner.querySelector(
              '.configurator-loading-message'
            );
            if (messageEl) {
              // Neutralize the rotation animation so the message doesn't flicker
              messageEl.style.animation = 'none';
              messageEl.textContent = message;
            }
          }

          // Update accessibility attributes
          spinner.setAttribute('aria-hidden', 'false');
          spinner.setAttribute('aria-busy', 'true');

          // Announce loading state for screen readers if not already present
          if (!spinner.hasAttribute('aria-live')) {
            spinner.setAttribute('aria-live', 'polite');
          }

          return spinner;
        }

        // Log warning if spinner not found
        isDev() && console.warn('Loading spinner element not found');
        return null;
      } catch (error) {
        isDev() && console.error('Error showing loading spinner:', error);
        return null;
      }
    },

    /**
     * Hides the configurator loading overlay.
     *
     * CRITICAL: the configurator overlay is hidden by the body.configurator-ready
     * CSS rule (display:none !important), NOT the .hidden class — so re-adding
     * configurator-ready is what actually dismisses it. Before the cart switched
     * to drawer mode this was masked: the configurator always redirected to /cart
     * after an add, and the page navigation tore down the overlay regardless. With
     * the drawer (no navigation) the overlay would otherwise stay up covering the
     * drawer. We both re-add the body class AND add .hidden for belt-and-suspenders.
     *
     * @returns {HTMLElement|null} The spinner element if found, null otherwise
     */
    hideLoadingSpinner() {
      try {
        // Re-add configurator-ready — the actual visibility gate for the overlay.
        document.body.classList.add('configurator-ready');

        const spinner =
          document.querySelector('.configurator-loading__spinner') ||
          document.querySelector('.loading__spinner');
        if (spinner) {
          spinner.classList.add('hidden');

          // Update accessibility attributes
          spinner.setAttribute('aria-hidden', 'true');
          spinner.setAttribute('aria-busy', 'false');

          return spinner;
        }

        // Log warning if spinner not found
        isDev() && console.warn('Loading spinner element not found');
        return null;
      } catch (error) {
        isDev() && console.error('Error hiding loading spinner:', error);
        return null;
      }
    },

    // 🚀 INSTAGRAM OPTIMIZATION: Show optimistic feedback immediately
    showOptimisticCartFeedback(message) {
      try {
        // Multi-target, same as updateSummaryStatus(): there are up to four
        // live Add to Cart copies (rail, mobile sticky bar, Traveling Preview,
        // the "Your door is ready!" badge). A single querySelector here put
        // the "Adding to cart..." swap on whichever copy came FIRST in the DOM,
        // so a customer who clicked any other copy saw no feedback on the
        // button under their thumb, and the copies they did not click stayed
        // enabled for a double add. Every copy now swaps and locks together.
        const addToCartButtons = document.querySelectorAll(
          SELECTORS.ADD_TO_CART
        );
        if (addToCartButtons.length) {
          addToCartButtons.forEach((addToCartButton) => {
            // Store original text if not already stored
            if (!addToCartButton.dataset.originalText) {
              addToCartButton.dataset.originalText =
                addToCartButton.textContent;
            }

            // Immediately update button to show feedback
            addToCartButton.textContent = message || 'Adding to cart...';
            addToCartButton.classList.add(
              'murphy-door-configurator__add-to-cart-button--loading'
            );
            addToCartButton.disabled = true;
          });

          isDev() &&
            console.log('🚀 UI UPDATER: Optimistic cart feedback shown');
        } else {
          isDev() &&
            console.warn('⚠️ UI UPDATER: Add to cart button not found');
        }
      } catch (error) {
        isDev() &&
          console.error(
            '❌ UI UPDATER: Error showing optimistic feedback:',
            error
          );
      }
    },

    // FIX 3: Restore button state after optimistic UI rollback
    restoreOptimisticCartFeedback() {
      try {
        // Mirror of showOptimisticCartFeedback(): every copy that was swapped
        // and locked is restored, not just the first one in the DOM.
        const addToCartButtons = document.querySelectorAll(
          SELECTORS.ADD_TO_CART
        );
        if (addToCartButtons.length) {
          addToCartButtons.forEach((addToCartButton) => {
            // Restore original text if stored
            if (addToCartButton.dataset.originalText) {
              addToCartButton.textContent =
                addToCartButton.dataset.originalText;
            }

            // Remove loading state
            addToCartButton.classList.remove(
              'murphy-door-configurator__add-to-cart-button--loading'
            );
            addToCartButton.disabled = false;
          });

          isDev() &&
            console.log('🚀 UI UPDATER: Optimistic cart feedback restored');
        } else {
          isDev() &&
            console.warn(
              '⚠️ UI UPDATER: Add to cart button not found for restore'
            );
        }
      } catch (error) {
        isDev() &&
          console.error(
            '❌ UI UPDATER: Error restoring optimistic feedback:',
            error
          );
      }
    },

    updateApplyButtonVisibility(category, show) {
      const applyButton = category?.querySelector(SELECTORS.APPLY_BUTTON);
      if (!applyButton) return;

      // SLIDER-ONLY GATE: Apply button is only relevant for slider categories
      // (Width, Height). Thumbnail-based categories use double-click-to-apply
      // instead, so the Apply button should never be shown for them.
      // Hide requests always proceed (cleanup path for applied state).
      if (show) {
        const categoryId = category?.getAttribute('data-category');
        const conductor = getConductor();
        if (
          conductor &&
          typeof conductor.shouldUseCategorySlider === 'function'
        ) {
          if (!conductor.shouldUseCategorySlider(categoryId)) {
            // Thumbnail category - suppress Apply button visibility
            return;
          }
        }
      }

      applyButton.style.display = show ? 'block' : 'none';
    },

    /**
     * Show or hide the preview indicator on a specific thumbnail
     * The preview indicator displays "PREVIEWING" inside the thumbnail caption
     * when the user enters BROWSING state (clicks a thumbnail to preview).
     *
     * @param {string} categoryId - Category ID
     * @param {string} productId - Product ID from event payload (used to find correct thumbnail)
     * @param {string} variantId - Variant ID from event payload (used to find correct thumbnail)
     * @param {boolean} show - True to show indicator, false to hide
     */
    updatePreviewIndicatorVisibility(categoryId, productId, variantId, show) {
      if (!categoryId) return;

      const PREVIEW_INDICATOR_CLASS =
        'murphy-door-configurator__preview-indicator';

      isDev() &&
        console.log(`[UIUpdater] updatePreviewIndicatorVisibility called:`, {
          categoryId,
          productId,
          variantId,
          show,
        });

      // 🔥 NUCLEAR OPTION: Clear ALL preview indicators across the ENTIRE configurator
      // This is more aggressive but ensures no orphaned badges anywhere
      const allIndicatorsEverywhere = document.querySelectorAll(
        `.${PREVIEW_INDICATOR_CLASS}`
      );
      isDev() &&
        console.log(
          `[UIUpdater] 🧹 CLEARING ${allIndicatorsEverywhere.length} preview indicators globally`
        );
      allIndicatorsEverywhere.forEach((indicator) => {
        isDev() && console.log(`[UIUpdater] Removing indicator:`, indicator);
        indicator.remove();
      });

      // If hiding, we're done (already cleared above)
      if (!show) return;

      // SIMPLIFIED 3Q: The active-selection border already communicates the choice;
      // the PREVIEWING badge is redundant and clutters the always-open tile layout.
      if (
        document.querySelector(
          `.murphy-door-configurator__category-button[data-simplified-visible="true"][data-category="${categoryId}"]`
        )
      ) {
        return;
      }

      // 🎯 FIX: Find thumbnail by productId/variantId from event payload (not by --active class)
      // This is more reliable because --active class might not be applied yet when event fires
      let targetThumbnail = null;

      if (variantId) {
        // Try to find by variant ID first (most specific)
        targetThumbnail = document.querySelector(
          `[data-category="${categoryId}"] .murphy-door-configurator__thumbnail-item[data-variant-id="${variantId}"]`
        );
      }

      if (!targetThumbnail && productId) {
        // Fallback to product ID (for single-variant products or composites)
        targetThumbnail = document.querySelector(
          `[data-category="${categoryId}"] .murphy-door-configurator__thumbnail-item[data-product-id="${productId}"]`
        );
      }

      isDev() &&
        console.log(`[UIUpdater] Target thumbnail found:`, !!targetThumbnail, {
          variantId,
          productId,
        });

      if (!targetThumbnail) {
        isDev() &&
          console.warn(
            '[UIUpdater] updatePreviewIndicatorVisibility: Thumbnail not found for:',
            { categoryId, productId, variantId }
          );
        return;
      }

      // Create the preview indicator element
      const previewIndicator = document.createElement('span');
      previewIndicator.className = PREVIEW_INDICATOR_CLASS;
      previewIndicator.textContent = 'PREVIEWING';

      // 🎯 UX FIX: Insert as FIRST child of thumbnail item (before thumbnail-image)
      // This ensures the badge stays on top regardless of stacking context issues
      targetThumbnail.insertBefore(
        previewIndicator,
        targetThumbnail.firstChild
      );

      isDev() &&
        console.log(`[UIUpdater] ✅ Preview indicator added to thumbnail`, {
          productId,
          variantId,
        });
    },

    resetCategoryStyle(category) {
      if (!category) return;

      // In classic/dual this means "the customer is browsing this category
      // again", and dropping SELECTED_CLOSED is how the card visibly reopens.
      // apo_style has no such transition -- every card is permanently open --
      // so the only thing this removal achieves there is deleting the
      // completed look off a card that still holds a confirmed selection.
      //
      // Live-reported by Daniel 2026-08-17 about Width/Height: "they are just
      // losing the style so it looks like we need to readjust". Measured: the
      // card kept CATEGORY_COMPLETED and its selection text but lost
      // SELECTED_CLOSED, so it went from navy back to pale while still being
      // answered. The three callers are all BROWSING-state paths, and a
      // material-filter re-render reaches them for slider categories, so this
      // fired on Width/Height whenever an unrelated component was added.
      //
      // Guarded here rather than at the three call sites so no future caller
      // can reintroduce it.
      const conductor = getConductor();
      const isApoStyle =
        conductor?._getConfiguratorExperience?.() === 'apo_style';
      if (
        isApoStyle &&
        category.classList.contains(CLASS_NAMES.CATEGORY_COMPLETED)
      ) {
        category.classList.add(CLASS_NAMES.CATEGORY_OPEN);
        return;
      }

      category.classList.remove(CLASS_NAMES.SELECTED_CLOSED);
      category.classList.add(CLASS_NAMES.CATEGORY_OPEN);
    },

    getDefaultDisplayTitle(category) {
      return (
        category?.querySelector('.murphy-door-configurator__thumbnail-caption')
          ?.textContent || ''
      );
    },

    /* Material Display Methods */
    updateMaterialDisplay(categoryId, filterResult) {
      // Scoped -- see DOM.getCategoryElement's comment (this file).
      const category = document.querySelector(
        `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
      );
      if (!category) return;

      switch (filterResult.mode) {
        case 'STANDARD':
          this.resetMaterialDisplay(category);
          break;

        case 'WOOD_FINISH_FILTERED':
          this.updateFinishOptions(category, filterResult);
          break;

        case 'DIRECT_VARIANT':
          this.showDirectVariant(category, filterResult.variantId);
          break;
      }

      // Update selection indicators
      this.updateSelectionIndicators(category);
    },

    resetMaterialDisplay(category) {
      // Show all options
      category.querySelectorAll('.material-option').forEach((option) => {
        option.classList.remove('hidden', 'disabled', 'material-filtered-out');
        option.removeAttribute('data-filtered');
      });

      // Reset selection states
      category.querySelectorAll('.material-selected').forEach((selected) => {
        selected.classList.remove('material-selected');
      });
    },

    updateFinishOptions(category, filterResult) {
      const finishOptions = category.querySelectorAll('[data-finish-sku]');

      finishOptions.forEach((option) => {
        const finishSku = option.getAttribute('data-finish-sku');
        const isCompatible = filterResult.compatibleFinishes.has(finishSku);

        option.classList.toggle('hidden', !isCompatible);
        option.classList.toggle('disabled', !isCompatible);
        option.classList.toggle('material-filtered-out', !isCompatible);
        option.setAttribute('data-filtered', !isCompatible);

        // Update aria attributes for accessibility
        option.setAttribute('aria-hidden', !isCompatible);
        if (!isCompatible) {
          option.setAttribute('aria-disabled', 'true');
        } else {
          option.removeAttribute('aria-disabled');
        }
      });

      // Mark wood selection
      const woodOption = category.querySelector(
        `[data-wood-sku="${filterResult.woodSku}"]`
      );
      if (woodOption) {
        woodOption.classList.add('material-selected');
      }
    },

    showDirectVariant(category, variantId) {
      // Hide all options during transition
      category.querySelectorAll('.material-option').forEach((option) => {
        option.classList.add('hidden');
      });

      // Show loading state
      this.showLoadingState(category, true);

      // Trigger variant display after brief delay for animation
      setTimeout(() => {
        // 🔥 MAJOR CCP VIOLATION FIX: Use event-based communication instead of direct state change call
        if (window.MurphyDoorConfigurator.CoreEventBus) {
          window.MurphyDoorConfigurator.CoreEventBus.publish(
            CONSTANTS.EVENTS.STATE_CHANGE_REQUESTED,
            {
              action: 'DIRECT_VARIANT',
              data: {
                categoryId: category.getAttribute('data-category'),
                variantId: variantId,
              },
              timestamp: Date.now(),
              source: 'ui-updater.directVariantApplication',
            }
          );
        } else {
          // CCP: Event bus is Phase 1 infrastructure - should always be available
          isDev() &&
            console.error(
              '[UIUpdater] CRITICAL: Event bus unavailable for direct variant application. ' +
                'This should never happen - event bus is Phase 1 infrastructure.'
            );
        }
        this.showLoadingState(category, false);
      }, 300);
    },

    updateSelectionIndicators(category) {
      const categoryId = category.getAttribute('data-category');

      // 🎯 PRIORITY 3 FIX: Event-driven material state request (CCP compliant)
      // Request fresh state - cache will be updated asynchronously via event subscription
      window.MurphyDoorConfigurator.CoreEventBus.publish(
        CONSTANTS.EVENTS.STATE_MATERIAL_STATE_REQUESTED,
        {
          requestId: `ui_updater_indicators_${Date.now()}`,
          categoryId,
          timestamp: Date.now(),
          source: 'ui-updater.updateSelectionIndicators',
        }
      );

      // Use cached state for synchronous UI decision (cache updated via event subscription)
      const cachedState = materialStateCache.get(categoryId);
      const materialState = cachedState?.state || null;

      if (
        !materialState ||
        (!materialState.woodSku && !materialState.finishSku)
      ) {
        return;
      }

      // Material relationship indicators are being refactored. Skip updating old indicators for now.
      // We can revisit this if the new system reuses the same indicators.
      // Leaving the original logic commented out for now:
      /*
      // Update wood selection
      if (materialState.woodSku) {
        const woodOption = category.querySelector(
          `[data-wood-sku="${materialState.woodSku}"]`
        );
        if (woodOption) {
          woodOption.classList.add('material-selected');
        }
      }

      // Update finish selection
      if (materialState.finishSku) {
        const finishOption = category.querySelector(
          `[data-finish-sku="${materialState.finishSku}"]`
        );
        if (finishOption) {
          finishOption.classList.add('material-selected');
        }
      }
      */
    },

    showLoadingState(category, isLoading) {
      const loadingIndicator = category.querySelector('.loading-indicator');
      if (loadingIndicator) {
        loadingIndicator.classList.toggle('hidden', !isLoading);
      }
    },
  };

  // UI Updater Module initialized with DOM access

  /**
   * 🎯 MATERIAL FILTER STATE HANDLER: Re-renders category thumbnails when material preferences change
   *
   * Called when MATERIAL_FILTER_STATE_UPDATED fires from material-preference-coordinator.
   * Iterates over all affected categories and re-renders their thumbnail grids so that
   * incompatible variants get the `material-filtered-out` CSS class (greyed-out appearance)
   * and compatible variants remain fully interactive.
   *
   * Also updates any open variant browsers within affected categories for full visual consistency.
   *
   * Architecture: Event-driven pub/sub -> this handler -> renderCategoryThumbnails() ->
   *   generateSmartSortedThumbnails() -> applyMaterialFilterClasses() -> applyBubbleUpTransformations() -> DOM update
   *
   * BUBBLE-UP FEATURE: When material preferences narrow a multi-variant product to exactly
   * 1 compatible variant, the product thumbnail morphs to show the variant's image/title.
   * Clicking the bubbled thumbnail enters BROWSING state with the pre-resolved variant,
   * skipping the product selection step. The data-bubbled="true" attribute signals this
   * to the click handler in event-handler.js.
   *
   * @param {Object} eventData - Event payload from material-preference-coordinator
   * @param {Map} eventData.categoryFilters - Map<categoryId, filterState> with compatible/incompatible variant lists
   * @param {Object} eventData.preferences - Current material preferences { woodApplied, finishApplied, ... }
   * @param {boolean} eventData.simplifiedUxEnabled - Whether simplified UX mode is active
   * @param {boolean} eventData.hasBubbleUpVariants - Whether any category has bubbled-up variants
   * @param {number} eventData.timestamp - Event timestamp
   */
  uiUpdater.handleMaterialFilterStateUpdated = function (eventData) {
    const METHOD_SIGNATURE = 'UIUpdater.handleMaterialFilterStateUpdated';
    const startTime = performance.now();

    try {
      const { categoryFilters, preferences, simplifiedUxEnabled } = eventData;

      if (
        !categoryFilters ||
        !(categoryFilters instanceof Map) ||
        categoryFilters.size === 0
      ) {
        isDev() &&
          console.warn(
            `${METHOD_SIGNATURE}: No category filters in event payload - skipping`
          );
        return;
      }

      // STAGE 1: START - Publish lifecycle event
      publishEvent(CONSTANTS.EVENTS.UI_MATERIAL_FILTER_RERENDER_START, {
        categoryCount: categoryFilters.size,
        preferences,
        simplifiedUxEnabled,
        timestamp: Date.now(),
      });

      let categoriesRerendered = 0;
      let variantBrowsersUpdated = 0;

      // STAGE 2: PROGRESS - Re-render each affected category's thumbnails
      categoryFilters.forEach((filterState, categoryId) => {
        try {
          // RACE CONDITION FIX: Skip categories that are locked by a recent unapply.
          // When a category is unapplied and opened in browsing mode, handleUserUnapply()
          // sets default HTML and adds the categoryId to _unapplyBrowsingLockedCategories.
          // Stale MATERIAL_FILTER_STATE_UPDATED events (from intermediate preference states
          // or re-asserted preferences from downstream categories) must NOT overwrite
          // the default browsing HTML. The lock is cleared when the user makes a new
          // selection or a new material preference is explicitly applied.
          if (_unapplyBrowsingLockedCategories.has(categoryId)) {
            isDev() &&
              console.log(
                `${METHOD_SIGNATURE}: Skipping locked category "${categoryId}" (unapply browsing lock active)`
              );
            return;
          }

          // WRONG-RENDERER GUARD. Everything below re-renders via
          // generateSmartSortedThumbnails(), which emits PRODUCT-level
          // thumbnails. That is correct for MIX / MULTI_VARIANT_ONLY, but
          // wrong for a LONELY_MULTI_VARIANT category, whose real options are
          // its VARIANTS -- one composite product with, say, 62 knob variants
          // renders as a single unselectable tile labelled with the category
          // name. It is equally wrong for a slider category, which needs
          // renderSlider().
          //
          // Traced live 2026-08-17 with an innerHTML interceptor: applying a
          // cabinet door fired this handler six times, and each pass
          // overwrote Top Knobs & Pulls' correct 62-variant grid with the
          // 2537-char product-level HTML. The last write won, so the card
          // reverted to one dead tile -- Daniel: "the knobs and pulls arent
          // showing again". showCategoryContent() was being called correctly
          // and its work was simply being clobbered afterwards.
          //
          // showCategoryContent() is the displayMode-aware renderer and reads
          // the same updated filter state, so routing through it keeps the
          // material filtering this loop exists to apply.
          //
          // Scoped to apo_style deliberately. The bug is real in every mode,
          // but classic/dual only ever have one card open at a time so a
          // LONELY category is rarely open when this fires, and this is a
          // shared function on the material-change path -- widening it needs
          // its own change and its own classic UAT rather than riding along.
          const _apoConductor = getConductor();
          if (_apoConductor?._getConfiguratorExperience?.() === 'apo_style') {
            const _dm = getPPD()?.categoryDisplayModeMap?.get(categoryId);
            const _isSlider =
              typeof _apoConductor.shouldUseCategorySlider === 'function' &&
              _apoConductor.shouldUseCategorySlider(categoryId);
            if (_dm?.displayMode === 'LONELY_MULTI_VARIANT' || _isSlider) {
              try {
                uiUpdater.showCategoryContent(categoryId);
                categoriesRerendered++;
              } catch (renderErr) {
                isDev() &&
                  console.error(
                    `${METHOD_SIGNATURE}: displayMode-aware re-render failed for "${categoryId}"`,
                    renderErr
                  );
              }
              return;
            }
          }

          // Find the thumbnails container for this category in the DOM
          const categoryButton = DOM.getElement(
            `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
          );

          if (!categoryButton) {
            // Category not in DOM (e.g., on a different tab) - skip silently
            return;
          }

          const thumbnailsContainer = categoryButton.querySelector(
            SELECTORS.THUMBNAILS
          );

          if (!thumbnailsContainer) {
            // No thumbnails container - skip (category may not have been opened yet)
            return;
          }

          // Re-render thumbnails using the existing PPD-first pipeline
          // generateSmartSortedThumbnails internally calls applyMaterialFilterClasses
          // which reads the UPDATED filter state from conductor.getMaterialFilterState()
          //
          // BUBBLE-UP ENHANCEMENT: After generating base HTML, morph product thumbnails
          // into variant thumbnails when material preferences narrow a product to exactly
          // 1 compatible variant. Replaces product image/title with variant data and adds
          // data-bubbled="true" attribute for click handler detection (skips product step).
          const baseHTML = uiUpdater.generateSmartSortedThumbnails(categoryId);
          const thumbnailsHTML = filterState.bubbleUpVariants
            ? applyBubbleUpTransformations(
                baseHTML,
                filterState.bubbleUpVariants
              )
            : baseHTML;
          // Pattern #17: XSS audit trail - PPD pre-sanitized HTML
          thumbnailsContainer.innerHTML = sanitizeHTML(thumbnailsHTML);
          categoriesRerendered++;

          // Also update any open variant browsers within this category
          const variantGrid = categoryButton.querySelector(
            SELECTORS.VARIANT_GRID
          );
          if (variantGrid) {
            const variantView = variantGrid.closest(SELECTORS.VARIANT_VIEW);
            if (variantView) {
              const productThumbnail = variantView.querySelector(
                `${SELECTORS.SELECTED_PRODUCT} [data-product-id]`
              );
              if (productThumbnail && productThumbnail.dataset.productId) {
                const productId = productThumbnail.dataset.productId;
                uiUpdater.renderVariantsWithMaterialAwareness(
                  productId,
                  categoryId
                );
                variantBrowsersUpdated++;
              } else {
                isDev() &&
                  console.warn(
                    `${METHOD_SIGNATURE}: Could not identify product for open variant browser in category ${categoryId}.`
                  );
              }
            }
          }
        } catch (categoryError) {
          isDev() &&
            console.error(
              `${METHOD_SIGNATURE}: Error re-rendering category ${categoryId}:`,
              categoryError
            );
          // Continue processing other categories - don't let one failure stop the rest
        }
      });

      // ⚠️ THE LOOP ABOVE DESTROYS THE APPLIED CUES. REPAINT THEM HERE.
      //
      // Every branch of it replaces a category's tiles wholesale, and BOTH
      // applied cues -- the --active ring (animated border + glow) and the
      // --applied outline -- live only on those tile NODES. Neither is
      // derived from state at paint time, so a rewrite silently erases them.
      //
      // Measured on a ?config= restore 2026-09-04 (apo_style + click-apply):
      // this handler fires 18x during one load and rewrites 234 grids. Of the
      // 51 tiles the load-time ring sync had marked, 47 were DETACHED from the
      // document by a later pass of this loop; zero had the class removed. The
      // element was thrown away, not un-styled -- which is why no listener on
      // class mutation or on _setTileActive ever saw it happen.
      //
      // The visible result was that the applied styling looked random: 4 of 20
      // completed categories kept it, and which 4 was decided purely by which
      // cards happened to be re-rendered last. Ryan, UAT 2026-09-04:
      // "sometimes I see this UI and sometimes I don't... Between some
      // categories, it's not present until another category also has been
      // applied." That last part is the same bug from the other side --
      // applying anything routes through updateSelectionDisplay, which calls
      // syncAppliedTileRings and incidentally repairs every OTHER card.
      //
      // 🚨 DO NOT "FIX" THIS BY EXTENDING THE LOAD-TIME RING SYNC INSTEAD.
      // That subscription (SUPPORT_PROGRESS_CALCULATION_COMPLETED, capped at
      // 12 attempts) cannot win this race and raising the cap does not help:
      // its last attempt landed at t=986ms while the final grid rewrites ran
      // at t=1150ms. The repaint has to be anchored to the thing that erases
      // the cue, not to a timer running alongside it.
      //
      // Both calls are safe to repeat. syncAppliedTileRings is additive-only
      // and no-ops outside the multi-open experiences.
      //
      // 🚨 THE TWO CUES MUST AGREE ON BROWSING PRECEDENCE, so the outline loop
      // below re-implements syncAppliedTileRings' browsing yield (L3147).
      // Without it the ring skips a category the customer is actively
      // browsing while _markAppliedTileInGrid still paints the SAVED answer,
      // leaving one card with its ring on the tile being explored and its
      // outline on a different tile. Reachable in normal use: the conductor
      // lets a forbidden option enter BROWSING without applying it
      // (`browsingIsForbidden`, md-config-core-conductor.js), and click=apply
      // deliberately refuses to auto-apply it -- so a category can sit
      // browsing-but-not-applied indefinitely while this handler fires.
      //
      // NOTE the outline is NOT gated on experience, and that is deliberate:
      // it is a click=apply (swap-in-place) cue, and showCategoryContent's
      // own hook gates it on _isClickApplyEnabled() alone. Matching that gate
      // keeps the two call sites from drifting. Only the RING is confined to
      // the multi-open experiences.
      // 🚨 THIS SWEEPS EVERY APPLIED CATEGORY ON PURPOSE. DO NOT "OPTIMISE"
      // IT DOWN TO THE CATEGORIES THIS PASS REWROTE -- that was tried, and it
      // is WRONG for a reason not visible from inside this function.
      //
      // The scoping argument is seductive: only a grid this loop rewrote can
      // have lost its cue, so walking the rest is waste. It is false because
      // THIS LOOP IS NOT THE ONLY THING THAT REWRITES A GRID. Measured on the
      // same restore, bucketed by innerHTML writer: Wood & Finish took 4
      // writes and NONE came from here (they came from showCategoryContent ->
      // showProductThumbnails), while its siblings took 19-21 writes mostly
      // from this loop. Scoped to the rewritten set, Wood & Finish is never
      // repainted and lands cue-less -- measured 17/18 categories cued with
      // the scoping vs 18/18 without it, back-to-back on the same URL.
      //
      // The sweep costs ~20 categories of querySelector + classList work per
      // firing, and it does NOT force layout: neither callee reads geometry
      // (no offsetWidth / getBoundingClientRect / getComputedStyle), so it is
      // style invalidation the browser resolves lazily, inside a handler whose
      // dominant cost is the innerHTML parse and subtree allocation above it.
      uiUpdater.repaintAppliedCues(METHOD_SIGNATURE);

      // STAGE 3: SUCCESS - Performance tracking and completion event
      const duration = performance.now() - startTime;

      publishEvent(CONSTANTS.EVENTS.UI_MATERIAL_FILTER_RERENDER_COMPLETE, {
        categoriesRerendered,
        variantBrowsersUpdated,
        totalCategories: categoryFilters.size,
        duration,
        timestamp: Date.now(),
      });

      // Performance threshold check
      if (duration > PERFORMANCE_THRESHOLDS.UI_UPDATE) {
        publishEvent(CONSTANTS.EVENTS.PERFORMANCE_SLOW_UI_OPERATION, {
          operation: 'material_filter_rerender',
          duration,
          threshold: PERFORMANCE_THRESHOLDS.UI_UPDATE,
          severity:
            duration > PERFORMANCE_THRESHOLDS.UI_UPDATE * 2
              ? 'critical'
              : 'warning',
          categoriesRerendered,
        });
      }

      isDev() &&
        console.log(
          `🎨 ${METHOD_SIGNATURE}: Re-rendered ${categoriesRerendered}/${categoryFilters.size} categories ` +
            `and ${variantBrowsersUpdated} variant browsers in ${duration.toFixed(1)}ms`
        );
    } catch (error) {
      const duration = performance.now() - startTime;

      isDev() &&
        console.error(
          `${METHOD_SIGNATURE}: Fatal error during material filter rerender:`,
          error
        );

      // STAGE 5: FAILED - Publish failed event with frozen payload
      publishEvent(CONSTANTS.EVENTS.UI_MATERIAL_FILTER_RERENDER_FAILED, {
        error: error.message,
        duration,
        timestamp: Date.now(),
      });
    }
  };

  // ============================================================================
  // HANDLER: SELECTION_APPLIED → Update category selection display text
  // Constructs the PPD lookup key from event payload and delegates to
  // uiUpdater.updateSelectionText() for O(1) PPD-first display rendering.
  // ============================================================================
  function handleSelectionAppliedForDisplay(eventData) {
    const METHOD_SIGNATURE = 'UIUpdater.handleSelectionAppliedForDisplay';
    const startTime = performance.now();

    try {
      const {
        categoryId,
        productId,
        variantId,
        displayTitle,
        price,
        productTitle,
      } = eventData;

      // Guard: Must have at minimum categoryId and one of productId/variantId
      if (!categoryId || (!productId && !variantId)) {
        isDev() &&
          console.warn(
            `${METHOD_SIGNATURE}: Incomplete event data - skipping`,
            { categoryId, productId, variantId }
          );
        return;
      }

      // Construct the PPD lookup key (same format as state-manager line 1230)
      const key = variantId ? `variant_${variantId}` : `product_${productId}`;

      // Find category button via data-category attribute
      const categoryButton = document.querySelector(
        `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
      );

      if (!categoryButton) {
        isDev() &&
          console.warn(
            `${METHOD_SIGNATURE}: Category button not found for category "${categoryId}"`
          );
        return;
      }

      // Delegate to updateSelectionText for PPD-first O(1) display rendering (category button)
      uiUpdater.updateSelectionText(
        categoryButton,
        key,
        displayTitle,
        price,
        productTitle
      );

      // ALSO update the Summary tab list (CCP Golden Standard: single event triggers all necessary UI updates)
      uiUpdater.updateSummaryList(
        categoryId,
        displayTitle,
        productTitle,
        price
      );

      const duration = performance.now() - startTime;
      isDev() &&
        console.log(
          `${METHOD_SIGNATURE}: Updated selection display for category "${categoryId}" (key: ${key}) in ${duration.toFixed(1)}ms`
        );

      // Performance threshold check
      if (duration > PERFORMANCE_THRESHOLDS.UI_UPDATE) {
        publishEvent(CONSTANTS.EVENTS.PERFORMANCE_SLOW_UI_OPERATION, {
          operation: 'selection_applied_display_update',
          duration,
          threshold: PERFORMANCE_THRESHOLDS.UI_UPDATE,
          severity:
            duration > PERFORMANCE_THRESHOLDS.UI_UPDATE * 2
              ? 'critical'
              : 'warning',
          categoryId,
        });
      }
    } catch (error) {
      isDev() &&
        console.error(
          `${METHOD_SIGNATURE}: Error updating selection display:`,
          error
        );

      // CCP GOLDEN STANDARD: Publish failed event with frozen payload
      publishEvent(
        CONSTANTS.EVENTS.UI_SELECTION_APPLIED_DISPLAY_UPDATE_FAILED,
        {
          error: error.message,
          categoryId: eventData?.categoryId,
          timestamp: Date.now(),
        }
      );
    }
  }

  // 🎯 CCP GOLDEN STANDARD: Subscribe to loading state events from conductor
  // Track cleanup handler for proper resource management
  if (window.MurphyDoorConfigurator?.CoreEventBus) {
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_LOADING_STATE_CHANGED,
        (eventData) => {
          if (eventData.state === 'loading') {
            uiUpdater.showLoadingSpinner();
          } else if (eventData.state === 'ready') {
            uiUpdater.hideLoadingSpinner();
          }
        }
      )
    );

    // D1: reflect the active dual-mode on the wrapper via data-configurator-mode
    // so CSS can toggle tab visibility (curated: My Door + Summary; full: all
    // 4 tabs). Set on every mode change + once at bootstrap for dual experiences.
    const _applyConfiguratorMode = (mode) => {
      const wrapper = DOM.getElement('.murphy-door-configurator-wrapper');
      if (wrapper && (mode === 'curated' || mode === 'full_control')) {
        wrapper.setAttribute('data-configurator-mode', mode);
      }
      // WCAG 4.1.2: expose the segmented control's selected state — active
      // segment is otherwise CSS-only, invisible to assistive tech.
      const curatedBtn = DOM.getElement(
        '.murphy-door-configurator__mode-btn--curated'
      );
      const fullBtn = DOM.getElement(
        '.murphy-door-configurator__mode-btn--full'
      );
      if (curatedBtn) {
        curatedBtn.setAttribute(
          'aria-pressed',
          mode === 'curated' ? 'true' : 'false'
        );
      }
      if (fullBtn) {
        fullBtn.setAttribute(
          'aria-pressed',
          mode === 'full_control' ? 'true' : 'false'
        );
      }
    };
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CONFIGURATOR_MODE_CHANGED,
        (eventData) => {
          if (eventData && eventData.mode) {
            _applyConfiguratorMode(eventData.mode);
          }
        }
      )
    );
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
        () => {
          const conductor = window.MurphyDoorConfigurator.main;
          if (
            conductor &&
            typeof conductor._getConfiguratorMode === 'function' &&
            typeof conductor._getConfiguratorExperience === 'function' &&
            conductor._getConfiguratorExperience() !== 'classic'
          ) {
            _applyConfiguratorMode(conductor._getConfiguratorMode());
          }
        }
      )
    );

    // Ryan, UAT 2026-08-27: a disabled Add to Cart click marks the category
    // still missing an answer "highlighted in the warning border color".
    //
    // The DOM write lives here rather than in the coordinator that decides
    // WHICH card, per CCP rule 4 (md-config-ui- modules are the only DOM
    // manipulators). md-config-bl-auto-navigation-coordinator.js resolves the
    // target and publishes; this paints it.
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_PENDING_CATEGORY_CALLOUT,
        (eventData) => {
          try {
            // Clear any previous callout first -- a customer who taps the
            // disabled button twice against two different pending categories
            // would otherwise leave the first one lit forever.
            document
              .querySelectorAll('.' + CLASS_NAMES.PENDING_CALLOUT)
              .forEach((el) =>
                el.classList.remove(CLASS_NAMES.PENDING_CALLOUT)
              );

            // A gate card is addressed by data-category-gate, a real category
            // by data-category -- the target carries whichever applies, so ask
            // for the gate first and fall back.
            const gateId = eventData && eventData.gateId;
            const categoryId = eventData && eventData.categoryId;
            const el = gateId
              ? document.querySelector(
                  '[data-category-gate="' + CSS.escape(gateId) + '"]'
                )
              : categoryId
                ? document.querySelector(
                    '.murphy-door-configurator__category-button[data-category="' +
                      CSS.escape(categoryId) +
                      '"]'
                  )
                : null;
            if (!el) return;

            el.classList.add(CLASS_NAMES.PENDING_CALLOUT);

            // Self-clearing. The point is to catch the eye on arrival, not to
            // leave a permanent warning on a card the customer is now looking
            // at -- and leaving it would also fight the card's own applied
            // styling the moment they answer it.
            setTimeout(() => {
              el.classList.remove(CLASS_NAMES.PENDING_CALLOUT);
            }, PENDING_CALLOUT_DURATION_MS);
          } catch (error) {
            // A missed highlight must never break the navigation it decorates.
            isDev() &&
              console.warn(
                '[UIUpdater] pending-category callout failed:',
                error
              );
          }
        }
      )
    );

    // 🎯 FIX: Subscribe to Summary tab activation instead of bootstrap
    // PROBLEM: Bootstrap fires BEFORE the Summary tab is rendered, so the Add to Cart button doesn't exist yet!
    // SOLUTION: UI_SUMMARY_TAB_ACTIVATED fires when the Summary tab is ACTUALLY RENDERED with button in DOM.
    // This ensures _setupDisabledButtonGuide() can find the button and attach the click handler.
    // PATTERN: Like working modules (auto-navigation, confetti), we wait for the RIGHT event, not just bootstrap!
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_SUMMARY_TAB_ACTIVATED,
        () => {
          isDev() &&
            console.log(
              '[UIUpdater] Summary tab activated - setting up Add to Cart button handler'
            );
          // Call updateSummaryStatus to attach disabled button click handler
          // Button NOW exists in DOM because Summary tab just rendered
          if (uiUpdater && uiUpdater.updateSummaryStatus) {
            uiUpdater.updateSummaryStatus();
          }
        }
      )
    );

    // CASCADE GATE: Disable Add to Cart during cascade auto-selection to prevent
    // cart submission with mid-cascade (possibly inconsistent) selections.
    // The button is disabled while cascadeActive=true and re-evaluated on COMPLETED.
    // See: mixed-finish-state bug post-mortem, commit 38226ad4.
    //
    // EVERY Add to Cart node, not the first one. SELECTORS.ADD_TO_CART matches
    // TWO rendered buttons -- the summary button (md-configurator.liquid) and the
    // mobile sticky-bar button (configurator-sticky-bar.liquid, rendered in every
    // experience). These handlers used DOM.getElement, i.e. querySelector, so the
    // gate only ever reached the first node in document order: the summary one.
    //
    // Measured live 2026-08-26 on a cascade at 100% completion, desktop:
    //   summary : DISABLED "Updating..." [stash="Add to Cart"]
    //   sticky  : ENABLED  "Add to Cart"      <-- never gated
    //
    // The severity is mobile. At 390x844 the summary button measures 0x0 and the
    // sticky button 370x44 -- the roles invert, so this was disabling the
    // INVISIBLE button and leaving the one the customer actually taps live for
    // the whole cascade, defeating the gate exactly where it matters. Which node
    // is visible also flips with completion state (at 100% the summary view
    // renders and they swap), so gating "the right one" was never an option.
    //
    // It also explains the stranded "Updating..." label: STARTED stashes
    // _cascadeText on one node, and if the summary re-renders (it rebuilds on tab
    // activation) COMPLETED can resolve to a different node, find no stash,
    // restore nothing, and still clear cascadeActive.
    //
    // updateSummaryStatus() already iterates all of them; these now agree with it.
    const forEachAddToCart = (fn) => {
      document.querySelectorAll(SELECTORS.ADD_TO_CART).forEach(fn);
    };

    const releaseCascadeGate = (reason) => {
      cascadeActive = false;
      forEachAddToCart((button) => {
        if (button._cascadeText) {
          button.textContent = button._cascadeText;
          delete button._cascadeText;
        }
      });
      // Re-evaluate enabled/disabled state via standard summary check
      if (uiUpdater && uiUpdater.updateSummaryStatus) {
        uiUpdater.updateSummaryStatus();
      }
      isDev() &&
        console.log(
          `[UIUpdater] Cascade ${reason} — Add to Cart button state restored`
        );
    };

    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_STARTED,
        () => {
          cascadeActive = true;
          forEachAddToCart((button) => {
            // An incomplete configuration already disabled it; stashing here
            // would capture "Add to Cart" from a button that was never live and
            // restore it as though it were.
            if (button.disabled) return;
            // Store original text for restoration (button has no child elements)
            button._cascadeText = button.textContent;
            button.textContent = 'Updating...';
            button.disabled = true;
            button.setAttribute('aria-disabled', 'true');
            button.setAttribute(
              'aria-label',
              'Updating your selection, please wait...'
            );
          });
          isDev() &&
            console.log(
              '[UIUpdater] Cascade started — Add to Cart button disabled'
            );
        }
      )
    );

    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED,
        () => releaseCascadeGate('completed')
      )
    );

    // Cascade FAILED: restore button the same way as COMPLETED so gate never
    // permanently locks the page session on error paths.
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_FAILED,
        () => releaseCascadeGate('failed')
      )
    );

    // 🎯 CCP GOLDEN STANDARD: Subscribe to UI update events from conductor
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_UPDATE_REQUESTED,
        (eventData) => {
          if (eventData.type === 'batch') {
            uiUpdater.batchUpdate(eventData.updateData);
          } else if (eventData.type === 'price') {
            uiUpdater.batchPriceUpdate(eventData.updateData);
          }
        }
      )
    );

    // 🎯 UX ENHANCEMENT: Show preview indicator on active thumbnail when entering BROWSING state
    // Displays "PREVIEWING" inside the active thumbnail caption
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SELECTION_BROWSING,
        (eventData) => {
          const categoryId = eventData?.categoryId;
          const productId = eventData?.productId;
          const variantId = eventData?.variantId;

          if (!categoryId) return;

          uiUpdater.updatePreviewIndicatorVisibility(
            categoryId,
            productId,
            variantId,
            true
          );
        }
      )
    );
    isDev() &&
      console.log(
        '[UIUpdater] ✅ Subscribed to SELECTION_BROWSING event for preview indicator display on thumbnail'
      );

    // ========================================================================
    // FULLSCREEN MODAL TAKEOVER
    // Promotes the configurator wrapper to a fixed fullscreen overlay.
    // ========================================================================

    /**
     * Promotes the configurator wrapper to a fixed fullscreen overlay.
     * Locks body scroll, sets up keyboard/touch handlers, publishes entry event.
     */
    function enterFullscreenConfigurator() {
      const wrapper = DOM.getConfiguratorWrapper();
      if (
        !wrapper ||
        wrapper.classList.contains(
          'murphy-door-configurator-wrapper--fullscreen'
        )
      )
        return;

      const startTime = performance.now();

      // 1. Save scroll position BEFORE locking body
      _savedScrollY = window.scrollY;

      // 2. Lock body scroll (iOS-safe: position:fixed + negative top preserves visual position)
      document.body.style.top = `-${_savedScrollY}px`;
      document.body.classList.add('configurator-fullscreen');

      // 3. Clear exit flag and promote wrapper to fullscreen overlay
      _userExitedFullscreen = false;
      wrapper.classList.add('murphy-door-configurator-wrapper--fullscreen');

      // 4. Setup Escape key handler
      const escapeHandler = (event) => {
        if (event.key === 'Escape') {
          exitFullscreenConfigurator();
        }
      };
      document.addEventListener('keydown', escapeHandler);
      _fullscreenCleanupFns.push(() =>
        document.removeEventListener('keydown', escapeHandler)
      );

      // 5. Setup elastic bounce prevention (iOS Safari)
      const bounceHandler = (event) => {
        const scrollable = event.target.closest(
          '.murphy-door-configurator__content, .murphy-door-configurator__categories, .murphy-door-configurator__summary-content, .murphy-door-configurator__summary-scroll'
        );
        if (!scrollable) {
          event.preventDefault();
        }
      };
      wrapper.addEventListener('touchmove', bounceHandler, { passive: false });
      _fullscreenCleanupFns.push(() =>
        wrapper.removeEventListener('touchmove', bounceHandler)
      );

      // 6. Setup visualViewport listener for keyboard handling (mobile)
      if (window.visualViewport) {
        const viewportHandler = () => {
          document.documentElement.style.setProperty(
            '--configurator-viewport-height',
            `${window.visualViewport.height}px`
          );
        };
        window.visualViewport.addEventListener('resize', viewportHandler);
        viewportHandler(); // Set initial value
        _fullscreenCleanupFns.push(() => {
          window.visualViewport.removeEventListener('resize', viewportHandler);
          document.documentElement.style.removeProperty(
            '--configurator-viewport-height'
          );
        });
      }

      // 7. Publish entry event
      publishEvent(CONSTANTS.EVENTS.UI_FULLSCREEN_ENTERED, {
        timestamp: Date.now(),
        source: 'ui-updater.enterFullscreenConfigurator',
      });

      const duration = performance.now() - startTime;
      isDev() &&
        console.log(
          `[UIUpdater] Fullscreen configurator ENTERED (${duration.toFixed(1)}ms)`
        );
    }

    /**
     * Exits fullscreen overlay mode and fully deactivates configurator back to PDP state (two-state model).
     * Restores body scroll, cleans up listeners, removes configurator--active, restores aria-hidden.
     */
    function exitFullscreenConfigurator() {
      const wrapper = DOM.getConfiguratorWrapper();
      if (
        !wrapper ||
        !wrapper.classList.contains(
          'murphy-door-configurator-wrapper--fullscreen'
        )
      )
        return;

      const startTime = performance.now();

      // 1. Remove fullscreen class from wrapper
      wrapper.classList.remove('murphy-door-configurator-wrapper--fullscreen');

      // 2. Unlock body scroll and restore position
      document.body.classList.remove('configurator-fullscreen');
      document.body.style.top = '';
      window.scrollTo(0, _savedScrollY);

      // 3. Run all cleanup functions (escape key, touchmove, visualViewport)
      _fullscreenCleanupFns.forEach((fn) => fn());
      _fullscreenCleanupFns = [];

      // 4. Fully deactivate configurator back to PDP state (two-state model)
      _userExitedFullscreen = true;
      wrapper.classList.remove('configurator--active');

      // 5. Restore aria-hidden to pre-JS default state
      const { fullwidthTitleElement, sidebarTitleElement } = CACHED_ELEMENTS;
      fullwidthTitleElement
        ?.querySelector('h1')
        ?.setAttribute('aria-hidden', 'true');
      sidebarTitleElement?.querySelector('h1')?.removeAttribute('aria-hidden');

      // 6. Publish exit event
      publishEvent(CONSTANTS.EVENTS.UI_FULLSCREEN_EXITED, {
        timestamp: Date.now(),
        source: 'ui-updater.exitFullscreenConfigurator',
      });

      const duration = performance.now() - startTime;
      isDev() &&
        console.log(
          `[UIUpdater] Layout mode: IDLE - returned to PDP (two-state model) (${duration.toFixed(1)}ms)`
        );
    }

    // 🎯 UX: Switch to active layout and scroll gallery price bar into view.
    // Layout toggle runs once (guarded by configurator--active). Scroll runs whenever gallery bar is out of view.
    // Scrolls to gallery price bar (not wrapper) so full-width title stays off-page above viewport.
    // Triggered by: SELECTION_BROWSING (user clicks option) OR UI_CONFIGURATOR_SLIDE_FOCUSED (thumbnail click / swipe)
    function activateConfiguratorLayout() {
      // SIMPLIFIED MODE: suppress fullscreen during the 3-step browsing phase.
      // data-simplified-mode flips to 'complete' before UI_CTA_ENGAGED fires from
      // the completion cascade, so fullscreen still triggers after Build My Door.
      const toolsEl = document.querySelector(
        '.murphy-door-configurator__tools[data-simplified-mode="active"]'
      );
      if (toolsEl) return;

      const wrapper = DOM.getConfiguratorWrapper();
      const { galleryPriceElement } = CACHED_ELEMENTS;

      // Don't auto-re-enter if user explicitly exited (two-state model)
      if (_userExitedFullscreen) return;

      // Switch to active layout (runs once — guarded by configurator--active check)
      const wasAlreadyActive =
        wrapper && wrapper.classList.contains('configurator--active');

      if (wrapper && !wasAlreadyActive) {
        wrapper.classList.add('configurator--active');
        // apo_style stays embedded in the page — no modal takeover on first
        // pick or on later edits. The image column is widened instead via
        // section-md-apo-mode.css so the preview doesn't need fullscreen
        // real estate to feel large.
        const conductor = getConductor();
        if (conductor?._getConfiguratorExperience?.() !== 'apo_style') {
          enterFullscreenConfigurator();
        }

        // Sync aria-hidden on both H1s so exactly one is ever exposed.
        //
        // 🚨 FOLLOW WHAT IS ACTUALLY DISPLAYED, do not assume the fullwidth one
        // wins. Since the buy-box parity work, DESKTOP keeps the title in the
        // info column and hides the fullwidth copy (see the parity block at the
        // end of section-md-configurator.css); only mobile still promotes the
        // fullwidth one. Transferring the accessible name unconditionally would,
        // on desktop, aria-hide the sidebar H1 while the fullwidth H1 is
        // display:none - removing BOTH from the accessibility tree and leaving
        // the page with no accessible product name. Reading the computed style
        // keeps the two in step at any viewport without duplicating the
        // breakpoint here in JS.
        const { fullwidthTitleElement, sidebarTitleElement } = CACHED_ELEMENTS;
        const fullwidthTitleShown =
          !!fullwidthTitleElement &&
          window.getComputedStyle(fullwidthTitleElement).display !== 'none';
        const exposed = fullwidthTitleShown
          ? fullwidthTitleElement
          : sidebarTitleElement;
        const concealed = fullwidthTitleShown
          ? sidebarTitleElement
          : fullwidthTitleElement;
        exposed?.querySelector('h1')?.removeAttribute('aria-hidden');
        concealed?.querySelector('h1')?.setAttribute('aria-hidden', 'true');

        isDev() &&
          console.log(
            '[UIUpdater] Layout mode: ACTIVE - price moved to gallery, title moved to full-width'
          );

        // RAF: Defer scroll to after layout reflow triggered by class change
        // On first activation, galleryPriceElement has not yet reflowed to its new position.
        // Reading getBoundingClientRect() synchronously gives stale sidebar geometry.
        requestAnimationFrame(() => {
          // 🚫 NOT ON LOAD -- see the block comment on hasUserInteracted.
          // Scrolling the page before the customer has touched anything moves
          // Chrome's sequential focus starting point past the top of the
          // document, which puts the "Skip to configurator options" link
          // BEHIND them: only Shift+Tab reaches it, so the one-key shortcut it
          // exists to provide is dead. Measured 2026-08-20 -- with this scroll
          // active the first Tab landed on "Start over", not the skip link.
          if (!hasUserInteracted()) return;
          // 🚫 NOT IN apo_style either. This walks the customer back UP to the
          // gallery/price whenever the configurator layout (re)activates, i.e.
          // on essentially every selection. In classic/dual that is the moment
          // the price physically moves into the gallery and is worth showing.
          // In apo_style the media grid is position:sticky, so the door and its
          // price already follow the customer down the page -- the scroll
          // showed them nothing new and cost them their reading position.
          // See isApoStyleExperience.
          if (isApoStyleExperience()) return;
          if (galleryPriceElement) {
            const rect = galleryPriceElement.getBoundingClientRect();
            if (rect.top < 0 || rect.top > 80) {
              window.MurphyDoorConfigurator.utils.scrollIntoViewSafe(
                galleryPriceElement,
                { behavior: 'smooth', block: 'start', inline: 'nearest' }
              );
            }
          }
        });
        return;
      }

      // Layout already active: scroll check runs synchronously (element already
      // in place). Same load gate -- re-activation only happens after a tab
      // switch or a selection, so in practice this never suppresses a real one.
      if (!hasUserInteracted()) return;
      // Same apo_style suppression as the first-activation branch above.
      if (isApoStyleExperience()) return;
      if (galleryPriceElement) {
        const rect = galleryPriceElement.getBoundingClientRect();
        if (rect.top < 0 || rect.top > 80) {
          window.MurphyDoorConfigurator.utils.scrollIntoViewSafe(
            galleryPriceElement,
            { behavior: 'smooth', block: 'start', inline: 'nearest' }
          );
        }
      }
    }

    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SELECTION_BROWSING,
        () => {
          // In any simplified mode state (active or complete) after user exited fullscreen,
          // SELECTION_BROWSING means the user deliberately clicked a category option — intentional re-entry.
          // Reset the flag here (no CTA button exists in simplified mode to reset it otherwise).
          // Other passive triggers (UI_CONFIGURATOR_SLIDE_FOCUSED) remain blocked.
          if (_userExitedFullscreen) {
            const simplifiedTools = document.querySelector(
              '.murphy-door-configurator__tools[data-simplified-mode]'
            );
            if (simplifiedTools) {
              _userExitedFullscreen = false;
            }
          }
          activateConfiguratorLayout();
        }
      )
    );

    // Also trigger on thumbnail click or swipe navigation to configurator slide
    // (covers users who navigate to the configurator before making any product selection)
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_CONFIGURATOR_SLIDE_FOCUSED,
        activateConfiguratorLayout
      )
    );
    // Simplified 3-step mode publishes UI_CTA_ENGAGED on completion as an
    // idempotent fullscreen guarantee (the auto-expand of the first category
    // already triggers fullscreen via SELECTION_BROWSING; this defends against
    // upstream ordering changes). Completing the build is intentional re-entry.
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_CTA_ENGAGED,
        () => {
          _userExitedFullscreen = false;
          activateConfiguratorLayout();
        }
      )
    );
    isDev() &&
      console.log(
        '[UIUpdater] ✅ Subscribed to SELECTION_BROWSING + UI_CONFIGURATOR_SLIDE_FOCUSED + UI_CTA_ENGAGED for configurator layout activation'
      );

    // Fullscreen exit requested (from close button via event-handler)
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_FULLSCREEN_EXIT_REQUESTED,
        () => {
          exitFullscreenConfigurator();
        }
      )
    );

    isDev() &&
      console.log('[UIUpdater] ✅ Subscribed to UI_FULLSCREEN_EXIT_REQUESTED');

    // 🔄 Layout restore: Fully deactivate when configuration is cleared
    // exitFullscreenConfigurator() handles everything under the two-state model
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.PERSISTENCE_CLEAR_CONFIRMED,
        () => {
          exitFullscreenConfigurator();
          _userExitedFullscreen = false; // Reset so a fresh interaction can re-enter fullscreen after clear
        }
      )
    );

    // 🎯 UX: Hide #back-to-top while configurator is actively in use.
    // Scroll watcher restores it if user scrolls past (above or below) the configurator.
    // PERSISTENCE_CLEAR_CONFIRMED restores it when the config is cleared.
    {
      let backToTopEl = null;
      let scrollWatcher = null;

      const showBackToTop = () => {
        if (backToTopEl) backToTopEl.style.display = 'block';
        if (scrollWatcher) {
          window.removeEventListener('scroll', scrollWatcher);
          scrollWatcher = null;
        }
      };

      cleanupHandlers.push(
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.SELECTION_BROWSING,
          () => {
            backToTopEl = backToTopEl || document.getElementById('back-to-top');
            if (!backToTopEl) return;
            backToTopEl.style.display = 'none';
            if (scrollWatcher) return; // already watching
            scrollWatcher = () => {
              const wrapper = DOM.getConfiguratorWrapper();
              if (!wrapper) {
                showBackToTop();
                return;
              }
              const { bottom, top } = wrapper.getBoundingClientRect();
              if (bottom < 0 || top > window.innerHeight) showBackToTop();
            };
            window.addEventListener('scroll', scrollWatcher, { passive: true });
          }
        )
      );

      cleanupHandlers.push(
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.PERSISTENCE_CLEAR_CONFIRMED,
          showBackToTop
        )
      );
    }

    // 🔥 BUGFIX: Subscribe to tab activation events to handle Summary vs Categories display
    // This ensures Summary content is hidden when navigating to other tabs
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_TAB_ACTIVATED,
        (eventData) => {
          if (eventData?.tabId && uiUpdater?.updateTabDisplay) {
            isDev() &&
              console.log(
                `[UIUpdater] Tab activated: ${eventData.tabId} - updating display`
              );
            uiUpdater.updateTabDisplay(eventData.tabId);
          }
        }
      )
    );

    // 🎯 CCP COMPLIANT: Forward tooltip initialization request via event (not direct call)
    // TooltipManager should subscribe to CONSTANTS.EVENTS.UI_TOOLTIP_MANAGER_INIT_REQUESTED
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_TOOLTIPS_INITIALIZATION_REQUESTED,
        (eventData) => {
          try {
            // 🎯 CCP FIX: Use event-driven pattern instead of direct module call
            publishEvent(CONSTANTS.EVENTS.UI_TOOLTIP_MANAGER_INIT_REQUESTED, {
              timestamp: Date.now(),
              source: 'ui-updater',
              requestId: eventData.requestId || `tooltip_init_${Date.now()}`,
            });
          } catch (error) {
            isDev() &&
              console.error(
                '🔥 UI Updater: Error forwarding tooltip initialization request:',
                error
              );
            // 🎯 CCP GOLDEN STANDARD: Publish failed event
            publishEvent(CONSTANTS.EVENTS.UI_TOOLTIPS_INITIALIZATION_FAILED, {
              error: error.message,
              timestamp: Date.now(),
            });
          }
        }
      )
    );

    // 🎯 CCP GOLDEN STANDARD: Subscribe to DOM wrapper request events from conductor
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_DOM_WRAPPER_REQUESTED,
        (eventData) => {
          try {
            const wrapper = DOM.getConfiguratorWrapper();

            // 🎯 Use publishEvent helper for frozen payloads
            publishEvent(CONSTANTS.EVENTS.UI_DOM_WRAPPER_RESPONSE, {
              wrapper,
              requestId: eventData.requestId,
              timestamp: Date.now(),
              source: 'ui-updater',
            });
          } catch (error) {
            isDev() &&
              console.error(
                '❌ UI Updater: Failed to fulfill DOM wrapper request:',
                error
              );

            // 🎯 CCP GOLDEN STANDARD: Publish failed event with frozen payload
            publishEvent(CONSTANTS.EVENTS.UI_DOM_WRAPPER_ERROR, {
              error: error.message,
              requestId: eventData.requestId,
              timestamp: Date.now(),
              source: 'ui-updater',
            });
          }
        }
      )
    );

    // 🎯 CCP GOLDEN STANDARD: Subscribe to thumbnail generation success events
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_COMPOSITE_THUMBNAIL_GENERATED,
        (eventData) => {
          if (pendingThumbnailRequests.has(eventData.requestId)) {
            const { resolve } = pendingThumbnailRequests.get(
              eventData.requestId
            );
            resolve(eventData.html);
            pendingThumbnailRequests.delete(eventData.requestId);
          }
        }
      )
    );

    // 🎯 CCP GOLDEN STANDARD: Subscribe to thumbnail generation failure events
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_COMPOSITE_THUMBNAIL_GENERATION_FAILED,
        (eventData) => {
          if (pendingThumbnailRequests.has(eventData.requestId)) {
            const { reject } = pendingThumbnailRequests.get(
              eventData.requestId
            );
            reject(new Error(eventData.error));
            pendingThumbnailRequests.delete(eventData.requestId);
          }
        }
      )
    );

    // BULK-APPLY RERENDER COALESCING. A bulk apply (?config= restore, "Build
    // this" preset, editor-bridge restore, simplified/curated defaults) is ONE
    // synchronous loop in the cascade coordinator: STARTED, then N
    // APPLY_SELECTIONs, then COMPLETED/FAILED, all in one task. Every pick that
    // carries a wood_sku/finish_sku re-runs
    // MaterialPreferenceCoordinator.applyMaterialPreference, which publishes
    // MATERIAL_FILTER_STATE_UPDATED even when the preference did not change
    // (noChange recompute), and each one used to trigger a full thumbnail
    // re-render of every filtered category. Measured 2026-09-30 on
    // flush-mount-bookcase-door-2-0 (preset 1 and the equivalent ?config=
    // load, prod and local alike): 17 picks -> 20 full re-renders, all inside
    // one long task; ~1.1s on prod desktop, 11s+ on local theme dev, minutes
    // on a 4x-throttled phone. Each event carries the COMPLETE filter state
    // for the current preferences, so only the last one inside the batch is
    // observable. The same holds for the per-category
    // UI_CATEGORY_AND_VARIANTS_NEED_RERENDER the navigation manager publishes
    // on each real preference change (latest per category wins). While a bulk
    // batch is open we keep just those and render them once (category
    // re-sorts first, then the filter pass, the order they ran in before),
    // right after the last pick lands (the final STEP, queueRemaining
    // 0, so COMPLETED subscribers still see the finished grid, as before), or
    // at the batch's COMPLETED/FAILED when the last pick was skipped. A
    // microtask backstop closes the bracket if neither arrives, so it can
    // never outlive the task.
    // Normal (queued, debounced) auto-apply cascades are NOT bracketed: their
    // picks land in separate tasks and render as before.
    const BULK_APPLY_CASCADE_CONTEXTS = Object.freeze({
      url_restoration: true,
      editor_bridge_restore: true,
      build_preset: true,
      simplified_mode: true,
      curated_defaults: true,
    });
    let bulkApplyOpen = false;
    let deferredMaterialFilterState = null;
    let bulkApplyDeferredCount = 0;
    const deferredCategoryRerenders = new Map(); // categoryId -> latest payload

    const resetBulkApplyDeferrals = () => {
      deferredMaterialFilterState = null;
      bulkApplyDeferredCount = 0;
      deferredCategoryRerenders.clear();
    };

    const flushBulkApplyRerender = (reason) => {
      if (!bulkApplyOpen) return;
      bulkApplyOpen = false;
      const pendingFilterState = deferredMaterialFilterState;
      const pendingCategories = Array.from(deferredCategoryRerenders.values());
      const coalesced = bulkApplyDeferredCount;
      resetBulkApplyDeferrals();
      isDev() &&
        console.log(
          `[UIUpdater] Bulk apply ${reason}: ${coalesced} material filter update(s) -> ${pendingFilterState ? 1 : 0} re-render, ${pendingCategories.length} category re-sort(s)`
        );
      pendingCategories.forEach((payload) => {
        try {
          renderCategoryAndVariants(payload);
        } catch (error) {
          isDev() &&
            console.error(
              '[UIUpdater] Deferred category re-render failed',
              error
            );
        }
      });
      if (pendingFilterState) {
        uiUpdater.handleMaterialFilterStateUpdated(pendingFilterState);
      }
      // Once more after EVERYTHING the batch deferred has rendered: categories
      // the batch filled without touching their grid (cascade fills applied
      // straight to APPLIED, never browsed) get their cue here, whether or not
      // a filter pass ran.
      uiUpdater.repaintAppliedCues('UIUpdater.flushBulkApplyRerender');
    };

    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_STARTED,
        (eventData) => {
          const context = eventData && eventData.context;
          if (
            typeof context !== 'string' ||
            BULK_APPLY_CASCADE_CONTEXTS[context] !== true
          ) {
            return;
          }
          bulkApplyOpen = true;
          resetBulkApplyDeferrals();
          Promise.resolve().then(() => flushBulkApplyRerender('backstop'));
        }
      )
    );
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_STEP,
        (eventData) => {
          if (bulkApplyOpen && eventData && eventData.queueRemaining === 0) {
            flushBulkApplyRerender('last_step');
          }
        }
      )
    );
    [
      CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_COMPLETED,
      CONSTANTS.EVENTS.BUSINESS_CASCADE_AUTO_SELECTION_FAILED,
    ].forEach((terminalEvent) => {
      cleanupHandlers.push(
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          terminalEvent,
          () => {
            flushBulkApplyRerender('terminal');
            // Any cascade, bulk or queued, can apply categories without
            // browsing them; anchor their cue to the cascade's end rather
            // than to whichever unrelated sweep happens to run later.
            uiUpdater.repaintAppliedCues('UIUpdater.cascadeTerminal');
          }
        )
      );
    });

    // 🎯 CCP GOLDEN STANDARD: Subscribe to category and variants rerender events
    // Triggered when material preferences change and both category thumbnails AND variant browsers need updating.
    // Re-sorts a category's product grid (or refreshes its open variant
    // browser) after a material preference change. Named so the bulk-apply
    // bracket above can replay the latest deferred request per category.
    const renderCategoryAndVariants = (eventData) => {
      const { categoryId, sortedProducts } = eventData;

      isDev() &&
        console.log(
          `🎯 UI Updater: Category and variants rerender requested for category ${categoryId}`
        );

      // 🚨 ORDER IS THE WHOLE BUG. This handler always MEANT to do two
      // things -- re-sort the product grid AND refresh the open variant
      // browser -- but the first call destroyed the thing the second one
      // looks for, so the second half had never run once.
      //
      // renderCategoryThumbnails() does
      // `thumbnailsContainer.innerHTML = generateSmartSortedThumbnails()`,
      // which replaces the WHOLE container -- variant view included. The
      // block below then queried SELECTORS.VARIANT_GRID, found nothing
      // (it had just been overwritten), and silently did nothing. Note
      // that even reordering the two calls would not have been enough:
      // renderVariantsWithMaterialAwareness -> renderVariantBrowserFromList
      // only REORDERS tiles inside an existing grid and early-returns when
      // there is none. It cannot rebuild the view.
      //
      // Customer-visible symptom, Ryan UAT 2026-08-27: "when the user has
      // made their selection of wood species and then finish, don't change
      // them to the first view of wood species ... the change from applied
      // to the first list of wood species is confusing UX." Picking a
      // finish sets a material preference, which publishes this event for
      // the very category being browsed, which threw the customer back to
      // the species grid the instant their choice landed.
      //
      // The fix is to do LESS: if the customer is in the variant view, the
      // product grid is not on screen, so re-sorting it is invisible work
      // whose only observable effect is wiping the view. Skip it and
      // refresh the variant browser instead -- which finally makes the
      // second half live. Nothing goes stale: the Back button routes
      // through restoreThumbnailView(), which rebuilds the grid from
      // generateSmartSortedThumbnails() at that moment anyway.
      //
      // Deliberately NOT scoped to apo_style. The handler's own comment
      // has always claimed both halves; this repairs that contract in
      // every mode. classic/dual simply never SAW the symptom, because
      // their cards collapse on apply and hide the reset.
      const categoryButton = DOM.getElement(
        `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
      );
      const openVariantProductId = (() => {
        if (!categoryButton) return null;
        const variantGrid = categoryButton.querySelector(
          SELECTORS.VARIANT_GRID
        );
        if (!variantGrid) return null;
        const variantView = variantGrid.closest(SELECTORS.VARIANT_VIEW);
        if (!variantView) return null;
        const productThumbnail = variantView.querySelector(
          `${SELECTORS.SELECTED_PRODUCT} [data-product-id]`
        );
        return productThumbnail?.dataset?.productId || null;
      })();

      if (openVariantProductId) {
        uiUpdater.renderVariantsWithMaterialAwareness(
          openVariantProductId,
          categoryId
        );
        return;
      }

      // No variant view open -- the product grid IS what the customer is
      // looking at, so re-sort it exactly as before.
      uiUpdater.renderCategoryThumbnails(categoryId, sortedProducts);
    };

    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_CATEGORY_AND_VARIANTS_NEED_RERENDER,
        (eventData) => {
          // Inside a bulk apply: the latest request per category wins.
          if (bulkApplyOpen && eventData && eventData.categoryId) {
            deferredCategoryRerenders.set(eventData.categoryId, eventData);
            return;
          }
          renderCategoryAndVariants(eventData);
        }
      )
    );

    // 🎯 CCP GOLDEN STANDARD: Subscribe to individual variant browser rerender events
    // Triggered when specific variant browsers need updating due to material changes
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_VARIANTS_VIEW_NEEDS_RERENDER,
        (eventData) => {
          const { productId, categoryId } = eventData;

          isDev() &&
            console.log(
              `🎯 UI Updater: Variant view rerender requested for product ${productId} in category ${categoryId}`
            );

          uiUpdater.renderVariantsWithMaterialAwareness(productId, categoryId);
        }
      )
    );

    // 🎯 BUG FIX: Subscribe to MATERIAL_FILTER_STATE_UPDATED for dynamic filter class updates
    // Previously, applyMaterialFilterClasses() was only called during initial thumbnail generation.
    // When material preferences changed dynamically (e.g., user selects a different wood species),
    // the filter state event was published but NO module was listening, so thumbnails were never
    // re-rendered with updated compatible/incompatible classes. Users saw all options as clickable
    // instead of greyed-out incompatible ones.
    //
    // Event payload: { categoryFilters: Map, preferences: Object, simplifiedUxEnabled: Boolean, timestamp: Number }
    // Source: md-config-bl-material-preference-coordinator.js (line 642)
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.MATERIAL_FILTER_STATE_UPDATED,
        (eventData) => {
          // PATTERN 6: Bootstrap Runtime Protection - skip if PPD not ready
          if (!bootstrapComplete) {
            isDev() &&
              console.log(
                '[UIUpdater] MATERIAL_FILTER_STATE_UPDATED received before bootstrap - queuing'
              );
            pendingCalls.push({
              fn: function () {
                uiUpdater.handleMaterialFilterStateUpdated(eventData);
              },
              context: null,
              args: [],
            });
            return;
          }

          // Inside a bulk apply: keep only the latest state (see
          // BULK-APPLY RERENDER COALESCING above).
          if (bulkApplyOpen) {
            deferredMaterialFilterState = eventData;
            bulkApplyDeferredCount++;
            return;
          }

          uiUpdater.handleMaterialFilterStateUpdated(eventData);
        }
      )
    );
    isDev() &&
      console.log(
        '[UIUpdater] ✅ Subscribed to MATERIAL_FILTER_STATE_UPDATED event for dynamic material filtering'
      );

    // 🎯 RACE CONDITION FIX: Clear unapply browsing locks when a NEW material preference is applied.
    // This means the user (or cascade) has made a new material selection, so the previously-locked
    // categories should now re-render with the new filter state on subsequent MATERIAL_FILTER_STATE_UPDATED events.
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.MATERIAL_PREFERENCE_APPLIED,
        () => {
          if (_unapplyBrowsingLockedCategories.size > 0) {
            isDev() &&
              console.log(
                '[UIUpdater] Clearing unapply browsing locks on MATERIAL_PREFERENCE_APPLIED (' +
                  [..._unapplyBrowsingLockedCategories].join(', ') +
                  ')'
              );
            _unapplyBrowsingLockedCategories.clear();
          }
        }
      )
    );

    // 🎯 RACE CONDITION FIX: Clear the unapply browsing lock for a specific category when
    // the user selects a variant in that category (indicates user has re-engaged with browsing).
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SELECTION_VARIANT_APPLIED,
        (eventData) => {
          const { categoryId } = eventData || {};
          if (categoryId && _unapplyBrowsingLockedCategories.has(categoryId)) {
            _unapplyBrowsingLockedCategories.delete(categoryId);
            isDev() &&
              console.log(
                '[UIUpdater] Cleared unapply browsing lock for "' +
                  categoryId +
                  '" on SELECTION_VARIANT_APPLIED'
              );
          }
        }
      )
    );

    // 🎯 RACE CONDITION FIX: Clear unapply browsing lock when a material preference SWITCHES to a
    // surviving contributor SKU (e.g., "Wood A" → "Wood B"). The RECOMPUTED_FROM_APPLIED event fires
    // AFTER cascade events settle, meaning the final preference state is now known. The lock on the
    // unapplied category should be cleared so that the subsequent MATERIAL_FILTER_STATE_UPDATED event
    // (published by computeAndPublishFilterState in the recompute path) is NOT suppressed.
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.MATERIAL_PREFERENCES_RECOMPUTED_FROM_APPLIED,
        (eventData) => {
          if (!bootstrapComplete) return;
          try {
            const { unappliedCategoryId } = eventData || {};
            if (
              unappliedCategoryId &&
              _unapplyBrowsingLockedCategories.has(unappliedCategoryId)
            ) {
              _unapplyBrowsingLockedCategories.delete(unappliedCategoryId);
              isDev() &&
                console.log(
                  '[UIUpdater] Cleared unapply browsing lock for "' +
                    unappliedCategoryId +
                    '" on MATERIAL_PREFERENCES_RECOMPUTED_FROM_APPLIED (preference switched, not cleared)'
                );
            }
          } catch (error) {
            isDev() &&
              console.error(
                '[UIUpdater] MATERIAL_PREFERENCES_RECOMPUTED_FROM_APPLIED handler failed:',
                error
              );
          }
        }
      )
    );

    // 🎯 BUG FIX: Subscribe to SELECTION_BROWSING to apply material filtering when variant browser opens
    // When a product enters BROWSING state, its variant browser is displayed. We need to apply
    // material filtering to ensure incompatible variants are hidden/greyed out immediately.
    // Without this, variant browsers show all unfiltered variants on initial display.
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SELECTION_BROWSING,
        (eventData) => {
          if (!bootstrapComplete) {
            isDev() &&
              console.log(
                '[UIUpdater] SELECTION_BROWSING received before bootstrap - queuing'
              );
            pendingCalls.push({
              fn: function () {
                const { productId, categoryId } = eventData;
                if (productId && categoryId) {
                  uiUpdater.renderVariantsWithMaterialAwareness(
                    productId,
                    categoryId
                  );
                }
              },
              context: null,
              args: [],
            });
            return;
          }

          const { productId, categoryId } = eventData;
          isDev() &&
            console.log(
              '[UIUpdater] 📢 Received SELECTION_BROWSING event - applying material filtering',
              { productId, categoryId, timestamp: Date.now() }
            );
          if (productId && categoryId) {
            uiUpdater.renderVariantsWithMaterialAwareness(
              productId,
              categoryId
            );
          }
        }
      )
    );
    isDev() &&
      console.log(
        '[UIUpdater] ✅ Subscribed to SELECTION_BROWSING event for variant browser material filtering'
      );

    // 🎯 UX GUARANTEE: Scroll open category AND active thumbnail into view whenever BROWSING state is entered.
    // Ensures the APPLY button is always visible after any variant selection, even when
    // the user has scrolled away from the category header. Also scrolls the active thumbnail
    // to the top of its container for optimal visibility.
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SELECTION_BROWSING,
        (eventData) => {
          if (!bootstrapComplete) return;

          // 🚫 NOT ON LOAD. bootstrapComplete is not enough: hydration opens
          // several categories and fires SELECTION_BROWSING for each, so the
          // LAST one won this scroll and dumped the customer partway down the
          // page at a card they had not asked for (measured 2026-08-20: three
          // events on load, Height last, page left at ~1114px).
          //
          // That is a plain UX bug on its own -- nothing is selected yet, so
          // there is nothing to scroll TO -- and it was also the trigger for
          // the focus-order defect, because Chrome derives the sequential
          // focus starting point from scroll position. This scroll exists to
          // follow the customer as they advance through cards, which by
          // definition cannot happen before they have touched anything.
          if (!hasUserInteracted()) return;

          // Extract IDs from event payload for race-condition-free thumbnail lookup
          const { categoryId, productId, variantId } = eventData || {};
          if (!categoryId) return;

          const targetCategory = DOM.getElement(
            `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
          );
          if (!targetCategory) return;

          // Scroll category button first.
          //
          // 🚫 NOT IN apo_style. This PAGE scroll exists to guarantee the
          // APPLY button is on screen after a variant click, which matters in
          // classic/dual where the open accordion lives in a constrained
          // inner scroll container. apo_style has no APPLY button and no
          // inner container -- the card the customer just clicked is by
          // definition already under their cursor -- so all this did was yank
          // that card to the top of the viewport on every click (measured
          // 2026-08-27: 300px -> 496px on the first wood tap). See
          // isApoStyleExperience.
          //
          // The thumbnail scroll below is deliberately NOT suppressed: it
          // moves an inner variant strip, never the page, so it cannot take
          // the page away from the customer.
          if (!isApoStyleExperience()) {
            requestAnimationFrame(() => {
              uiUpdater.scrollCategoryIntoView(targetCategory);
            });
          }

          // Delay thumbnail scroll to prevent visual conflict with category scroll animation.
          // A 100ms delay gives the 'smooth' scroll on the category container time to start
          // and settle, preventing a jerky user experience from two simultaneous scroll events.
          setTimeout(() => {
            requestAnimationFrame(() => {
              // Pass productId and variantId from event payload (stable data attributes)
              uiUpdater.scrollActiveThumbnailIntoView(
                categoryId,
                productId,
                variantId
              );
            });
          }, 100);
        }
      )
    );
    isDev() &&
      console.log(
        '[UIUpdater] ✅ Subscribed to SELECTION_BROWSING event for scroll-to-category and scroll-to-thumbnail on browsing'
      );

    // 🎯 "MAKE THE INERT TAPS MEAN SOMETHING" — the two UI-intent events.
    //
    // Publishers: md-config-core-event-handler.js (card header, applied-tile
    // re-tap), md-config-ui-mobile-swipe.js (Summary step dot) and
    // md-config-bl-auto-navigation-coordinator.js (its answer to a
    // confirm-and-continue). Every handler is a no-op when its target element
    // is absent — see handleCategoryFocusRequest / handleBuyAreaFocusRequest.
    //
    // No bootstrapComplete gate on purpose: both are pure DOM/scroll work with
    // no PPD dependency, and they can only be reached by a real customer
    // gesture on markup bootstrap already rendered. Gating them would mean a
    // tap silently doing nothing — the exact defect this change exists to fix.
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_CATEGORY_FOCUS_REQUESTED,
        (eventData) => {
          try {
            uiUpdater.handleCategoryFocusRequest(eventData || {});
          } catch (error) {
            isDev() &&
              console.error(
                '[UIUpdater] UI_CATEGORY_FOCUS_REQUESTED failed:',
                error
              );
          }
        }
      )
    );

    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_BUY_AREA_FOCUS_REQUESTED,
        (eventData) => {
          try {
            uiUpdater.handleBuyAreaFocusRequest(eventData || {});
          } catch (error) {
            isDev() &&
              console.error(
                '[UIUpdater] UI_BUY_AREA_FOCUS_REQUESTED failed:',
                error
              );
          }
        }
      )
    );

    isDev() &&
      console.log(
        '[UIUpdater] ✅ Subscribed to UI_CATEGORY_FOCUS_REQUESTED + UI_BUY_AREA_FOCUS_REQUESTED'
      );

    // 🎚️ SLIDER: Subscribe to UI_SLIDER_BROWSING_UPDATED for animated selection text updates
    // When the slider position changes during drag, update the category selection display
    // with a fade-out/fade-in animation to provide smooth visual feedback.
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_SLIDER_BROWSING_UPDATED,
        (eventData) => {
          if (!bootstrapComplete) return;

          try {
            const { categoryId, displayTitle, price, variantId } = eventData;
            if (!categoryId) return;

            // Find the category button element
            const categoryButton = document.querySelector(
              `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
            );
            if (!categoryButton) return;

            const selectionDisplay = categoryButton.querySelector(
              '.murphy-door-configurator__category-user-selection'
            );
            if (!selectionDisplay) return;

            // Animated fade-out/fade-in transition for slider value changes
            const key = 'variant_' + variantId;

            // DIRTY-CHECK: Performed before animateValueChange (unlike price/shipping/PTS call sites)
            // because we want to skip the expensive updateSelectionText call entirely when unchanged.
            // variantId + price fully identifies what updateSelectionText will render.
            const sliderCacheKey = String(variantId) + '|' + String(price);
            if (selectionDisplay.dataset.sliderCache === sliderCacheKey) {
              isDev() &&
                console.log(
                  '[UIUpdater] Slider dirty-check skip: content unchanged for category',
                  categoryId
                );
              return;
            }

            const applySliderContent = () => {
              uiUpdater.updateSelectionText(
                categoryButton,
                key,
                displayTitle,
                price,
                eventData.productTitle
              );
              // Update cache after successful write so next event can dirty-check against it
              selectionDisplay.dataset.sliderCache = sliderCacheKey;
            };

            // Use shared animateValueChange — eliminates the prior inline fade duplication (DRY)
            Animations.animateValueChange(
              'slider_' + categoryId,
              [selectionDisplay],
              applySliderContent,
              120
            );
          } catch (error) {
            isDev() &&
              console.error(
                '[UIUpdater] Error handling slider browsing update:',
                error
              );
          }
        }
      )
    );
    isDev() &&
      console.log(
        '[UIUpdater] ✅ Subscribed to UI_SLIDER_BROWSING_UPDATED event for animated slider display updates'
      );

    // 🎯 BUG FIX: Subscribe to SELECTION_APPLIED to update category selection display text
    // Previously, updateSelectionText() existed (line ~3021) but was NEVER called from an event
    // subscription. The method was only invoked from internal UI flows (thumbnail clicks, variant
    // selections), but when a selection was APPLIED via the Apply button, the state manager published
    // SELECTION_APPLIED and NO handler in ui-updater was listening to update the display.
    // This caused the `.murphy-door-configurator__category-user-selection` DOM elements to remain
    // empty or stale after applying selections.
    //
    // Event payload: { productId, variantId, categoryId, displayTitle, price, productTitle, state, timestamp }
    // Source: md-config-core-state-manager.js (lines 783, 1323)
    //
    // The customer's own pick passes through BROWSING first; the announcer
    // uses that to name it even when a cascade entry reaches it earlier.
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SELECTION_BROWSING,
        noteUserBrowsedCategory
      )
    );
    // Collapsed grids are inert (see CLOSED GRIDS ARE INERT).
    cleanupHandlers.push(installClosedGridInert());
    // Keyboard focus survives tile re-renders (see GRID FOCUS KEEPER).
    cleanupHandlers.push(installGridFocusKeeper());
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SELECTION_APPLIED,
        (eventData) => {
          // Before the bootstrap guard's early return so a customer pick made
          // while PPD is still settling is not lost. A ?config= restore is
          // NOT confirmed here any more (no customer gesture: see
          // CUSTOMER-INITIATED ONLY in SELECTION ANNOUNCER).
          announceSelectionApplied(eventData);

          // PATTERN 6: Bootstrap Runtime Protection - skip if PPD not ready
          if (!bootstrapComplete) {
            isDev() &&
              console.log(
                '[UIUpdater] SELECTION_APPLIED received before bootstrap - queuing'
              );
            pendingCalls.push({
              fn: function () {
                handleSelectionAppliedForDisplay(eventData);
              },
              context: null,
              args: [],
            });
            return;
          }

          handleSelectionAppliedForDisplay(eventData);

          // 🎯 CRITICAL FIX: Update Add to Cart button state after each selection
          // This ensures the button updates from disabled→enabled when config completes
          if (uiUpdater && uiUpdater.updateSummaryStatus) {
            uiUpdater.updateSummaryStatus();
          }

          // 🎯 UX ENHANCEMENT: Hide preview indicator when selection is applied
          const categoryId = eventData?.categoryId;
          if (categoryId) {
            uiUpdater.updatePreviewIndicatorVisibility(
              categoryId,
              null,
              null,
              false
            );
          }
        }
      )
    );
    isDev() &&
      console.log(
        '[UIUpdater] ✅ Subscribed to SELECTION_APPLIED event for category selection display updates and preview indicator hide'
      );

    // "Build this" bracket for the selection announcer (see
    // isPresetAnnounceSuppressed). Terminal events start the short tail.
    {
      const bus = window.MurphyDoorConfigurator.CoreEventBus;
      const EV = CONSTANTS.EVENTS;
      const requestKey = (d) =>
        d && d.requestId ? String(d.requestId) : null;
      const closeRequest = (d) => closePresetAnnounceBracket(requestKey(d));
      cleanupHandlers.push(
        bus.subscribe(EV.BUSINESS_BUILD_PRESET_REQUESTED, (d) =>
          openPresetAnnounceBracket(requestKey(d))
        ),
        bus.subscribe(EV.BUSINESS_BUILD_PRESET_APPLIED, closeRequest),
        bus.subscribe(EV.BUSINESS_BUILD_PRESET_PARTIAL, closeRequest),
        bus.subscribe(EV.BUSINESS_BUILD_PRESET_FAILED, closeRequest),
        bus.subscribe(EV.BUSINESS_BUILD_PRESET_CLEARED, () =>
          Array.from(_presetAnnounceBrackets.keys()).forEach(
            closePresetAnnounceBracket
          )
        ),
        () => {
          _presetAnnounceBrackets.forEach((timer) => clearTimeout(timer));
          _presetAnnounceBrackets.clear();
        }
      );
    }

    // 🎯 UX ENHANCEMENT: Hide preview indicator when back button is clicked (selection cancelled)
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.STATE_SELECTION_CLEARED,
        (eventData) => {
          const categoryId = eventData?.categoryId;
          if (categoryId) {
            uiUpdater.updatePreviewIndicatorVisibility(
              categoryId,
              null,
              null,
              false
            );
            uiUpdater.clearAnsweredClaims(categoryId);
          }
        }
      )
    );

    // Every add that ends WITHOUT a line in the cart returns the Add to Cart
    // button to ready: hide the spinner, undo the "Adding to cart..." swap on
    // every copy, then let the normal recompute set each copy's disabled state,
    // aria-label and the "N choices left" counter from the applied Map. This
    // only touches the button; any error message stays exactly as its owner
    // shows it (the cart error modal answers BUSINESS_CART_ADD_FAILED itself).
    const resetAddToCartAfterUnsuccessfulAdd = (label) => {
      try {
        uiUpdater.hideLoadingSpinner();
        uiUpdater.restoreOptimisticCartFeedback();
        uiUpdater.updateSummaryStatus();
      } catch (error) {
        isDev() &&
          console.error(`[UIUpdater] ${label} UI reset failed:`, error);
      }
    };

    // An Add to Cart refused for an unfinished door (conductor or cart-manager
    // readiness gate). Deliberately no error notification: auto-navigation is
    // already taking the customer to the category that needs them.
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CART_ADD_REFUSED_INCOMPLETE,
        () => resetAddToCartAfterUnsuccessfulAdd('Refused-cart')
      )
    );

    // An Add to Cart that FAILED: /cart/add.js answered 4xx/5xx, the network
    // rejected, the response was not JSON, the request timed out, Shopify added
    // fewer lines than were sent, or preparation threw. cart-manager publishes
    // BUSINESS_CART_ADD_FAILED for every one of them. Until 2026-10-02 nothing
    // here listened for it (the only restore hung off ERROR_CART_ADD_FAILED,
    // which no module publishes), so a 502 measured on production left every
    // copy disabled on "Adding to cart..." beside the error modal, and the
    // modal's own "Try Again" bailed out because the button was disabled.
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.BUSINESS_CART_ADD_FAILED,
        () => resetAddToCartAfterUnsuccessfulAdd('Failed-cart')
      )
    );
    isDev() &&
      console.log(
        '[UIUpdater] ✅ Subscribed to STATE_SELECTION_CLEARED event for preview indicator hide on back button'
      );

    // 🎯 Subscribe to RECOVERY_ANIMATION_COMPLETE to populate Summary list after URL recovery
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.RECOVERY_ANIMATION_COMPLETE,
        () => {
          isDev() &&
            console.log(
              '[UIUpdater] 🎬 Recovery animation complete - populating Summary list'
            );

          // Iterate through all category buttons and populate summary list
          const categoryButtons = document.querySelectorAll(
            '.murphy-door-configurator__category-button[data-category]'
          );
          categoryButtons.forEach((button) => {
            const categoryId = button.getAttribute('data-category');
            const selectionDisplay = button.querySelector(
              '.murphy-door-configurator__category-user-selection'
            );

            if (selectionDisplay && selectionDisplay.textContent.trim()) {
              // Parse the selection text (format: "Product | Finish | $250.00")
              const fullText = selectionDisplay.textContent.trim();
              const parts = fullText.split(' | ');

              if (parts.length >= 2) {
                const displayTitle = parts.slice(0, -1).join(' | '); // Everything except last part
                const price = parts[parts.length - 1]; // Last part is price

                // Call updateSummaryList to add to Summary tab
                uiUpdater.updateSummaryList(
                  categoryId,
                  displayTitle,
                  null,
                  price
                );
              }
            }
          });
        }
      )
    );
    isDev() &&
      console.log(
        '[UIUpdater] ✅ Subscribed to RECOVERY_ANIMATION_COMPLETE for Summary list population'
      );

    // 🎯 100% COMPLETION: promote the Add to Cart INTO the sticky actions bar.
    //
    // Hung off the celebration request rather than off updateProgressBar's own
    // 100% branch, and that is deliberate: the celebration event is the ONE
    // signal both completion paths agree on. The conductor publishes it from
    // handleProgressUpdate, and the simplified-mode coordinator publishes its
    // own staggered copy after pre-claiming the conductor's slot -- so a
    // progress-bar hook would fire twice in one flow and not at all in the
    // other, while this fires exactly once per celebration in both.
    //
    // The handler is SYNCHRONOUS and the event bus dispatches synchronously
    // (CoreEventBus.publish -> executeCallbacks), so the move has completed
    // before md-config-ui-confetti-manager.js resumes past its first `await`.
    // Its lead-in delay is what gives the promoted button time to animate in
    // and settle; it is NOT what makes this ordering work, so shortening the
    // delay cannot reintroduce a race here.
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_CONFETTI_CELEBRATION_REQUESTED,
        () => {
          // M1: arm FIRST. The promotion below is now conditional on the
          // actions bar being somewhere the customer can see, so when it
          // declines the observer is what later promotes on a scroll back up.
          _atcPromotionArmed = true;
          _observeStickyBarHomeForAtc();
          _promoteAddToCartToStickyBar();
        }
      )
    );
    cleanupHandlers.push(_disconnectStickyBarHomeObserver);

    // M0: start tracking the rail ATC so the sticky bar hides only while that
    // button is actually on screen, instead of whenever the configuration
    // happens to be complete. Idempotent -- it early-returns once observing --
    // so the immediate call and the ready-event call cannot double-subscribe.
    // Registered here because both this and the promotion above are about who
    // owns the Add to Cart at a given moment.
    _observeRailAtcForStickyBar();
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
        () => {
          // The rail ATC is server-rendered, but a re-render can replace the
          // node the observer is holding; re-running rebinds to the live one.
          _disconnectRailAtcObserver();
          _observeRailAtcForStickyBar();
        }
      )
    );
    // cleanup() calls every entry as a function, so the disconnect goes in
    // alongside the unsubscribes.
    cleanupHandlers.push(_disconnectRailAtcObserver);
    isDev() &&
      console.log(
        '[UIUpdater] ✅ Subscribed to UI_CONFETTI_CELEBRATION_REQUESTED for Add to Cart promotion'
      );

    // 🎉 CCP GOLDEN STANDARD: Subscribe to confetti coordinate requests from confetti manager
    // The confetti manager publishes UI_CONFETTI_COORDINATES_REQUESTED when it needs to know
    // WHERE on screen to spawn particles. ui-updater owns the DOM, so it resolves the progress
    // bar's bounding rect and responds with UI_CONFETTI_COORDINATES_RESPONSE.
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_CONFETTI_COORDINATES_REQUESTED,
        (eventData) => {
          const startTime = performance.now();

          try {
            isDev() &&
              console.log(
                '[UIUpdater] 🎉 Confetti coordinate request received',
                eventData
              );

            // 🚨 CAPABILITY, NOT PRESENCE. This used to read
            //   const progressBar = DOM.getElement('...__progress-bar');
            //   if (progressBar) { ...use its rect... } else { ...fallback... }
            // apo_style keeps the progress bar in the DOM but sets it to
            // `display: none`, so querySelector returned a truthy element whose
            // getBoundingClientRect() is all zeros. The truthy check passed, the
            // else-branch never ran, and the burst resolved to (0, 0) — the
            // top-left corner of the screen. Measured live on staging
            // (apo_style, 1440x900): resolvedOrigin (0,0), source progress_bar.
            //
            // Same family as the cart-drawer incident in CLAUDE.md: an element
            // being *there* proves nothing about it being *usable*. Anchors are
            // therefore required to be rendered AND on screen, never merely
            // present.
            //
            // Ordered by what the customer is actually looking at when the
            // celebration fires. No experience branching is needed: in
            // classic/dual the progress bar is visible and wins; in apo_style it
            // is display:none and is skipped automatically, falling through to
            // whichever CTA is on screen.
            const anchor = _resolveConfettiAnchor();

            let x;
            let y;
            let anchorUsed = anchor ? anchor.selector : null;

            if (anchor) {
              x = anchor.x;
              y = anchor.y;
              isDev() &&
                console.log(
                  `[UIUpdater] 🎉 Confetti anchored to ${anchorUsed}: (${x}, ${y})`,
                  anchor.rect
                );
            }

            if (!anchorUsed) {
              // ✅ FALLBACK: no anchor is rendered and on screen (e.g. apo_style
              // scrolled away from every CTA) — burst from the upper third of
              // the viewport, which is always somewhere the customer is looking.
              isDev() &&
                console.warn(
                  '[UIUpdater] 🎉 No on-screen confetti anchor, using viewport fallback'
                );

              if (window.MurphyDoorConfigurator?.conductor) {
                const viewportData =
                  window.MurphyDoorConfigurator.conductor.getViewportDimensions();
                if (viewportData) {
                  x = viewportData.width / 2;
                  y = viewportData.height / 3;
                  isDev() &&
                    console.log(
                      `[UIUpdater] 🎉 Using conductor viewport fallback: (${x}, ${y})`
                    );
                }
              }

              // ✅ LAST RESORT: Hardcoded viewport center if conductor unavailable
              if (x === undefined || y === undefined) {
                x = window.innerWidth / 2;
                y = window.innerHeight / 3;
                isDev() &&
                  console.warn(
                    `[UIUpdater] 🎉 Using window viewport last resort: (${x}, ${y})`
                  );
              }
            }

            // ✅ GOLDEN STANDARD: Publish frozen coordinate response via publishEvent helper
            publishEvent(CONSTANTS.EVENTS.UI_CONFETTI_COORDINATES_RESPONSE, {
              x,
              y,
              // Names the anchor actually used, so a wrong-looking burst can be
              // diagnosed from the event alone rather than by re-deriving it.
              source: anchorUsed || 'viewport_fallback',
              trigger: eventData.trigger || 'unknown',
              timestamp: Date.now(),
            });

            // ✅ GOLDEN STANDARD: Performance tracking
            const duration = performance.now() - startTime;
            if (duration > PERFORMANCE_THRESHOLDS.UI_UPDATE) {
              isDev() &&
                console.warn(
                  `[UIUpdater] 🎉 Confetti coordinate resolution exceeded threshold: ${duration.toFixed(2)}ms > ${PERFORMANCE_THRESHOLDS.UI_UPDATE}ms`
                );
            }

            isDev() &&
              console.log(
                `[UIUpdater] 🎉 Confetti coordinates published: (${x}, ${y}) in ${duration.toFixed(2)}ms`
              );
          } catch (error) {
            isDev() &&
              console.error(
                '[UIUpdater] 🎉 Confetti coordinate resolution failed:',
                error
              );

            // ✅ GOLDEN STANDARD: Publish failed event with error details
            publishEvent(CONSTANTS.EVENTS.UI_CONFETTI_COORDINATES_RESPONSE, {
              x: window.innerWidth / 2,
              y: window.innerHeight / 3,
              source: 'error_fallback',
              trigger: eventData.trigger || 'unknown',
              error: error.message,
              timestamp: Date.now(),
            });
          }
        }
      )
    );
    isDev() &&
      console.log(
        '[UIUpdater] ✅ Subscribed to UI_CONFETTI_COORDINATES_REQUESTED for confetti positioning'
      );

    // 🚨 FORBIDDEN VALIDATOR EVENT CHAIN FIX #1: Subscribe to browsing validation results
    // Source: md-config-bl-forbidden-validator.js (line 1769) publishes STATE_BROWSING_RESULT
    // when a user hovers/clicks a thumbnail in browsing mode and it conflicts with applied selections.
    // Without this subscription, users see NO visual feedback on forbidden thumbnails.
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.STATE_BROWSING_RESULT,
        (eventData) => {
          try {
            const { itemId, validationResult } = eventData || {};

            if (!validationResult || !validationResult.isConflicted) {
              return; // No conflict - nothing to show
            }

            isDev() &&
              console.log('[UIUpdater] 🚫 Forbidden browsing result received', {
                itemId,
                conflictCount: validationResult.conflicts?.length || 0,
                categoryId: eventData.categoryId,
              });

            // Apply forbidden visual state to matching thumbnail element
            const element = document.querySelector(
              `[data-variant-id="${itemId}"], [data-product-id="${itemId}"]`
            );

            if (element) {
              element.classList.add('material-filtered-out');

              // Set tooltip with conflict reason for user feedback
              const reason = validationResult.conflicts?.[0]?.reason;
              if (reason) {
                element.setAttribute('title', `Conflict: ${reason}`);
              }

              isDev() &&
                console.log(
                  '[UIUpdater] 🚫 Applied forbidden visual state to element',
                  {
                    itemId,
                    reason: reason || 'unknown',
                  }
                );
            }
          } catch (error) {
            isDev() &&
              console.error(
                '[UIUpdater] ❌ Error handling browsing validation result:',
                error
              );

            // CCP GOLDEN STANDARD: Publish failed event
            publishEvent(
              CONSTANTS.EVENTS.UI_FORBIDDEN_BROWSING_FEEDBACK_FAILED,
              {
                error: error.message,
                itemId: eventData?.itemId,
                timestamp: Date.now(),
              }
            );
          }
        }
      )
    );
    isDev() &&
      console.log(
        '[UIUpdater] ✅ Subscribed to STATE_BROWSING_RESULT for forbidden thumbnail feedback'
      );

    // 🚨 FORBIDDEN VALIDATOR EVENT CHAIN FIX #2: Subscribe to applied conflict detection
    // Source: md-config-bl-forbidden-validator.js (line 1957) publishes STATE_APPLIED_CONFLICT_DETECTED
    // when a user clicks Apply and the selection conflicts with other applied selections.
    // Without this subscription, users see NOTHING when applying conflicting selections (critical UX failure).
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.STATE_APPLIED_CONFLICT_DETECTED,
        (eventData) => {
          try {
            const { conflicts, categoryId, itemId } = eventData || {};

            if (!conflicts || conflicts.length === 0) {
              return; // No conflicts to report
            }

            const conflictReason =
              conflicts[0]?.reason ||
              'This selection is not compatible with your other choices.';

            isDev() &&
              console.log(
                '[UIUpdater] 🚨 Applied conflict detected - showing notification',
                {
                  categoryId,
                  itemId,
                  conflictCount: conflicts.length,
                  reason: conflictReason,
                }
              );

            // CCP COMPLIANT: Publish notification event for user feedback
            publishEvent(CONSTANTS.EVENTS.UI_SHOW_ERROR_NOTIFICATION, {
              message: `Conflict Detected: ${conflictReason}`,
              type: 'warning',
              duration: 7000,
              source: 'forbidden-validator',
            });
          } catch (error) {
            isDev() &&
              console.error(
                '[UIUpdater] ❌ Error handling applied conflict detection:',
                error
              );

            // CCP GOLDEN STANDARD: Publish failed event
            publishEvent(
              CONSTANTS.EVENTS.UI_FORBIDDEN_APPLIED_CONFLICT_FEEDBACK_FAILED,
              {
                error: error.message,
                categoryId: eventData?.categoryId,
                timestamp: Date.now(),
              }
            );
          }
        }
      )
    );
    isDev() &&
      console.log(
        '[UIUpdater] ✅ Subscribed to STATE_APPLIED_CONFLICT_DETECTED for conflict notifications'
      );

    // =========================================================================
    // SLIDER FORBIDDEN STATE - APPLY BUTTON VISUAL FEEDBACK
    // =========================================================================
    // Subscribes to UI_SLIDER_FORBIDDEN_STATE_CHANGED events from horizontal slider.
    // Updates APPLY button visual state (grayed out, disabled) when slider is on
    // a forbidden value to provide clear visual feedback to users.
    // =========================================================================
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_SLIDER_FORBIDDEN_STATE_CHANGED,
        (eventData) => {
          try {
            if (!eventData || !eventData.categoryId) return;

            // Find the APPLY button for this category
            const categoryButton = document.querySelector(
              `.murphy-door-configurator__category-button[data-category="${eventData.categoryId}"]`
            );
            if (!categoryButton) return;

            const applyButton = categoryButton.querySelector(
              '.murphy-door-configurator__apply-button'
            );
            if (!applyButton) return;

            if (eventData.isForbidden) {
              // Add disabled styling (disabled= handles aria semantics natively — no aria-disabled needed)
              applyButton.classList.add('forbidden-disabled');
              applyButton.disabled = true;

              // Attach direct pointerdown guide (fires even on disabled buttons from direct listeners)
              // Clean up any existing listener first to prevent stacking
              if (applyButton._sliderForbiddenGuide) {
                applyButton.removeEventListener(
                  'pointerdown',
                  applyButton._sliderForbiddenGuide
                );
              }
              applyButton._sliderForbiddenGuide = function () {
                // Shake the apply button
                applyButton.classList.remove('slider-forbidden-shake');
                void applyButton.offsetWidth; // force reflow to re-trigger animation
                applyButton.classList.add('slider-forbidden-shake');
                applyButton.addEventListener(
                  'animationend',
                  function onShakeEnd() {
                    applyButton.classList.remove('slider-forbidden-shake');
                    applyButton.removeEventListener('animationend', onShakeEnd);
                  },
                  { once: true }
                );

                // Pulse the slider warning tooltip in the same category
                const warningTooltip = categoryButton.querySelector(
                  '.murphy-door-configurator__slider-warning-tooltip'
                );
                if (warningTooltip) {
                  // Capture current smart-positioned transform so the animation doesn't jump
                  // (updateSmartTooltipPosition may have set translateX(calc(-50% + Npx)) inline)
                  warningTooltip.style.setProperty(
                    '--slider-warning-transform',
                    warningTooltip.style.transform || 'translateX(-50%)'
                  );
                  warningTooltip.classList.remove('slider-warning-pulse');
                  void warningTooltip.offsetWidth; // force reflow
                  warningTooltip.classList.add('slider-warning-pulse');
                  warningTooltip.addEventListener(
                    'animationend',
                    function onPulseEnd() {
                      warningTooltip.classList.remove('slider-warning-pulse');
                      warningTooltip.removeEventListener(
                        'animationend',
                        onPulseEnd
                      );
                    },
                    { once: true }
                  );
                }
              };
              applyButton.addEventListener(
                'pointerdown',
                applyButton._sliderForbiddenGuide
              );
            } else {
              // Remove disabled styling
              applyButton.classList.remove('forbidden-disabled');
              applyButton.disabled = false;

              // Clean up guide listener
              if (applyButton._sliderForbiddenGuide) {
                applyButton.removeEventListener(
                  'pointerdown',
                  applyButton._sliderForbiddenGuide
                );
                delete applyButton._sliderForbiddenGuide;
              }
            }

            // Update category header forbidden indicator
            if (eventData.isForbidden) {
              categoryButton.classList.add(
                'murphy-door-configurator__category-button--slider-forbidden'
              );
            } else {
              categoryButton.classList.remove(
                'murphy-door-configurator__category-button--slider-forbidden'
              );
            }
          } catch (error) {
            isDev() &&
              console.error(
                '[UIUpdater] ❌ Error updating APPLY button forbidden state:',
                error
              );
          }
        }
      )
    );
    isDev() &&
      console.log(
        '[UIUpdater] ✅ Subscribed to UI_SLIDER_FORBIDDEN_STATE_CHANGED for APPLY button visual feedback'
      );

    // =========================================================================
    // PREEMPTIVE FORBIDDEN UI DISABLING
    // =========================================================================
    // Subscribes to UI_UPDATE_PREEMPTIVE_FORBIDDEN events from the forbidden-validator.
    // When a selection is applied, the validator calculates which items across ALL
    // categories should be DISABLED (greyed out, unclickable, with tooltip).
    // This handler manages the DOM updates for that preemptive state.
    //
    // Payload: { itemsToDisable: Array<[variantId, [{reason, ruleId, ruleName}]]>, ... }
    // =========================================================================
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_UPDATE_PREEMPTIVE_FORBIDDEN,
        (eventData) => {
          // PATTERN 6: Bootstrap Runtime Protection
          if (!bootstrapComplete) {
            isDev() &&
              console.log(
                '[UIUpdater] UI_UPDATE_PREEMPTIVE_FORBIDDEN received before bootstrap - queuing'
              );
            pendingCalls.push({
              fn: function () {
                // Re-publish event to self after bootstrap
                publishEvent(
                  CONSTANTS.EVENTS.UI_UPDATE_PREEMPTIVE_FORBIDDEN,
                  eventData
                );
              },
              context: null,
              args: [],
            });
            return;
          }

          const startTime = performance.now();

          try {
            const itemsToDisable = eventData.itemsToDisable || [];

            isDev() &&
              console.log(
                '[UIUpdater] 🛡️ Preemptive forbidden UI update received',
                {
                  itemsToDisableCount: itemsToDisable.length,
                  triggerCategoryId: eventData.triggerCategoryId,
                  source: eventData.source,
                }
              );

            // Step 1: CLEAR all previously forbidden-disabled items (clean slate)
            // This ensures items that are no longer forbidden get re-enabled
            const previouslyDisabled = document.querySelectorAll(
              '.forbidden-disabled'
            );
            let clearedCount = 0;

            previouslyDisabled.forEach(function (element) {
              element.classList.remove('forbidden-disabled');
              // Clear the assistive-tech state in the same pass as the class. Miss this
              // and a tile that becomes selectable again stays announced as disabled
              // while looking and behaving perfectly normal — a silent, sighted-invisible
              // regression.
              window.MurphyDoorConfigurator.utils.setAriaDisabled(
                element,
                false
              );
              // Drop the aria-describedby node too. Paired with (E) in the disable
              // pass — leave it and the tile keeps describing a restriction that no
              // longer applies, which is worse than never having said it.
              setForbiddenDescription(element, false);
              // Remove tooltip library integration classes/attributes
              element.classList.remove('murphy-door-button-tooltip');
              element.removeAttribute('data-category');
              element.removeAttribute('data-forbidden-reason');
              element.removeAttribute('data-forbidden-rule-id');
              element.removeAttribute('data-forbidden-rule-name');
              // Remove any lingering title attribute from old forbidden state
              if (
                element.getAttribute('title') &&
                element.getAttribute('title').indexOf('Forbidden:') === 0
              ) {
                element.removeAttribute('title');
              }
              clearedCount++;
            });

            // Step 1b: CLEAR product-level forbidden disabling (clean slate)
            // Remove product-level forbidden classes, tooltip attributes, and data-category
            // that were previously applied when ALL variants were forbidden
            const previouslyForbiddenProducts = document.querySelectorAll(
              '.forbidden-product-all-disabled'
            );
            let clearedProductCount = 0;

            previouslyForbiddenProducts.forEach(function (element) {
              // Remove visual disabling classes
              element.classList.remove('forbidden-product-all-disabled');
              element.classList.remove('murphy-door-button-tooltip');

              // Remove data-category attribute (used for tooltip registration)
              const dataCategory = element.getAttribute('data-category');
              if (
                dataCategory &&
                dataCategory.indexOf('forbidden-product-') === 0
              ) {
                element.removeAttribute('data-category');
              }

              // Remove the forbidden-specific title tooltip
              if (
                element.getAttribute('title') &&
                element.getAttribute('title').indexOf('Not compatible') === 0
              ) {
                element.removeAttribute('title');
              }

              // Paired with the set in Step 3b. Without this a product that becomes
              // selectable again stays announced as disabled forever, which is the
              // worse half of the bug: the customer is told they cannot buy
              // something they can.
              element.removeAttribute('data-forbidden-reason');
              window.MurphyDoorConfigurator.utils.setAriaDisabled(
                element,
                false
              );
              setForbiddenDescription(element, false);

              clearedProductCount++;
            });

            if (clearedCount > 0) {
              isDev() &&
                console.log(
                  '[UIUpdater] 🛡️ Cleared ' +
                    clearedCount +
                    ' previously forbidden-disabled elements'
                );
            }

            // Step 2: Apply forbidden state to newly disabled items
            let disabledCount = 0;
            let notFoundCount = 0;

            // ── Index once, look up N times ──────────────────────────────────
            // This loop used to run `document.querySelectorAll('[data-variant-id="X"]')`
            // — and, on a miss, a second scan for '[data-product-id="X"]' — once
            // PER FORBIDDEN ITEM. On the flush-mount bookcase door that is 91
            // items, and the forbidden pass itself reruns ~10x per selection, so
            // a single option click cost well over a thousand document-wide
            // attribute scans.
            //
            // Profiled on production with unminified assets swapped in
            // (2026-09-04): this callback was 460ms of querySelectorAll time
            // across 6 clicks — 54% of all remaining DOM-query time on the page
            // and the largest configurator-owned cost left after the tooltip
            // coalescing fix. It is the other half of the QA "device gets hot"
            // finding.
            //
            // Building two Maps costs exactly two document scans and turns every
            // per-item lookup into an O(1) hit.
            //
            // 🚨 Equivalence rests on one property, verified before this landed:
            // NOTHING this loop does adds or removes a data-variant-id or
            // data-product-id attribute, so the indexed set cannot drift while
            // the loop runs. setForbiddenDescription() does append a <span>, but
            // that span carries neither attribute and so can never belong to
            // either index. If a future edit makes this loop create or retag
            // tiles, this index must be rebuilt — or reverted to live queries.
            const variantElementIndex = new Map();
            document.querySelectorAll('[data-variant-id]').forEach(function (el) {
              const key = el.getAttribute('data-variant-id');
              const bucket = variantElementIndex.get(key);
              if (bucket) bucket.push(el);
              else variantElementIndex.set(key, [el]);
            });

            const productElementIndex = new Map();
            document.querySelectorAll('[data-product-id]').forEach(function (el) {
              const key = el.getAttribute('data-product-id');
              const bucket = productElementIndex.get(key);
              if (bucket) bucket.push(el);
              else productElementIndex.set(key, [el]);
            });

            itemsToDisable.forEach(function (entry) {
              const variantId = entry[0];
              const ruleEntries = entry[1];

              if (!variantId || !ruleEntries || ruleEntries.length === 0)
                return;

              // Find ALL DOM elements by variant-id or product-id (handles
              // duplicates). Index lookups, not document scans — see above.
              // Attribute selectors match the attribute's STRING value, so the
              // key is stringified to keep a numeric variantId matching exactly
              // as the old '[data-variant-id="' + variantId + '"]' did.
              const variantKey = String(variantId);
              let elements = variantElementIndex.get(variantKey) || [];

              // Fallback: try product-id if no variant-id matches
              if (elements.length === 0) {
                elements = productElementIndex.get(variantKey) || [];
              }

              if (elements.length > 0) {
                // Build human-readable reason from all rules affecting this item
                const reasons = ruleEntries.map(function (r) {
                  return r.ruleName;
                });
                const reasonText = reasons.join('; ');

                // Apply to ALL matching elements (handles duplicate DOM elements)
                elements.forEach(function (element) {
                  // A) Add CSS class for styling (greyed out, cursor not-allowed)
                  element.classList.add('forbidden-disabled');

                  // A2) Mirror the state to assistive tech. Paired with the clear pass
                  // in Step 1 above and with the in-memory block in
                  // md-config-core-event-handler.js — all three sites must agree or a
                  // tile ends up greyed but announced as available, or announced as
                  // disabled forever after it becomes selectable again.
                  window.MurphyDoorConfigurator.utils.setAriaDisabled(
                    element,
                    true
                  );

                  // B) Add tooltip library integration classes and attributes
                  element.classList.add('murphy-door-button-tooltip');
                  element.setAttribute(
                    'data-category',
                    'forbidden-' + variantId
                  );

                  // C) Store metadata for debugging and tooltip display
                  element.setAttribute('data-forbidden-reason', reasonText);
                  element.setAttribute(
                    'data-forbidden-rule-id',
                    ruleEntries[0].ruleId
                  );
                  element.setAttribute(
                    'data-forbidden-rule-name',
                    ruleEntries[0].ruleName
                  );

                  // D) Remove title attribute - let TooltipManager handle display via library
                  element.removeAttribute('title');

                  // E) Expose the reason to assistive tech (WCAG 1.3.1). MUST run
                  // after (C) — it reads data-forbidden-reason, so calling it up
                  // beside setAriaDisabled would find nothing and silently no-op.
                  setForbiddenDescription(element, true);
                });

                disabledCount += elements.length;

                isDev() &&
                  console.log(
                    '[UIUpdater] 🚫 Forbidden-disabled variant ' + variantId,
                    {
                      reason: reasonText.substring(0, 80),
                      ruleCount: ruleEntries.length,
                      elementsFound: elements.length,
                    }
                  );
              } else {
                notFoundCount++;
                isDev() &&
                  console.log(
                    '[UIUpdater] 🔍 No DOM element found for forbidden variant ' +
                      variantId +
                      ' (may not be rendered yet)'
                  );
              }
            });

            // Step 3: PRODUCT-LEVEL FORBIDDEN AGGREGATION
            // Mirrors applyMaterialFilterClasses() pattern (lines 244-310):
            // Build product-level map from variant-level forbidden state,
            // then disable product thumbnails where ALL variants are forbidden.
            // This prevents users from clicking a product only to see all variants greyed out.
            let forbiddenProductCount = 0;

            if (itemsToDisable.length > 0) {
              try {
                const ppd = getPPD();
                if (ppd && ppd.variantIdToProductIdMap) {
                  // Build Set of forbidden variant IDs for O(1) lookups
                  // Also build Map of variantId -> rule entries to aggregate real rule descriptions
                  const forbiddenVariantIds = new Set();
                  const forbiddenVariantRulesMap = new Map();
                  itemsToDisable.forEach(function (entry) {
                    const vidStr = String(entry[0]);
                    forbiddenVariantIds.add(vidStr);
                    forbiddenVariantRulesMap.set(vidStr, entry[1] || []);
                  });

                  // Build product-level forbidden map: productId -> { forbiddenCount, totalCount }
                  // Uses PPD category data to access product-variant relationships (same pattern as material filter)
                  const forbiddenProductMap = new Map();

                  if (ppd.categories) {
                    ppd.categories.forEach(function (categoryData) {
                      if (categoryData && categoryData.products) {
                        categoryData.products.forEach(function (product) {
                          // Skip products we've already processed (products can appear in multiple category views)
                          if (forbiddenProductMap.has(product.id)) return;

                          let forbiddenCount = 0;
                          const totalCount = product.variants
                            ? product.variants.length
                            : 0;

                          // DEBUG: Log composite product checking
                          const isComposite =
                            product.isComposite ||
                            product.id.startsWith('composite-');
                          isComposite &&
                            isDev() &&
                            console.log(
                              '[UIUpdater] 🔍 Checking COMPOSITE product: ' +
                                product.id +
                                ' with ' +
                                totalCount +
                                ' variants'
                            );

                          if (
                            product.variants &&
                            Array.isArray(product.variants)
                          ) {
                            product.variants.forEach(function (variant) {
                              if (forbiddenVariantIds.has(String(variant.id))) {
                                forbiddenCount++;
                                isComposite &&
                                  isDev() &&
                                  console.log(
                                    '[UIUpdater] 🚫 COMPOSITE variant forbidden: ' +
                                      variant.id
                                  );
                              }
                            });
                          }

                          // Only track products that have at least one forbidden variant
                          if (forbiddenCount > 0) {
                            forbiddenProductMap.set(product.id, {
                              forbiddenCount: forbiddenCount,
                              totalCount: totalCount,
                              productVariants: product.variants || [],
                            });

                            isComposite &&
                              isDev() &&
                              console.log(
                                '[UIUpdater] 📊 COMPOSITE product forbidden stats: ' +
                                  forbiddenCount +
                                  '/' +
                                  totalCount +
                                  ' (' +
                                  (forbiddenCount >= totalCount
                                    ? 'FULLY FORBIDDEN'
                                    : 'PARTIALLY FORBIDDEN') +
                                  ')'
                              );
                          }
                        });
                      }
                    });
                  }

                  // Disable product thumbnails where ALL variants are forbidden
                  // Collect product-level tooltips to register after DOM updates
                  const productTooltipsToRegister = [];

                  // HTML escape utility for tooltip content (prevents XSS from metaobject data)
                  const escapeHTML = function (str) {
                    return String(str || '').replace(/[&<>'"]/g, function (c) {
                      return {
                        '&': '&amp;',
                        '<': '&lt;',
                        '>': '&gt;',
                        "'": '&#39;',
                        '"': '&quot;',
                      }[c];
                    });
                  };

                  forbiddenProductMap.forEach(function (counts, productId) {
                    if (
                      counts.totalCount > 0 &&
                      counts.forbiddenCount >= counts.totalCount
                    ) {
                      // CRITICAL FIX: ALWAYS queue tooltip registration (DOM-independent)
                      // This ensures tooltips are registered even if DOM elements don't exist yet
                      // (e.g., composite product thumbnails before material filtering applied)
                      // Fixes bug where clicking into composite product shows no tooltip

                      // Aggregate actual rule descriptions from forbidden variants instead of generic text
                      let tooltipRuleName = 'Product Not Compatible';
                      let tooltipRuleDescription =
                        'All options for this product are incompatible with your current selections.';
                      try {
                        const seenRuleIds = {};
                        const uniqueRules = [];
                        (counts.productVariants || []).forEach(
                          function (variant) {
                            const rules =
                              forbiddenVariantRulesMap.get(
                                String(variant.id)
                              ) || [];
                            rules.forEach(function (rule) {
                              if (rule.ruleId && !seenRuleIds[rule.ruleId]) {
                                seenRuleIds[rule.ruleId] = true;
                                uniqueRules.push(rule);
                              }
                            });
                          }
                        );
                        if (uniqueRules.length > 0) {
                          tooltipRuleName = uniqueRules
                            .map(function (r) {
                              return escapeHTML(r.ruleName || 'Rule');
                            })
                            .join('; ');
                          tooltipRuleDescription = uniqueRules
                            .map(function (r) {
                              return escapeHTML(r.reason || r.ruleName || '');
                            })
                            .join('<br>');
                        }
                      } catch (e) {
                        isDev() &&
                          console.error(
                            '[UIUpdater] Rule aggregation failed for product-level forbidden tooltip, falling back to generic text',
                            e
                          );
                      }

                      productTooltipsToRegister.push({
                        productId: productId,
                        categoryId: 'forbidden-product-' + productId,
                        ruleName: tooltipRuleName,
                        ruleDescription: tooltipRuleDescription,
                      });

                      // Find product thumbnail DOM elements - target product-level only
                      // Product thumbnails have data-product-id but NOT data-variant-id
                      // Variant thumbnails have BOTH data-product-id AND data-variant-id
                      const productElements = document.querySelectorAll(
                        '[data-product-id="' +
                          productId +
                          '"]:not([data-variant-id])'
                      );

                      productElements.forEach(function (element) {
                        // Only target thumbnail items (safety check)
                        if (
                          element.classList.contains(
                            'murphy-door-configurator__thumbnail-item'
                          )
                        ) {
                          // Add visual disabling classes (matches variant-level forbidden styling)
                          element.classList.add(
                            'forbidden-product-all-disabled'
                          );
                          element.classList.add('murphy-door-button-tooltip');

                          // Add data-category for tooltip registration
                          element.setAttribute(
                            'data-category',
                            'forbidden-product-' + productId
                          );

                          // Set basic title as fallback (rich tooltip will override)
                          element.setAttribute(
                            'title',
                            'Not compatible with current selections'
                          );

                          // Mirror the state to assistive tech, exactly as the
                          // variant-level pass does. This was missed when that pass
                          // was fixed on 2026-08-21: a product whose variants are ALL
                          // forbidden gets a DIFFERENT class here, so the fix keyed on
                          // .forbidden-disabled never reached it and the tile stayed
                          // greyed-but-announced-as-available. Found by reading the
                          // live DOM on 2026-08-24, not by any test.
                          element.setAttribute(
                            'data-forbidden-reason',
                            'Not compatible with current selections'
                          );
                          window.MurphyDoorConfigurator.utils.setAriaDisabled(
                            element,
                            true
                          );
                          setForbiddenDescription(element, true);

                          forbiddenProductCount++;
                        }
                      });

                      isDev() &&
                        console.log(
                          '[UIUpdater] 🚫 Product fully forbidden: ' +
                            productId +
                            ' (' +
                            counts.forbiddenCount +
                            '/' +
                            counts.totalCount +
                            ' variants forbidden)' +
                            ' (DOM elements found: ' +
                            productElements.length +
                            ')'
                        );
                    }
                  });

                  // Register product-level tooltips with TooltipManager
                  if (productTooltipsToRegister.length > 0) {
                    productTooltipsToRegister.forEach(function (tooltipData) {
                      publishEvent(
                        CONSTANTS.EVENTS.UI_REGISTER_CUSTOM_TOOLTIP,
                        {
                          categoryId: tooltipData.categoryId,
                          tooltipData: {
                            text: tooltipData.ruleName,
                            html: tooltipData.ruleDescription,
                          },
                          timestamp: Date.now(),
                        }
                      );
                    });

                    // Trigger TooltipManager to initialize the new product-level tooltips
                    publishEvent(CONSTANTS.EVENTS.UI_INITIALIZE_TOOLTIPS, {
                      source: 'ui-updater.product-level-forbidden',
                      count: productTooltipsToRegister.length,
                      timestamp: Date.now(),
                    });

                    isDev() &&
                      console.log(
                        '[UIUpdater] 📋 Registered ' +
                          productTooltipsToRegister.length +
                          ' product-level forbidden tooltips'
                      );
                  }

                  if (forbiddenProductCount > 0) {
                    isDev() &&
                      console.log(
                        '[UIUpdater] 🛡️ Product-level forbidden: disabled ' +
                          forbiddenProductCount +
                          ' product thumbnails where ALL variants are forbidden'
                      );
                  }
                }
              } catch (productError) {
                isDev() &&
                  console.error(
                    '[UIUpdater] ❌ Product-level forbidden aggregation failed:',
                    productError
                  );
                // Non-fatal: variant-level disabling still works, product-level is enhancement
              }
            }

            const duration = performance.now() - startTime;

            // Performance tracking
            if (duration > PERFORMANCE_THRESHOLDS.UI_UPDATE) {
              isDev() &&
                console.warn(
                  '[UIUpdater] 🛡️ Preemptive forbidden UI update exceeded threshold: ' +
                    duration.toFixed(2) +
                    'ms > ' +
                    PERFORMANCE_THRESHOLDS.UI_UPDATE +
                    'ms'
                );
            }

            // Publish completion event (includes itemsToDisable for tooltip initializer)
            publishEvent(
              CONSTANTS.EVENTS.UI_UPDATE_PREEMPTIVE_FORBIDDEN_COMPLETE,
              {
                disabledCount: disabledCount,
                clearedCount: clearedCount,
                clearedProductCount: clearedProductCount,
                forbiddenProductCount: forbiddenProductCount,
                notFoundCount: notFoundCount,
                totalItems: itemsToDisable.length,
                itemsToDisable: itemsToDisable,
                duration: duration,
                triggerCategoryId: eventData.triggerCategoryId,
                timestamp: Date.now(),
                source: 'ui-updater.preemptive-forbidden',
              }
            );

            isDev() &&
              console.log(
                '[UIUpdater] 🛡️ Preemptive forbidden UI update complete',
                {
                  disabledCount: disabledCount,
                  clearedCount: clearedCount,
                  clearedProductCount: clearedProductCount,
                  forbiddenProductCount: forbiddenProductCount,
                  notFoundCount: notFoundCount,
                  duration: duration.toFixed(2) + 'ms',
                }
              );
          } catch (error) {
            isDev() &&
              console.error(
                '[UIUpdater] ❌ Preemptive forbidden UI update failed:',
                error
              );

            // CCP GOLDEN STANDARD: Publish failed event
            publishEvent(
              CONSTANTS.EVENTS.UI_UPDATE_PREEMPTIVE_FORBIDDEN_FAILED,
              {
                error: error.message,
                triggerCategoryId: eventData.triggerCategoryId,
                timestamp: Date.now(),
                source: 'ui-updater.preemptive-forbidden',
              }
            );
          }
        }
      )
    );
    isDev() &&
      console.log(
        '[UIUpdater] ✅ Subscribed to UI_UPDATE_PREEMPTIVE_FORBIDDEN for preemptive forbidden disabling'
      );

    // =========================================================================
    // HAPTIC FEEDBACK SYSTEM
    // =========================================================================
    // Provides tactile feedback on mobile devices for meaningful configurator
    // interactions. Uses the Vibration API with graceful degradation:
    // - No-op on iOS (navigator.vibrate not supported)
    // - No-op on desktop (touch detection guard)
    // - Respects prefers-reduced-motion accessibility setting
    //
    // CCP Compliant: All triggers are event-driven via CoreEventBus subscriptions.
    // No direct module-to-module calls. Self-contained private helper.
    // =========================================================================

    /**
     * Detect if the current device supports and should receive haptic feedback.
     * Cached at subscription time to avoid repeated checks per event.
     * Guards: touch device + vibration API support + motion preference respected.
     */
    const _hapticSupported = (function () {
      // Guard 1: Mobile/touch device only
      const isTouchDevice =
        'ontouchstart' in window || navigator.maxTouchPoints > 0;
      if (!isTouchDevice) return false;

      // Guard 2: Vibration API available
      if (typeof navigator.vibrate !== 'function') return false;

      // Guard 3: Respect reduced-motion accessibility preference
      if (
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      )
        return false;

      return true;
    })();

    // Named vibration patterns - centralizes magic numbers for easy UX tuning
    const HAPTIC_PATTERNS = Object.freeze({
      SUCCESS: 30, // Subtle tap - selection confirmed
      SUCCESS_PULSE: [30, 50, 100], // Rising pulse - cart add success
      ERROR: [50, 30, 50], // Error buzz - forbidden selection
      ERROR_STRONG: [80, 40, 80], // Strong error - cart add failure
      WARNING: [40, 20, 40], // Warning pattern - conflict detected
      PROGRESS: 20, // Micro tap - category completed
      CELEBRATION: [50, 30, 80, 30, 120], // Party pattern - confetti
      CONFIRM: [20, 30, 20], // Double tap - restoration complete
    });

    // Throttle state - prevents haptic spam during rapid-fire event sequences
    // (e.g. cascade auto-selection, URL parameter restoration)
    let _lastHapticTime = 0;
    const HAPTIC_THROTTLE_MS = 200;

    /**
     * Private haptic feedback trigger with all safety guards.
     * @param {number|number[]} pattern - Vibration pattern (ms) or single duration
     */
    function _triggerHaptic(pattern) {
      if (!_hapticSupported) return;
      const now = Date.now();
      if (now - _lastHapticTime < HAPTIC_THROTTLE_MS) return;
      _lastHapticTime = now;
      try {
        navigator.vibrate(pattern);
      } catch (e) {
        // Silent catch - haptic failure must never break UI flow
      }
    }

    if (_hapticSupported) {
      isDev() &&
        console.log(
          '[UIUpdater] Haptic feedback enabled (touch device with Vibration API)'
        );

      // --- Tier 1: Must Have ---

      // Selection applied - subtle tap confirming user choice
      cleanupHandlers.push(
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.SELECTION_APPLIED,
          () => {
            _triggerHaptic(HAPTIC_PATTERNS.SUCCESS);
          }
        )
      );

      // Forbidden selection attempt - error buzz pattern
      cleanupHandlers.push(
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.UI_SELECTION_FORBIDDEN,
          () => {
            _triggerHaptic(HAPTIC_PATTERNS.ERROR);
          }
        )
      );

      // Cart add success - rising pulse celebration
      cleanupHandlers.push(
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.BUSINESS_CART_ADD_COMPLETED,
          () => {
            _triggerHaptic(HAPTIC_PATTERNS.SUCCESS_PULSE);
          }
        )
      );

      // Cart add failure - error pattern
      cleanupHandlers.push(
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.BUSINESS_CART_ADD_FAILED,
          () => {
            _triggerHaptic(HAPTIC_PATTERNS.ERROR_STRONG);
          }
        )
      );

      // Confetti celebration - party pattern
      cleanupHandlers.push(
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.UI_CONFETTI_CELEBRATION_REQUESTED,
          () => {
            _triggerHaptic(HAPTIC_PATTERNS.CELEBRATION);
          }
        )
      );

      // --- Tier 2: Nice to Have ---

      // Category completed - micro tap
      cleanupHandlers.push(
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.STATE_CATEGORY_COMPLETED,
          () => {
            _triggerHaptic(HAPTIC_PATTERNS.PROGRESS);
          }
        )
      );

      // Forbidden conflict detected - warning pattern
      cleanupHandlers.push(
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.FORBIDDEN_CONFLICT_DETECTED,
          () => {
            _triggerHaptic(HAPTIC_PATTERNS.WARNING);
          }
        )
      );

      // URL restoration complete - double tap confirmation
      cleanupHandlers.push(
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          CONSTANTS.EVENTS.RESTORATION_COMPLETE,
          () => {
            _triggerHaptic(HAPTIC_PATTERNS.CONFIRM);
          }
        )
      );

      isDev() &&
        console.log(
          '[UIUpdater] Subscribed to 8 haptic feedback events (5 Tier 1 + 3 Tier 2)'
        );
    } else {
      isDev() &&
        console.log(
          '[UIUpdater] Haptic feedback disabled (non-touch device, no Vibration API, or prefers-reduced-motion)'
        );
    }
  } else {
    isDev() &&
      console.warn(
        'UI Updater: CoreEventBus not available for loading state events'
      );
  }

  // 🎬 CCP COMPLIANCE: Event handlers for URL Recovery Animator DOM requests
  if (window.MurphyDoorConfigurator?.CoreEventBus) {
    // Style injection support for URL Recovery Animator
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_INJECT_STYLES,
        (data) => {
          isDev() &&
            console.log('🎨 UI UPDATER: Style injection requested:', data);
          const { styleId, styles, source } = data;

          // Check if style already exists
          if (document.getElementById(styleId)) {
            isDev() &&
              console.log(
                '💄 UI UPDATER: Style already exists, skipping:',
                styleId
              );
            return;
          }

          const styleElement = document.createElement('style');
          styleElement.id = styleId;
          styleElement.textContent = styles;
          document.head.appendChild(styleElement);
          isDev() &&
            console.log('✅ UI UPDATER: Style injected successfully:', {
              styleId,
              source,
            });
        }
      )
    );

    // Element creation support for URL Recovery Animator
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_CREATE_ELEMENT,
        (data) => {
          isDev() &&
            console.log('🔨 UI UPDATER: Element creation requested:', data);
          const { type, className, innerHTML, parent, id, source } = data;

          const element = document.createElement(type);
          if (className) element.className = className;
          // Pattern #17: XSS audit trail - event-sourced HTML from trusted modules
          if (innerHTML) element.innerHTML = sanitizeHTML(innerHTML);
          if (id) element.id = id;

          const parentElement =
            parent === 'body' ? document.body : document.querySelector(parent);
          if (parentElement) {
            parentElement.appendChild(element);
            isDev() &&
              console.log('✅ UI UPDATER: Element created successfully:', {
                type,
                id,
                source,
              });
          } else {
            isDev() &&
              console.warn('⚠️ UI UPDATER: Parent element not found:', parent);
          }
        }
      )
    );

    // Element update support for URL Recovery Animator
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_UPDATE_ELEMENTS,
        (data) => {
          isDev() &&
            console.log('🔄 UI UPDATER: Element updates requested:', data);
          const { updates, source } = data;

          updates.forEach((update) => {
            const element = document.querySelector(update.selector);
            if (element) {
              if (update.property.includes('.')) {
                // Handle nested properties like 'style.width'
                const parts = update.property.split('.');
                let target = element;
                for (let i = 0; i < parts.length - 1; i++) {
                  target = target[parts[i]];
                }
                target[parts[parts.length - 1]] = update.value;
              } else {
                element[update.property] = update.value;
              }
            }
          });
          isDev() &&
            console.log('✅ UI UPDATER: Elements updated successfully:', {
              count: updates.length,
              source,
            });
        }
      )
    );

    // Element removal support for URL Recovery Animator
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_REMOVE_ELEMENT,
        (data) => {
          isDev() &&
            console.log('🗑️ UI UPDATER: Element removal requested:', data);
          const { selector, source } = data;

          const element = document.querySelector(selector);
          if (element) {
            element.remove();
            isDev() &&
              console.log('✅ UI UPDATER: Element removed successfully:', {
                selector,
                source,
              });
          } else {
            isDev() &&
              console.warn(
                '⚠️ UI UPDATER: Element not found for removal:',
                selector
              );
          }
        }
      )
    );

    // Element search support for URL Recovery Animator
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_SEARCH_ELEMENT,
        (data) => {
          isDev() &&
            console.log('🔍 UI UPDATER: Element search requested:', data);
          const { selectors, source, searchId } = data;

          let foundElement = null;
          for (const selector of selectors) {
            foundElement = document.querySelector(selector);
            if (foundElement) {
              isDev() &&
                console.log('✅ UI UPDATER: Element found:', {
                  selector,
                  searchId,
                });
              break;
            }
          }

          // Publish search result
          window.MurphyDoorConfigurator.CoreEventBus.publish(
            CONSTANTS.EVENTS.UI_ELEMENT_SEARCH_RESULT,
            {
              searchId,
              element: foundElement,
              source: 'ui-updater',
            }
          );
        }
      )
    );

    // Animation state check support
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_CHECK_ANIMATION_STATE,
        (data) => {
          isDev() &&
            console.log(
              '🎭 UI UPDATER: Animation state check requested:',
              data
            );
          const { source } = data;

          const isQuiet = !document.querySelector('.transitioning, .animating');

          // Publish animation state result
          window.MurphyDoorConfigurator.CoreEventBus.publish(
            CONSTANTS.EVENTS.UI_ANIMATION_STATE_RESULT,
            {
              isQuiet,
              source: 'ui-updater',
            }
          );
        }
      )
    );

    // Click simulation support for URL Recovery Animator
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_SIMULATE_CLICK,
        (data) => {
          isDev() &&
            console.log('🖱️ UI UPDATER: Click simulation requested:', data);
          const { selector, source } = data;

          const element = document.querySelector(selector);
          if (element) {
            const clickEvent = new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window,
            });
            element.dispatchEvent(clickEvent);
            isDev() &&
              console.log('✅ UI UPDATER: Click simulated successfully:', {
                selector,
                source,
              });
          } else {
            isDev() &&
              console.warn(
                '⚠️ UI UPDATER: Element not found for click simulation:',
                selector
              );
          }
        }
      )
    );

    // Element highlighting support for URL Recovery Animator
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_HIGHLIGHT_ELEMENT,
        (data) => {
          isDev() &&
            console.log('✨ UI UPDATER: Element highlighting requested:', data);
          const { element, className, duration, source } = data;

          if (element) {
            element.classList.add(className);

            // Remove highlight after duration
            setTimeout(() => {
              element.classList.remove(className);
            }, duration);

            isDev() &&
              console.log('✅ UI UPDATER: Element highlighted successfully:', {
                className,
                duration,
                source,
              });
          } else {
            isDev() &&
              console.warn(
                '⚠️ UI UPDATER: No element provided for highlighting'
              );
          }
        }
      )
    );

    // URL Recovery Animator specific UI events (CCP compliance enhancement)
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_ADD_RECOVERY_COMPLETE_EFFECT,
        (data) => {
          isDev() &&
            console.log(
              '🎬 UI UPDATER: Recovery complete effect requested:',
              data
            );
          const { thumbnailElement, className, duration, source } = data;

          if (thumbnailElement) {
            // Look for img element first, fallback to thumbnail element
            const finalThumbnailImage = thumbnailElement.querySelector('img');
            const targetElement = finalThumbnailImage || thumbnailElement;

            if (targetElement) {
              targetElement.classList.add(className);
              setTimeout(() => {
                targetElement.classList.remove(className);
              }, duration);
              isDev() &&
                console.log(
                  '✅ UI UPDATER: Recovery complete effect applied:',
                  {
                    className,
                    duration,
                    source,
                  }
                );
            }
          } else {
            isDev() &&
              console.warn(
                '⚠️ UI UPDATER: No thumbnail element provided for recovery effect'
              );
          }
        }
      )
    );

    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_SEARCH_ELEMENTS,
        (data) => {
          isDev() &&
            console.log('🔍 UI UPDATER: Elements search requested:', data);
          const { selector, callback } = data;

          const elements = document.querySelectorAll(selector);

          if (callback && window.MurphyDoorConfigurator?.CoreEventBus) {
            window.MurphyDoorConfigurator.CoreEventBus.publish(callback, {
              elements: elements,
              selector: selector,
              count: elements.length,
            });
          }

          isDev() &&
            console.log('✅ UI UPDATER: Elements search completed:', {
              selector,
              count: elements.length,
            });
        }
      )
    );

    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_CHECK_ELEMENT_CLASS,
        (data) => {
          isDev() &&
            console.log('🔍 UI UPDATER: Element class check requested:', data);
          const { element, className, callback } = data;

          const hasClass = element && element.classList.contains(className);

          if (callback && window.MurphyDoorConfigurator?.CoreEventBus) {
            window.MurphyDoorConfigurator.CoreEventBus.publish(callback, {
              hasClass: hasClass,
              element: element,
              className: className,
            });
          }

          isDev() &&
            console.log('✅ UI UPDATER: Element class check completed:', {
              className,
              hasClass,
            });
        }
      )
    );

    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_TEMPORARY_CLASS,
        (data) => {
          isDev() &&
            console.log('⏱️ UI UPDATER: Temporary class requested:', data);
          const { element, className, operation, duration, source } = data;

          if (element && operation === 'add') {
            element.classList.add(className);

            if (duration) {
              setTimeout(() => {
                element.classList.remove(className);
              }, duration);
            }

            isDev() &&
              console.log('✅ UI UPDATER: Temporary class applied:', {
                className,
                duration,
                source,
              });
          } else {
            isDev() &&
              console.warn(
                '⚠️ UI UPDATER: Invalid temporary class request:',
                data
              );
          }
        }
      )
    );

    // Subscribe to cart loading spinner events from cart manager
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_SHOW_LOADING_SPINNER,
        (data) => {
          isDev() &&
            console.log('⏳ UI UPDATER: Show loading spinner requested:', data);
          // The add-to-cart flow gets its own message so the spinner doesn't
          // reuse the page-load "Crafting your custom door experience…" copy.
          // Product noun is sourced from the configurator wrapper's
          // data-product-noun attribute (Liquid: hardcoded "door" for MVP,
          // future-ready for shelves/beds/etc via a per-product metafield).
          let cartMessage;
          if (data && data.operation === 'add_to_cart') {
            const wrapper = document.querySelector(
              '.murphy-door-configurator-wrapper'
            );
            const noun =
              (wrapper && wrapper.getAttribute('data-product-noun')) || 'door';
            cartMessage = `Adding your customized ${noun} to cart...`;
          }
          uiUpdater.showLoadingSpinner(cartMessage);
        }
      )
    );

    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_HIDE_LOADING_SPINNER,
        (data) => {
          isDev() &&
            console.log('✅ UI UPDATER: Hide loading spinner requested:', data);
          uiUpdater.hideLoadingSpinner();
        }
      )
    );

    // Stored configured-door image PREFETCH (Gap Plan #25b, merged door line).
    // The door picture must already be uploaded when Add to Cart is clicked,
    // so the add can carry _Configuration_Image on the BASE line (a desktop
    // add navigates to /cart and would cancel an upload started at the click).
    // This module owns the Add to Cart DOM, so it decides WHEN: a pointer or
    // keyboard focus on an enabled Add to Cart button, or 5 s with no
    // selection change while one is on screen. cart-manager decides WHETHER:
    // it uploads at most once per configuration state (the route allows 20
    // uploads per 10 min per IP), so these requests are cheap to repeat.
    const DOOR_IMAGE_PREFETCH_IDLE_MS = 5000;
    const DOOR_IMAGE_PREFETCH_THROTTLE_MS = 1000;
    const DOOR_IMAGE_ATC_SELECTOR =
      '.murphy-door-configurator__add-to-cart-button';
    let doorImageIdleTimer = null;
    let doorImageLastRequestAt = 0;
    const isDoorImageAtcReady = (button) =>
      !!button &&
      !button.disabled &&
      button.getAttribute('aria-disabled') !== 'true';
    const isDoorImageAtcOnScreen = () =>
      Array.from(document.querySelectorAll(DOOR_IMAGE_ATC_SELECTOR)).some(
        (button) => {
          if (!isDoorImageAtcReady(button)) return false;
          const rect = button.getBoundingClientRect();
          return (
            rect.width > 0 &&
            rect.height > 0 &&
            rect.bottom > 0 &&
            rect.right > 0 &&
            rect.top < (window.innerHeight || 0) &&
            rect.left < (window.innerWidth || 0)
          );
        }
      );
    const requestDoorImagePrefetch = (trigger) => {
      const now = Date.now();
      if (now - doorImageLastRequestAt < DOOR_IMAGE_PREFETCH_THROTTLE_MS) {
        return;
      }
      doorImageLastRequestAt = now;
      publishEvent(CONSTANTS.EVENTS.UI_CONFIGURED_DOOR_IMAGE_PREFETCH_REQUESTED, {
        source: 'ui-updater',
        trigger,
        timestamp: now,
      });
    };
    const onDoorImageAtcIntent = (event) => {
      const target = event && event.target;
      const button =
        target && typeof target.closest === 'function'
          ? target.closest(DOOR_IMAGE_ATC_SELECTOR)
          : null;
      if (!isDoorImageAtcReady(button)) return;
      requestDoorImagePrefetch(event.type === 'focusin' ? 'focus' : 'pointer');
    };
    const armDoorImageIdle = () => {
      clearTimeout(doorImageIdleTimer);
      doorImageIdleTimer = setTimeout(() => {
        if (isDoorImageAtcOnScreen()) requestDoorImagePrefetch('idle');
      }, DOOR_IMAGE_PREFETCH_IDLE_MS);
    };
    document.addEventListener('pointerover', onDoorImageAtcIntent, {
      passive: true,
    });
    document.addEventListener('focusin', onDoorImageAtcIntent);
    [
      CONSTANTS.EVENTS.SELECTION_VARIANT_APPLIED,
      CONSTANTS.EVENTS.SELECTION_AUTO_APPLY_COMPLETED,
      CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_COMPLETED,
      CONSTANTS.EVENTS.STATE_SELECTION_CLEARED,
    ].forEach((eventType) => {
      cleanupHandlers.push(
        window.MurphyDoorConfigurator.CoreEventBus.subscribe(
          eventType,
          armDoorImageIdle
        )
      );
    });
    armDoorImageIdle();
    cleanupHandlers.push(() => {
      clearTimeout(doorImageIdleTimer);
      document.removeEventListener('pointerover', onDoorImageAtcIntent);
      document.removeEventListener('focusin', onDoorImageAtcIntent);
    });

    // Cart-type-aware reveal after a successful configurator add. Mirrors
    // native product-form.js element detection: when the theme cart type is
    // Drawer the <cart-drawer> custom element exists, so we hand off to its
    // proven section-fetch + slide-open flow (via the
    // configurator:cart-add-complete DOM event the drawer listens for).
    // Otherwise (Page mode, or Notification mode whose single-product toast
    // cannot represent a grouped multi-line door config) we redirect to the
    // full /cart page. This is the ONLY module permitted DOM access for the
    // reveal — the BL cart-manager just publishes UI_CART_REVEAL_REQUESTED.
    // Cart thumbnail snapshot gate. cart-manager publishes
    // UI_CART_IMAGE_SNAPSHOT_REQUESTED right before POST /cart/add.js and the
    // image export manager answers with _COMPLETED / _FAILED once the flattened
    // PNG is persisted (or given up on). When the reveal below is going to
    // NAVIGATE to /cart (page mode, notification mode, or the drawer's own
    // "mobile only" rule on tablet+), a navigation that outruns the snapshot
    // destroys it mid-flight and the cart shows the stock photo. So a
    // navigating reveal waits for the snapshot to settle, bounded by
    // CART_SNAPSHOT_REVEAL_WAIT_MS. An in-place drawer open never waits:
    // <cart-configuration-image> picks up a late save on its own.
    // Measured 0.34-0.84s end-to-end on broadband (local + staging); the cap
    // is ~2x that. Past it the cart simply shows the stock photo, which is
    // the designed fail-open, so the customer never waits long for Delight.
    const CART_SNAPSHOT_REVEAL_WAIT_MS = 1500;
    let pendingCartSnapshot = null; // { configId, promise, settle }
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_CART_IMAGE_SNAPSHOT_REQUESTED,
        (data) => {
          // A pre-click image prefetch (Gap Plan #25b) is not an add: it must
          // never become the snapshot a navigating reveal waits on.
          if (data && data.purpose === 'prefetch') return;
          let settle;
          const promise = new Promise((resolve) => {
            settle = resolve;
          });
          pendingCartSnapshot = {
            configId: data && data.configId,
            promise,
            settle,
          };
        }
      )
    );
    const settleCartSnapshot = (data) => {
      if (
        pendingCartSnapshot &&
        (!data ||
          !data.configId ||
          data.configId === pendingCartSnapshot.configId)
      ) {
        pendingCartSnapshot.settle();
        pendingCartSnapshot = null;
      }
    };
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_CART_IMAGE_SNAPSHOT_COMPLETED,
        settleCartSnapshot
      )
    );
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_CART_IMAGE_SNAPSHOT_FAILED,
        settleCartSnapshot
      )
    );

    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_CART_REVEAL_REQUESTED,
        (data) => {
          isDev() && console.log('🛒 UI UPDATER: Cart reveal requested:', data);
          // Presence is not readiness. If the drawer's controller never loaded, the
          // element is still in the DOM but is a bare HTMLElement with no listener for
          // configurator:cart-add-complete - so the old truthy check took the drawer
          // branch, dispatched into the void, and SKIPPED the /cart fallback below. The
          // add succeeded and the customer saw nothing at all. Checking for .open() keeps
          // that documented fallback reachable, which is the whole reason it exists.
          const cartDrawerEl = document.querySelector('cart-drawer');
          const cartDrawerReady =
            cartDrawerEl && typeof cartDrawerEl.open === 'function';
          // The drawer redirects to /cart itself under its "mobile only"
          // setting on tablet+ (cart-drawer.js shouldUseCartPage), so that
          // counts as a navigating reveal too.
          const revealNavigates =
            !cartDrawerReady ||
            (typeof cartDrawerEl.shouldUseCartPage === 'function' &&
              cartDrawerEl.shouldUseCartPage() === true);
          const reveal = () => {
            if (cartDrawerReady) {
              // Exit the configurator's fullscreen takeover FIRST. The fullscreen
              // wrapper and the cart drawer both sit at z-index 1000, and the
              // configurator comes later in the DOM, so in fullscreen the drawer
              // opens BEHIND it (covered). Exiting fullscreen restores the normal
              // PDP and lets the drawer slide over it — exactly like native PDP
              // ATC (drawer over the page). exitFullscreenConfigurator no-ops
              // safely if the configurator wasn't fullscreen.
              window.MurphyDoorConfigurator.CoreEventBus.publish(
                CONSTANTS.EVENTS.UI_FULLSCREEN_EXIT_REQUESTED,
                { source: 'cart_reveal', timestamp: Date.now() }
              );
              // Reset the ATC button's optimistic "Adding to cart…" state. The
              // old flow redirected to /cart so the button text never needed
              // restoring; in drawer mode the configurator stays on the page, so
              // without this the button would read "Adding to cart…" forever if
              // the user closes the drawer and reopens the configurator.
              uiUpdater.restoreOptimisticCartFeedback();
              // Let the fullscreen-exit reflow + the loading-spinner hide paint
              // before the drawer slides in.
              setTimeout(() => {
                document.dispatchEvent(
                  new CustomEvent('configurator:cart-add-complete')
                );
              }, 150);
            } else {
              // Let /cart announce the successful add to screen readers
              // (consumed by assets/cart.js announceCartAddAfterRedirect).
              try {
                sessionStorage.setItem(
                  'mdCartAddAnnounce',
                  String(Date.now())
                );
              } catch (e) {
                /* storage unavailable - /cart just will not announce */
              }
              setTimeout(() => {
                window.location.href = '/cart';
              }, 100);
            }
          };

          if (revealNavigates && pendingCartSnapshot) {
            // The add has already succeeded; do not let the button keep
            // saying "Adding to cart..." while the thumbnail is written.
            uiUpdater.showOptimisticCartFeedback('Added, opening your cart...');
            const snapshot = pendingCartSnapshot;
            let revealed = false;
            const revealOnce = () => {
              if (revealed) return;
              revealed = true;
              reveal();
            };
            snapshot.promise.then(revealOnce);
            setTimeout(revealOnce, CART_SNAPSHOT_REVEAL_WAIT_MS);
          } else {
            reveal();
          }
        }
      )
    );

    // 🚀 INSTAGRAM OPTIMIZATION: Optimistic UI feedback for add to cart
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_CART_ADDING_OPTIMISTIC,
        (data) => {
          isDev() &&
            console.log(
              '🚀 UI UPDATER: Showing optimistic cart feedback:',
              data
            );
          uiUpdater.showOptimisticCartFeedback(data.message);
        }
      )
    );

    // Subscribe to cart error events for user feedback
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.ERROR_CART_ADD_FAILED,
        (data) => {
          isDev() && console.log('❌ UI UPDATER: Cart add error:', data);
          uiUpdater.hideLoadingSpinner();

          // FIX 3: Restore button state after optimistic UI rollback
          uiUpdater.restoreOptimisticCartFeedback();

          // CCP COMPLIANT: Show error message via proper UI notification system
          window.MurphyDoorConfigurator.CoreEventBus.publish(
            CONSTANTS.EVENTS.UI_SHOW_ERROR_NOTIFICATION,
            {
              message: 'Failed to add items to cart. Please try again.',
              type: 'error',
              duration: 5000,
              source: 'ui-updater',
            }
          );
        }
      )
    );

    // Subscribe to button handler setup requests from persistence manager
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_SETUP_BUTTON_HANDLERS,
        (data) => {
          isDev() &&
            console.log('🔘 UI UPDATER: Button handler setup requested:', data);
          const { buttons, source } = data;

          // Set up event handlers for each button configuration
          buttons.forEach((buttonConfig) => {
            const { selector, event, preventDefault } = buttonConfig;
            const elements = document.querySelectorAll(selector);

            isDev() &&
              console.log(
                `🔘 UI UPDATER: Setting up ${elements.length} handlers for ${selector} -> ${event}`
              );

            elements.forEach((element) => {
              element.addEventListener('click', (e) => {
                if (preventDefault) {
                  e.preventDefault();
                }

                isDev() &&
                  console.log(
                    `🔘 UI UPDATER: Button clicked, publishing ${event}`,
                    { selector, source }
                  );

                // Publish the requested event
                window.MurphyDoorConfigurator.CoreEventBus.publish(event, {
                  source: 'ui-updater',
                  originalSource: source,
                  element: e.target,
                });
              });
            });
          });

          isDev() &&
            console.log(
              '✅ UI UPDATER: Button handlers configured successfully'
            );
        }
      )
    );

    // Subscribe to modal display requests from other modules
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_SHOW_MODAL,
        (data) => {
          isDev() &&
            console.log('📱 UI UPDATER: Modal display requested:', data);
          const { type, title, message, buttons, source, url } = data;

          // Determine which modal to show based on type
          var modalSelector, contentSelector;

          if (type === 'clear-confirmation') {
            modalSelector = '.murphy-door-configurator__clear-modal';
            contentSelector = '.murphy-door-configurator__clear-modal';
          } else if (type === 'share-success') {
            modalSelector = '.murphy-door-configurator__share-modal';
            contentSelector = '.murphy-door-configurator__share-modal';
          } else {
            isDev() && console.warn('⚠️ UI UPDATER: Unknown modal type:', type);
            return;
          }

          // Get modal elements
          var modal = document.querySelector(modalSelector);
          if (!modal) {
            isDev() &&
              console.error(
                '❌ UI UPDATER: Modal element not found:',
                modalSelector
              );
            return;
          }

          // Clean up any previous share modal listeners before adding new ones
          if (modal._cleanupShareListeners) {
            modal._cleanupShareListeners();
          }

          // Share modal: populate URL preview and wire up copy + dismiss behavior
          if (type === 'share-success' && url) {
            var modalTitleEl = modal.querySelector(
              '.murphy-door-configurator__modal-title'
            );
            if (modalTitleEl && title) modalTitleEl.textContent = title;

            var urlPreview = modal.querySelector(
              '.murphy-door-configurator__share-url-preview'
            );
            if (urlPreview) {
              // Full URL rendered; CSS text-overflow: ellipsis handles truncation
              urlPreview.textContent = url;
            }

            // Reset icon state to clipboard (in case modal was previously used)
            var clipboardIcon = modal.querySelector(
              '.murphy-door-configurator__share-icon--clipboard'
            );
            var checkmarkIcon = modal.querySelector(
              '.murphy-door-configurator__share-icon--checkmark'
            );
            var btnLabel = modal.querySelector(
              '.murphy-door-configurator__share-btn-label'
            );
            if (clipboardIcon) clipboardIcon.style.display = '';
            if (checkmarkIcon) checkmarkIcon.style.display = 'none';
            if (btnLabel) btnLabel.textContent = 'Copy to Clipboard';
          }

          // Show modal with flexbox centering
          modal.style.display = 'flex';

          // Share modal: wire up the "Copy to Clipboard" button with icon swap + auto-close
          if (type === 'share-success' && url) {
            var copyButton = modal.querySelector(
              '.murphy-door-configurator__share-modal-close'
            );
            var dismissButton = modal.querySelector(
              '.murphy-door-configurator__share-modal-dismiss'
            );

            // Centralized cleanup for all share modal listeners
            var cleanupShareListeners = function () {
              if (copyButton)
                copyButton.removeEventListener('click', handleCopyClick);
              if (dismissButton)
                dismissButton.removeEventListener('click', handleDismissClick);
              modal.removeEventListener('click', handleOutsideClick);
              modal._cleanupShareListeners = null;
            };

            var resetShareModalState = function () {
              var clipIcon = modal.querySelector(
                '.murphy-door-configurator__share-icon--clipboard'
              );
              var checkIcon = modal.querySelector(
                '.murphy-door-configurator__share-icon--checkmark'
              );
              var label = modal.querySelector(
                '.murphy-door-configurator__share-btn-label'
              );
              if (clipIcon) clipIcon.style.display = '';
              if (checkIcon) checkIcon.style.display = 'none';
              if (label) label.textContent = 'Copy to Clipboard';
            };

            var copyInProgress = false;

            var handleCopyClick = function (e) {
              e.preventDefault();
              if (copyInProgress) return;
              copyInProgress = true;
              isDev() &&
                console.log('📱 UI UPDATER: Copy to Clipboard clicked');

              var showSuccess = function () {
                var clipIcon = modal.querySelector(
                  '.murphy-door-configurator__share-icon--clipboard'
                );
                var checkIcon = modal.querySelector(
                  '.murphy-door-configurator__share-icon--checkmark'
                );
                var label = modal.querySelector(
                  '.murphy-door-configurator__share-btn-label'
                );
                if (clipIcon) clipIcon.style.display = 'none';
                if (checkIcon) checkIcon.style.display = '';
                if (label) label.textContent = 'Copied!';

                // Auto-close after 1s so user sees checkmark confirmation
                setTimeout(function () {
                  modal.style.display = 'none';
                  resetShareModalState();
                  cleanupShareListeners();
                  copyInProgress = false;
                }, 1000);
              };

              // Copy URL to clipboard, show feedback only on success
              if (navigator.clipboard) {
                navigator.clipboard
                  .writeText(url)
                  .then(function () {
                    isDev() &&
                      console.log(
                        '📱 UI UPDATER: URL copied to clipboard successfully'
                      );
                    showSuccess();
                  })
                  .catch(function (err) {
                    isDev() &&
                      console.warn(
                        '📱 UI UPDATER: Clipboard write failed:',
                        err
                      );
                    var label = modal.querySelector(
                      '.murphy-door-configurator__share-btn-label'
                    );
                    if (label) label.textContent = 'Copy Failed';
                    setTimeout(function () {
                      resetShareModalState();
                      copyInProgress = false;
                    }, 2000);
                  });
              } else {
                // Fallback: clipboard API unavailable
                isDev() &&
                  console.warn('📱 UI UPDATER: Clipboard API not available');
                var label = modal.querySelector(
                  '.murphy-door-configurator__share-btn-label'
                );
                if (label) label.textContent = 'Copy Failed';
                setTimeout(function () {
                  resetShareModalState();
                  copyInProgress = false;
                }, 2000);
              }
            };

            var handleDismissClick = function (e) {
              e.preventDefault();
              isDev() &&
                console.log(
                  '📱 UI UPDATER: Share modal dismissed via X button'
                );
              modal.style.display = 'none';
              cleanupShareListeners();
            };

            if (copyButton)
              copyButton.addEventListener('click', handleCopyClick);
            if (dismissButton)
              dismissButton.addEventListener('click', handleDismissClick);

            // Store cleanup ref so it can be called on next modal open or by UI_HIDE_MODAL
            modal._cleanupShareListeners = cleanupShareListeners;
          } else {
            // Non-share modals: set up button handlers as before
            buttons.forEach(function (buttonConfig) {
              var text = buttonConfig.text;
              var action = buttonConfig.action;
              var style = buttonConfig.style;
              var buttonSelector;

              if (style === 'cancel') {
                buttonSelector =
                  '.murphy-door-configurator__clear-modal-cancel';
              } else if (style === 'proceed') {
                buttonSelector =
                  '.murphy-door-configurator__clear-modal-proceed';
              } else if (style === 'primary') {
                buttonSelector = '.murphy-door-configurator__share-modal-close';
              }

              var button = document.querySelector(buttonSelector);
              if (button) {
                var handleClick = function (e) {
                  e.preventDefault();
                  isDev() &&
                    console.log(
                      '📱 UI UPDATER: Modal button clicked, publishing ' +
                        action
                    );

                  // Hide modal first
                  modal.style.display = 'none';

                  // Publish the action if it's not just hiding the modal
                  if (action !== CONSTANTS.EVENTS.UI_HIDE_MODAL) {
                    window.MurphyDoorConfigurator.CoreEventBus.publish(action, {
                      source: 'ui-updater',
                      originalSource: source,
                    });
                  }

                  // Clean up event listener
                  button.removeEventListener('click', handleClick);
                };

                button.addEventListener('click', handleClick);
              }
            });
          }

          // Close modal when clicking outside content
          var handleOutsideClick = function (event) {
            if (event.target === modal) {
              isDev() &&
                console.log('📱 UI UPDATER: Modal closed by clicking outside');
              modal.style.display = 'none';
              if (modal._cleanupShareListeners) {
                modal._cleanupShareListeners();
              } else {
                modal.removeEventListener('click', handleOutsideClick);
              }
            }
          };

          modal.addEventListener('click', handleOutsideClick);

          isDev() && console.log('✅ UI UPDATER: Modal displayed successfully');
        }
      )
    );

    // Subscribe to modal hide requests
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_HIDE_MODAL,
        (data) => {
          isDev() && console.log('📱 UI UPDATER: Modal hide requested:', data);

          // Hide all modals and clean up any share modal listeners
          const modals = document.querySelectorAll(
            '.murphy-door-configurator__modal'
          );
          modals.forEach((modal) => {
            if (modal._cleanupShareListeners) {
              modal._cleanupShareListeners();
            }
            modal.style.display = 'none';
          });

          isDev() && console.log('✅ UI UPDATER: All modals hidden');
        }
      )
    );

    isDev() &&
      console.log(
        '✅ UI UPDATER: CCP compliance event handlers initialized for DOM delegation'
      );
  }

  // 🎯 PRIORITY 3 FIX: Event subscriptions for price calculation responses
  if (window.MurphyDoorConfigurator?.CoreEventBus) {
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.PRICE_CALCULATION_COMPLETED,
        (eventData) => {
          // Only handle responses to our requests (correlation via requestId)
          if (
            eventData.requestId &&
            (eventData.requestId.startsWith('ui_updater_selection_') ||
              eventData.requestId.startsWith('ui_updater_composite_'))
          ) {
            isDev() &&
              console.log(
                '💰 UI UPDATER: Price calculation completed, updating display:',
                {
                  requestId: eventData.requestId,
                  totalPrice: eventData.totalPrice,
                  label: eventData.label,
                }
              );

            // Update price display with calculated price.
            // Default is BASE, not CONFIGURED: a label we did not receive is a
            // label we cannot vouch for, and "Starting at" over a correct
            // number is conservative where "Your Price" would be a quote.
            // Matches md-config-core-utils.js resolvePriceLabel()'s own
            // unreadable-input rule.
            uiUpdater.batchPriceUpdate({
              label:
                eventData.label ||
                window.MurphyDoorConfigurator?.utils?.PRICE_LABELS?.BASE ||
                'Starting at',
              amount: eventData.totalPrice,
            });
          }
        }
      )
    );

    isDev() &&
      console.log(
        '✅ UI UPDATER: Price calculation event subscription initialized'
      );
  }

  // 🎯 PRIORITY 3 FIX: Event subscriptions for material state responses
  if (window.MurphyDoorConfigurator?.CoreEventBus) {
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.STATE_MATERIAL_STATE_RESPONSE,
        (eventData) => {
          // Only handle responses to our requests (correlation via requestId)
          if (
            eventData.requestId &&
            (eventData.requestId.startsWith('ui_updater_thumbnails_') ||
              eventData.requestId.startsWith('ui_updater_indicators_'))
          ) {
            isDev() &&
              console.log(
                '🎨 UI UPDATER: Material state response received, updating cache:',
                {
                  requestId: eventData.requestId,
                  categoryId: eventData.categoryId,
                  materialState: eventData.materialState,
                }
              );

            // Update cache for synchronous access
            materialStateCache.set(eventData.categoryId, {
              state: eventData.materialState,
              timestamp: eventData.timestamp,
            });
          }
        }
      )
    );

    isDev() &&
      console.log(
        '✅ UI UPDATER: Material state event subscription initialized'
      );

    // 🚀 PERF MEMO: coarse defense-in-depth invalidation for a genuine PPD rebuild
    // mid-session. NOT the fix for the hydration-race gap (see thumbnailMemoCache doc
    // comment) — that's handled by never caching a pre-hydration result in the first
    // place — this just clears everything on the one event that legitimately signals
    // "the PPD is (re)ready", which is the same event md-config-html-cache-hydrator.js
    // itself subscribes to for its own hydration pass.
    cleanupHandlers.push(
      window.MurphyDoorConfigurator.CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
        () => {
          if (thumbnailMemoCache.size > 0) {
            isDev() &&
              console.log(
                `🧹 UI UPDATER: Clearing thumbnail memo cache (${thumbnailMemoCache.size} entries) on PPD ready`
              );
            thumbnailMemoCache.clear();
          }
        }
      )
    );
  }

  /**
   * CCP COMPLIANT: Publish tab readiness state for reactive synchronization
   * This enables the conductor to maintain cached state without polling
   * State Synchronization pattern - UI modules proactively publish state changes
   */
  function publishTabReadinessState() {
    try {
      // Query current tab DOM state
      const tabElements = document.querySelectorAll('[data-tab]');
      const activeTab = document.querySelector(
        '.murphy-tab-button.active[data-tab]'
      );

      const tabReadinessState = {
        tabsExist: tabElements.length > 0,
        hasActiveTab: !!activeTab,
        tabCount: tabElements.length,
        timestamp: performance.now(),
      };

      isDev() &&
        console.log(
          '🎯 UI UPDATER: Publishing tab readiness state (WORKING system)',
          tabReadinessState
        );

      // CCP COMPLIANT: Proactively publish state changes via event bus
      if (window.MurphyDoorConfigurator.CoreEventBus) {
        window.MurphyDoorConfigurator.CoreEventBus.publish(
          CONSTANTS.EVENTS.UI_TAB_READINESS_CHANGED,
          tabReadinessState
        );
      }
    } catch (error) {
      isDev() &&
        console.error(
          '❌ UI UPDATER: Failed to publish tab readiness state',
          error
        );
    }
  }

  // Add publishTabReadinessState to the uiUpdater object so it can be called from updateTabDisplay
  uiUpdater.publishTabReadinessState = publishTabReadinessState;

  /**
   * Render variants with material-aware filtering or sorting
   *
   * This method provides two UX modes:
   * - Simplified UX: Shows ONLY variants matching current material preferences
   * - Full UX: Shows ALL variants but sorted with matches first
   *
   * @param {string} productId - Product ID to render variants for
   * @param {string} categoryId - Category ID for rendering context
   */
  uiUpdater.renderVariantsWithMaterialAwareness = function (
    productId,
    categoryId
  ) {
    const ppd = getPPD();
    if (!ppd) {
      isDev() &&
        console.error(
          '❌ UI UPDATER: Cannot render material-aware variants without PPD'
        );
      return;
    }

    const conductor = window.MurphyDoorConfigurator.conductor;
    if (!conductor) {
      isDev() &&
        console.error(
          '❌ UI UPDATER: Cannot render material-aware variants without conductor'
        );
      return;
    }

    const preferences = conductor.getMaterialPreferences() || {
      woodApplied: false,
      finishApplied: false,
      appliedWoodSku: null,
      appliedFinishSku: null,
    };

    const simplifiedMode = ppd.simplifiedUxConfig?.enabled || false;

    // MATERIAL SOURCE GUARD -- the fourth consumer of "the master category
    // must not be filtered by its own preferences", after
    // applyMaterialFilterClasses (:377) and the two fed by
    // _computeFilterStateFallback (auto-apply and bubble-up).
    //
    // Simplified mode renders ONLY variants matching the applied preferences.
    // For a dependent category that is the whole point. For the material
    // SOURCE it means opening the wood you already have shows exactly the one
    // finish you already picked -- every other stain on that wood is rendered
    // and then hidden with an inline display:none by
    // renderVariantBrowserFromList, so the card opens onto a dead end.
    //
    // Latent until 2026-08-19: the master's tile used to be bubbled, so
    // clicking it applied a pre-resolved variant directly and nobody ever
    // reached this variant view. Removing that bubble-up made the path
    // reachable and the existing defect visible.
    //
    // The sorted branch is the right answer rather than a special case: it
    // returns every variant with the current match first, which is exactly
    // what "show me the other stains on this wood" needs.
    const materialSourceCategories = CONSTANTS.MATERIAL_SOURCE_CATEGORIES || [];
    const isMaterialSourceCategory =
      materialSourceCategories.indexOf(categoryId) !== -1;

    let variantsToRender;

    if (simplifiedMode && !isMaterialSourceCategory) {
      // Simplified UX: Show ONLY matching variants
      variantsToRender = conductor.getFilteredVariantsForProduct(
        productId,
        preferences
      );
      isDev() &&
        console.log(
          '🎯 UI UPDATER: Rendering FILTERED variants (Simplified UX)',
          { productId, count: variantsToRender?.length }
        );
    } else {
      // Full UX: Show ALL variants but sorted with matches first
      variantsToRender = conductor.getSortedVariantsForProduct(
        productId,
        preferences
      );
      isDev() &&
        console.log('🎯 UI UPDATER: Rendering SORTED variants (Full UX)', {
          productId,
          count: variantsToRender?.length,
        });
    }

    // Render variant browser with the filtered/sorted list
    this.renderVariantBrowserFromList(productId, categoryId, variantsToRender);

    // Ryan, UAT 2026-08-27: "when the customer clicks on a wood type and we
    // then show the various finish options available, auto-select the lowest
    // cost one for them." Runs after the grid exists, so it can respect which
    // finishes actually came out selectable.
    if (isMaterialSourceCategory) {
      this.autoSelectCheapestVariant(categoryId);
    }
  };

  /**
   * apo_style / click=apply ONLY. Picks the cheapest SELECTABLE variant tile in
   * an already-rendered variant grid and clicks it, so landing on the finish
   * list leaves the customer with a valid, cheapest-first choice they can then
   * change.
   *
   * 🚨 PRICE IS PARSED, NOT READ OFF POSITION. Do not "simplify" this to
   * "take the first tile". The grid's order comes from
   * getSortedVariantsForProduct via sortVariantsForSecondView
   * (md-config-support-sorting-manager.js), which sorts by MATERIAL SCORE
   * first and uses price only as a tiebreak -- and its price comparator also
   * floats any "No "-prefixed title to the front regardless of cost. Once a
   * wood is applied (exactly this moment) a material preference exists, so
   * position 0 is the best material match, NOT the cheapest.
   *
   * 🚨 PRICES ARE FORMATTED STRINGS. PPD variant.price is e.g. "1,190.00", and
   * parseFloat("1,190.00") is 1 -- which would rank the most expensive stain as
   * the cheapest. The [$,] strip is load-bearing and matches
   * getProductPrice() in md-config-core-data-pipeline.js.
   *
   * @param {string} categoryId - Category whose variant grid to act on
   * @returns {boolean} true when a tile was auto-selected
   */
  uiUpdater.autoSelectCheapestVariant = function (categoryId) {
    const METHOD_SIG = 'UIUpdater.autoSelectCheapestVariant';
    try {
      const conductor = window.MurphyDoorConfigurator.conductor;
      if (!conductor) return false;

      // GATE: apo_style, or the explicit click=apply setting. Both are
      // one-step experiences where a selection commits on click, so seeding one
      // is a head start rather than a hidden extra step the customer must undo.
      const experience =
        typeof conductor._getConfiguratorExperience === 'function'
          ? conductor._getConfiguratorExperience()
          : 'classic';
      const clickApply =
        typeof conductor._isClickApplyEnabled === 'function'
          ? conductor._isClickApplyEnabled()
          : false;
      if (experience !== 'apo_style' && !clickApply) return false;

      // NEVER OVERRIDE THE CUSTOMER. If this category already has a choice --
      // browsing or applied -- the grid is being re-rendered around a decision
      // they already made (re-opening their own wood, a cascade re-render),
      // and replacing it with the cheapest option would silently undo them.
      // Re-selecting the same wood species clears the category first, so the
      // genuine "show me the finishes" case still reaches the code below.
      const browsing = conductor.getBrowsingSelections?.();
      const applied = conductor.getAppliedSelections?.();
      if (
        browsing?.[categoryId]?.variantId ||
        applied?.[categoryId]?.variantId
      ) {
        return false;
      }

      const categoryElement = DOM.getElement(
        `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
      );
      if (!categoryElement) return false;

      const ppd = getPPD();
      const variantMap = ppd && ppd.variantIdToVariantMap;

      // Only tiles the customer could actually click: a forbidden or
      // material-filtered tile is not a legal auto-pick, and an already-active
      // one means a selection exists after all.
      const tiles = Array.from(
        categoryElement.querySelectorAll('[data-variant-id]')
      ).filter((tile) => {
        if (tile.classList.contains('forbidden-disabled')) return false;
        if (tile.classList.contains('material-filtered-out')) return false;
        if (tile.style.display === 'none') return false;
        return true;
      });
      if (!tiles.length) return false;

      const priceOf = (tile) => {
        const id = tile.getAttribute('data-variant-id');
        const variant =
          variantMap &&
          (variantMap.get(String(id)) || variantMap.get(Number(id)));
        const raw = variant && variant.price;
        if (raw === undefined || raw === null || raw === '') return NaN;
        return parseFloat(String(raw).replace(/[$,]/g, ''));
      };

      let cheapest = null;
      let cheapestPrice = Infinity;
      tiles.forEach((tile) => {
        const price = priceOf(tile);
        // A tile whose price cannot be resolved is skipped rather than treated
        // as free -- guessing 0 would make an unpriced variant always "win".
        if (!Number.isFinite(price)) return;
        if (price < cheapestPrice) {
          cheapestPrice = price;
          cheapest = tile;
        }
      });
      if (!cheapest) return false;

      // Reuse the real click path rather than re-implementing selection: the
      // delegated handler in md-config-core-event-handler.js resolves the
      // product, runs the forbidden checks and enters browsing exactly as it
      // would for a human click, so provenance and the cascade are inherited.
      isDev() &&
        console.log(
          `${METHOD_SIG}: auto-selecting cheapest variant in ${categoryId}`,
          {
            variantId: cheapest.getAttribute('data-variant-id'),
            price: cheapestPrice,
            candidates: tiles.length,
          }
        );
      cheapest.click();
      return true;
    } catch (error) {
      isDev() && console.error(`${METHOD_SIG} failed:`, error);
      return false;
    }
  };

  /**
   * Render variant browser by reordering/filtering DOM tiles based on provided variant list.
   * CCP-COMPLIANT: UI module performing DOM manipulation based on conductor-delegated data.
   *
   * @param {string} productId - Product ID whose variant grid to update
   * @param {string} categoryId - Category ID for context
   * @param {Array|null} variantsToRender - Ordered array of variant objects to display
   */
  uiUpdater.renderVariantBrowserFromList = function (
    productId,
    categoryId,
    variantsToRender
  ) {
    const METHOD_SIG = 'UIUpdater.renderVariantBrowserFromList';

    try {
      if (!variantsToRender || variantsToRender.length === 0) {
        isDev() &&
          console.log(
            `${METHOD_SIG}: No variants to render for product ${productId} - skipping`
          );
        return;
      }

      // Find the variant grid in the DOM for this category
      const categoryElement = DOM.getElement(
        `.murphy-door-configurator__category-button[data-category="${categoryId}"]`
      );
      if (!categoryElement) {
        isDev() &&
          console.warn(
            `${METHOD_SIG}: Category element not found for ${categoryId}`
          );
        return;
      }

      const variantGrid = categoryElement.querySelector(SELECTORS.VARIANT_GRID);
      if (!variantGrid) {
        isDev() &&
          console.log(
            `${METHOD_SIG}: No variant grid found for category ${categoryId} - variant view may not be open`
          );
        return;
      }

      // Build set of variant IDs to render for quick lookup
      const variantIdsToRender = new Set(
        variantsToRender.map((v) => String(v.id || v))
      );

      // Get all variant tile elements in the grid
      const variantTiles = variantGrid.querySelectorAll('[data-variant-id]');
      if (variantTiles.length === 0) {
        isDev() &&
          console.warn(
            `${METHOD_SIG}: No variant tiles found in grid for category ${categoryId}`
          );
        return;
      }

      // Create a map of variant ID -> DOM element for O(1) lookup
      const tileMap = new Map();
      variantTiles.forEach((tile) => {
        const variantId = tile.getAttribute('data-variant-id');
        if (variantId) {
          tileMap.set(String(variantId), tile);
        }
      });

      // 🎯 DRY FIX: Get selected variant from browsing state to apply --active class
      // This matches the pattern used in generateCompositeVariantViewHTML (lines 1746-1752)
      //
      // ...falling back to the APPLIED selection. Browsing state is cleared the
      // moment a choice is committed, so on an applied category this read
      // returned undefined and EVERY tile came back unhighlighted -- the
      // customer's own answer was the one tile with no marking on it.
      //
      // Latent until 2026-08-27: applying used to blow the whole variant view
      // away (see the UI_CATEGORY_AND_VARIANTS_NEED_RERENDER handler), so
      // nobody ever saw the applied view long enough to notice it was blank.
      // Fixing that made this reachable, and Ryan asked for the other half in
      // the same breath: "Keep them where they're at with their selection
      // bolder border."
      //
      // 🚨 BROWSING WINS. Order matters and must not be flipped: while the
      // customer is looking at a DIFFERENT finish than the one committed, the
      // highlight has to follow what they are looking at, not what is saved.
      const conductor = window.MurphyDoorConfigurator.conductor;
      const browsingSelections = conductor?.getBrowsingSelections?.();
      const appliedSelections = conductor?.getAppliedSelections?.();
      const selectedVariantId =
        browsingSelections?.[categoryId]?.variantId ??
        appliedSelections?.[categoryId]?.variantId;

      // 🚨 THE MATERIAL SOURCE KEEPS ITS OWN ORDER. Ryan, UAT 2026-08-27:
      // "When I select one of the finish options, they shift around to put the
      // one that was selected into first position of the list. This is not
      // needed and is a confusing UX. Keep their positions the same. The
      // animated and highlighted ring around them is sufficient to let the user
      // know it was applied."
      //
      // Reproduced: picking Cherry Stain (4th of 7) moved it to 1st and pushed
      // every other finish down a slot, under the customer's cursor.
      //
      // WHY IT ONLY MISBEHAVES HERE. The sort is getSortedVariantsForProduct's
      // "matches first", where a match means "agrees with the applied material
      // preference". On a DEPENDENT category that is real information -- it
      // floats the casings/entablatures compatible with the chosen wood to the
      // top of a long list. On the material SOURCE, the applied preference IS
      // the customer's own pick, so "matches first" degrades to "the tile you
      // just clicked, first": a reshuffle carrying no information the --active
      // ring is not already giving, and it moves the neighbours they were
      // comparing against.
      //
      // Same guard, same constant and same reasoning as the MATERIAL SOURCE
      // GUARD in renderVariantsWithMaterialAwareness above, which already
      // refuses to FILTER this category by its own preferences. This refuses to
      // SORT it by them. Scoped rather than global on purpose: killing the
      // reorder everywhere would take the compatibility-first surfacing off the
      // dependent categories, which nobody has asked for and which is the
      // feature this code was written to provide.
      //
      // Visibility and the --active class are still applied in both branches --
      // only the DOM move is skipped, so filtered/simplified mode keeps working.
      // Array.isArray, not `|| []`. A truthy-but-not-an-array CONSTANTS entry
      // sails past `||` and then throws on .indexOf -- straight into this
      // function's own try/catch, which swallows it and aborts the ENTIRE
      // variant render, tile visibility and active ring included. Caught by
      // tests/assets/variant-view-persistence.test.js on the first run.
      const materialSourceCategories = CONSTANTS.MATERIAL_SOURCE_CATEGORIES;
      const preserveTileOrder =
        Array.isArray(materialSourceCategories) &&
        materialSourceCategories.indexOf(categoryId) !== -1;

      // Reorder tiles: append in the order specified by variantsToRender.
      //
      // 🚨 ONLY WHEN THE ORDER IS ACTUALLY WRONG (bug A, staging UAT
      // 2026-10-02). appendChild of an attached node is a remove + insert, and
      // removing the focused tile blurs it to <body> even though it is back in
      // the same task. This render runs on every pick inside a drill-in
      // (Locking Systems -> Book Latch -> Space on "With Book"), so a keyboard
      // user lost focus on every variant pick. Already-sorted tiles (the
      // common case: the sort did not change) are now left where they are;
      // when a move IS needed the GRID FOCUS KEEPER restores focus to the same
      // tile (MOVED, NOT REPLACED in restoreGridFocusIfLost).
      const orderedTiles = [];
      variantsToRender.forEach((variant) => {
        const tile = tileMap.get(String(variant.id || variant));
        if (tile) orderedTiles.push(tile);
      });
      if (!preserveTileOrder && !isInRelativeOrder(variantGrid, orderedTiles)) {
        orderedTiles.forEach((tile) => variantGrid.appendChild(tile));
      }
      let reorderedCount = 0;
      variantsToRender.forEach((variant) => {
        const variantId = String(variant.id || variant);
        const tile = tileMap.get(variantId);
        if (tile) {
          tile.style.display = '';
          reorderedCount++;

          // 🎯 DRY FIX: Apply --active class to selected variant (matches composite product pattern)
          uiUpdater._setTileActive(
            tile,
            !!selectedVariantId && String(selectedVariantId) === variantId
          );
        }
      });

      // Hide tiles not in the variantsToRender list (for simplified/filtered mode)
      tileMap.forEach((tile, variantId) => {
        if (!variantIdsToRender.has(variantId)) {
          tile.style.display = 'none';
          // Remove active class from hidden tiles
          uiUpdater._setTileActive(tile, false);
        }
      });

      // Re-assert rings on every OTHER applied category. This is what covers
      // PAGE LOAD: restoring a ?config= link applies a dozen categories without
      // any of them passing through updateSelectionDisplay, so nothing else
      // would ring them. Measured on a restored link -- 10 completed categories
      // sat at ring 0 until this ran, then every one of them showed exactly 1.
      //
      // `categoryId` is excluded because the loop above has already set this
      // card's ring from browsing ?? applied, which is the more specific answer.
      uiUpdater.syncAppliedTileRings(categoryId);

      isDev() &&
        console.log(
          `${METHOD_SIG}: Rendered ${reorderedCount}/${tileMap.size} variant tiles for product ${productId}`,
          {
            categoryId,
            totalTiles: tileMap.size,
            visibleTiles: reorderedCount,
            selectedVariantId,
          }
        );
    } catch (error) {
      isDev() &&
        console.error(`${METHOD_SIG}: Error rendering variant browser`, error);
    }
  };

  // 🎯 CCP GOLDEN STANDARD: Add cleanup function to module API
  uiUpdater.cleanup = cleanup;

  /**
   * Get current module bootstrap state for debugging.
   * PATTERN 18: Returns frozen state object.
   * @returns {Object} Current module state (frozen)
   */
  uiUpdater.getBootstrapState = function () {
    return Object.freeze({
      isInitialized,
      bootstrapComplete,
      pendingCallCount: pendingCalls.length,
      cleanupHandlerCount: cleanupHandlers.length,
    });
  };

  // ============================================================================
  // EVENT SUBSCRIPTIONS: Conductor-published UI delegation events
  // The conductor publishes these instead of calling uiUpdater methods directly,
  // completing the CCP event-driven decoupling for unapply flows.
  // ============================================================================
  if (CoreEventBus && CONSTANTS?.EVENTS) {
    // ========================================================================
    // LOAD-TIME RING SYNC — rings the answers restored from a ?config= link.
    //
    // 🚨 THE HOOK IS MEASURED, NOT GUESSED, AND THE OBVIOUS ONES ARE WRONG.
    // Traced a full restore load from document-start (1653 events):
    //   ~2.0s  first moment applied state AND tiles both exist
    //   ~4.4s  load settles: appliedN 4, tiles 5, rings 0
    // SELECTION_APPLIED fires for every restored category BEFORE its tiles are
    // in the DOM, so subscribing to it rings nothing -- which is exactly why
    // the restore path had no rings at all. A call from the apo_style
    // reveal-all in md-config-ui-navigation-manager.js has the same problem
    // and was tried and reverted: it runs before the restore populates state.
    //
    // SUPPORT_PROGRESS_CALCULATION_COMPLETED is late enough on every observed
    // run (it fired 4x AFTER both were ready) and, unlike the module-ready
    // events firing at the same moment, it is about configurator state rather
    // than an unrelated module's lifecycle.
    //
    // 🚨 IT DOES NOT STOP AT THE FIRST SUCCESS, and that was a real bug on the
    // way here. Latching on "marked > 0" retired the subscription as soon as
    // ONE category was ringed, and the cards do not all render at the same
    // moment: measured on a restored link, Door Depth ringed while Jamb Depth
    // was still tile-less, so it stayed blank even though a manual sync a
    // moment later marked it instantly. Every attempt in the window runs.
    //
    // Running it repeatedly is safe. It is additive (clearing is
    // _clearTileActiveForSelection's sole job), it skips any category with a
    // browsing selection, and the clear-then-highlight pair in
    // updateSelectionDisplay is SYNCHRONOUS -- a bus event cannot interleave
    // between them, so this cannot double-ring the card being clicked.
    //
    // The attempt cap is what confines it to load: this event also fires on
    // ordinary interaction, and the cap retires the subscription long before
    // the customer has configured a door.
    // ========================================================================
    let loadRingSyncAttempts = 0;
    cleanupHandlers.push(
      CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SUPPORT_PROGRESS_CALCULATION_COMPLETED,
        () => {
          if (loadRingSyncAttempts >= 12) return;
          loadRingSyncAttempts++;
          try {
            const marked = uiUpdater.syncAppliedTileRings();
            if (marked > 0) {
              isDev() &&
                console.log(
                  `[UI_UPDATER] load ring sync: ringed ${marked} restored categories`
                );
            }
          } catch (error) {
            isDev() &&
              console.error('[UI_UPDATER] load ring sync failed:', error);
          }
        }
      )
    );

    // Subscribe to UI_USER_UNAPPLY_REQUESTED (conductor publishes this instead of direct method call)
    cleanupHandlers.push(
      CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_USER_UNAPPLY_REQUESTED,
        (eventData) => {
          const { categoryId } = eventData || {};
          if (!categoryId) return;
          try {
            uiUpdater.handleUserUnapply(categoryId);
          } catch (error) {
            isDev() &&
              console.error(
                '[UI_UPDATER] handleUserUnapply failed for "' +
                  categoryId +
                  '":',
                error
              );
          }
        }
      )
    );

    // ENFORCED_PAIR customer notice (published by the cascade coordinator)
    cleanupHandlers.push(
      CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_ENFORCED_PAIR_NOTICE_REQUESTED,
        (eventData) => {
          const { categoryId, message, reveal, variantId } = eventData || {};
          showEnforcedPairNotice(
            categoryId,
            message,
            reveal === true,
            variantId
          );
        }
      )
    );
    // A notice is about the selection that was there when it was written;
    // once that category is cleared or unapplied it is stale. (The enforced
    // clear itself re-publishes its notice AFTER its STATE_SELECTION_CLEARED.)
    [
      CONSTANTS.EVENTS.STATE_SELECTION_CLEARED,
      CONSTANTS.EVENTS.SELECTION_USER_UNAPPLY_COMPLETED,
    ].forEach((eventType) => {
      cleanupHandlers.push(
        CoreEventBus.subscribe(eventType, (eventData) => {
          if (eventData && eventData.categoryId) {
            clearEnforcedPairNotice(eventData.categoryId);
          }
        })
      );
    });
    // ...and it goes away once ANYTHING lands in that category. Auto-applied
    // fills count too: a notice saying "please choose X" is wrong the moment
    // X is filled, whoever filled it. The one exception is an automatic
    // re-apply of the very variant a force-apply notice is explaining (the
    // force's own apply runs BEFORE its notice is published, so it never hits
    // this; a later cascade re-landing the same variant must not erase it).
    cleanupHandlers.push(
      CoreEventBus.subscribe(
        CONSTANTS.EVENTS.SELECTION_VARIANT_APPLIED,
        (eventData) => {
          if (!eventData || !eventData.categoryId) return;
          if (eventData.auto_applied === true) {
            const notice = findEnforcedPairNotice(eventData.categoryId);
            const explained = notice
              ? notice.getAttribute('data-notice-variant')
              : '';
            if (explained && explained === String(eventData.variantId)) return;
          }
          clearEnforcedPairNotice(eventData.categoryId);
        }
      )
    );

    // "Build this" settled: ring each preset category's applied tile the way
    // a manual click's browsing pass does (md-config-support-build-preset.js).
    cleanupHandlers.push(
      CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_APPLIED_TILE_RINGS_SYNC_REQUESTED,
        (eventData) => {
          uiUpdater.resyncTileRingsForCategories(eventData?.categoryIds);
        }
      )
    );

    // Subscribe to UI_PROGRESS_UPDATE_REQUESTED (conductor publishes this instead of direct method call)
    cleanupHandlers.push(
      CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_PROGRESS_UPDATE_REQUESTED,
        () => {
          try {
            uiUpdater.updateProgressAndSummary();
          } catch (error) {
            isDev() &&
              console.error(
                '[UI_UPDATER] updateProgressAndSummary failed:',
                error
              );
          }
        }
      )
    );

    // Subscribe to UI_MATERIAL_REMAP_CONFLICT -- apo_style only, published by
    // md-config-bl-material-remap-coordinator.js when a late wood/finish change
    // leaves one or more applied selections with no equivalent in the new
    // material.
    //
    // Deliberately NOT routed through the generic UI_SHOW_MODAL path: that
    // helper hardcodes the clear-modal's own button selectors
    // (.clear-modal-cancel / .clear-modal-proceed) for its cancel/proceed
    // styles, so reusing it would mean editing the shared modal plumbing that
    // "Start over" and Share also depend on. A dedicated subscriber keeps the
    // blast radius on this feature.
    cleanupHandlers.push(
      CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_MATERIAL_REMAP_CONFLICT,
        (eventData) => {
          try {
            const modal = DOM.getElement(
              '.murphy-door-configurator__material-remap-modal'
            );
            if (!modal) return;

            // Name the options that cannot come across, so the warning is
            // specific rather than "something broke". textContent, never
            // innerHTML -- these strings come from product titles.
            // Lists every category the change will clear, not just the ones
            // with no equivalent in the new material -- the reset is now all
            // styling components, so naming only a subset would understate it.
            // Falls back to the unmappable list for older payloads.
            const list = modal.querySelector('[data-remap-conflict-list]');
            if (list) {
              list.textContent = '';
              const names =
                Array.isArray(eventData?.affected) && eventData.affected.length
                  ? eventData.affected
                  : (eventData?.unmappable || []).map(
                      (u) => u.productTitle || u.categoryId
                    );
              names.forEach((name) => {
                const li = document.createElement('li');
                li.textContent = name;
                list.appendChild(li);
              });
            }

            modal.classList.remove('hidden');

            const keepBtn = modal.querySelector(
              '.murphy-door-configurator__material-remap-keep'
            );
            const restartBtn = modal.querySelector(
              '.murphy-door-configurator__material-remap-restart'
            );

            // Bind once. Handlers are stored on the element so a second
            // conflict in the same session cannot stack duplicate listeners
            // and fire the action twice.
            const bind = (btn, action) => {
              if (!btn) return;
              if (btn.__mdRemapHandler) {
                btn.removeEventListener('click', btn.__mdRemapHandler);
              }
              const handler = (e) => {
                e.preventDefault();
                modal.classList.add('hidden');
                CoreEventBus.publish(action, {
                  timestamp: Date.now(),
                  source: 'ui-updater.materialRemapModal',
                });
              };
              btn.__mdRemapHandler = handler;
              btn.addEventListener('click', handler);
            };

            bind(keepBtn, CONSTANTS.EVENTS.UI_MATERIAL_REMAP_KEEP_EXISTING);
            bind(restartBtn, CONSTANTS.EVENTS.UI_MATERIAL_REMAP_START_FRESH);

            // Move focus into the dialog. Defaults to the non-destructive
            // choice so an accidental Enter keeps the customer's work rather
            // than wiping it.
            if (keepBtn && typeof keepBtn.focus === 'function') {
              keepBtn.focus();
            }
          } catch (error) {
            isDev() &&
              console.error(
                '[UI_UPDATER] material remap conflict modal failed:',
                error
              );
          }
        }
      )
    );

    // Subscribe to UI_STALE_SELECTION_DETECTED (cascade coordinator publishes when material preferences change)
    // Adds 'is-stale' CSS class to applied selection view in the affected categories.
    cleanupHandlers.push(
      CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_STALE_SELECTION_DETECTED,
        (eventData) => {
          const { categoryIds } = eventData || {};
          if (!categoryIds || !Array.isArray(categoryIds)) return;
          try {
            categoryIds.forEach((categoryId) => {
              const category = DOM.getElement(
                '.murphy-door-configurator__category-button[data-category="' +
                  categoryId +
                  '"]'
              );
              if (!category) return;
              const appliedView = category.querySelector(
                '.murphy-door-configurator__applied-selection-view'
              );
              if (appliedView) {
                appliedView.classList.add('is-stale');
                isDev() &&
                  console.log(
                    '[UI_UPDATER] Added stale indicator to category "' +
                      categoryId +
                      '"'
                  );
              }
            });
          } catch (error) {
            isDev() &&
              console.error(
                '[UI_UPDATER] stale selection detected handler failed:',
                error
              );
          }
        }
      )
    );

    // Subscribe to UI_STALE_SELECTION_CLEARED (cascade coordinator publishes when user acts on stale category)
    // Removes 'is-stale' CSS class from the affected categories.
    cleanupHandlers.push(
      CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_STALE_SELECTION_CLEARED,
        (eventData) => {
          const { categoryIds } = eventData || {};
          if (!categoryIds || !Array.isArray(categoryIds)) return;
          try {
            categoryIds.forEach((categoryId) => {
              const category = DOM.getElement(
                '.murphy-door-configurator__category-button[data-category="' +
                  categoryId +
                  '"]'
              );
              if (!category) return;
              const appliedView = category.querySelector(
                '.murphy-door-configurator__applied-selection-view'
              );
              if (appliedView) {
                appliedView.classList.remove('is-stale');
                isDev() &&
                  console.log(
                    '[UI_UPDATER] Removed stale indicator from category "' +
                      categoryId +
                      '"'
                  );
              }
            });
          } catch (error) {
            isDev() &&
              console.error(
                '[UI_UPDATER] stale selection cleared handler failed:',
                error
              );
          }
        }
      )
    );

    isDev() &&
      console.log(
        '✅ UI UPDATER: Unapply delegation event subscriptions initialized'
      );
  }

  // PATTERN #16: Mark module as initialized before export
  isInitialized = true;

  // Export the modules
  window.MurphyDoorConfigurator.uiUpdater = uiUpdater;

  // 🎯 CCP GOLDEN STANDARD: Export helper functions for testing and external use
  window.MurphyDoorConfigurator.uiUpdaterHelpers = {
    publishEvent,
    cleanup,
    resolveConfettiAnchor: _resolveConfettiAnchor,
    CONFETTI_ANCHORS,
    promoteAddToCartToStickyBar: _promoteAddToCartToStickyBar,
    demoteAddToCartToRail: _demoteAddToCartToRail,
    reconcileAddToCartHome: _reconcileAddToCartHome,
    stickyBarCanShowAddToCart: _stickyBarCanShowAddToCart,
    // Test seam: the reconcile is a no-op until the celebration arms it, and
    // a jsdom harness has no event bus to publish that celebration through.
    armAddToCartPromotion: (on = true) => {
      _atcPromotionArmed = !!on;
    },
    formatPrice: formatConfiguratorPrice,
    parseWeeksRange: _parseWeeksRange,
    formatDeliveryDateRange: _formatDeliveryDateRange,
    getDeliveryDateRangeCaption: _getDeliveryDateRangeCaption,
    Animations,
    _activeAnimations,
  };

  // Return the module for testing purposes
  return uiUpdater;
})(window);
